import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { PageLayout } from '../components/page-layout';
import { useAuth } from '../contexts/auth-context';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious, PaginationEllipsis } from '../components/ui/pagination';
import { useToast } from '../hooks/use-toast';
import { ReservationActionMenu } from '../components/hotel/reservation-action-menu';
import { TranslatedText } from '../components/translated-text';
import { useTranslation } from '../hooks/useTranslation';
import { apiService } from '../lib/api';
import { getCompanyId, getApiUrl } from '../lib/config';
import { PaymentInvoicePDFGenerator } from '../lib/payment-invoice-pdf-generator';
import { formatRole } from '../lib/utils';
import { MoonLoader } from '../components/ui/moon-loader';
import RoomAvailabilityCalendar from '../components/room-availability-calendar';
import { CustomerLoanSelector } from '../components/customer-loan-selector';
import { MoreVertical } from 'lucide-react';
import {
  Hotel,
  Bed,
  Users,
  Calendar,
  DollarSign,
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  Key,
  Wrench,
  Sparkles,
  Receipt,
  CreditCard,
  Star,
  Image as ImageIcon,
  FileText,
  Loader2,
  TrendingUp,
  MessageSquare,
  TrendingDown,
  BarChart3,
  Circle,
  PieChart as PieChartIcon,
  Activity,
  Link2Off,
  Home,
  UserCheck,
  UserX,
  UserPlus,
  AlertCircle,
  AlertTriangle,
  RefreshCw,
  CheckCircle2,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Users2,
  Clock,
  Tag,
  Gift,
  FileBarChart,
  Building2,
  Download,
  Send,
  Check,
  Upload,
  Package,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
} from 'recharts';
import { toast } from 'sonner';

// Currency formatting function - default to FRW
const formatCurrency = (amount: number | string, currency: string = 'FRW'): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '0';
  
  if (currency === 'FRW' || currency === 'RWF' || currency === 'Frw' || currency === 'frw') {
    // Format Rwandan Franc without decimals
    return `${Math.round(numAmount).toLocaleString('en-US')} FRW`;
  } else if (currency === 'USD') {
    // Format USD with decimals and $ symbol
    return `$${numAmount.toFixed(2)}`;
  } else {
    // Format other currencies with decimals
    return `${currency} ${numAmount.toFixed(2)}`;
  }
};

// Simple currency conversion rates (FRW as base)
const CURRENCY_RATES: Record<string, number> = {
  'FRW': 1,
  'RWF': 1,
  'Frw': 1, // Handle lowercase variant
  'frw': 1,
  'USD': 0.00077, // 1 FRW = 0.00077 USD (approximately 1300 FRW = 1 USD)
  'EUR': 0.00071,
  'GBP': 0.00061,
};

// Convert amount from one currency to another
const convertCurrency = (amount: number, fromCurrency: string, toCurrency: string): number => {
  if (fromCurrency === toCurrency) return amount;
  
  // Normalize currency codes
  const normalizeCode = (code: string) => {
    if (code === 'Frw' || code === 'frw' || code === 'RWF') return 'FRW';
    return code;
  };
  
  const normalizedFrom = normalizeCode(fromCurrency);
  const normalizedTo = normalizeCode(toCurrency);
  
  if (normalizedFrom === normalizedTo) return amount;
  
  const fromRate = CURRENCY_RATES[normalizedFrom] || CURRENCY_RATES['FRW'];
  const toRate = CURRENCY_RATES[normalizedTo] || CURRENCY_RATES['FRW'];
  
  // Convert to FRW first, then to target currency
  const inFRW = amount / fromRate;
  return inFRW * toRate;
};

// Format currency with display currency preference
const formatCurrencyDisplay = (amount: number | string, originalCurrency: string = 'FRW', displayCurrency: 'FRW' | 'USD' = 'FRW'): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return formatCurrency(0, displayCurrency);
  
  // Normalize the original currency
  const normalizeCode = (code: string) => {
    if (code === 'Frw' || code === 'frw' || code === 'RWF') return 'FRW';
    return code;
  };
  
  const normalizedOriginal = normalizeCode(originalCurrency);
  
  // If display currency matches original, just format it
  if (normalizedOriginal === displayCurrency) {
    return formatCurrency(numAmount, displayCurrency);
  }
  
  // Convert and format
  const converted = convertCurrency(numAmount, normalizedOriginal, displayCurrency);
  return formatCurrency(converted, displayCurrency);
};

// Format with currency conversion display
const formatWithConversion = (amount: number, originalCurrency: string, displayCurrency: string): string => {
  if (originalCurrency === displayCurrency) {
    return formatCurrency(amount, displayCurrency);
  }
  
  const converted = convertCurrency(amount, originalCurrency, displayCurrency);
  return `${formatCurrency(converted, displayCurrency)} (${formatCurrency(amount, originalCurrency)})`;
};

// Format with FRW approximation for USD amounts
const formatCurrencyWithApprox = (amount: number, currency: string = 'FRW'): string => {
  // Just return the formatted currency without approximation
  return formatCurrency(amount, currency);
};

// Chart color palette for BI Dashboard
const CHART_COLORS = {
  primary: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'],
  revenue: ['#3b82f6', '#10b981', '#f59e0b'],
  status: {
    paid: '#10b981',
    partial: '#f59e0b',
    unpaid: '#ef4444',
  },
  reservation: {
    pending: '#f59e0b',
    confirmed: '#3b82f6',
    checkedIn: '#10b981',
    checkedOut: '#8b5cf6',
    cancelled: '#ef4444',
  },
};

// Comprehensive countries list for guest form
const COUNTRIES = [
  // African Countries (prioritized)
  { value: 'RW', label: 'Rwanda' },
  { value: 'KE', label: 'Kenya' },
  { value: 'UG', label: 'Uganda' },
  { value: 'TZ', label: 'Tanzania' },
  { value: 'ET', label: 'Ethiopia' },
  { value: 'ZA', label: 'South Africa' },
  { value: 'NG', label: 'Nigeria' },
  { value: 'GH', label: 'Ghana' },
  { value: 'EG', label: 'Egypt' },
  { value: 'MA', label: 'Morocco' },
  { value: 'DZ', label: 'Algeria' },
  { value: 'TN', label: 'Tunisia' },
  { value: 'SN', label: 'Senegal' },
  { value: 'CI', label: 'Côte d\'Ivoire' },
  { value: 'CM', label: 'Cameroon' },
  { value: 'AO', label: 'Angola' },
  { value: 'SD', label: 'Sudan' },
  { value: 'MZ', label: 'Mozambique' },
  { value: 'MG', label: 'Madagascar' },
  { value: 'ZM', label: 'Zambia' },
  { value: 'ZW', label: 'Zimbabwe' },
  { value: 'ML', label: 'Mali' },
  { value: 'BF', label: 'Burkina Faso' },
  { value: 'NE', label: 'Niger' },
  { value: 'TD', label: 'Chad' },
  { value: 'SO', label: 'Somalia' },
  { value: 'ER', label: 'Eritrea' },
  { value: 'DJ', label: 'Djibouti' },
  { value: 'BI', label: 'Burundi' },
  { value: 'CD', label: 'Democratic Republic of the Congo' },
  { value: 'CG', label: 'Republic of the Congo' },
  { value: 'CF', label: 'Central African Republic' },
  { value: 'SS', label: 'South Sudan' },
  { value: 'LY', label: 'Libya' },
  { value: 'MR', label: 'Mauritania' },
  { value: 'GM', label: 'Gambia' },
  { value: 'GW', label: 'Guinea-Bissau' },
  { value: 'GN', label: 'Guinea' },
  { value: 'SL', label: 'Sierra Leone' },
  { value: 'LR', label: 'Liberia' },
  { value: 'TG', label: 'Togo' },
  { value: 'BJ', label: 'Benin' },
  { value: 'GA', label: 'Gabon' },
  { value: 'GQ', label: 'Equatorial Guinea' },
  { value: 'ST', label: 'São Tomé and Príncipe' },
  { value: 'BW', label: 'Botswana' },
  { value: 'NA', label: 'Namibia' },
  { value: 'LS', label: 'Lesotho' },
  { value: 'SZ', label: 'Eswatini' },
  { value: 'MW', label: 'Malawi' },
  { value: 'MU', label: 'Mauritius' },
  { value: 'SC', label: 'Seychelles' },
  { value: 'KM', label: 'Comoros' },
  { value: 'CV', label: 'Cape Verde' },
  // Major Countries
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'ES', label: 'Spain' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'BE', label: 'Belgium' },
  { value: 'CH', label: 'Switzerland' },
  { value: 'AT', label: 'Austria' },
  { value: 'SE', label: 'Sweden' },
  { value: 'NO', label: 'Norway' },
  { value: 'DK', label: 'Denmark' },
  { value: 'FI', label: 'Finland' },
  { value: 'PL', label: 'Poland' },
  { value: 'PT', label: 'Portugal' },
  { value: 'GR', label: 'Greece' },
  { value: 'IE', label: 'Ireland' },
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'JP', label: 'Japan' },
  { value: 'CN', label: 'China' },
  { value: 'IN', label: 'India' },
  { value: 'KR', label: 'South Korea' },
  { value: 'SG', label: 'Singapore' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'TH', label: 'Thailand' },
  { value: 'VN', label: 'Vietnam' },
  { value: 'PH', label: 'Philippines' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'BR', label: 'Brazil' },
  { value: 'MX', label: 'Mexico' },
  { value: 'AR', label: 'Argentina' },
  { value: 'CL', label: 'Chile' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Peru' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'IL', label: 'Israel' },
  { value: 'TR', label: 'Turkey' },
  { value: 'RU', label: 'Russia' },
  { value: 'UA', label: 'Ukraine' },
  { value: 'PK', label: 'Pakistan' },
  { value: 'BD', label: 'Bangladesh' },
  { value: 'LK', label: 'Sri Lanka' },
  { value: 'NP', label: 'Nepal' },
  { value: 'MM', label: 'Myanmar' },
  { value: 'KH', label: 'Cambodia' },
  { value: 'LA', label: 'Laos' },
  { value: 'MN', label: 'Mongolia' },
  { value: 'KZ', label: 'Kazakhstan' },
  { value: 'UZ', label: 'Uzbekistan' },
  { value: 'IQ', label: 'Iraq' },
  { value: 'IR', label: 'Iran' },
  { value: 'AF', label: 'Afghanistan' },
  { value: 'JO', label: 'Jordan' },
  { value: 'LB', label: 'Lebanon' },
  { value: 'SY', label: 'Syria' },
  { value: 'YE', label: 'Yemen' },
  { value: 'OM', label: 'Oman' },
  { value: 'KW', label: 'Kuwait' },
  { value: 'QA', label: 'Qatar' },
  { value: 'BH', label: 'Bahrain' },
  { value: 'CY', label: 'Cyprus' },
  { value: 'MT', label: 'Malta' },
  { value: 'IS', label: 'Iceland' },
  { value: 'LU', label: 'Luxembourg' },
  { value: 'CZ', label: 'Czech Republic' },
  { value: 'HU', label: 'Hungary' },
  { value: 'RO', label: 'Romania' },
  { value: 'BG', label: 'Bulgaria' },
  { value: 'HR', label: 'Croatia' },
  { value: 'SI', label: 'Slovenia' },
  { value: 'SK', label: 'Slovakia' },
  { value: 'EE', label: 'Estonia' },
  { value: 'LV', label: 'Latvia' },
  { value: 'LT', label: 'Lithuania' },
  { value: 'Other', label: 'Other' },
].sort((a, b) => a.label.localeCompare(b.label));

// Hotel service types list
const SERVICE_TYPES = [
  { value: 'room_service', label: 'Room Service' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'dry_cleaning', label: 'Dry Cleaning' },
  { value: 'spa', label: 'Spa & Wellness' },
  { value: 'massage', label: 'Massage' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'bar', label: 'Bar & Beverages' },
  { value: 'minibar', label: 'Minibar' },
  { value: 'parking', label: 'Parking' },
  { value: 'airport_transfer', label: 'Airport Transfer' },
  { value: 'taxi', label: 'Taxi Service' },
  { value: 'car_rental', label: 'Car Rental' },
  { value: 'tour', label: 'Tour & Excursion' },
  { value: 'concierge', label: 'Concierge Service' },
  { value: 'business_center', label: 'Business Center' },
  { value: 'printing', label: 'Printing & Copying' },
  { value: 'fax', label: 'Fax Service' },
  { value: 'internet', label: 'Internet/WiFi' },
  { value: 'phone', label: 'Phone Service' },
  { value: 'gym', label: 'Gym & Fitness' },
  { value: 'pool', label: 'Pool Access' },
  { value: 'babysitting', label: 'Babysitting' },
  { value: 'pet_service', label: 'Pet Service' },
  { value: 'luggage_storage', label: 'Luggage Storage' },
  { value: 'late_checkout', label: 'Late Checkout' },
  { value: 'early_checkin', label: 'Early Check-in' },
  { value: 'room_upgrade', label: 'Room Upgrade' },
  { value: 'extra_bed', label: 'Extra Bed' },
  { value: 'crib', label: 'Crib/Cot' },
  { value: 'extra_towel', label: 'Extra Towels' },
  { value: 'extra_pillow', label: 'Extra Pillows' },
  { value: 'iron', label: 'Iron & Ironing Board' },
  { value: 'hair_dryer', label: 'Hair Dryer' },
  { value: 'safe', label: 'Safe Rental' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snacks', label: 'Snacks' },
  { value: 'room_decoration', label: 'Room Decoration' },
  { value: 'flowers', label: 'Flowers' },
  { value: 'gift_basket', label: 'Gift Basket' },
  { value: 'champagne', label: 'Champagne Service' },
  { value: 'cake', label: 'Cake Service' },
  { value: 'photography', label: 'Photography' },
  { value: 'videography', label: 'Videography' },
  { value: 'event_planning', label: 'Event Planning' },
  { value: 'meeting_room', label: 'Meeting Room' },
  { value: 'conference', label: 'Conference Service' },
  { value: 'banquet', label: 'Banquet Service' },
  { value: 'catering', label: 'Catering' },
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'medical', label: 'Medical Service' },
  { value: 'pharmacy', label: 'Pharmacy' },
  { value: 'other', label: 'Other' },
].sort((a, b) => a.label.localeCompare(b.label));

// Skeleton Components
const RoomCardSkeleton = () => (
  <Card className="group relative overflow-hidden border-2 animate-pulse">
    <div className="relative h-48 bg-gradient-to-br from-muted to-muted/50" />
    <CardHeader className="pb-3">
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-2">
          <div className="h-5 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
        <div className="h-8 w-8 bg-muted rounded" />
      </div>
    </CardHeader>
    <CardContent className="flex-1 flex flex-col pt-0 pb-3">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="h-4 bg-muted rounded w-24" />
          <div className="h-4 bg-muted rounded w-20" />
        </div>
        <div className="h-4 bg-muted rounded w-32" />
        <div className="h-4 bg-muted rounded w-28" />
      </div>
      <div className="flex flex-wrap gap-1.5 pt-2 border-t mt-3">
        <div className="h-5 bg-muted rounded w-16" />
        <div className="h-5 bg-muted rounded w-20" />
        <div className="h-5 bg-muted rounded w-14" />
      </div>
    </CardContent>
  </Card>
);

const ReservationCardSkeleton = () => (
  <Card className="flex flex-col border-l-4 border-l-muted animate-pulse">
    <CardHeader className="pb-3">
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-5 bg-muted rounded w-2/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="h-5 bg-muted rounded w-16" />
          <div className="h-7 w-7 bg-muted rounded" />
        </div>
      </div>
    </CardHeader>
    <CardContent className="flex-1 flex flex-col pt-0 pb-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div className="h-4 bg-muted rounded w-20" />
        <div className="h-4 bg-muted rounded w-24" />
        <div className="h-4 bg-muted rounded w-16" />
        <div className="h-5 bg-muted rounded w-20" />
      </div>
    </CardContent>
  </Card>
);

const GuestCardSkeleton = () => (
  <Card className="animate-pulse">
    <CardHeader>
      <div className="flex justify-between items-start">
        <div className="space-y-2 flex-1">
          <div className="h-6 bg-muted rounded w-3/4" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
        <div className="h-8 w-8 bg-muted rounded" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="space-y-2">
        <div className="h-4 bg-muted rounded" />
        <div className="h-4 bg-muted rounded w-5/6" />
        <div className="h-4 bg-muted rounded w-4/6" />
      </div>
    </CardContent>
  </Card>
);

const StatCardSkeleton = () => (
  <Card className="bg-gradient-to-br from-muted/50 to-muted/30 border-2 animate-pulse">
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <div className="h-4 bg-muted rounded w-24" />
      <div className="h-5 w-5 bg-muted rounded" />
    </CardHeader>
    <CardContent>
      <div className="h-8 bg-muted rounded w-20 mb-2" />
      <div className="h-3 bg-muted rounded w-32" />
    </CardContent>
  </Card>
);

export default function HotelPage() {
  const { toast } = useToast();
  const { t, tSync } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [companyId, setCompanyId] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const companyIdRef = useRef<string>('');
  
  // Currency display state
  const [displayCurrency, setDisplayCurrency] = useState<'FRW' | 'USD'>('FRW');
  
  // Loading states
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingReports, setLoadingReports] = useState(false);
  const { user } = useAuth();
  
  // Rooms state
  const [rooms, setRooms] = useState<any[]>([]);
  const [roomTypes, setRoomTypes] = useState<any[]>([]);
  const [amenities, setAmenities] = useState<any[]>([]);
  const [conferenceRooms, setConferenceRooms] = useState<any[]>([]);
  const [equipment, setEquipment] = useState<any[]>([]);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [showConferenceRoomDialog, setShowConferenceRoomDialog] = useState(false);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [selectedConferenceRoom, setSelectedConferenceRoom] = useState<any>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<any>(null);
  const [isCreatingConferenceRoom, setIsCreatingConferenceRoom] = useState(false);
  const [isCreatingEquipment, setIsCreatingEquipment] = useState(false);
  const [conferenceRoomForm, setConferenceRoomForm] = useState({
    name: '',
    roomNumber: '',
    capacity: '10',
    floor: '',
    size: '',
    hourlyRate: '',
    dailyRate: '',
    amenities: '',
    description: '',
    isActive: true,
    status: 'AVAILABLE',
  });
  const [equipmentForm, setEquipmentForm] = useState({
    name: '',
    equipmentNumber: '',
    category: '',
    brand: '',
    model: '',
    hourlyRate: '',
    dailyRate: '',
    description: '',
    isActive: true,
    status: 'AVAILABLE',
    quantity: '1',
  });
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [showAmenityDialog, setShowAmenityDialog] = useState(false);
  const [amenityForm, setAmenityForm] = useState({ name: '', description: '', icon: '', category: '' });
  const [isCreatingAmenity, setIsCreatingAmenity] = useState(false);
  const [showRoomTypeDialog, setShowRoomTypeDialog] = useState(false);
  const [roomTypeForm, setRoomTypeForm] = useState({ name: '', description: '', maxCapacity: '2', basePrice: '' });
  const [isCreatingRoomType, setIsCreatingRoomType] = useState(false);
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);
  // Room availability calendar
  const [showAvailabilityCalendar, setShowAvailabilityCalendar] = useState(false);
  const [selectedRoomForCalendar, setSelectedRoomForCalendar] = useState<any>(null);
  // Rooms pagination
  const [roomsPage, setRoomsPage] = useState(1);
  const [roomsPerPage, setRoomsPerPage] = useState(9);
  // Rooms filters
  const [roomsFilters, setRoomsFilters] = useState({
    isActive: 'all', // 'all', 'active', 'inactive'
    reservationStatus: 'all', // 'all', 'reserved', 'notReserved'
    roomTypeId: 'all',
    floor: '',
    search: '', // search by room number
  });
  const [showRoomsFilters, setShowRoomsFilters] = useState(false);
  // Room images state
  const [roomImages, setRoomImages] = useState<File[]>([]);
  const [roomImagePreviews, setRoomImagePreviews] = useState<string[]>([]);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  
  // Conference room images
  const [conferenceRoomImages, setConferenceRoomImages] = useState<File[]>([]);
  const [conferenceRoomImagePreviews, setConferenceRoomImagePreviews] = useState<string[]>([]);
  const [existingConferenceRoomImages, setExistingConferenceRoomImages] = useState<any[]>([]);
  const [uploadingConferenceRoomImages, setUploadingConferenceRoomImages] = useState(false);
  const [deletingConferenceRoomImageId, setDeletingConferenceRoomImageId] = useState<string | null>(null);
  
  // Equipment images
  const [equipmentImages, setEquipmentImages] = useState<File[]>([]);
  const [equipmentImagePreviews, setEquipmentImagePreviews] = useState<string[]>([]);
  const [existingEquipmentImages, setExistingEquipmentImages] = useState<any[]>([]);
  const [uploadingEquipmentImages, setUploadingEquipmentImages] = useState(false);
  const [deletingEquipmentImageId, setDeletingEquipmentImageId] = useState<string | null>(null);
  
  // Room details dialog
  const [showRoomDetailsDialog, setShowRoomDetailsDialog] = useState(false);
  const [selectedRoomDetails, setSelectedRoomDetails] = useState<any>(null);
  const [loadingRoomDetails, setLoadingRoomDetails] = useState(false);
  
  // Image gallery for view room dialog
  const [showViewRoomImageGallery, setShowViewRoomImageGallery] = useState(false);
  const [viewRoomCurrentImageIndex, setViewRoomCurrentImageIndex] = useState(0);
  
  const [roomForm, setRoomForm] = useState({
    roomNumber: '',
    title: '',
    roomTypeId: '',
    floor: '',
    capacity: '2',
    size: '',
    basePricePerNight: '',
    currency: 'RWF', // Default currency
    description: '',
    isActive: true,
    amenityIds: [] as string[],
  });
  const [roomErrors, setRoomErrors] = useState<Record<string, string>>({});

  // Reservations state
  const [reservations, setReservations] = useState<any[]>([]);
  const [guests, setGuests] = useState<any[]>([]);
  const [ratePlans, setRatePlans] = useState<any[]>([]);
  // Reservations pagination
  const [reservationsPage, setReservationsPage] = useState(1);
  const [reservationsPerPage, setReservationsPerPage] = useState(10);
  
  // Group bookings pagination
  const [groupBookingsPage, setGroupBookingsPage] = useState(1);
  const [groupBookingsPerPage, setGroupBookingsPerPage] = useState(10);
  
  // Waitlist pagination
  const [waitlistPage, setWaitlistPage] = useState(1);
  const [waitlistPerPage, setWaitlistPerPage] = useState(10);
  // Reservations filters
  const [reservationsFilters, setReservationsFilters] = useState({
    status: 'active', // 'all', 'active' (PENDING, CONFIRMED, CHECKED_IN), 'PENDING', 'CONFIRMED', 'CHECKED_IN', 'CHECKED_OUT', 'CANCELLED'
    guestSearch: '', // search by guest name
    roomNumber: '', // search by room number
    reservationNumber: '', // search by reservation number
    paymentStatus: 'all', // 'all', 'PAID', 'PARTIAL', 'UNPAID'
  });
  const [showReservationsFilters, setShowReservationsFilters] = useState(false);
  const [showReservationDialog, setShowReservationDialog] = useState(false);
  const [showReservationDetailsDialog, setShowReservationDetailsDialog] = useState(false);
  const [showEditReservationDialog, setShowEditReservationDialog] = useState(false);
  const [selectedReservationDetails, setSelectedReservationDetails] = useState<any>(null);
  const [selectedReservationForEdit, setSelectedReservationForEdit] = useState<any>(null);
  const [isCreatingReservation, setIsCreatingReservation] = useState(false);
  const [isUpdatingReservation, setIsUpdatingReservation] = useState(false);
  const [isDeletingReservation, setIsDeletingReservation] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [reservationForm, setReservationForm] = useState({
    bookingType: 'ROOM' as 'ROOM' | 'CONFERENCE_ROOM' | 'EQUIPMENT' | 'SERVICE' | 'TABLE' | 'EVENT_SPACE' | 'TRANSPORT' | 'OTHER',
    roomId: '',
    bookableItemId: '', // For non-room bookings
    bookableItemType: '', // Type of bookable item
    guestId: '',
    ratePlanId: '',
    groupBookingId: '', // Add group booking ID
    promotionId: '', // Applied promotion (for backward compatibility - will store first promotion or comma-separated)
    promotionIds: [] as string[], // Multiple promotions
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    totalAmount: '',
    depositAmount: '0',
    specialRequests: '',
  });
  const [originalTotalAmount, setOriginalTotalAmount] = useState<string>(''); // Store original amount before discount
  const [amountBreakdown, setAmountBreakdown] = useState<{
    roomPricePerNight: number;
    numberOfNights: number;
    subtotal: number;
    ratePlanAdjustment: number;
    promotionDiscount: number;
    promotionDiscounts: Array<{ id: string; name: string; discount: number }>; // Individual promotion discounts
    finalAmount: number;
    currency: string; // Room currency
  } | null>(null);
  const [reservationErrors, setReservationErrors] = useState<Record<string, string>>({});

  // Guests state
  const [showGuestDialog, setShowGuestDialog] = useState(false);
  const [isCreatingGuest, setIsCreatingGuest] = useState(false);
  const [isDeletingGuest, setIsDeletingGuest] = useState(false);
  // Guests pagination
  const [guestsPage, setGuestsPage] = useState(1);
  const [guestsPerPage, setGuestsPerPage] = useState(9);
  // Guests filters
  const [guestsFilters, setGuestsFilters] = useState({
    search: '', // search by name, email, or phone
    city: '',
    state: '',
  });
  const [showGuestsFilters, setShowGuestsFilters] = useState(false);
  const [guestForm, setGuestForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    idType: '', // 'passport' or 'national_id'
    passportNumber: '',
    idNumber: '',
  });
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});

  // Availability check
  const [availabilityCheck, setAvailabilityCheck] = useState({
    checkInDate: '',
    checkOutDate: '',
    roomTypeId: '',
    numberOfGuests: '',
  });
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);
  // Available rooms pagination
  const [availableRoomsPage, setAvailableRoomsPage] = useState(1);
  const [availableRoomsPerPage, setAvailableRoomsPerPage] = useState(9);
  
  // Available rooms filters
  const [availableRoomsFilters, setAvailableRoomsFilters] = useState({
    search: '', // search by room number
    roomTypeId: 'all',
    floor: '',
    minPrice: '',
    maxPrice: '',
  });
  const [showAvailableRoomsFilters, setShowAvailableRoomsFilters] = useState(false);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Folio state
  const [folios, setFolios] = useState<any[]>([]);
  const [selectedReservationForFolio, setSelectedReservationForFolio] = useState<string>('');
  // Folios pagination
  const [foliosPage, setFoliosPage] = useState(1);
  const [foliosPerPage, setFoliosPerPage] = useState(10);
  // Folios filters
  const [foliosFilters, setFoliosFilters] = useState({
    reservationNumber: '', // search by reservation number
    guestSearch: '', // search by guest name
    status: 'all', // 'all', 'SETTLED', 'PENDING', etc.
    minTotalAmount: '',
    maxTotalAmount: '',
    minBalance: '',
    maxBalance: '',
  });
  const [showFoliosFilters, setShowFoliosFilters] = useState(false);

  // Services state
  const [services, setServices] = useState<any[]>([]);
  // Services pagination
  const [servicesPage, setServicesPage] = useState(1);
  const [servicesPerPage, setServicesPerPage] = useState(10);
  // Services filters
  const [servicesFilters, setServicesFilters] = useState({
    reservationNumber: '', // search by reservation number
    serviceName: '', // search by service name
    serviceType: 'all', // 'all' or specific service type
    status: 'all', // 'all' or specific status
    minPrice: '',
    maxPrice: '',
  });
  const [showServicesFilters, setShowServicesFilters] = useState(false);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [serviceForm, setServiceForm] = useState({
    reservationId: '',
    serviceType: '',
    serviceName: '',
    description: '',
    quantity: '1',
    unitPrice: '',
    notes: '',
  });
  const [serviceErrors, setServiceErrors] = useState<Record<string, string>>({});
  const [isCreatingService, setIsCreatingService] = useState(false);

  // Payments state
  const [payments, setPayments] = useState<any[]>([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showPaymentEmailDialog, setShowPaymentEmailDialog] = useState(false);
  const [selectedPaymentForEmail, setSelectedPaymentForEmail] = useState<any>(null);
  const [paymentEmailTo, setPaymentEmailTo] = useState('');
  const [paymentEmailSubject, setPaymentEmailSubject] = useState('');
  const [paymentEmailMessage, setPaymentEmailMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    reservationId: '',
    groupBookingId: '', // Add group booking support
    guestId: '',
    paymentType: 'full_payment',
    amount: '',
    paymentMethod: 'cash',
    transactionId: '',
    notes: '',
  });
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [isCreatingPayment, setIsCreatingPayment] = useState(false);
  const [paymentMode, setPaymentMode] = useState<'reservation' | 'group'>('reservation'); // Payment mode: reservation or group
  const [selectedPaymentForEdit, setSelectedPaymentForEdit] = useState<any>(null);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);
  const [isMarkingPaymentAsPaid, setIsMarkingPaymentAsPaid] = useState(false);
  const [isDownloadingPaymentInvoice, setIsDownloadingPaymentInvoice] = useState(false);

  // Loan-related states for hotel
  const [showLoanCustomerSelector, setShowLoanCustomerSelector] = useState(false);
  const [selectedLoanCustomer, setSelectedLoanCustomer] = useState<any>(null);
  const [processingLoan, setProcessingLoan] = useState(false);

  // Housekeeping state
  const [housekeepingTasks, setHousekeepingTasks] = useState<any[]>([]);
  const [showHousekeepingDialog, setShowHousekeepingDialog] = useState(false);
  // Housekeeping pagination
  const [housekeepingPage, setHousekeepingPage] = useState(1);
  const [housekeepingPerPage, setHousekeepingPerPage] = useState(10);
  // Housekeeping filters
  const [housekeepingFilters, setHousekeepingFilters] = useState({
    roomNumber: '', // search by room number
    status: 'all', // 'all', 'PENDING', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'
    priority: 'all', // 'all', 'LOW', 'NORMAL', 'HIGH', 'URGENT'
    assignedTo: '', // search by assigned staff
  });
  const [showHousekeepingFilters, setShowHousekeepingFilters] = useState(false);
  const [housekeepingForm, setHousekeepingForm] = useState({
    roomId: '',
    assignedTo: '',
    status: 'PENDING',
    priority: 'NORMAL',
    description: '',
    scheduledAt: '',
  });
  const [housekeepingErrors, setHousekeepingErrors] = useState<Record<string, string>>({});
  const [isCreatingHousekeepingTask, setIsCreatingHousekeepingTask] = useState(false);
  const [isUpdatingHousekeepingStatus, setIsUpdatingHousekeepingStatus] = useState<string | null>(null);

  // Maintenance state
  const [maintenanceRequests, setMaintenanceRequests] = useState<any[]>([]);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  // Maintenance pagination
  const [maintenancePage, setMaintenancePage] = useState(1);
  const [maintenancePerPage, setMaintenancePerPage] = useState(10);
  // Maintenance filters
  const [maintenanceFilters, setMaintenanceFilters] = useState({
    roomNumber: '', // search by room number
    status: 'all', // 'all', 'OPEN', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'
    priority: 'all', // 'all', 'LOW', 'NORMAL', 'HIGH', 'URGENT'
    assignedTo: '', // search by assigned staff
    requestedBy: '', // search by requester
  });
  const [showMaintenanceFilters, setShowMaintenanceFilters] = useState(false);
  const [maintenanceForm, setMaintenanceForm] = useState({
    roomId: '',
    description: '',
    status: 'OPEN',
    priority: 'NORMAL',
    requestedBy: '',
    assignedTo: '',
  });
  const [maintenanceErrors, setMaintenanceErrors] = useState<Record<string, string>>({});

  // Reviews state
  const [reviews, setReviews] = useState<any[]>([]);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [isCreatingReview, setIsCreatingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    guestId: '',
    roomId: '',
    reservationId: '',
    rating: 5,
    comment: '',
  });
  const [reviewErrors, setReviewErrors] = useState<Record<string, string>>({});

  // Key Cards state
  const [keyCards, setKeyCards] = useState<any[]>([]);
  const [showKeyCardDialog, setShowKeyCardDialog] = useState(false);
  // Key Cards pagination
  const [keyCardsPage, setKeyCardsPage] = useState(1);
  const [keyCardsPerPage, setKeyCardsPerPage] = useState(10);
  // Key Cards filters
  const [keyCardsFilters, setKeyCardsFilters] = useState({
    cardNumber: '', // search by card number
    roomNumber: '', // search by room number
    reservationNumber: '', // search by reservation number
    status: 'all', // 'all', 'ACTIVE', 'DEACTIVATED', 'LOST', 'RETURNED'
  });
  const [showKeyCardsFilters, setShowKeyCardsFilters] = useState(false);
  // Key Card edit state
  const [selectedKeyCardForEdit, setSelectedKeyCardForEdit] = useState<any>(null);
  const [showEditKeyCardDialog, setShowEditKeyCardDialog] = useState(false);
  const [editKeyCardForm, setEditKeyCardForm] = useState({
    status: 'ACTIVE',
    returnedAt: '',
    expiresAt: '',
    notes: '',
  });
  const [keyCardForm, setKeyCardForm] = useState({
    roomId: '',
    reservationId: '',
    cardNumber: '',
    status: 'ACTIVE',
    expiresAt: '',
  });
  const [keyCardErrors, setKeyCardErrors] = useState<Record<string, string>>({});
  const [isCreatingKeyCard, setIsCreatingKeyCard] = useState(false);
  const [isUpdatingKeyCard, setIsUpdatingKeyCard] = useState(false);

  // Group Bookings state
  const [groupBookings, setGroupBookings] = useState<any[]>([]);
  const [showGroupBookingDialog, setShowGroupBookingDialog] = useState(false);
  const [selectedGroupBookingForEdit, setSelectedGroupBookingForEdit] = useState<any>(null);
  const [groupBookingForm, setGroupBookingForm] = useState({
    groupName: '',
    groupType: 'CORPORATE',
    contactPersonId: '',
    contactEmail: '',
    contactPhone: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfRooms: '', // Optional - rooms added via reservations
    numberOfGuests: '', // Optional - guests tracked via reservations
    totalAmount: '', // Optional - calculated from reservations
    depositAmount: '0',
    status: 'PENDING',
    specialRequests: '',
    notes: '',
  });
  const [groupBookingErrors, setGroupBookingErrors] = useState<Record<string, string>>({});
  const [showAddReservationsToGroupDialog, setShowAddReservationsToGroupDialog] = useState(false);
  const [selectedGroupForReservations, setSelectedGroupForReservations] = useState<any>(null);
  const [addReservationMode, setAddReservationMode] = useState<'create' | 'link'>('link'); // Default to 'link' mode
  const [selectedReservationsToLink, setSelectedReservationsToLink] = useState<string[]>([]);
  const [initialLinkedReservationIds, setInitialLinkedReservationIds] = useState<string[]>([]);
  const [showGroupBookingDetailsDialog, setShowGroupBookingDetailsDialog] = useState(false);
  const [selectedGroupBookingForDetails, setSelectedGroupBookingForDetails] = useState<any>(null);
  
  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogConfig, setConfirmDialogConfig] = useState<{
    title: string;
    description: string;
    onConfirm: () => void;
    confirmText?: string;
    cancelText?: string;
    variant?: 'default' | 'destructive';
  } | null>(null);

  // Waitlist state
  const [waitlist, setWaitlist] = useState<any[]>([]);
  const [showWaitlistDialog, setShowWaitlistDialog] = useState(false);
  const [waitlistForm, setWaitlistForm] = useState({
    guestId: '',
    roomTypeId: '',
    checkInDate: '',
    checkOutDate: '',
    numberOfGuests: '1',
    priority: '0',
    notes: '',
  });
  const [waitlistErrors, setWaitlistErrors] = useState<Record<string, string>>({});
  const [isCreatingWaitlist, setIsCreatingWaitlist] = useState(false);

  // Promotions state
  const [promotions, setPromotions] = useState<any[]>([]);
  const [selectedPromotionForEdit, setSelectedPromotionForEdit] = useState<any>(null);
  const [isUpdatingPromotion, setIsUpdatingPromotion] = useState(false);
  const [isDeletingPromotion, setIsDeletingPromotion] = useState(false);
  const [showPromotionDialog, setShowPromotionDialog] = useState(false);
  const [promotionForm, setPromotionForm] = useState({
    name: '',
    code: '',
    description: '',
    discountType: 'PERCENTAGE',
    discountValue: '',
    minStay: '',
    maxStay: '',
    validFrom: '',
    validTo: '',
    maxUses: '',
    isActive: true,
  });
  const [promotionErrors, setPromotionErrors] = useState<Record<string, string>>({});
  const [isCreatingPromotion, setIsCreatingPromotion] = useState(false);

  // Packages state
  const [packages, setPackages] = useState<any[]>([]);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  const [packageForm, setPackageForm] = useState({
    name: '',
    description: '',
    roomTypeId: '',
    basePrice: '',
    validFrom: '',
    validTo: '',
    minStay: '1',
    maxStay: '',
    isActive: true,
  });
  const [packageErrors, setPackageErrors] = useState<Record<string, string>>({});
  const [isCreatingPackage, setIsCreatingPackage] = useState(false);

  // Reports state
  const [revenueReport, setRevenueReport] = useState<any>(null);
  const [occupancyReport, setOccupancyReport] = useState<any>(null);
  const [roomReports, setRoomReports] = useState<Record<string, any>>({});
  const [selectedRoomForReport, setSelectedRoomForReport] = useState<string>('all');
  
  // Messages/Chat state
  const [guestsWithMessages, setGuestsWithMessages] = useState<any[]>([]);
  const [selectedGuestForChat, setSelectedGuestForChat] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const [loadingGuestsWithMessages, setLoadingGuestsWithMessages] = useState(false);
  const [newChatMessage, setNewChatMessage] = useState('');
  const [sendingChatMessage, setSendingChatMessage] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  const [reportDateRange, setReportDateRange] = useState({
    startDate: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    // Store the user's selected company to prevent it from being reset
    const USER_SELECTED_COMPANY_KEY = 'user_selected_company_id';
    
    // Initialize user selected company on mount
    try {
      const initialId = getCompanyId();
      if (initialId && !localStorage.getItem(USER_SELECTED_COMPANY_KEY)) {
        localStorage.setItem(USER_SELECTED_COMPANY_KEY, initialId);
      }
    } catch (error) {
      // Ignore
    }
    
    const loadCompanyId = async (skipRestore = false) => {
      try {
        const id = getCompanyId();
        const userSelectedId = localStorage.getItem(USER_SELECTED_COMPANY_KEY);
        
        // If user has explicitly selected a company and current ID is different, restore it
        if (!skipRestore && userSelectedId && userSelectedId !== id) {
          console.log('Company was reset by system, restoring user selection:', userSelectedId);
          localStorage.setItem('company_id', userSelectedId);
          localStorage.setItem('companyId', userSelectedId);
          localStorage.setItem('company', userSelectedId);
          // Reload with the restored company
          const restoredId = getCompanyId();
          if (restoredId !== companyIdRef.current) {
            companyIdRef.current = restoredId;
            setCompanyId(restoredId);
            try {
              const company = await apiService.getCompany(restoredId);
              setCompanyName(company.name);
            } catch (error) {
              console.error('Error loading company:', error);
            }
          }
          return;
        }
        
        if (id !== companyIdRef.current) {
          // Update the stored user selection if this is a new selection
          if (id && (!userSelectedId || userSelectedId !== id)) {
            localStorage.setItem(USER_SELECTED_COMPANY_KEY, id);
          }
          
          companyIdRef.current = id;
          setCompanyId(id);
          // Load company name
          try {
            const company = await apiService.getCompany(id);
            setCompanyName(company.name);
          } catch (error) {
            console.error('Error loading company:', error);
          }
        }
      } catch (error) {
        console.error('Error loading company ID:', error);
      }
    };
    loadCompanyId();

    // Listen for storage changes (when company is changed elsewhere)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'company_id' || e.key === 'companyId' || e.key === 'company') {
        if (e.newValue && e.newValue !== companyIdRef.current) {
          loadCompanyId();
        }
      }
    };

    // Listen for custom company change event from header
    const handleCompanyChanged = (e: CustomEvent) => {
      const { companyId: newCompanyId } = e.detail || {};
      if (newCompanyId) {
        localStorage.setItem(USER_SELECTED_COMPANY_KEY, newCompanyId);
        loadCompanyId(true); // Skip restore since this is a user-initiated change
      }
    };

    // Also check periodically in case storage event doesn't fire (same-origin issue)
    const interval = setInterval(() => {
      try {
        const currentId = getCompanyId();
        const userSelectedId = localStorage.getItem(USER_SELECTED_COMPANY_KEY);
        
        // If company was reset but user had selected one, restore it
        if (userSelectedId && currentId !== userSelectedId) {
          console.log('Detected company reset, restoring user selection');
          localStorage.setItem('company_id', userSelectedId);
          localStorage.setItem('companyId', userSelectedId);
          localStorage.setItem('company', userSelectedId);
          loadCompanyId(true);
        } else if (currentId !== companyIdRef.current) {
          loadCompanyId();
        }
      } catch (error) {
        // Company ID not available, ignore
      }
    }, 10000); // Check every 10 seconds (reduced frequency to prevent unnecessary re-renders)

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('companyChanged', handleCompanyChanged as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('companyChanged', handleCompanyChanged as EventListener);
      clearInterval(interval);
    };
  }, []); // Only run on mount

  // Define all load functions with useCallback BEFORE using them in useEffect
  const loadRooms = useCallback(async () => {
    if (!companyId) return;
    setLoadingRooms(true);
    try {
      const data = await apiService.getHotelRooms({ companyId });
      setRooms(data);
      setLoadingOverview(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load rooms',
        variant: 'destructive',
      });
    } finally {
      setLoadingRooms(false);
    }
  }, [companyId, toast]);

  const loadRoomTypes = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await apiService.getHotelRoomTypes(companyId);
      setRoomTypes(data);
    } catch (error: any) {
      console.error('Failed to load room types:', error);
    }
  }, [companyId]);

  const loadAmenities = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await apiService.getHotelAmenities(companyId);
      setAmenities(data);
    } catch (error: any) {
      console.error('Failed to load amenities:', error);
    }
  }, [companyId]);

  const loadConferenceRooms = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await apiService.getConferenceRooms({ companyId, isActive: true });
      setConferenceRooms(data);
    } catch (error: any) {
      console.error('Failed to load conference rooms:', error);
    }
  }, [companyId]);

  const loadEquipment = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await apiService.getEquipment({ companyId, isActive: true });
      setEquipment(data);
    } catch (error: any) {
      console.error('Failed to load equipment:', error);
    }
  }, [companyId]);

  const loadReservations = useCallback(async () => {
    if (!companyId) return;
    setLoadingReservations(true);
    try {
      const data = await apiService.getHotelReservations({ companyId });
      setReservations(data);
      setLoadingOverview(false);
      // Removed nested loadGuests() call - guests are loaded separately to prevent cascading API calls
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load reservations',
        variant: 'destructive',
      });
    } finally {
      setLoadingReservations(false);
    }
  }, [companyId, toast]);

  const loadGuests = useCallback(async () => {
    if (!companyId) {
      console.warn('Cannot load guests: companyId is not set');
      return;
    }
    
    setLoadingGuests(true);
    try {
      const data = await apiService.getHotelGuests({ companyId });
      setGuests(Array.isArray(data) ? data : []);
      setLoadingOverview(false);
    } catch (error: any) {
      console.error('Failed to load guests:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load guests',
        variant: 'destructive',
      });
      setGuests([]);
    } finally {
      setLoadingGuests(false);
    }
  }, [companyId, toast]);

  const loadRatePlans = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await apiService.getHotelRatePlans(companyId);
      setRatePlans(data);
    } catch (error: any) {
      console.error('Failed to load rate plans:', error);
    }
  }, [companyId]);

  // Base data loading - only when companyId changes (moved after function definitions)
  useEffect(() => {
    if (companyId) {
      loadRooms();
      loadRoomTypes();
      loadAmenities();
      loadReservations();
      loadGuests();
      loadRatePlans();
      loadConferenceRooms();
      loadEquipment();
    }
  }, [companyId, loadRooms, loadRoomTypes, loadAmenities, loadReservations, loadGuests, loadRatePlans, loadConferenceRooms, loadEquipment]);

  // Ensure calendar is closed on component mount/unmount
  useEffect(() => {
    return () => {
      setShowAvailabilityCalendar(false);
      setSelectedRoomForCalendar(null);
    };
  }, []);

  // Tab-specific data loading - only when tab changes
  useEffect(() => {
    if (!companyId) return;
    
    switch(activeTab) {
      case 'reservations':
        // Reload reservations when switching to this tab to pick up any payment status changes
        loadReservations();
        break;
      case 'messages':
        loadGuestsWithMessages();
        break;
      case 'folio':
        loadFolios();
        break;
      case 'services':
        loadServices();
        break;
      case 'payments':
        loadPayments();
        break;
      case 'housekeeping':
        loadHousekeepingTasks();
        break;
      case 'maintenance':
        loadMaintenanceRequests();
        break;
      case 'reviews':
        loadReviews();
        break;
      case 'keycards':
        loadKeyCards();
        break;
      case 'group-bookings':
        loadGroupBookings();
        break;
      case 'waitlist':
        loadWaitlist();
        break;
      case 'promotions':
        loadPromotions();
        break;
      case 'reports':
        loadReports();
        break;
      // Guests are already loaded in base data loading above
    }
  }, [activeTab, companyId]);

  // Image navigation functions for view room gallery
  const getViewRoomImageUrl = (image: any) => {
    if (!image || !image.imageUrl) return '';
    if (image.imageUrl.startsWith('http')) {
      return image.imageUrl;
    }
    return image.imageUrl.startsWith('/')
      ? `${apiService['baseUrl']}${image.imageUrl}`
      : `${apiService['baseUrl']}/${image.imageUrl}`;
  };

  const nextViewRoomImage = () => {
    if (selectedRoomDetails && selectedRoomDetails.images && selectedRoomDetails.images.length > 0) {
      setViewRoomCurrentImageIndex((prev) => (prev + 1) % selectedRoomDetails.images.length);
    }
  };

  const prevViewRoomImage = () => {
    if (selectedRoomDetails && selectedRoomDetails.images && selectedRoomDetails.images.length > 0) {
      setViewRoomCurrentImageIndex((prev) => 
        prev === 0 ? selectedRoomDetails.images.length - 1 : prev - 1
      );
    }
  };

  const loadFolios = async () => {
    try {
      // Load folios from reservations - include folio with items
      const res = await apiService.getHotelReservations({ companyId });
      const foliosData = res
        .filter(r => r.folio)
        .map(r => ({
          ...r.folio,
          reservation: {
            reservationNumber: r.reservationNumber,
            checkInDate: r.checkInDate,
            checkOutDate: r.checkOutDate,
            guest: r.guest,
            room: r.room,
          }
        }))
        // Sort by newest first (descending by creation date)
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA; // Newest first
        });
      setFolios(foliosData);
    } catch (error: any) {
      console.error('Failed to load folios:', error);
    }
  };

  const handleDownloadFolioPDF = async (folio: any) => {
    try {
      // Get company info for PDF
      const company = await apiService.getCompany(companyId);
      
      // Create a minimal payment object for PDF generation (required by generator)
      const dummyPayment = {
        id: folio.id,
        transactionId: folio.reservation?.reservationNumber || folio.id,
        amount: Number(folio.totalAmount),
        paymentMethod: 'N/A',
        paymentType: 'bill',
        status: folio.status || 'OPEN',
        paidAt: null,
        createdAt: folio.createdAt || new Date().toISOString(),
        notes: '',
        reservation: folio.reservation ? {
          reservationNumber: folio.reservation.reservationNumber,
          checkInDate: folio.reservation.checkInDate,
          checkOutDate: folio.reservation.checkOutDate,
          room: folio.reservation.room ? {
            roomNumber: folio.reservation.room.roomNumber,
            currency: folio.reservation.room.currency,
          } : undefined,
        } : undefined,
        guest: folio.reservation?.guest ? {
          firstName: folio.reservation.guest.firstName,
          lastName: folio.reservation.guest.lastName,
          email: folio.reservation.guest.email,
          phone: folio.reservation.guest.phone,
          address: folio.reservation.guest.address,
        } : undefined,
      };

      // Generate PDF using the existing generator
      const generator = new PaymentInvoicePDFGenerator({
        payment: dummyPayment,
        folio: {
          totalAmount: Number(folio.totalAmount),
          balance: Number(folio.balance),
          items: folio.items?.map((item: any) => ({
            name: item.name,
            description: item.description,
            price: Number(item.price),
            quantity: item.quantity,
            total: Number(item.total),
            category: item.category,
          })) || [],
        },
        company: {
          name: company.name || '',
          logoUrl: (company as any).logoUrl,
          primaryColor: (company as any).primaryColor,
          secondaryColor: (company as any).secondaryColor,
          address: (company as any).address,
          city: (company as any).city,
          state: (company as any).state,
          postalCode: (company as any).postalCode,
          email: (company as any).email,
          phone: (company as any).phone,
          website: (company as any).website,
          taxId: (company as any).taxId,
          invoiceTerms: (company as any).invoiceTerms,
          invoiceFooter: (company as any).invoiceFooter,
        },
      });

      const filename = `bill-${folio.reservation?.reservationNumber || folio.id.slice(-8)}.pdf`;
      await generator.download(filename);
      
      toast({
        title: 'Success',
        description: 'Folio PDF downloaded successfully',
      });
    } catch (error: any) {
      console.error('Error generating folio PDF:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate folio PDF',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadUnpaidItemsPDF = async (reservation: any) => {
    try {
      // Get company info for PDF
      const company = await apiService.getCompany(companyId);
      
      const folio = reservation.folio;
      if (!folio) {
        toast({
          title: 'Error',
          description: 'No folio found for this reservation',
          variant: 'destructive',
        });
        return;
      }

      // Get only charge items (exclude payments)
      const charges = folio.items?.filter((item: any) => 
        item.category !== 'payment' && item.category !== 'PAYMENT'
      ) || [];

      const balance = Number(folio.balance);
      
      // If balance is 0 or negative, nothing to show
      if (balance <= 0) {
        toast({
          title: 'Info',
          description: 'No unpaid balance',
        });
        return;
      }

      // Calculate which items are unpaid by working backwards from newest items
      // until we reach the balance amount
      let remainingBalance = balance;
      const unpaidItems: any[] = [];
      
      // Work backwards through charges (newest first)
      for (let i = charges.length - 1; i >= 0 && remainingBalance > 0; i--) {
        const item = charges[i];
        const itemTotal = Number(item.total);
        
        if (remainingBalance >= itemTotal) {
          // Entire item is unpaid
          unpaidItems.unshift(item);
          remainingBalance -= itemTotal;
        } else if (remainingBalance > 0) {
          // Partial item is unpaid
          const unpaidQuantity = (remainingBalance / itemTotal) * item.quantity;
          unpaidItems.unshift({
            ...item,
            quantity: unpaidQuantity,
            total: remainingBalance,
            description: `${item.description || ''} (Partial)`.trim(),
          });
          remainingBalance = 0;
        }
      }

      // Create a minimal payment object for PDF generation
      const billCurrency = reservation.room?.currency || 'FRW';
      const dummyPayment = {
        id: folio.id,
        transactionId: reservation.reservationNumber,
        amount: balance,
        paymentMethod: 'N/A',
        paymentType: 'bill',
        status: 'UNPAID',
        paidAt: null,
        createdAt: folio.createdAt || new Date().toISOString(),
        notes: `Unpaid Items - Balance Due: ${balance.toFixed(2)} ${billCurrency}`,
        reservation: {
          reservationNumber: reservation.reservationNumber,
          checkInDate: reservation.checkInDate,
          checkOutDate: reservation.checkOutDate,
          room: reservation.room ? {
            roomNumber: reservation.room.roomNumber,
            currency: reservation.room.currency,
          } : undefined,
        },
        guest: reservation.guest ? {
          firstName: reservation.guest.firstName,
          lastName: reservation.guest.lastName,
          email: reservation.guest.email,
          phone: reservation.guest.phone,
          address: reservation.guest.address,
        } : undefined,
      };

      // Generate PDF with only unpaid items
      const generator = new PaymentInvoicePDFGenerator({
        payment: dummyPayment,
        folio: {
          totalAmount: balance,
          balance: balance,
          items: unpaidItems.map((item: any) => ({
            name: item.name,
            description: item.description,
            price: Number(item.price),
            quantity: item.quantity,
            total: Number(item.total),
            category: item.category,
          })),
        },
        company: {
          name: company.name || '',
          logoUrl: (company as any).logoUrl,
          primaryColor: (company as any).primaryColor,
          secondaryColor: (company as any).secondaryColor,
          address: (company as any).address,
          city: (company as any).city,
          state: (company as any).state,
          postalCode: (company as any).postalCode,
          email: (company as any).email,
          phone: (company as any).phone,
          website: (company as any).website,
          taxId: (company as any).taxId,
          invoiceTerms: (company as any).invoiceTerms,
          invoiceFooter: (company as any).invoiceFooter,
        },
      });

      const filename = `unpaid-items-${reservation.reservationNumber}.pdf`;
      await generator.download(filename);
      
      toast({
        title: 'Success',
        description: 'Unpaid items PDF downloaded successfully',
      });
    } catch (error: any) {
      console.error('Error generating unpaid items PDF:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate unpaid items PDF',
        variant: 'destructive',
      });
    }
  };

  const loadServices = async () => {
    try {
      // Services are linked to reservations, so we'll load all
      const data = await apiService.getHotelServices({});
      // Filter by company through reservations if needed
      const filtered = data.filter((s: any) => {
        const reservation = reservations.find(r => r.id === s.reservationId);
        return reservation?.companyId === companyId;
      });
      setServices(filtered);
    } catch (error: any) {
      console.error('Failed to load services:', error);
    }
  };

  const loadPayments = async () => {
    try {
      const data = await apiService.getHotelPayments({});
      // Filter by company through reservations/guests if needed
      const filtered = data.filter((p: any) => {
        if (p.reservationId) {
          const reservation = reservations.find(r => r.id === p.reservationId);
          return reservation?.companyId === companyId;
        }
        if (p.guestId) {
          const guest = guests.find(g => g.id === p.guestId);
          return guest?.companyId === companyId;
        }
        return false;
      });
      setPayments(filtered);
    } catch (error: any) {
      console.error('Failed to load payments:', error);
    }
  };

  // Load payments on initial mount and when reservations/guests change (for filtering)
  useEffect(() => {
    if (companyId && reservations.length > 0) {
      loadPayments();
    }
  }, [companyId, reservations.length, guests.length]);

  const loadHousekeepingTasks = async () => {
    try {
      const data = await apiService.getHousekeepingTasks({ companyId });
      setHousekeepingTasks(data);
    } catch (error: any) {
      console.error('Failed to load housekeeping tasks:', error);
    }
  };

  const loadMaintenanceRequests = async () => {
    try {
      const data = await apiService.getMaintenanceRequests({ companyId });
      setMaintenanceRequests(data);
    } catch (error: any) {
      console.error('Failed to load maintenance requests:', error);
    }
  };

  const loadReviews = async () => {
    try {
      const reviewsData = await apiService.getHotelReviews({ companyId });
      setReviews(reviewsData);
    } catch (error: any) {
      console.error('Failed to load reviews:', error);
      setReviews([]);
    }
  };

  const validateReview = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!reviewForm.guestId) {
      errors.guestId = 'Please select a guest';
    }
    
    if (!reviewForm.roomId) {
      errors.roomId = 'Please select a room';
    }
    
    if (!reviewForm.rating || reviewForm.rating < 1 || reviewForm.rating > 5) {
      errors.rating = 'Please provide a rating between 1 and 5';
    }
    
    setReviewErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateReview = async () => {
    if (!validateReview()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields correctly',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingReview(true);
    setReviewErrors({});
    
    try {
      await apiService.createHotelReview({
        guestId: reviewForm.guestId,
        roomId: reviewForm.roomId,
        reservationId: reviewForm.reservationId || undefined,
        rating: reviewForm.rating,
        comment: reviewForm.comment || undefined,
      });
      
      toast({
        title: 'Success',
        description: 'Review submitted successfully',
      });
      
      setShowReviewDialog(false);
      setReviewForm({
        guestId: '',
        roomId: '',
        reservationId: '',
        rating: 5,
        comment: '',
      });
      loadReviews();
    } catch (error: any) {
      console.error('Failed to create review:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit review. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingReview(false);
    }
  };

  const handleOpenReviewDialog = (reservation?: any) => {
    if (reservation) {
      setReviewForm({
        guestId: reservation.guestId || '',
        roomId: reservation.roomId || '',
        reservationId: reservation.id || '',
        rating: 5,
        comment: '',
      });
    } else {
      setReviewForm({
        guestId: '',
        roomId: '',
        reservationId: '',
        rating: 5,
        comment: '',
      });
    }
    setReviewErrors({});
    setShowReviewDialog(true);
  };

  const loadKeyCards = async () => {
    try {
      const data = await apiService.getKeyCards({ companyId });
      setKeyCards(data);
    } catch (error: any) {
      console.error('Failed to load key cards:', error);
    }
  };

  const loadGroupBookings = async () => {
    try {
      const data = await apiService.getGroupBookings({ companyId });
      // Include reservations count for each group booking
      const bookingsWithReservations = await Promise.all(
        data.map(async (booking) => {
          try {
            const reservations = await apiService.getHotelReservations({ 
              companyId, 
              groupBookingId: booking.id 
            });
            return {
              ...booking,
              linkedReservationsCount: reservations.length || 0,
              linkedReservations: reservations || [],
            };
          } catch (error) {
            return {
              ...booking,
              linkedReservationsCount: 0,
              linkedReservations: [],
            };
          }
        })
      );
      setGroupBookings(bookingsWithReservations);
    } catch (error: any) {
      console.error('Failed to load group bookings:', error);
    }
  };

  const loadWaitlist = async () => {
    try {
      const data = await apiService.getWaitlist({ companyId });
      setWaitlist(data);
    } catch (error: any) {
      console.error('Failed to load waitlist:', error);
    }
  };

  const loadPromotions = async () => {
    try {
      const data = await apiService.getPromotions({ companyId });
      setPromotions(data);
    } catch (error: any) {
      console.error('Failed to load promotions:', error);
    }
  };

  const handleTogglePromotion = async (promotionId: string, newStatus: boolean) => {
    try {
      await apiService.updatePromotion(promotionId, { isActive: newStatus });
      // Update local state
      setPromotions(prev => prev.map(p => 
        p.id === promotionId ? { ...p, isActive: newStatus } : p
      ));
      toast({
        title: 'Success',
        description: `Promotion ${newStatus ? 'activated' : 'deactivated'} successfully`,
      });
    } catch (error: any) {
      console.error('Failed to toggle promotion:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update promotion status',
        variant: 'destructive',
      });
    }
  };

  const handleEditPromotion = (promotion: any) => {
    setSelectedPromotionForEdit(promotion);
    setPromotionForm({
      name: promotion.name || '',
      code: promotion.code || '',
      description: promotion.description || '',
      discountType: promotion.discountType || 'PERCENTAGE',
      discountValue: promotion.discountValue?.toString() || '',
      minStay: promotion.minStay?.toString() || '',
      maxStay: promotion.maxStay?.toString() || '',
      validFrom: promotion.validFrom ? new Date(promotion.validFrom).toISOString().split('T')[0] : '',
      validTo: promotion.validTo ? new Date(promotion.validTo).toISOString().split('T')[0] : '',
      maxUses: promotion.maxUses?.toString() || '',
      isActive: promotion.isActive !== undefined ? promotion.isActive : true,
    });
    setShowPromotionDialog(true);
  };

  const handleDeletePromotion = async (promotionId: string) => {
    setConfirmDialogConfig({
      title: 'Delete Promotion',
      description: 'Are you sure you want to delete this promotion? This action cannot be undone.',
      onConfirm: () => {
        setShowConfirmDialog(false);
        performDeletePromotion(promotionId);
      },
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    setShowConfirmDialog(true);
  };

  const performDeletePromotion = async (promotionId: string) => {
    setIsDeletingPromotion(true);
    try {
      await apiService.deletePromotion(promotionId);
      toast({
        title: 'Success',
        description: 'Promotion deleted successfully',
      });
      loadPromotions();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete promotion',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingPromotion(false);
    }
  };

  const loadPackages = async () => {
    try {
      const data = await apiService.getPackages({ companyId });
      setPackages(data);
    } catch (error: any) {
      console.error('Failed to load packages:', error);
    }
  };

  // Load guests with messages
  const loadGuestsWithMessages = async () => {
    if (!companyId) return;
    setLoadingGuestsWithMessages(true);
    try {
      const guests = await apiService.getGuestsWithMessages();
      setGuestsWithMessages(guests || []);
      
      // Load unread count
      const unreadData = await apiService.getUnreadMessageCount();
      setUnreadMessageCount(unreadData?.count || 0);
    } catch (error: any) {
      console.error('Failed to load guests with messages:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load messages',
        variant: 'destructive',
      });
    } finally {
      setLoadingGuestsWithMessages(false);
    }
  };

  // Load messages for a specific guest
  const loadChatMessages = async (guestId: string) => {
    setLoadingChatMessages(true);
    try {
      const messages = await apiService.getGuestMessages({ guestId });
      setChatMessages(messages || []);
      
      // Mark all messages as read
      await apiService.markAllMessagesAsRead(guestId);
      
      // Reload guests list to update unread counts
      await loadGuestsWithMessages();
    } catch (error: any) {
      console.error('Failed to load chat messages:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load messages',
        variant: 'destructive',
      });
    } finally {
      setLoadingChatMessages(false);
    }
  };

  // Send a message to a guest
  const handleSendChatMessage = async () => {
    if (!newChatMessage.trim() || !selectedGuestForChat || sendingChatMessage) return;
    
    setSendingChatMessage(true);
    try {
      await apiService.createGuestMessage({
        guestId: selectedGuestForChat,
        messageText: newChatMessage.trim(),
        isFromGuest: false,
      });
      
      setNewChatMessage('');
      await loadChatMessages(selectedGuestForChat);
      await loadGuestsWithMessages();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message',
        variant: 'destructive',
      });
    } finally {
      setSendingChatMessage(false);
    }
  };

  const loadReports = async () => {
    setLoadingReports(true);
    try {
      // If a specific room is selected, only load that room's report
      if (selectedRoomForReport !== 'all') {
        const selectedRoom = rooms.find(r => r.id === selectedRoomForReport);
        if (!selectedRoom) {
          toast({
            title: 'Error',
            description: 'Selected room not found',
            variant: 'destructive',
          });
          setLoadingReports(false);
          return;
        }

        try {
          const roomRevenue = await apiService.getRevenueReport({
            companyId,
            startDate: reportDateRange.startDate,
            endDate: reportDateRange.endDate,
            roomId: selectedRoomForReport,
          });
          const roomOccupancy = await apiService.getOccupancyReport({
            companyId,
            startDate: reportDateRange.startDate,
            endDate: reportDateRange.endDate,
            roomId: selectedRoomForReport,
          });
          
          setRoomReports({
            [selectedRoomForReport]: {
              revenue: roomRevenue,
              occupancy: roomOccupancy,
              roomNumber: selectedRoom.roomNumber,
              roomTitle: selectedRoom.title,
              currency: roomRevenue.currency || selectedRoom.currency || 'FRW', // Use currency from backend response
            }
          });
          
          // Clear general reports when viewing a specific room
          setRevenueReport(null);
          setOccupancyReport(null);
        } catch (error: any) {
          toast({
            title: 'Error',
            description: error.message || `Failed to load report for room ${selectedRoom.roomNumber}`,
            variant: 'destructive',
          });
        }
      } else {
        // Load general reports for all rooms (summary)
        const revenue = await apiService.getRevenueReport({
          companyId,
          startDate: reportDateRange.startDate,
          endDate: reportDateRange.endDate,
        });
        const occupancy = await apiService.getOccupancyReport({
          companyId,
          startDate: reportDateRange.startDate,
          endDate: reportDateRange.endDate,
        });
        setRevenueReport(revenue);
        setOccupancyReport(occupancy);
        
        // Load individual reports for each room when "all" is selected
        const allRoomReports: Record<string, any> = {};
        
        for (const room of rooms) {
          try {
            const roomRevenue = await apiService.getRevenueReport({
              companyId,
              startDate: reportDateRange.startDate,
              endDate: reportDateRange.endDate,
              roomId: room.id,
            });
            const roomOccupancy = await apiService.getOccupancyReport({
              companyId,
              startDate: reportDateRange.startDate,
              endDate: reportDateRange.endDate,
              roomId: room.id,
            });
            
            allRoomReports[room.id] = {
              revenue: roomRevenue,
              occupancy: roomOccupancy,
              roomNumber: room.roomNumber,
              roomTitle: room.title,
              currency: roomRevenue.currency || room.currency || 'FRW', // Use currency from backend response
            };
          } catch (error: any) {
            console.error(`Failed to load report for room ${room.roomNumber}:`, error);
            // Continue loading other rooms even if one fails
          }
        }
        
        setRoomReports(allRoomReports);
      }
    } catch (error: any) {
      console.error('Failed to load reports:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load reports',
        variant: 'destructive',
      });
    } finally {
      setLoadingReports(false);
    }
  };

  // Validation functions
  const validateGroupBooking = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Group Name validation
    if (!groupBookingForm.groupName.trim()) {
      errors.groupName = 'Group name is required';
    } else if (groupBookingForm.groupName.trim().length < 2) {
      errors.groupName = 'Group name must be at least 2 characters';
    } else if (groupBookingForm.groupName.trim().length > 100) {
      errors.groupName = 'Group name must be less than 100 characters';
    }
    
    // Contact Person validation
    if (!groupBookingForm.contactPersonId) {
      errors.contactPersonId = 'Contact person is required';
    }
    
    // Contact Email validation
    if (groupBookingForm.contactEmail && groupBookingForm.contactEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(groupBookingForm.contactEmail.trim())) {
      errors.contactEmail = 'Invalid email format';
      } else if (groupBookingForm.contactEmail.trim().length > 255) {
        errors.contactEmail = 'Email must be less than 255 characters';
      }
    }
    
    // Contact Phone validation
    if (groupBookingForm.contactPhone && groupBookingForm.contactPhone.trim()) {
      const phoneRegex = /^[\d\s\-\+\(\)]+$/;
      if (!phoneRegex.test(groupBookingForm.contactPhone.trim())) {
        errors.contactPhone = 'Invalid phone number format';
      } else if (groupBookingForm.contactPhone.trim().length > 20) {
        errors.contactPhone = 'Phone number must be less than 20 characters';
      }
    }
    
    // Check-In Date validation
    if (!groupBookingForm.checkInDate) {
      errors.checkInDate = 'Check-in date is required';
    } else {
      const checkInDate = new Date(groupBookingForm.checkInDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (checkInDate < today) {
        errors.checkInDate = 'Check-in date cannot be in the past';
      }
    }
    
    // Check-Out Date validation
    if (!groupBookingForm.checkOutDate) {
      errors.checkOutDate = 'Check-out date is required';
    } else if (groupBookingForm.checkInDate) {
      const checkInDate = new Date(groupBookingForm.checkInDate);
      const checkOutDate = new Date(groupBookingForm.checkOutDate);
      if (checkOutDate <= checkInDate) {
        errors.checkOutDate = 'Check-out date must be after check-in date';
      }
      // Check if stay is too long (optional - you can adjust this)
      const daysDiff = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 365) {
        errors.checkOutDate = 'Stay duration cannot exceed 365 days';
      }
    }
    
    // Number of Rooms validation (optional field)
    if (groupBookingForm.numberOfRooms && groupBookingForm.numberOfRooms.trim()) {
      const numRooms = parseInt(groupBookingForm.numberOfRooms);
      if (isNaN(numRooms)) {
        errors.numberOfRooms = 'Number of rooms must be a valid number';
      } else if (numRooms < 1) {
        errors.numberOfRooms = 'Number of rooms must be at least 1';
      } else if (numRooms > 1000) {
        errors.numberOfRooms = 'Number of rooms cannot exceed 1000';
      }
    }
    
    // Number of Guests validation (optional field)
    if (groupBookingForm.numberOfGuests && groupBookingForm.numberOfGuests.trim()) {
      const numGuests = parseInt(groupBookingForm.numberOfGuests);
      if (isNaN(numGuests)) {
        errors.numberOfGuests = 'Number of guests must be a valid number';
      } else if (numGuests < 1) {
        errors.numberOfGuests = 'Number of guests must be at least 1';
      } else if (numGuests > 10000) {
        errors.numberOfGuests = 'Number of guests cannot exceed 10000';
      }
    }
    
    // Total Amount validation (optional field)
    if (groupBookingForm.totalAmount && groupBookingForm.totalAmount.trim()) {
      const totalAmount = parseFloat(groupBookingForm.totalAmount);
      if (isNaN(totalAmount)) {
        errors.totalAmount = 'Total amount must be a valid number';
      } else if (totalAmount < 0) {
        errors.totalAmount = 'Total amount cannot be negative';
      } else if (totalAmount > 100000000) {
        errors.totalAmount = 'Total amount is too large';
      }
    }
    
    // Deposit Amount validation
    if (groupBookingForm.depositAmount && groupBookingForm.depositAmount.trim()) {
      const depositAmount = parseFloat(groupBookingForm.depositAmount);
      if (isNaN(depositAmount)) {
        errors.depositAmount = 'Deposit amount must be a valid number';
      } else if (depositAmount < 0) {
        errors.depositAmount = 'Deposit amount cannot be negative';
      } else if (groupBookingForm.totalAmount && groupBookingForm.totalAmount.trim()) {
        const totalAmount = parseFloat(groupBookingForm.totalAmount);
        if (!isNaN(totalAmount) && depositAmount > totalAmount) {
          errors.depositAmount = 'Deposit amount cannot exceed total amount';
        }
      }
    } else {
      // Set default to 0 if empty
      setGroupBookingForm({ ...groupBookingForm, depositAmount: '0' });
    }
    
    // Special Requests validation (optional - just length check)
    if (groupBookingForm.specialRequests && groupBookingForm.specialRequests.length > 1000) {
      errors.specialRequests = 'Special requests must be less than 1000 characters';
    }
    
    // Notes validation (optional - just length check)
    if (groupBookingForm.notes && groupBookingForm.notes.length > 2000) {
      errors.notes = 'Notes must be less than 2000 characters';
    }
    
    setGroupBookingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateRoom = (): boolean => {
    const errors: Record<string, string> = {};
    if (!roomForm.roomNumber.trim()) errors.roomNumber = 'Room number is required';
    if (!roomForm.roomTypeId) errors.roomTypeId = 'Room type is required';
    if (!roomForm.capacity || roomForm.capacity.trim() === '') {
      errors.capacity = 'Capacity is required';
    } else if (parseInt(roomForm.capacity) < 1) {
      errors.capacity = 'Capacity must be at least 1';
    } else if (isNaN(parseInt(roomForm.capacity))) {
      errors.capacity = 'Capacity must be a valid number';
    }
    if (!roomForm.basePricePerNight || roomForm.basePricePerNight.trim() === '') {
      errors.basePricePerNight = 'Price per night is required';
    } else if (isNaN(parseFloat(roomForm.basePricePerNight))) {
      errors.basePricePerNight = 'Price must be a valid number';
    } else if (parseFloat(roomForm.basePricePerNight) < 0) {
      errors.basePricePerNight = 'Price cannot be negative';
    }
    setRoomErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateReservation = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Room is required only for ROOM bookings
    if (reservationForm.bookingType === 'ROOM' && !reservationForm.roomId) {
      errors.roomId = 'Room is required';
    }
    
    // For non-room bookings, either bookableItemId or roomId should be provided
    if (reservationForm.bookingType !== 'ROOM' && !reservationForm.bookableItemId && !reservationForm.roomId) {
      errors.bookableItemId = 'Item is required for this booking type';
    }
    
    if (!reservationForm.guestId) errors.guestId = 'Guest is required';
    if (!reservationForm.checkInDate) errors.checkInDate = 'Check-in date is required';
    if (!reservationForm.checkOutDate) errors.checkOutDate = 'Check-out date is required';
    if (reservationForm.checkInDate && reservationForm.checkOutDate && new Date(reservationForm.checkInDate) >= new Date(reservationForm.checkOutDate)) {
      errors.checkOutDate = 'Check-out date must be after check-in date';
    }
    if (!reservationForm.numberOfGuests || parseInt(reservationForm.numberOfGuests) < 1) {
      errors.numberOfGuests = 'Number of guests must be at least 1';
    } else if (reservationForm.roomId) {
      // Check if number of guests exceeds room capacity
      const selectedRoom = rooms.find(r => r.id === reservationForm.roomId);
      if (selectedRoom && parseInt(reservationForm.numberOfGuests) > selectedRoom.capacity) {
        errors.numberOfGuests = `Number of guests cannot exceed room capacity (${selectedRoom.capacity})`;
      }
    }
    if (!reservationForm.totalAmount || parseFloat(reservationForm.totalAmount) <= 0) errors.totalAmount = 'Total amount must be greater than 0';
    setReservationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateGuest = (): boolean => {
    const errors: Record<string, string> = {};
    if (!guestForm.firstName.trim()) errors.firstName = 'First name is required';
    if (!guestForm.lastName.trim()) errors.lastName = 'Last name is required';
    if (guestForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestForm.email)) {
      errors.email = 'Invalid email format';
    }
    // Validate country (required)
    if (!guestForm.country) errors.country = 'Country is required';
    // Validate ID Type (required)
    if (!guestForm.idType) errors.idType = 'ID Type is required';
    // Validate ID number based on type
    if (guestForm.idType === 'passport' && !guestForm.passportNumber.trim()) {
      errors.passportNumber = 'Passport number is required';
    }
    if (guestForm.idType === 'national_id' && !guestForm.idNumber.trim()) {
      errors.idNumber = 'National ID number is required';
    }
    setGuestErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateService = (): boolean => {
    const errors: Record<string, string> = {};
    if (!serviceForm.reservationId) errors.reservationId = 'Reservation is required';
    if (!serviceForm.serviceType) errors.serviceType = 'Service type is required';
    if (!serviceForm.serviceName.trim()) errors.serviceName = 'Service name is required';
    if (!serviceForm.quantity || parseInt(serviceForm.quantity) < 1) errors.quantity = 'Quantity must be at least 1';
    if (!serviceForm.unitPrice || parseFloat(serviceForm.unitPrice) <= 0) errors.unitPrice = 'Unit price must be greater than 0';
    setServiceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePayment = (): boolean => {
    const errors: Record<string, string> = {};
    if (!paymentForm.reservationId && !paymentForm.groupBookingId) {
      errors.reservationId = paymentMode === 'reservation' ? 'Reservation is required' : 'Group booking is required';
    }
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) errors.amount = 'Amount must be greater than 0';
    if (!paymentForm.transactionId.trim()) errors.transactionId = 'Transaction ID is required';
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Process loan payment after customer selection
  const processLoanPayment = async (customer: any) => {
    setProcessingLoan(true);
    try {
      // Create payment with loan method and link to customer
      // Note: guestId is from the reservation, customer is for loan tracking in notes
      await apiService.createHotelPayment({
        companyId,
        reservationId: paymentForm.reservationId || undefined,
        guestId: paymentForm.guestId || undefined, // Use reservation's guestId, not customer id
        paymentType: paymentForm.paymentType,
        amount: parseFloat(paymentForm.amount),
        paymentMethod: 'loan',
        transactionId: paymentForm.transactionId || `LOAN-${Date.now()}`,
        status: 'loan', // Loan payments have loan status
        notes: paymentForm.notes 
          ? `${paymentForm.notes}\nLoan for customer: ${customer.name} (ID: ${customer.id})`
          : `Loan payment for customer: ${customer.name} (ID: ${customer.id})`,
      });

      toast({
        title: 'Loan Payment Recorded',
        description: `Loan payment recorded for ${customer.name}`,
      });

      // Close dialogs and reset form
      setShowLoanCustomerSelector(false);
      setShowPaymentDialog(false);
      setPaymentForm({
        reservationId: '',
        groupBookingId: '',
        guestId: '',
        paymentType: 'full_payment',
        amount: '',
        paymentMethod: 'cash',
        transactionId: '',
        notes: '',
      });

      // Refresh data
      await Promise.all([
        loadReservations(),
        loadFolios(),
      ]);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to record loan payment',
        variant: 'destructive',
      });
    } finally {
      setProcessingLoan(false);
    }
  };

  const validateHousekeeping = (): boolean => {
    const errors: Record<string, string> = {};
    if (!housekeepingForm.roomId) errors.roomId = 'Room is required';
    if (!housekeepingForm.description.trim()) errors.description = 'Description is required';
    setHousekeepingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateMaintenance = (): boolean => {
    const errors: Record<string, string> = {};
    if (!maintenanceForm.roomId) errors.roomId = 'Room is required';
    if (!maintenanceForm.description.trim()) errors.description = 'Description is required';
    setMaintenanceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateKeyCard = (): boolean => {
    const errors: Record<string, string> = {};
    if (!keyCardForm.roomId) errors.roomId = 'Room is required';
    if (!keyCardForm.reservationId) errors.reservationId = 'Reservation is required';
    setKeyCardErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateWaitlist = (): boolean => {
    const errors: Record<string, string> = {};
    if (!waitlistForm.guestId) errors.guestId = 'Guest is required';
    if (!waitlistForm.checkInDate) errors.checkInDate = 'Check-in date is required';
    if (!waitlistForm.checkOutDate) errors.checkOutDate = 'Check-out date is required';
    if (waitlistForm.checkInDate && waitlistForm.checkOutDate && new Date(waitlistForm.checkInDate) >= new Date(waitlistForm.checkOutDate)) {
      errors.checkOutDate = 'Check-out date must be after check-in date';
    }
    if (!waitlistForm.numberOfGuests || parseInt(waitlistForm.numberOfGuests) < 1) errors.numberOfGuests = 'Number of guests must be at least 1';
    setWaitlistErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePromotion = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Name validation
    if (!promotionForm.name.trim()) {
      errors.name = 'Promotion name is required';
    }
    
    // Discount value validation
    if (!promotionForm.discountValue || parseFloat(promotionForm.discountValue) <= 0) {
      errors.discountValue = 'Discount value must be greater than 0';
    } else {
      // Type-specific validations
      if (promotionForm.discountType === 'PERCENTAGE' && parseFloat(promotionForm.discountValue) > 100) {
        errors.discountValue = 'Percentage discount cannot exceed 100%';
      }
    }
    
    // Date validations
    if (!promotionForm.validFrom) {
      errors.validFrom = 'Valid from date is required';
    }
    if (!promotionForm.validTo) {
      errors.validTo = 'Valid to date is required';
    }
    if (promotionForm.validFrom && promotionForm.validTo) {
      const fromDate = new Date(promotionForm.validFrom);
      const toDate = new Date(promotionForm.validTo);
      if (fromDate >= toDate) {
      errors.validTo = 'Valid to date must be after valid from date';
    }
      // Check if valid from is in the past (optional - you might want to allow past dates)
      // if (fromDate < new Date()) {
      //   errors.validFrom = 'Valid from date cannot be in the past';
      // }
    }
    
    // Min/Max stay validations
    if (promotionForm.minStay && parseInt(promotionForm.minStay) < 1) {
      errors.minStay = 'Minimum stay must be at least 1 night';
    }
    if (promotionForm.maxStay && parseInt(promotionForm.maxStay) < 1) {
      errors.maxStay = 'Maximum stay must be at least 1 night';
    }
    if (promotionForm.minStay && promotionForm.maxStay) {
      const minStay = parseInt(promotionForm.minStay);
      const maxStay = parseInt(promotionForm.maxStay);
      if (minStay > maxStay) {
        errors.maxStay = 'Maximum stay must be greater than or equal to minimum stay';
      }
    }
    
    // Max uses validation
    if (promotionForm.maxUses && parseInt(promotionForm.maxUses) < 1) {
      errors.maxUses = 'Maximum uses must be at least 1';
    }
    
    setPromotionErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePackage = (): boolean => {
    const errors: Record<string, string> = {};
    if (!packageForm.name.trim()) errors.name = 'Package name is required';
    if (!packageForm.basePrice || parseFloat(packageForm.basePrice) <= 0) errors.basePrice = 'Base price must be greater than 0';
    if (!packageForm.validFrom) errors.validFrom = 'Valid from date is required';
    if (!packageForm.validTo) errors.validTo = 'Valid to date is required';
    if (packageForm.validFrom && packageForm.validTo && new Date(packageForm.validFrom) >= new Date(packageForm.validTo)) {
      errors.validTo = 'Valid to date must be after valid from date';
    }
    setPackageErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateRoom = async () => {
    console.log('🏨 [CREATE ROOM] Starting room creation/update');
    console.log('🏨 [CREATE ROOM] Selected images count:', roomImages.length);
    console.log('🏨 [CREATE ROOM] Room images:', roomImages);
    console.log('🏨 [CREATE ROOM] Is editing existing room:', !!selectedRoom);
    
    if (!validateRoom()) {
      toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
      return;
    }
    setIsCreatingRoom(true);
    try {
      let roomId: string;
      
      if (selectedRoom) {
        // Update existing room
        await apiService.updateHotelRoom(selectedRoom.id, {
          ...roomForm,
          floor: roomForm.floor ? parseInt(roomForm.floor) : undefined,
          capacity: parseInt(roomForm.capacity),
          basePricePerNight: parseFloat(roomForm.basePricePerNight),
          amenityIds: roomForm.amenityIds || [],
        });
        roomId = selectedRoom.id;
        
        // Reload rooms to get updated data with amenities
        await loadRooms();
        
        // Upload new images if any
        if (roomImages.length > 0) {
          console.log('🏨 [UPDATE ROOM] Uploading', roomImages.length, 'new images');
          await uploadRoomImages(roomId);
        } else {
          console.log('🏨 [UPDATE ROOM] No new images to upload');
        }
        
        toast({
          title: 'Success',
          description: 'Room updated successfully',
        });
      } else {
        if (!companyId || companyId === 'guest-company' || companyId.trim() === '') {
          toast({
            title: 'Error',
            description: 'Please select a company before creating a room',
            variant: 'destructive',
          });
          setIsCreatingRoom(false);
          return;
        }
        console.log('🏨 [CREATE ROOM] Creating new room with data:', roomForm);
        // Create new room
        const newRoom = await apiService.createHotelRoom({
          companyId,
          ...roomForm,
          floor: roomForm.floor ? parseInt(roomForm.floor) : undefined,
          capacity: parseInt(roomForm.capacity),
          basePricePerNight: parseFloat(roomForm.basePricePerNight),
          amenityIds: roomForm.amenityIds,
        });
        roomId = newRoom.id;
        console.log('🏨 [CREATE ROOM] Room created with ID:', roomId);
        
        // Upload images if any were selected
        if (roomImages.length > 0) {
          console.log('🏨 [CREATE ROOM] Preparing to upload', roomImages.length, 'images');
          try {
            const formData = new FormData();
            roomImages.forEach((image, index) => {
              console.log(`🏨 [CREATE ROOM] Adding image ${index + 1}:`, {
                name: image.name,
                size: image.size,
                type: image.type
              });
              formData.append('images', image);
            });
            
            console.log('🏨 [CREATE ROOM] FormData prepared, calling uploadRoomImages API');
            const result = await apiService.uploadRoomImages(roomId, formData);
            console.log('🏨 [CREATE ROOM] Upload result:', result);
            
            toast({
              title: 'Success',
              description: `Room created successfully with ${roomImages.length} image(s)`,
            });
          } catch (imageError: any) {
            console.error('🏨 [CREATE ROOM] ❌ Failed to upload images:', imageError);
            console.error('🏨 [CREATE ROOM] ❌ Error details:', {
              message: imageError.message,
              status: imageError.status,
              response: imageError.response
            });
            toast({
              title: 'Warning',
              description: `Room created but images failed to upload: ${imageError.message}`,
              variant: 'destructive',
            });
          }
        } else {
          console.log('🏨 [CREATE ROOM] No images selected to upload');
          toast({
            title: 'Success',
            description: 'Room created successfully',
          });
        }
      }
      
      setShowRoomDialog(false);
      setRoomErrors({});
      resetRoomForm();
      // Auto-refresh rooms and related data
      await loadRooms();
      // Also refresh room types in case room type was updated
      await loadRoomTypes();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save room',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingRoom(false);
    }
  };

  const handleCreateReservation = async () => {
    if (!validateReservation()) {
      toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
      return;
    }
    setIsCreatingReservation(true);
    try {
      // Convert dates to ISO-8601 format with UTC midnight to avoid timezone issues
      // Parse date string (YYYY-MM-DD) and create UTC date at midnight
      let checkInDate = '';
      let checkOutDate = '';
      
      if (reservationForm.checkInDate) {
        const [year, month, day] = reservationForm.checkInDate.split('-').map(Number);
        checkInDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
      }
      
      if (reservationForm.checkOutDate) {
        const [year, month, day] = reservationForm.checkOutDate.split('-').map(Number);
        checkOutDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
      }
      
      await apiService.createHotelReservation({
        companyId,
        bookingType: reservationForm.bookingType,
        roomId: reservationForm.bookingType === 'ROOM' ? reservationForm.roomId : undefined,
        bookableItemId: reservationForm.bookingType !== 'ROOM' ? reservationForm.bookableItemId : undefined,
        bookableItemType: reservationForm.bookingType !== 'ROOM' ? reservationForm.bookableItemType : undefined,
        guestId: reservationForm.guestId,
        ratePlanId: reservationForm.bookingType === 'ROOM' ? (reservationForm.ratePlanId || undefined) : undefined,
        groupBookingId: reservationForm.groupBookingId || undefined, // Include if provided
        promotionId: reservationForm.bookingType === 'ROOM' 
          ? (reservationForm.promotionIds.length > 0 
              ? reservationForm.promotionIds[0] 
              : (reservationForm.promotionId || undefined))
          : undefined, // Only for room bookings
        appliedPromotionIds: reservationForm.bookingType === 'ROOM' ? reservationForm.promotionIds : undefined,
        checkInDate,
        checkOutDate,
        numberOfGuests: parseInt(reservationForm.numberOfGuests),
        totalAmount: parseFloat(reservationForm.totalAmount),
        depositAmount: parseFloat(reservationForm.depositAmount),
        specialRequests: reservationForm.specialRequests || undefined,
      });
      
      // Get guest and room details for the toast message
      const guest = guests.find(g => g.id === reservationForm.guestId);
      const room = rooms.find(r => r.id === reservationForm.roomId);
      const guestName = guest ? `${guest.firstName} ${guest.lastName}` : 'Guest';
      const roomInfo = room ? ` - Room ${room.roomNumber}` : '';
      const checkInInfo = reservationForm.checkInDate ? ` - Check-in: ${new Date(reservationForm.checkInDate).toLocaleDateString()}` : '';
      
      toast({
        title: '✅ Reservation Created Successfully',
        description: `${guestName}${roomInfo}${checkInInfo}`,
      });
      setShowReservationDialog(false);
      setReservationErrors({});
      resetReservationForm();
      // Auto-refresh all related data
      await loadReservations();
      await loadGuests(); // Also reload guests to ensure newly created guests from reservations appear in guests tab
      if (reservationForm.groupBookingId) {
        await loadGroupBookings(); // Refresh group bookings if linked
      }
      // Refresh folios if on folio tab
      if (activeTab === 'folio') {
        await loadFolios();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create reservation',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingReservation(false);
    }
  };

  const handleCreateGuest = async () => {
    if (!validateGuest()) {
      toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
      return;
    }
    setIsCreatingGuest(true);
    try {
      // Convert country code to country name for storage
      const countryName = guestForm.country 
        ? COUNTRIES.find(c => c.value === guestForm.country)?.label || guestForm.country
        : '';
      
      // Prepare guest data - only include passportNumber or idNumber based on idType
      const guestData: any = {
        firstName: guestForm.firstName,
        lastName: guestForm.lastName,
        email: guestForm.email,
        phone: guestForm.phone,
        address: guestForm.address,
        city: guestForm.city,
        state: guestForm.state,
        postalCode: guestForm.postalCode,
        country: countryName, // Store country name instead of code
      };
      
      // Add ID fields based on selected type
      if (guestForm.idType === 'passport') {
        guestData.passportNumber = guestForm.passportNumber;
        // Clear idNumber if passport is selected
        guestData.idNumber = null;
      } else if (guestForm.idType === 'national_id') {
        guestData.idNumber = guestForm.idNumber;
        // Clear passportNumber if national ID is selected
        guestData.passportNumber = null;
      }

      if (selectedRoom) {
        // Update existing guest (reusing selectedRoom for guest ID)
        await apiService.updateHotelGuest(selectedRoom.id, guestData);
        toast({
          title: 'Success',
          description: 'Guest updated successfully',
        });
      } else {
        // Create new guest
        await apiService.createHotelGuest({
          companyId,
          ...guestData,
        });
        toast({
          title: 'Success',
          description: 'Guest created successfully',
        });
      }
      setShowGuestDialog(false);
      setGuestErrors({});
      resetGuestForm();
      loadGuests();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save guest',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingGuest(false);
    }
  };

  const handleCheckAvailability = async () => {
    if (!availabilityCheck.checkInDate || !availabilityCheck.checkOutDate) {
      toast({
        title: 'Error',
        description: 'Please select check-in and check-out dates',
        variant: 'destructive',
      });
      return;
    }

    // Validate that check-out date is after check-in date
    const checkIn = new Date(availabilityCheck.checkInDate);
    const checkOut = new Date(availabilityCheck.checkOutDate);
    
    if (checkOut <= checkIn) {
      toast({
        title: 'Invalid Dates',
        description: 'Check-out date must be after check-in date',
        variant: 'destructive',
      });
      return;
    }

    setIsCheckingAvailability(true);
    try {
      // Backend already performs comprehensive availability checking including:
      // - Room criteria (type, capacity, active status)
      // - Availability records (blocked/out-of-order dates)
      // - Active reservations (overlapping dates)
      // So we can trust the backend result without additional frontend filtering
      const result = await apiService.checkRoomAvailability({
        companyId,
        checkInDate: availabilityCheck.checkInDate,
        checkOutDate: availabilityCheck.checkOutDate,
        roomTypeId: availabilityCheck.roomTypeId || undefined,
        numberOfGuests: availabilityCheck.numberOfGuests ? parseInt(availabilityCheck.numberOfGuests) : undefined,
      });
      
      setAvailableRooms(result.rooms);
      
      // Provide more informative feedback
      if (result.rooms.length === 0) {
        const filters = [];
        if (availabilityCheck.roomTypeId) {
          const selectedRoomType = roomTypes.find(rt => rt.id === availabilityCheck.roomTypeId);
          if (selectedRoomType) {
            filters.push(`Room Type: ${selectedRoomType.name}`);
          }
        }
        if (availabilityCheck.numberOfGuests) {
          filters.push(`Guests: ${availabilityCheck.numberOfGuests}`);
        }
        
        const filterText = filters.length > 0 ? ` (Filtered by: ${filters.join(', ')})` : '';
        toast({
          title: 'No Available Rooms',
          description: `No rooms available for ${availabilityCheck.checkInDate} to ${availabilityCheck.checkOutDate}${filterText}. Check if rooms are active, match the room type, have sufficient capacity, or are blocked/reserved for these dates.`,
          variant: 'default',
        });
      } else {
        toast({
          title: 'Availability Check',
          description: `Found ${result.rooms.length} available room${result.rooms.length === 1 ? '' : 's'}`,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to check availability',
        variant: 'destructive',
      });
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const handleCheckIn = async (reservationId: string) => {
    setIsCheckingIn(true);
    try {
      // Find the reservation to check payment status
      const reservation = reservations.find(r => r.id === reservationId);
      
      if (!reservation) {
        toast({
          title: 'Error',
          description: 'Reservation not found',
          variant: 'destructive',
        });
        setIsCheckingIn(false);
        return;
      }

      // Check if reservation is paid before allowing check-in
      const paymentStatus = reservation.paymentStatus || 'UNPAID';
      
      if (paymentStatus !== 'PAID') {
        toast({
          title: 'Payment Required',
          description: 'Guest must complete payment before check-in',
          variant: 'destructive',
        });
        setIsCheckingIn(false);
        return;
      }

      await apiService.checkInReservation(reservationId);
      toast({
        title: 'Success',
        description: 'Guest checked in successfully',
      });
      loadReservations();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to check in',
        variant: 'destructive',
      });
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleCheckOut = async (reservationId: string) => {
    setIsCheckingOut(true);
    try {
      // Find the reservation to check payment status
      const reservation = reservations.find(r => r.id === reservationId);
      
      if (!reservation) {
        toast({
          title: 'Error',
          description: 'Reservation not found',
          variant: 'destructive',
        });
        setIsCheckingOut(false);
        return;
      }

      // Check if reservation is paid before allowing checkout
      const paymentStatus = reservation.paymentStatus || 'UNPAID';
      const hasFolio = reservation.folio !== null && reservation.folio !== undefined;
      
      // Calculate total paid from completed payments if available
      const payments = reservation.payments || [];
      const completedPayments = payments.filter((p: any) => p.status === 'COMPLETED');
      const totalPaid = completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
      
      // Determine total amount (use folio totalAmount if available, otherwise reservation totalAmount)
      const totalAmount = hasFolio 
        ? Number(reservation.folio?.totalAmount || 0)
        : Number(reservation.totalAmount || 0);
      
      // Calculate balance
      const folioBalance = hasFolio ? Number(reservation.folio?.balance || 0) : (totalAmount - totalPaid);
      
      // Check if fully paid: paymentStatus is PAID, or folio balance is 0 or less, or total paid >= total amount
      const isPaid = paymentStatus === 'PAID' || 
                     (hasFolio && folioBalance <= 0) || 
                     (!hasFolio && totalPaid >= totalAmount && totalAmount > 0);

      // Check if reservation belongs to a group booking and if group is paid
      if (reservation.groupBookingId) {
        const groupBooking = groupBookings.find(g => g.id === reservation.groupBookingId);
        if (groupBooking && groupBooking.paymentStatus !== 'PAID') {
          toast({
            title: 'Group Booking Payment Required',
            description: `Cannot check out guest. This reservation belongs to group booking "${groupBooking.groupName}" which is not paid. Please pay the group booking first.`,
            variant: 'destructive',
          });
          setIsCheckingOut(false);
          return;
        }
      }

      if (!isPaid) {
        toast({
          title: 'Payment Required',
          description: `Cannot check out guest. Payment status: ${paymentStatus}. ${hasFolio ? `Balance: ${formatCurrency(folioBalance)}` : 'Please ensure payment is completed before checkout.'}`,
          variant: 'destructive',
        });
        setIsCheckingOut(false);
        return;
      }

      await apiService.checkOutReservation(reservationId);
      toast({
        title: 'Success',
        description: 'Guest checked out successfully',
      });
      loadReservations();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to check out',
        variant: 'destructive',
      });
    } finally {
      setIsCheckingOut(false);
    }
  };

  // Image upload handlers
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length !== files.length) {
      toast({
        title: tSync("Invalid Files"),
        description: tSync("Only image files are allowed"),
        variant: "destructive",
      });
    }
    
    if (roomImages.length + validImages.length > 10) {
      toast({
        title: tSync("Too Many Images"),
        description: tSync("Maximum 10 images allowed per room"),
        variant: "destructive",
      });
      return;
    }
    
    setRoomImages(prev => [...prev, ...validImages]);
    
    // Create previews
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setRoomImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image from upload queue
  const handleRemoveImage = (index: number) => {
    setRoomImages(prev => prev.filter((_, i) => i !== index));
    setRoomImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Upload images for existing room
  const uploadRoomImages = async (roomId: string) => {
    if (roomImages.length === 0) return;
    
    setUploadingImages(true);
    try {
      const formData = new FormData();
      roomImages.forEach(image => {
        formData.append('images', image);
      });
      
      await apiService.uploadRoomImages(roomId, formData);
      
      toast({
        title: tSync("Success"),
        description: tSync(`${roomImages.length} image(s) uploaded successfully`),
      });
      
      // Clear upload queue
      setRoomImages([]);
      setRoomImagePreviews([]);
      
      // Reload room images
      await loadRoomImages(roomId);
    } catch (error: any) {
      toast({
        title: tSync("Upload Failed"),
        description: error.message || tSync("Failed to upload images"),
        variant: "destructive",
      });
    } finally {
      setUploadingImages(false);
    }
  };

  // Load existing room images
  const loadRoomImages = async (roomId: string) => {
    try {
      const images = await apiService.getRoomImages(roomId);
      // Filter out invalid images before setting them
      const basicFiltered = filterValidImages(images);
      const validImages = await filterValidImagesAsync(basicFiltered);
      setExistingImages(validImages);
    } catch (error) {
      console.error('Failed to load room images:', error);
      setExistingImages([]);
    }
  };

  // Delete existing image
  const handleDeleteExistingImage = async (roomId: string, imageId: string) => {
    setDeletingImageId(imageId);
    try {
      await apiService.deleteRoomImage(roomId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Image deleted successfully"),
      });
      await loadRoomImages(roomId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to delete image"),
        variant: "destructive",
      });
    } finally {
      setDeletingImageId(null);
    }
  };

  // Set primary image
  const handleSetPrimaryImage = async (roomId: string, imageId: string) => {
    try {
      await apiService.setPrimaryRoomImage(roomId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Primary image updated"),
      });
      await loadRoomImages(roomId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to set primary image"),
        variant: "destructive",
      });
    }
  };

  // Conference Room Image handlers
  const handleConferenceRoomImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length !== files.length) {
      toast({
        title: tSync("Invalid Files"),
        description: tSync("Only image files are allowed"),
        variant: "destructive",
      });
    }
    
    if (conferenceRoomImages.length + validImages.length > 10) {
      toast({
        title: tSync("Too Many Images"),
        description: tSync("Maximum 10 images allowed"),
        variant: "destructive",
      });
      return;
    }
    
    setConferenceRoomImages(prev => [...prev, ...validImages]);
    
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setConferenceRoomImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveConferenceRoomImage = (index: number) => {
    setConferenceRoomImages(prev => prev.filter((_, i) => i !== index));
    setConferenceRoomImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadConferenceRoomImages = async (conferenceRoomId: string) => {
    if (conferenceRoomImages.length === 0) return;
    
    setUploadingConferenceRoomImages(true);
    try {
      const formData = new FormData();
      conferenceRoomImages.forEach(image => {
        formData.append('images', image);
      });
      
      await apiService.uploadConferenceRoomImages(conferenceRoomId, formData);
      
      toast({
        title: tSync("Success"),
        description: tSync(`${conferenceRoomImages.length} image(s) uploaded successfully`),
      });
      
      setConferenceRoomImages([]);
      setConferenceRoomImagePreviews([]);
      await loadConferenceRoomImages(conferenceRoomId);
    } catch (error: any) {
      toast({
        title: tSync("Upload Failed"),
        description: error.message || tSync("Failed to upload images"),
        variant: "destructive",
      });
    } finally {
      setUploadingConferenceRoomImages(false);
    }
  };

  const loadConferenceRoomImages = async (conferenceRoomId: string) => {
    try {
      const images = await apiService.getConferenceRoomImages(conferenceRoomId);
      setExistingConferenceRoomImages(images);
    } catch (error) {
      console.error('Failed to load conference room images:', error);
      setExistingConferenceRoomImages([]);
    }
  };

  const handleDeleteConferenceRoomImage = async (conferenceRoomId: string, imageId: string) => {
    setDeletingConferenceRoomImageId(imageId);
    try {
      await apiService.deleteConferenceRoomImage(conferenceRoomId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Image deleted successfully"),
      });
      await loadConferenceRoomImages(conferenceRoomId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to delete image"),
        variant: "destructive",
      });
    } finally {
      setDeletingConferenceRoomImageId(null);
    }
  };

  const handleSetPrimaryConferenceRoomImage = async (conferenceRoomId: string, imageId: string) => {
    try {
      await apiService.setPrimaryConferenceRoomImage(conferenceRoomId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Primary image updated"),
      });
      await loadConferenceRoomImages(conferenceRoomId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to set primary image"),
        variant: "destructive",
      });
    }
  };

  // Equipment Image handlers
  const handleEquipmentImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validImages = files.filter(file => file.type.startsWith('image/'));
    
    if (validImages.length !== files.length) {
      toast({
        title: tSync("Invalid Files"),
        description: tSync("Only image files are allowed"),
        variant: "destructive",
      });
    }
    
    if (equipmentImages.length + validImages.length > 10) {
      toast({
        title: tSync("Too Many Images"),
        description: tSync("Maximum 10 images allowed"),
        variant: "destructive",
      });
      return;
    }
    
    setEquipmentImages(prev => [...prev, ...validImages]);
    
    validImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEquipmentImagePreviews(prev => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveEquipmentImage = (index: number) => {
    setEquipmentImages(prev => prev.filter((_, i) => i !== index));
    setEquipmentImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const uploadEquipmentImages = async (equipmentId: string) => {
    if (equipmentImages.length === 0) return;
    
    setUploadingEquipmentImages(true);
    try {
      const formData = new FormData();
      equipmentImages.forEach(image => {
        formData.append('images', image);
      });
      
      await apiService.uploadEquipmentImages(equipmentId, formData);
      
      toast({
        title: tSync("Success"),
        description: tSync(`${equipmentImages.length} image(s) uploaded successfully`),
      });
      
      setEquipmentImages([]);
      setEquipmentImagePreviews([]);
      await loadEquipmentImages(equipmentId);
    } catch (error: any) {
      toast({
        title: tSync("Upload Failed"),
        description: error.message || tSync("Failed to upload images"),
        variant: "destructive",
      });
    } finally {
      setUploadingEquipmentImages(false);
    }
  };

  const loadEquipmentImages = async (equipmentId: string) => {
    try {
      const images = await apiService.getEquipmentImages(equipmentId);
      setExistingEquipmentImages(images);
    } catch (error) {
      console.error('Failed to load equipment images:', error);
      setExistingEquipmentImages([]);
    }
  };

  const handleDeleteEquipmentImage = async (equipmentId: string, imageId: string) => {
    setDeletingEquipmentImageId(imageId);
    try {
      await apiService.deleteEquipmentImage(equipmentId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Image deleted successfully"),
      });
      await loadEquipmentImages(equipmentId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to delete image"),
        variant: "destructive",
      });
    } finally {
      setDeletingEquipmentImageId(null);
    }
  };

  const handleSetPrimaryEquipmentImage = async (equipmentId: string, imageId: string) => {
    try {
      await apiService.setPrimaryEquipmentImage(equipmentId, imageId);
      toast({
        title: tSync("Success"),
        description: tSync("Primary image updated"),
      });
      await loadEquipmentImages(equipmentId);
    } catch (error: any) {
      toast({
        title: tSync("Error"),
        description: error.message || tSync("Failed to set primary image"),
        variant: "destructive",
      });
    }
  };

  // Helper function to test if an image URL is actually loadable
  const testImageUrl = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      // For GridFS URLs with tokens, we need to be more lenient
      // as they might have authentication issues in the Image constructor
      if (url.includes('/api/files/download/') && url.includes('token=')) {
        resolve(true); // Assume it's valid and let the actual img element handle it
        return;
      }
      
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = url;
      
      // Set a shorter timeout for faster response
      setTimeout(() => resolve(false), 2000);
    });
  };

  // Helper function to filter out potentially invalid images (async version)
  const filterValidImagesAsync = async (images: any[]): Promise<any[]> => {
    if (!images || images.length === 0) return [];
    
    const validImages = [];
    
    for (const image of images) {
      // Basic validation first
      if (!image.id || !image.imageUrl) {
        continue;
      }
      
      if (typeof image.imageUrl !== 'string' || image.imageUrl.trim() === '') {
        continue;
      }
      
      // Construct full URL
      const imageUrl = image.imageUrl?.startsWith('http') 
        ? image.imageUrl 
        : `${apiService['baseUrl']}${image.imageUrl}`;
      
      // Test if image can actually be loaded
      const isLoadable = await testImageUrl(imageUrl);
      if (isLoadable) {
        validImages.push(image);
      }
    }
    
    return validImages;
  };

  // Helper function to filter out potentially invalid images (sync version for basic checks)
  const filterValidImages = (images: any[]): any[] => {
    if (!images || images.length === 0) return [];
    
    return images.filter(image => {
      // Check if image has required properties
      if (!image.id || !image.imageUrl) {
        return false;
      }
      
      // Check if imageUrl looks valid
      if (typeof image.imageUrl !== 'string' || image.imageUrl.trim() === '') {
        return false;
      }
      
      // Check for common patterns that indicate deleted/invalid images
      const url = image.imageUrl.toLowerCase();
      if (url.includes('undefined') || url.includes('null') || url === '/undefined' || url === '/null') {
        return false;
      }
      
      // Additional check: if the image URL looks like a GridFS path but seems suspicious
      if (url.includes('/api/files/') && (url.length < 20 || url.includes('..') || url.includes('///'))) {
        return false;
      }
      
      return true;
    });
  };

  // View room details
  const handleViewRoom = async (room: any) => {
    setLoadingRoomDetails(true);
    setShowRoomDetailsDialog(true);
    setSelectedRoomDetails(null); // Clear previous data
    
    try {
      // Always fetch full room details to ensure amenities are included with proper structure
      const roomDetails = await apiService.getHotelRoom(room.id);
      
      // If API doesn't return amenities but original room object has them, use those
      if ((!roomDetails.amenities || roomDetails.amenities.length === 0) && room.amenities && room.amenities.length > 0) {
        roomDetails.amenities = room.amenities;
      }
      
      // Always fetch fresh images to ensure we have the most up-to-date data (same as edit mode)
      try {
        const images = await apiService.getRoomImages(room.id);
        // Filter out invalid images before setting them
        const basicFiltered = filterValidImages(images);
        const validImages = await filterValidImagesAsync(basicFiltered);
        setSelectedRoomDetails({ ...roomDetails, images: validImages });
      } catch (imageError) {
        // If image loading fails, still show room with amenities
        console.warn('Failed to load images:', imageError);
        setSelectedRoomDetails(roomDetails);
      }
    } catch (error) {
      console.error('Failed to load room details:', error);
      // Fallback to room object if API call fails
      setSelectedRoomDetails(room);
      toast({
        title: tSync("Error"),
        description: tSync("Failed to load room details"),
        variant: "destructive",
      });
    } finally {
      setLoadingRoomDetails(false);
    }
  };

  // Conference Room handlers
  const handleCreateConferenceRoom = async () => {
    if (!companyId) {
      toast({
        title: 'Error',
        description: 'Please select a company before creating a conference room',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingConferenceRoom(true);
    try {
      if (selectedConferenceRoom) {
        // Update existing
        await apiService.updateConferenceRoom(selectedConferenceRoom.id, {
          name: conferenceRoomForm.name,
          roomNumber: conferenceRoomForm.roomNumber || undefined,
          capacity: parseInt(conferenceRoomForm.capacity),
          floor: conferenceRoomForm.floor ? parseInt(conferenceRoomForm.floor) : undefined,
          size: conferenceRoomForm.size || undefined,
          hourlyRate: conferenceRoomForm.hourlyRate ? parseFloat(conferenceRoomForm.hourlyRate) : undefined,
          dailyRate: conferenceRoomForm.dailyRate ? parseFloat(conferenceRoomForm.dailyRate) : undefined,
          amenities: conferenceRoomForm.amenities || undefined,
          description: conferenceRoomForm.description || undefined,
          isActive: conferenceRoomForm.isActive,
          status: conferenceRoomForm.status,
        });
        toast({
          title: 'Success',
          description: 'Conference room updated successfully',
        });
      } else {
        // Create new
        const newConferenceRoom = await apiService.createConferenceRoom({
          companyId,
          name: conferenceRoomForm.name,
          roomNumber: conferenceRoomForm.roomNumber || undefined,
          capacity: parseInt(conferenceRoomForm.capacity),
          floor: conferenceRoomForm.floor ? parseInt(conferenceRoomForm.floor) : undefined,
          size: conferenceRoomForm.size || undefined,
          hourlyRate: conferenceRoomForm.hourlyRate ? parseFloat(conferenceRoomForm.hourlyRate) : undefined,
          dailyRate: conferenceRoomForm.dailyRate ? parseFloat(conferenceRoomForm.dailyRate) : undefined,
          amenities: conferenceRoomForm.amenities || undefined,
          description: conferenceRoomForm.description || undefined,
          isActive: conferenceRoomForm.isActive,
          status: conferenceRoomForm.status,
        });
        
        // Upload images if any
        if (conferenceRoomImages.length > 0) {
          await uploadConferenceRoomImages(newConferenceRoom.id);
        }
        
        toast({
          title: 'Success',
          description: 'Conference room created successfully',
        });
      }
      
      // Upload images for update
      if (selectedConferenceRoom && conferenceRoomImages.length > 0) {
        await uploadConferenceRoomImages(selectedConferenceRoom.id);
      }
      
      setShowConferenceRoomDialog(false);
      setSelectedConferenceRoom(null);
      setConferenceRoomImages([]);
      setConferenceRoomImagePreviews([]);
      loadConferenceRooms();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save conference room',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingConferenceRoom(false);
    }
  };

  // Equipment handlers
  const handleCreateEquipment = async () => {
    if (!companyId) {
      toast({
        title: 'Error',
        description: 'Please select a company before creating equipment',
        variant: 'destructive',
      });
      return;
    }

    setIsCreatingEquipment(true);
    try {
      if (selectedEquipment) {
        // Update existing
        await apiService.updateEquipment(selectedEquipment.id, {
          name: equipmentForm.name,
          equipmentNumber: equipmentForm.equipmentNumber || undefined,
          category: equipmentForm.category || undefined,
          brand: equipmentForm.brand || undefined,
          model: equipmentForm.model || undefined,
          hourlyRate: equipmentForm.hourlyRate ? parseFloat(equipmentForm.hourlyRate) : undefined,
          dailyRate: equipmentForm.dailyRate ? parseFloat(equipmentForm.dailyRate) : undefined,
          description: equipmentForm.description || undefined,
          isActive: equipmentForm.isActive,
          status: equipmentForm.status,
          quantity: parseInt(equipmentForm.quantity),
        });
        toast({
          title: 'Success',
          description: 'Equipment updated successfully',
        });
      } else {
        // Create new
        const newEquipment = await apiService.createEquipment({
          companyId,
          name: equipmentForm.name,
          equipmentNumber: equipmentForm.equipmentNumber || undefined,
          category: equipmentForm.category || undefined,
          brand: equipmentForm.brand || undefined,
          model: equipmentForm.model || undefined,
          hourlyRate: equipmentForm.hourlyRate ? parseFloat(equipmentForm.hourlyRate) : undefined,
          dailyRate: equipmentForm.dailyRate ? parseFloat(equipmentForm.dailyRate) : undefined,
          description: equipmentForm.description || undefined,
          isActive: equipmentForm.isActive,
          status: equipmentForm.status,
          quantity: parseInt(equipmentForm.quantity),
        });
        
        // Upload images if any
        if (equipmentImages.length > 0) {
          await uploadEquipmentImages(newEquipment.id);
        }
        
        toast({
          title: 'Success',
          description: 'Equipment created successfully',
        });
      }
      
      // Upload images for update
      if (selectedEquipment && equipmentImages.length > 0) {
        await uploadEquipmentImages(selectedEquipment.id);
      }
      
      setShowEquipmentDialog(false);
      setSelectedEquipment(null);
      setEquipmentImages([]);
      setEquipmentImagePreviews([]);
      loadEquipment();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save equipment',
        variant: 'destructive',
      });
    } finally {
      setIsCreatingEquipment(false);
    }
  };

  const handleEditRoom = async (room: any) => {
    setSelectedRoom(room);
    
    // Extract amenity IDs - handle both structures
    const amenityIds = room.amenities?.map((a: any) => {
      // Handle { amenityId: '...' } or { amenity: { id: '...' } } or { id: '...' }
      return a.amenityId || a.amenity?.id || a.id;
    }).filter((id: any) => id) || [];
    
    setRoomForm({
      roomNumber: room.roomNumber,
      title: room.title || '',
      roomTypeId: room.roomTypeId || '',
      floor: room.floor?.toString() || '',
      capacity: room.capacity.toString(),
      size: room.size || '',
      basePricePerNight: room.basePricePerNight.toString(),
      currency: room.currency || 'RWF',
      description: room.description || '',
      isActive: room.isActive,
      amenityIds: amenityIds,
    });
    
    // Always load images from API to ensure we have the latest data
    if (room.id) {
      await loadRoomImages(room.id);
    }
    
    setShowRoomDialog(true);
  };

  const handleDeleteRoom = async (roomId: string) => {
    setConfirmDialogConfig({
      title: 'Delete Room',
      description: 'Are you sure you want to delete this room? This action cannot be undone.',
      onConfirm: () => {
        setShowConfirmDialog(false);
        performDeleteRoom(roomId);
      },
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    setShowConfirmDialog(true);
  };

  const performDeleteRoom = async (roomId: string) => {
    setIsDeletingRoom(true);
    try {
      await apiService.deleteHotelRoom(roomId);
      toast({
        title: 'Success',
        description: 'Room deleted successfully',
      });
      loadRooms();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete room',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingRoom(false);
    }
  };

  // Check if a room is reserved based on active reservations (using memoized version)
  const isRoomReserved = (roomId: string): boolean => {
    return isRoomReservedMemo(roomId);
  };

  // Memoized room reservation check
  const isRoomReservedMemo = useMemo(() => {
    const reservedRoomIds = new Set(
      reservations
        .filter(r => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT')
        .map(r => r.roomId)
    );
    return (roomId: string) => reservedRoomIds.has(roomId);
  }, [reservations]);

  // Get booked dates for a room (formatted string)
  const getRoomBookedDates = useMemo(() => {
    const activeReservations = reservations.filter(
      r => r.status !== 'CANCELLED' && r.status !== 'CHECKED_OUT'
    );
    
    return (roomId: string): string => {
      const roomReservations = activeReservations
        .filter(r => r.roomId === roomId)
        .sort((a, b) => new Date(a.checkInDate).getTime() - new Date(b.checkInDate).getTime());
      
      if (roomReservations.length === 0) {
        return '';
      }
      
      // Format dates: "Jan 15 - Jan 20, Jan 25 - Jan 30"
      const dateRanges = roomReservations.map(res => {
        const checkIn = new Date(res.checkInDate);
        const checkOut = new Date(res.checkOutDate);
        const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
      });
      
      return dateRanges.join(', ');
    };
  }, [reservations]);

  // Filter and pagination helper functions for rooms (memoized)
  const filteredRooms = useMemo(() => {
    return rooms.filter((room) => {
      // Filter by active status
      if (roomsFilters.isActive !== 'all') {
        const isActive = roomsFilters.isActive === 'active';
        if (room.isActive !== isActive) return false;
      }

      // Filter by reservation status
      if (roomsFilters.reservationStatus !== 'all') {
        const isReserved = isRoomReservedMemo(room.id);
        if (roomsFilters.reservationStatus === 'reserved' && !isReserved) return false;
        if (roomsFilters.reservationStatus === 'notReserved' && isReserved) return false;
      }

      // Filter by room type
      if (roomsFilters.roomTypeId !== 'all' && room.roomTypeId !== roomsFilters.roomTypeId) {
        return false;
      }

      // Filter by floor
      if (roomsFilters.floor && room.floor?.toString() !== roomsFilters.floor) {
        return false;
      }

      // Filter by search (room number)
      if (roomsFilters.search) {
        const searchLower = roomsFilters.search.toLowerCase();
        if (!room.roomNumber?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [rooms, roomsFilters, isRoomReservedMemo]);

  const getPaginatedRooms = useMemo(() => {
    const startIndex = (roomsPage - 1) * roomsPerPage;
    const endIndex = startIndex + roomsPerPage;
    return filteredRooms.slice(startIndex, endIndex);
  }, [filteredRooms, roomsPage, roomsPerPage]);

  const totalRoomsPages = Math.ceil(filteredRooms.length / roomsPerPage);

  // Filter and pagination helper functions for reservations (memoized)
  const filteredReservations = useMemo(() => {
    return reservations.filter((reservation) => {
      // Filter by status
      if (reservationsFilters.status === 'active') {
        // Show only PENDING, CONFIRMED, and CHECKED_IN
        if (!['PENDING', 'CONFIRMED', 'CHECKED_IN'].includes(reservation.status)) {
          return false;
        }
      } else if (reservationsFilters.status !== 'all' && reservation.status !== reservationsFilters.status) {
        return false;
      }

      // Filter by guest name
      if (reservationsFilters.guestSearch) {
        const searchLower = reservationsFilters.guestSearch.toLowerCase();
        const guestName = `${reservation.guest?.firstName || ''} ${reservation.guest?.lastName || ''}`.toLowerCase();
        if (!guestName.includes(searchLower)) {
          return false;
        }
      }

      // Filter by room number
      if (reservationsFilters.roomNumber) {
        const roomNumber = reservation.room?.roomNumber?.toString() || '';
        if (!roomNumber.includes(reservationsFilters.roomNumber)) {
          return false;
        }
      }

      // Filter by reservation number
      if (reservationsFilters.reservationNumber) {
        const reservationNumber = reservation.reservationNumber?.toString() || '';
        if (!reservationNumber.toLowerCase().includes(reservationsFilters.reservationNumber.toLowerCase())) {
          return false;
        }
      }

      // Filter by payment status
      if (reservationsFilters.paymentStatus !== 'all') {
        const paymentStatus = reservation.paymentStatus || 'UNPAID';
        if (paymentStatus !== reservationsFilters.paymentStatus) {
          return false;
        }
      }

      return true;
    });
  }, [reservations, reservationsFilters]);

  // Helper function to check if a reservation is overdue
  const isReservationOverdue = (reservation: any): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Check if checkout date has passed for CHECKED_IN reservations
    if (reservation.status === 'CHECKED_IN') {
      const checkOutDate = new Date(reservation.checkOutDate);
      checkOutDate.setHours(0, 0, 0, 0);
      return checkOutDate < today;
    }
    
    // Check if check-in date has passed for PENDING/CONFIRMED reservations
    if (reservation.status === 'PENDING' || reservation.status === 'CONFIRMED') {
      const checkInDate = new Date(reservation.checkInDate);
      checkInDate.setHours(0, 0, 0, 0);
      return checkInDate < today;
    }
    
    return false;
  };

  const getPaginatedReservations = useMemo(() => {
    const startIndex = (reservationsPage - 1) * reservationsPerPage;
    const endIndex = startIndex + reservationsPerPage;
    return filteredReservations.slice(startIndex, endIndex);
  }, [filteredReservations, reservationsPage, reservationsPerPage]);

  const totalReservationsPages = Math.ceil(filteredReservations.length / reservationsPerPage);

  // Group bookings pagination
  const getPaginatedGroupBookings = () => {
    const startIndex = (groupBookingsPage - 1) * groupBookingsPerPage;
    const endIndex = startIndex + groupBookingsPerPage;
    return groupBookings.slice(startIndex, endIndex);
  };

  const totalGroupBookingsPages = Math.ceil(groupBookings.length / groupBookingsPerPage);

  // Waitlist pagination
  const activeWaitlist = waitlist.filter(w => w.status === 'ACTIVE').sort((a, b) => (b.priority || 0) - (a.priority || 0));
  const getPaginatedWaitlist = () => {
    const startIndex = (waitlistPage - 1) * waitlistPerPage;
    const endIndex = startIndex + waitlistPerPage;
    return activeWaitlist.slice(startIndex, endIndex);
  };
  const totalWaitlistPages = Math.ceil(activeWaitlist.length / waitlistPerPage);

  // Filter and pagination helper functions for guests (memoized)
  const filteredGuests = useMemo(() => {
    return guests.filter((guest) => {
      // Filter by search (name, email, phone)
      if (guestsFilters.search) {
        const searchLower = guestsFilters.search.toLowerCase();
        const fullName = `${guest.firstName || ''} ${guest.lastName || ''}`.toLowerCase();
        const email = (guest.email || '').toLowerCase();
        const phone = (guest.phone || '').toLowerCase();
        
        if (!fullName.includes(searchLower) && !email.includes(searchLower) && !phone.includes(searchLower)) {
          return false;
        }
      }

      // Filter by city
      if (guestsFilters.city) {
        const cityLower = guestsFilters.city.toLowerCase();
        if (!guest.city?.toLowerCase().includes(cityLower)) {
          return false;
        }
      }

      // Filter by state
      if (guestsFilters.state) {
        const stateLower = guestsFilters.state.toLowerCase();
        if (!guest.state?.toLowerCase().includes(stateLower)) {
          return false;
        }
      }

      return true;
    });
  }, [guests, guestsFilters]);

  const getPaginatedGuests = useMemo(() => {
    const startIndex = (guestsPage - 1) * guestsPerPage;
    const endIndex = startIndex + guestsPerPage;
    return filteredGuests.slice(startIndex, endIndex);
  }, [filteredGuests, guestsPage, guestsPerPage]);

  const totalGuestsPages = Math.ceil(filteredGuests.length / guestsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setRoomsPage(1);
  }, [roomsFilters]);

  useEffect(() => {
    setReservationsPage(1);
  }, [reservationsFilters]);

  useEffect(() => {
    setGuestsPage(1);
  }, [guestsFilters]);

  // Filter and pagination helper functions for available rooms
  const getFilteredAvailableRooms = () => {
    // Backend already filters out reserved rooms, so we just apply UI filters
    return availableRooms.filter((room) => {
      // Filter by search (room number)
      if (availableRoomsFilters.search) {
        const searchLower = availableRoomsFilters.search.toLowerCase();
        if (!room.roomNumber?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Filter by room type
      if (availableRoomsFilters.roomTypeId !== 'all' && room.roomTypeId !== availableRoomsFilters.roomTypeId) {
        return false;
      }

      // Filter by floor
      if (availableRoomsFilters.floor && room.floor?.toString() !== availableRoomsFilters.floor) {
        return false;
      }

      // Filter by min price
      if (availableRoomsFilters.minPrice) {
        const minPrice = parseFloat(availableRoomsFilters.minPrice);
        if (room.basePricePerNight < minPrice) {
          return false;
        }
      }

      // Filter by max price
      if (availableRoomsFilters.maxPrice) {
        const maxPrice = parseFloat(availableRoomsFilters.maxPrice);
        if (room.basePricePerNight > maxPrice) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredAvailableRooms = getFilteredAvailableRooms();

  const getPaginatedAvailableRooms = () => {
    const startIndex = (availableRoomsPage - 1) * availableRoomsPerPage;
    const endIndex = startIndex + availableRoomsPerPage;
    return filteredAvailableRooms.slice(startIndex, endIndex);
  };

  const totalAvailableRoomsPages = Math.ceil(filteredAvailableRooms.length / availableRoomsPerPage);

  useEffect(() => {
    setAvailableRoomsPage(1);
  }, [availableRoomsFilters]);

  useEffect(() => {
    if (filteredAvailableRooms.length > 0 && availableRoomsPage > totalAvailableRoomsPages) {
      setAvailableRoomsPage(1);
    }
  }, [filteredAvailableRooms.length, totalAvailableRoomsPages]);

  // Filter and pagination helper functions for folios
  const getFilteredFolios = () => {
    return folios.filter((folio) => {
      // Filter by reservation number
      if (foliosFilters.reservationNumber) {
        const reservationNumber = folio.reservation?.reservationNumber?.toString() || '';
        const filterValue = foliosFilters.reservationNumber.toLowerCase();
        const matches = reservationNumber.toLowerCase().includes(filterValue);
        
        // Debug logging
        if (foliosFilters.reservationNumber && folios.length > 0 && folios.indexOf(folio) === 0) {
          console.log('Reservation filter debug:', {
            filterValue,
            reservationNumber,
            matches,
            folioData: folio.reservation
          });
        }
        
        if (!matches) {
          return false;
        }
      }

      // Filter by guest name
      if (foliosFilters.guestSearch) {
        const searchLower = foliosFilters.guestSearch.toLowerCase();
        const guestName = `${folio.reservation?.guest?.firstName || ''} ${folio.reservation?.guest?.lastName || ''}`.toLowerCase();
        if (!guestName.includes(searchLower)) {
          return false;
        }
      }

      // Filter by status
      if (foliosFilters.status !== 'all' && folio.status !== foliosFilters.status) {
        return false;
      }

      // Filter by min total amount
      if (foliosFilters.minTotalAmount) {
        const minTotal = parseFloat(foliosFilters.minTotalAmount);
        if (folio.totalAmount < minTotal) {
          return false;
        }
      }

      // Filter by max total amount
      if (foliosFilters.maxTotalAmount) {
        const maxTotal = parseFloat(foliosFilters.maxTotalAmount);
        if (folio.totalAmount > maxTotal) {
          return false;
        }
      }

      // Filter by min balance
      if (foliosFilters.minBalance) {
        const minBalance = parseFloat(foliosFilters.minBalance);
        if (folio.balance < minBalance) {
          return false;
        }
      }

      // Filter by max balance
      if (foliosFilters.maxBalance) {
        const maxBalance = parseFloat(foliosFilters.maxBalance);
        if (folio.balance > maxBalance) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredFolios = getFilteredFolios();

  const getPaginatedFolios = () => {
    const startIndex = (foliosPage - 1) * foliosPerPage;
    const endIndex = startIndex + foliosPerPage;
    return filteredFolios.slice(startIndex, endIndex);
  };

  const totalFoliosPages = Math.ceil(filteredFolios.length / foliosPerPage);

  useEffect(() => {
    setFoliosPage(1);
  }, [foliosFilters]);

  useEffect(() => {
    if (filteredFolios.length > 0 && foliosPage > totalFoliosPages) {
      setFoliosPage(1);
    }
  }, [filteredFolios.length, totalFoliosPages]);

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs, textareas, or contenteditable elements
      const target = e.target as HTMLElement;
      if (!target) return;
      
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      const isContentEditable = target.isContentEditable;
      const isSelect = target.tagName === 'SELECT' || target.closest('select');
      
      // Allow shortcuts in search inputs, but block in other inputs
      if ((isInput || isContentEditable || isSelect) && !target.closest('[role="combobox"]')) {
        return;
      }

      // Only handle Ctrl/Cmd combinations
      if (!(e.ctrlKey || e.metaKey)) {
        // Handle Escape key for closing modals
        if (e.key === 'Escape') {
          setShowRoomDialog(false);
          setShowReservationDialog(false);
          setShowGuestDialog(false);
          setShowServiceDialog(false);
          setShowPaymentDialog(false);
          setShowHousekeepingDialog(false);
          setShowMaintenanceDialog(false);
          setShowKeyCardDialog(false);
          setShowGroupBookingDialog(false);
          setShowWaitlistDialog(false);
          setShowPromotionDialog(false);
          setShowReservationDetailsDialog(false);
          setShowEditReservationDialog(false);
        }
        return;
      }

      // Prevent default browser behavior
      e.preventDefault();
      e.stopPropagation();

      // Hotel Navigation Shortcuts (Ctrl+1-7)
      if (!e.shiftKey && !e.altKey) {
        if (e.key === '1') {
          setActiveTab('rooms');
        } else if (e.key === '2') {
          setActiveTab('reservations');
        } else if (e.key === '3') {
          setActiveTab('guests');
        } else if (e.key === '4') {
          setActiveTab('availability');
        } else if (e.key === '5') {
          setActiveTab('folio');
        } else if (e.key === '6') {
          setActiveTab('services');
        } else if (e.key === '7') {
          setActiveTab('payments');
        }
      }

      // Hotel Action Shortcuts
      if (!e.shiftKey && !e.altKey) {
        // Ctrl+N: Add new item based on active tab
        if (e.key === 'n' || e.key === 'N') {
          if (activeTab === 'rooms') {
            setShowRoomDialog(true);
          } else if (activeTab === 'reservations') {
            setShowReservationDialog(true);
            // Load promotions when opening reservation dialog
            if (companyId) {
              loadPromotions();
            }
          } else if (activeTab === 'guests') {
            setShowGuestDialog(true);
          } else if (activeTab === 'services') {
            setShowServiceDialog(true);
          } else if (activeTab === 'payments') {
            setShowPaymentDialog(true);
          } else if (activeTab === 'housekeeping') {
            setShowHousekeepingDialog(true);
          } else if (activeTab === 'maintenance') {
            setShowMaintenanceDialog(true);
          } else if (activeTab === 'keyCards') {
            setShowKeyCardDialog(true);
          } else if (activeTab === 'groupBookings') {
            setShowGroupBookingDialog(true);
          } else if (activeTab === 'waitlist') {
            setShowWaitlistDialog(true);
          } else if (activeTab === 'promotions') {
            setShowPromotionDialog(true);
          }
        }
        // Ctrl+F: Toggle filters based on active tab
        else if (e.key === 'f' || e.key === 'F') {
          if (activeTab === 'rooms') {
            setShowRoomsFilters(!showRoomsFilters);
          } else if (activeTab === 'reservations') {
            setShowReservationsFilters(!showReservationsFilters);
          } else if (activeTab === 'guests') {
            setShowGuestsFilters(!showGuestsFilters);
          } else if (activeTab === 'availability') {
            setShowAvailableRoomsFilters(!showAvailableRoomsFilters);
          } else if (activeTab === 'folio') {
            setShowFoliosFilters(!showFoliosFilters);
          } else if (activeTab === 'services') {
            setShowServicesFilters(!showServicesFilters);
          }
        }
        // Ctrl+R: Refresh current tab
        else if (e.key === 'r' || e.key === 'R') {
          if (activeTab === 'rooms') {
            loadRooms();
            setRoomsPage(1);
          } else if (activeTab === 'reservations') {
            loadReservations();
            setReservationsPage(1);
          } else if (activeTab === 'guests') {
            loadGuests();
            setGuestsPage(1);
          } else if (activeTab === 'availability') {
            handleCheckAvailability();
            setAvailableRoomsPage(1);
          } else if (activeTab === 'folio') {
            loadFolios();
            setFoliosPage(1);
          } else if (activeTab === 'services') {
            loadServices();
            setServicesPage(1);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [
    activeTab, 
    showRoomsFilters, 
    showReservationsFilters, 
    showGuestsFilters, 
    showAvailableRoomsFilters, 
    showFoliosFilters, 
    showServicesFilters,
    loadRooms,
    loadReservations,
    loadGuests,
    loadFolios,
    loadServices,
    handleCheckAvailability
  ]);

  // Filter and pagination helper functions for services
  const getFilteredServices = () => {
    return services.filter((service) => {
      // Get reservation for this service
      const reservation = reservations.find(r => r.id === service.reservationId);
      const reservationNumber = reservation?.reservationNumber || '';

      // Filter by reservation number
      if (servicesFilters.reservationNumber) {
        if (!reservationNumber.toLowerCase().includes(servicesFilters.reservationNumber.toLowerCase())) {
          return false;
        }
      }

      // Filter by service name
      if (servicesFilters.serviceName) {
        const serviceName = (service.serviceName || '').toLowerCase();
        if (!serviceName.includes(servicesFilters.serviceName.toLowerCase())) {
          return false;
        }
      }

      // Filter by service type
      if (servicesFilters.serviceType !== 'all' && service.serviceType !== servicesFilters.serviceType) {
        return false;
      }

      // Filter by status
      if (servicesFilters.status !== 'all' && service.status !== servicesFilters.status) {
        return false;
      }

      // Filter by min price
      if (servicesFilters.minPrice) {
        const minPrice = parseFloat(servicesFilters.minPrice);
        if (service.unitPrice < minPrice) {
          return false;
        }
      }

      // Filter by max price
      if (servicesFilters.maxPrice) {
        const maxPrice = parseFloat(servicesFilters.maxPrice);
        if (service.unitPrice > maxPrice) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredServices = getFilteredServices();

  const getPaginatedServices = () => {
    const startIndex = (servicesPage - 1) * servicesPerPage;
    const endIndex = startIndex + servicesPerPage;
    return filteredServices.slice(startIndex, endIndex);
  };

  const totalServicesPages = Math.ceil(filteredServices.length / servicesPerPage);

  useEffect(() => {
    setServicesPage(1);
  }, [servicesFilters]);

  useEffect(() => {
    if (filteredServices.length > 0 && servicesPage > totalServicesPages) {
      setServicesPage(1);
    }
  }, [filteredServices.length, totalServicesPages]);

  // Filter and pagination helper functions for housekeeping
  const getFilteredHousekeepingTasks = () => {
    return housekeepingTasks.filter((task) => {
      // Filter by room number
      if (housekeepingFilters.roomNumber) {
        const roomNumber = task.room?.roomNumber?.toString() || '';
        if (!roomNumber.includes(housekeepingFilters.roomNumber)) {
          return false;
        }
      }

      // Filter by status
      if (housekeepingFilters.status !== 'all' && task.status !== housekeepingFilters.status) {
        return false;
      }

      // Filter by priority
      if (housekeepingFilters.priority !== 'all' && task.priority !== housekeepingFilters.priority) {
        return false;
      }

      // Filter by assigned to
      if (housekeepingFilters.assignedTo) {
        const assignedTo = (task.assignedTo || '').toLowerCase();
        if (!assignedTo.includes(housekeepingFilters.assignedTo.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredHousekeepingTasks = getFilteredHousekeepingTasks();

  const getPaginatedHousekeepingTasks = () => {
    const startIndex = (housekeepingPage - 1) * housekeepingPerPage;
    const endIndex = startIndex + housekeepingPerPage;
    return filteredHousekeepingTasks.slice(startIndex, endIndex);
  };

  const totalHousekeepingPages = Math.ceil(filteredHousekeepingTasks.length / housekeepingPerPage);

  useEffect(() => {
    setHousekeepingPage(1);
  }, [housekeepingFilters]);

  useEffect(() => {
    if (filteredHousekeepingTasks.length > 0 && housekeepingPage > totalHousekeepingPages) {
      setHousekeepingPage(1);
    }
  }, [filteredHousekeepingTasks.length, totalHousekeepingPages]);

  // Filter and pagination helper functions for maintenance
  const getFilteredMaintenanceRequests = () => {
    return maintenanceRequests.filter((request) => {
      // Filter by room number
      if (maintenanceFilters.roomNumber) {
        const roomNumber = request.room?.roomNumber?.toString() || '';
        if (!roomNumber.includes(maintenanceFilters.roomNumber)) {
          return false;
        }
      }

      // Filter by status
      if (maintenanceFilters.status !== 'all' && request.status !== maintenanceFilters.status) {
        return false;
      }

      // Filter by priority
      if (maintenanceFilters.priority !== 'all' && request.priority !== maintenanceFilters.priority) {
        return false;
      }

      // Filter by assigned to
      if (maintenanceFilters.assignedTo) {
        const assignedTo = (request.assignedTo || '').toLowerCase();
        if (!assignedTo.includes(maintenanceFilters.assignedTo.toLowerCase())) {
          return false;
        }
      }

      // Filter by requested by
      if (maintenanceFilters.requestedBy) {
        const requestedBy = (request.requestedBy || '').toLowerCase();
        if (!requestedBy.includes(maintenanceFilters.requestedBy.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredMaintenanceRequests = getFilteredMaintenanceRequests();

  const getPaginatedMaintenanceRequests = () => {
    const startIndex = (maintenancePage - 1) * maintenancePerPage;
    const endIndex = startIndex + maintenancePerPage;
    return filteredMaintenanceRequests.slice(startIndex, endIndex);
  };

  const totalMaintenancePages = Math.ceil(filteredMaintenanceRequests.length / maintenancePerPage);

  useEffect(() => {
    setMaintenancePage(1);
  }, [maintenanceFilters]);

  useEffect(() => {
    if (filteredMaintenanceRequests.length > 0 && maintenancePage > totalMaintenancePages) {
      setMaintenancePage(1);
    }
  }, [filteredMaintenanceRequests.length, totalMaintenancePages]);

  // Filter and pagination helper functions for key cards
  const getFilteredKeyCards = () => {
    return keyCards.filter((card) => {
      // Filter by card number
      if (keyCardsFilters.cardNumber) {
        const cardNumber = (card.cardNumber || '').toLowerCase();
        if (!cardNumber.includes(keyCardsFilters.cardNumber.toLowerCase())) {
          return false;
        }
      }

      // Filter by room number
      if (keyCardsFilters.roomNumber) {
        const roomNumber = card.room?.roomNumber?.toString() || '';
        if (!roomNumber.includes(keyCardsFilters.roomNumber)) {
          return false;
        }
      }

      // Filter by reservation number
      if (keyCardsFilters.reservationNumber) {
        const reservationNumber = card.reservation?.reservationNumber?.toLowerCase() || '';
        if (!reservationNumber.includes(keyCardsFilters.reservationNumber.toLowerCase())) {
          return false;
        }
      }

      // Filter by status
      if (keyCardsFilters.status !== 'all' && card.status !== keyCardsFilters.status) {
        return false;
      }

      return true;
    });
  };

  const filteredKeyCards = getFilteredKeyCards();

  const getPaginatedKeyCards = () => {
    const startIndex = (keyCardsPage - 1) * keyCardsPerPage;
    const endIndex = startIndex + keyCardsPerPage;
    return filteredKeyCards.slice(startIndex, endIndex);
  };

  const totalKeyCardsPages = Math.ceil(filteredKeyCards.length / keyCardsPerPage);

  useEffect(() => {
    setKeyCardsPage(1);
  }, [keyCardsFilters]);

  useEffect(() => {
    if (filteredKeyCards.length > 0 && keyCardsPage > totalKeyCardsPages) {
      setKeyCardsPage(1);
    }
  }, [filteredKeyCards.length, totalKeyCardsPages]);

  // Reset page if out of bounds when data changes
  useEffect(() => {
    if (filteredRooms.length > 0 && roomsPage > totalRoomsPages) {
      setRoomsPage(1);
    }
  }, [filteredRooms.length, totalRoomsPages]);

  useEffect(() => {
    if (filteredReservations.length > 0 && reservationsPage > totalReservationsPages) {
      setReservationsPage(1);
    }
  }, [filteredReservations.length, totalReservationsPages]);

  // Reset group bookings page when group bookings change
  useEffect(() => {
    if (groupBookings.length > 0 && groupBookingsPage > totalGroupBookingsPages) {
      setGroupBookingsPage(1);
    }
  }, [groupBookings.length, totalGroupBookingsPages]);

  // Reset waitlist page when waitlist changes
  useEffect(() => {
    const active = waitlist.filter(w => w.status === 'ACTIVE');
    const totalPages = Math.ceil(active.length / waitlistPerPage);
    if (active.length > 0 && waitlistPage > totalPages) {
      setWaitlistPage(1);
    }
  }, [waitlist.length, waitlistPerPage]);

  useEffect(() => {
    if (filteredGuests.length > 0 && guestsPage > totalGuestsPages) {
      setGuestsPage(1);
    }
  }, [filteredGuests.length, totalGuestsPages]);

  // Helper function to convert country name to code (for backward compatibility)
  // Returns the country code if found, otherwise returns the original value
  const getCountryCode = (countryValue: string): string => {
    if (!countryValue) return '';
    // Check if it's already a code (2-letter ISO code)
    const countryByCode = COUNTRIES.find(c => c.value === countryValue);
    if (countryByCode) return countryValue;
    // Try to find by label (for backward compatibility with existing data that stores country names)
    const countryByName = COUNTRIES.find(c => 
      c.label.toLowerCase() === countryValue.toLowerCase() ||
      c.label.toLowerCase().includes(countryValue.toLowerCase()) ||
      countryValue.toLowerCase().includes(c.label.toLowerCase())
    );
    return countryByName ? countryByName.value : '';
  };

  const handleEditGuest = (guest: any) => {
    // Determine ID type based on which field has a value
    let idType = '';
    if (guest.passportNumber) {
      idType = 'passport';
    } else if (guest.idNumber) {
      idType = 'national_id';
    }
    
    setGuestForm({
      firstName: guest.firstName,
      lastName: guest.lastName,
      email: guest.email || '',
      phone: guest.phone || '',
      address: guest.address || '',
      city: guest.city || '',
      state: guest.state || '',
      postalCode: guest.postalCode || '',
      country: getCountryCode(guest.country || ''),
      idType: idType,
      passportNumber: guest.passportNumber || '',
      idNumber: guest.idNumber || '',
    });
    setSelectedRoom(guest); // Reusing selectedRoom for guest
    setShowGuestDialog(true);
  };

  const handleDeleteGuest = async (guestId: string) => {
    setConfirmDialogConfig({
      title: 'Delete Guest',
      description: 'Are you sure you want to delete this guest? This action cannot be undone.',
      onConfirm: () => {
        setShowConfirmDialog(false);
        performDeleteGuest(guestId);
      },
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    setShowConfirmDialog(true);
  };

  const performDeleteGuest = async (guestId: string) => {
    setIsDeletingGuest(true);
    try {
      await apiService.deleteHotelGuest(guestId);
      toast({
        title: 'Success',
        description: 'Guest deleted successfully',
      });
      loadGuests();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete guest',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingGuest(false);
    }
  };

  const handleViewReservation = async (reservation: any) => {
    try {
      // Fetch full reservation details with folio items
      const fullReservation = await apiService.getHotelReservation(reservation.id);
      setSelectedReservationDetails(fullReservation);
      setShowReservationDetailsDialog(true);
    } catch (error: any) {
      // Fallback to the reservation from list if fetch fails
      setSelectedReservationDetails(reservation);
      setShowReservationDetailsDialog(true);
      console.error('Failed to load full reservation details:', error);
    }
  };

  const handleEditReservation = async (reservation: any) => {
    // Prevent editing checked out reservations
    if (reservation.status === 'CHECKED_OUT') {
      toast({
        title: 'Cannot Edit',
        description: 'Checked out reservations cannot be edited. The reservation has been completed.',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      // Fetch full reservation details with payments to validate payment status
      const fullReservation = await apiService.getHotelReservation(reservation.id);
      // Initialize edit fields for PENDING reservations
      if (fullReservation.status === 'PENDING') {
        fullReservation.editCheckInDate = new Date(fullReservation.checkInDate).toISOString().split('T')[0];
        fullReservation.editCheckOutDate = new Date(fullReservation.checkOutDate).toISOString().split('T')[0];
        fullReservation.editNumberOfGuests = fullReservation.numberOfGuests;
        fullReservation.editSpecialRequests = fullReservation.specialRequests || '';
      }
      setSelectedReservationForEdit(fullReservation);
      setShowEditReservationDialog(true);
    } catch (error: any) {
      // Fallback to the reservation from list if fetch fails
      const reservationWithEditFields = { ...reservation };
      if (reservation.status === 'PENDING') {
        reservationWithEditFields.editCheckInDate = new Date(reservation.checkInDate).toISOString().split('T')[0];
        reservationWithEditFields.editCheckOutDate = new Date(reservation.checkOutDate).toISOString().split('T')[0];
        reservationWithEditFields.editNumberOfGuests = reservation.numberOfGuests;
        reservationWithEditFields.editSpecialRequests = reservation.specialRequests || '';
      }
      setSelectedReservationForEdit(reservationWithEditFields);
      setShowEditReservationDialog(true);
      console.error('Failed to load full reservation details:', error);
    }
  };

  const handleUpdateReservation = async () => {
    if (!selectedReservationForEdit) return;
    
    // Prevent editing checked out reservations
    const originalStatus = selectedReservationForEdit.status;
    if (originalStatus === 'CHECKED_OUT') {
      toast({
        title: 'Cannot Edit',
        description: 'Checked out reservations cannot be edited. The reservation has been completed.',
        variant: 'destructive',
      });
      return;
    }
    
    // For PENDING reservations, validate date and guest changes
    if (originalStatus === 'PENDING') {
      const editCheckInDate = selectedReservationForEdit.editCheckInDate;
      const editCheckOutDate = selectedReservationForEdit.editCheckOutDate;
      const editNumberOfGuests = selectedReservationForEdit.editNumberOfGuests;
      
      // Get original dates as strings for comparison
      const originalCheckIn = new Date(selectedReservationForEdit.checkInDate).toISOString().split('T')[0];
      const originalCheckOut = new Date(selectedReservationForEdit.checkOutDate).toISOString().split('T')[0];
      
      // Check if dates actually changed
      const datesChanged = editCheckInDate && editCheckOutDate && 
                          (editCheckInDate !== originalCheckIn || editCheckOutDate !== originalCheckOut);
      
      // Validate dates if they were provided (even if unchanged)
      if (editCheckInDate || editCheckOutDate) {
        if (!editCheckInDate || !editCheckOutDate) {
          toast({
            title: 'Validation Error',
            description: 'Both check-in and check-out dates are required.',
            variant: 'destructive',
          });
          return;
        }
        
        if (editCheckOutDate <= editCheckInDate) {
          toast({
            title: 'Validation Error',
            description: 'Check-out date must be after check-in date.',
            variant: 'destructive',
          });
          return;
        }
        
        // Only check room availability if dates actually changed
        if (datesChanged) {
          try {
            const availabilityResult = await apiService.checkRoomAvailability({
              companyId: selectedReservationForEdit.companyId,
              checkInDate: editCheckInDate,
              checkOutDate: editCheckOutDate,
              roomTypeId: selectedReservationForEdit.room?.roomTypeId,
              numberOfGuests: editNumberOfGuests || selectedReservationForEdit.numberOfGuests,
            });
            
            // Check if the room is still available (excluding current reservation)
            const isRoomAvailable = availabilityResult.rooms.some((room: any) => 
              room.id === selectedReservationForEdit.roomId
            );
            
            if (!isRoomAvailable || availabilityResult.rooms.length === 0) {
              toast({
                title: 'Room No Longer Available',
                description: `The room is no longer available for the selected dates. Please choose different dates.`,
                variant: 'destructive',
              });
              return;
            }
          } catch (error: any) {
            toast({
              title: 'Availability Check Failed',
              description: error.message || 'Failed to check room availability. Please try again.',
              variant: 'destructive',
            });
            return;
          }
        }
      }
      
      // Validate number of guests
      if (editNumberOfGuests !== undefined && (editNumberOfGuests < 1 || !Number.isInteger(Number(editNumberOfGuests)))) {
        toast({
          title: 'Validation Error',
          description: 'Number of guests must be at least 1.',
          variant: 'destructive',
        });
        return;
      }
      
      // Validate number of guests against room capacity
      if (editNumberOfGuests !== undefined && selectedReservationForEdit.room) {
        const roomCapacity = selectedReservationForEdit.room.capacity;
        if (editNumberOfGuests > roomCapacity) {
          toast({
            title: 'Validation Error',
            description: `Number of guests (${editNumberOfGuests}) cannot exceed room capacity (${roomCapacity}).`,
            variant: 'destructive',
          });
          return;
        }
      }
    }
    
    // Validate payment status change - prevent setting to PAID without actual payment
    const newPaymentStatus = selectedReservationForEdit.paymentStatus;
    const payments = selectedReservationForEdit.payments || [];
    const completedPayments = payments.filter((p: any) => p.status === 'COMPLETED');
    const totalPaid = completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
    const hasFolio = selectedReservationForEdit.folio !== null && selectedReservationForEdit.folio !== undefined;
    const totalAmount = hasFolio 
      ? Number(selectedReservationForEdit.folio?.totalAmount || 0)
      : Number(selectedReservationForEdit.totalAmount || 0);
    const folioBalance = hasFolio ? Number(selectedReservationForEdit.folio?.balance || 0) : (totalAmount - totalPaid);
    
    // Check if trying to set to PAID without actual payment
    if (newPaymentStatus === 'PAID' && totalPaid < totalAmount && folioBalance > 0) {
      toast({
        title: 'Payment Validation Error',
        description: `Cannot set payment status to PAID. Total paid: ${formatCurrency(totalPaid)}, Total amount: ${formatCurrency(totalAmount)}, Balance: ${formatCurrency(folioBalance)}. Please record actual payment first.`,
        variant: 'destructive',
      });
      return;
    }
    
    // Validate: Cannot set status to CHECKED_OUT if not paid
    const isPaid = newPaymentStatus === 'PAID' || 
                   (hasFolio && folioBalance <= 0) || 
                   (!hasFolio && totalPaid >= totalAmount && totalAmount > 0);
    
    if (selectedReservationForEdit.status === 'CHECKED_OUT' && !isPaid) {
      toast({
        title: 'Payment Required',
        description: `Cannot set status to CHECKED_OUT. Payment status must be PAID. Current: ${newPaymentStatus || 'UNPAID'}. Balance: ${formatCurrency(folioBalance)}.`,
        variant: 'destructive',
      });
      return;
    }
    
    // Auto-set status to CHECKED_OUT if payment is PAID and currently CHECKED_IN
    if (newPaymentStatus === 'PAID' && selectedReservationForEdit.status === 'CHECKED_IN') {
      selectedReservationForEdit.status = 'CHECKED_OUT';
    }
    
    setIsUpdatingReservation(true);
    try {
      // Build update payload
      const updateData: any = {
        status: selectedReservationForEdit.status,
        paymentStatus: selectedReservationForEdit.paymentStatus,
        notes: selectedReservationForEdit.notes || '',
      };
      
      // For PENDING reservations, include date and guest changes
      if (originalStatus === 'PENDING') {
        if (selectedReservationForEdit.editCheckInDate) {
          // Convert to UTC midnight to avoid timezone issues
          const [year, month, day] = selectedReservationForEdit.editCheckInDate.split('-').map(Number);
          updateData.checkInDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
        }
        if (selectedReservationForEdit.editCheckOutDate) {
          // Convert to UTC midnight to avoid timezone issues
          const [year, month, day] = selectedReservationForEdit.editCheckOutDate.split('-').map(Number);
          updateData.checkOutDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
        }
        if (selectedReservationForEdit.editNumberOfGuests !== undefined) {
          updateData.numberOfGuests = parseInt(selectedReservationForEdit.editNumberOfGuests.toString());
        }
        if (selectedReservationForEdit.editSpecialRequests !== undefined) {
          updateData.specialRequests = selectedReservationForEdit.editSpecialRequests || undefined;
        }
      }
      
      await apiService.updateHotelReservation(selectedReservationForEdit.id, updateData);
      toast({
        title: 'Success',
        description: 'Reservation updated successfully',
      });
      
      // Reset state before closing to prevent UI blocking
      const updatedReservation = selectedReservationForEdit;
      setSelectedReservationForEdit(null);
      setShowEditReservationDialog(false);
      
      // Reload all related data to refresh UI
      await loadReservations();
      await loadGuests(); // Refresh guests in case reservation changes affected guest data
      
      // If group booking details is open and this reservation belongs to a group, refresh it
      if (showGroupBookingDetailsDialog && selectedGroupBookingForDetails && updatedReservation.groupBookingId) {
        try {
          const updatedGroupDetails = await apiService.getGroupBooking(selectedGroupBookingForDetails.id);
          setSelectedGroupBookingForDetails(updatedGroupDetails);
        } catch (error) {
          // If refresh fails, just reload group bookings
          await loadGroupBookings();
        }
      } else {
        await loadGroupBookings();
      }
      
      // Refresh folios if on folio tab
      if (activeTab === 'folio') {
        await loadFolios();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update reservation',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingReservation(false);
    }
  };

  const handleDeleteReservation = async (reservationId: string, reason?: string) => {
    const reservation = reservations.find(r => r.id === reservationId);
    const isCancelled = reservation?.status === 'CANCELLED';
    const title = isCancelled ? 'Delete Reservation' : 'Cancel Reservation';
    const description = isCancelled 
      ? 'Are you sure you want to delete this cancelled reservation? This action cannot be undone.'
      : 'Are you sure you want to cancel this reservation? This action cannot be undone.';
    
    setConfirmDialogConfig({
      title,
      description,
      onConfirm: async () => {
        await performDeleteReservation(reservationId, reason, isCancelled);
        setShowConfirmDialog(false);
      },
      confirmText: isCancelled ? 'Delete' : 'Cancel',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    setShowConfirmDialog(true);
  };

  const performDeleteReservation = async (reservationId: string, reason?: string, isCancelled?: boolean) => {
    setIsDeletingReservation(true);
    try {
      if (isCancelled) {
        // For cancelled reservations, delete them permanently
        await apiService.deleteHotelReservation(reservationId);
        toast({
          title: 'Success',
          description: 'Reservation deleted successfully',
        });
      } else {
        // For pending reservations, cancel them
      await apiService.cancelReservation(reservationId, reason);
      toast({
        title: 'Success',
        description: 'Reservation cancelled successfully',
      });
      }
      loadReservations();
      
      // If group booking details is open, refresh it
      if (showGroupBookingDetailsDialog && selectedGroupBookingForDetails) {
        try {
          const updatedGroupDetails = await apiService.getGroupBooking(selectedGroupBookingForDetails.id);
          setSelectedGroupBookingForDetails(updatedGroupDetails);
        } catch (error) {
          loadGroupBookings();
        }
      } else {
        loadGroupBookings();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel reservation',
        variant: 'destructive',
      });
    } finally {
      setIsDeletingReservation(false);
    }
  };

  const handleDeleteGroupBooking = async (groupBookingId: string) => {
    const groupBooking = groupBookings.find(g => g.id === groupBookingId);
    const isCancelled = groupBooking?.status === 'CANCELLED';
    const title = isCancelled ? 'Delete Group Booking' : 'Cancel Group Booking';
    const description = isCancelled 
      ? 'Are you sure you want to delete this cancelled group booking? This action cannot be undone.'
      : 'Are you sure you want to cancel this group booking? This action cannot be undone.';
    
    setConfirmDialogConfig({
      title,
      description,
      onConfirm: () => {
        setShowConfirmDialog(false);
        performDeleteGroupBooking(groupBookingId, isCancelled);
      },
      confirmText: isCancelled ? 'Delete' : 'Cancel',
      cancelText: 'Cancel',
      variant: 'destructive',
    });
    setShowConfirmDialog(true);
  };

  const performDeleteGroupBooking = async (groupBookingId: string, isCancelled?: boolean) => {
    
    try {
      if (isCancelled) {
        // For cancelled group bookings, try to delete by setting a deleted flag or using update
        // Since there's no delete API, we'll update status to CANCELLED if not already
        await apiService.updateGroupBooking(groupBookingId, { status: 'CANCELLED' });
        toast({
          title: 'Success',
          description: 'Group booking deleted successfully',
        });
      } else {
        // For pending group bookings, cancel them
        await apiService.updateGroupBooking(groupBookingId, { status: 'CANCELLED' });
        toast({
          title: 'Success',
          description: 'Group booking cancelled successfully',
        });
      }
      loadGroupBookings();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isCancelled ? 'delete' : 'cancel'} group booking`,
        variant: 'destructive',
      });
    }
  };

  const handleUnlinkReservationFromGroup = async (reservationId: string, groupBookingId: string) => {
    setConfirmDialogConfig({
      title: 'Unlink Reservation',
      description: 'Are you sure you want to unlink this reservation from the group booking? The reservation will remain but will no longer be associated with this group.',
      onConfirm: () => {
        setShowConfirmDialog(false);
        performUnlinkReservationFromGroup(reservationId, groupBookingId);
      },
      confirmText: 'Unlink',
      cancelText: 'Cancel',
    });
    setShowConfirmDialog(true);
  };

  const performUnlinkReservationFromGroup = async (reservationId: string, groupBookingId: string) => {
    setIsUpdatingReservation(true);
    try {
      await apiService.updateHotelReservation(reservationId, {
        groupBookingId: null, // Unlink by setting to null
      });
      toast({
        title: 'Success',
        description: 'Reservation unlinked from group booking successfully',
      });
      loadReservations();
      
      // Refresh group booking details
      if (showGroupBookingDetailsDialog && selectedGroupBookingForDetails) {
        try {
          const updatedGroupDetails = await apiService.getGroupBooking(selectedGroupBookingForDetails.id);
          setSelectedGroupBookingForDetails(updatedGroupDetails);
        } catch (error) {
          loadGroupBookings();
        }
      } else {
        loadGroupBookings();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to unlink reservation from group',
        variant: 'destructive',
      });
    } finally {
      setIsUpdatingReservation(false);
    }
  };

  const resetRoomForm = () => {
    setRoomForm({
      roomNumber: '',
      title: '',
      roomTypeId: '',
      floor: '',
      capacity: '2',
      size: '',
      basePricePerNight: '',
      currency: 'RWF',
      description: '',
      isActive: true,
      amenityIds: [],
    });
    setSelectedRoom(null);
    // Clear images
    setRoomImages([]);
    setRoomImagePreviews([]);
    setExistingImages([]);
  };

  const resetReservationForm = () => {
    setReservationForm({
      bookingType: 'ROOM',
      roomId: '',
      bookableItemId: '',
      bookableItemType: '',
      guestId: '',
      ratePlanId: '',
      groupBookingId: '',
      promotionId: '',
      promotionIds: [],
      checkInDate: '',
      checkOutDate: '',
      numberOfGuests: '1',
      totalAmount: '',
      depositAmount: '0',
      specialRequests: '',
    });
    setOriginalTotalAmount('');
    setAmountBreakdown(null);
  };

  // Calculate reservation amount based on room, dates, rate plan, and promotion
  const calculateReservationAmount = () => {
    // For group bookings, use group dates if reservation dates are not set
    const checkInDate = reservationForm.checkInDate || (selectedGroupForReservations?.checkInDate ? new Date(selectedGroupForReservations.checkInDate).toISOString().split('T')[0] : '');
    const checkOutDate = reservationForm.checkOutDate || (selectedGroupForReservations?.checkOutDate ? new Date(selectedGroupForReservations.checkOutDate).toISOString().split('T')[0] : '');
    
    if (!reservationForm.roomId || !checkInDate || !checkOutDate) {
      setAmountBreakdown(null);
      return;
    }

    const selectedRoom = rooms.find(r => r.id === reservationForm.roomId);
    if (!selectedRoom || !selectedRoom.basePricePerNight) {
      setAmountBreakdown(null);
      return;
    }

    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    const numberOfNights = Math.ceil((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
    
    if (numberOfNights <= 0) {
      setAmountBreakdown(null);
      return;
    }

    const roomPricePerNight = parseFloat(selectedRoom.basePricePerNight.toString());
    let subtotal = roomPricePerNight * numberOfNights;
    let ratePlanAdjustment = 0;

    // Apply rate plan if selected
    if (reservationForm.ratePlanId) {
      const selectedRatePlan = ratePlans.find(rp => rp.id === reservationForm.ratePlanId);
      if (selectedRatePlan) {
        const ratePlanPrice = parseFloat(selectedRatePlan.price.toString());
        // Rate plan can override the price or adjust it
        // For now, we'll use rate plan price per night if available
        subtotal = ratePlanPrice * numberOfNights;
        ratePlanAdjustment = (ratePlanPrice - roomPricePerNight) * numberOfNights;
      }
    }

    // Apply promotion discounts if selected (multiple promotions)
    let totalPromotionDiscount = 0;
    const promotionDiscounts: Array<{ id: string; name: string; discount: number }> = [];
    let currentSubtotal = subtotal; // Track subtotal as promotions are applied
    
    // Use promotionIds array if available, otherwise fall back to promotionId
    const activePromotionIds = reservationForm.promotionIds.length > 0 
      ? reservationForm.promotionIds 
      : (reservationForm.promotionId ? [reservationForm.promotionId] : []);
    
    activePromotionIds.forEach((promoId) => {
      const promotion = promotions.find(p => p.id === promoId && p.isActive);
      if (promotion) {
        let discount = 0;
        if (promotion.discountType === 'PERCENTAGE') {
          discount = currentSubtotal * (parseFloat(promotion.discountValue.toString()) / 100);
        } else if (promotion.discountType === 'FIXED_AMOUNT') {
          discount = parseFloat(promotion.discountValue.toString());
        } else if (promotion.discountType === 'FREE_NIGHT') {
          const freeNights = parseInt(promotion.discountValue.toString());
          discount = roomPricePerNight * Math.min(freeNights, numberOfNights);
        }
        
        if (discount > 0) {
          promotionDiscounts.push({
            id: promotion.id,
            name: promotion.name,
            discount: discount,
          });
          totalPromotionDiscount += discount;
          currentSubtotal = Math.max(0, currentSubtotal - discount);
        }
      }
    });
    
    const finalAmount = Math.max(0, subtotal - totalPromotionDiscount);

    setAmountBreakdown({
      roomPricePerNight,
      numberOfNights,
      subtotal,
      ratePlanAdjustment,
      promotionDiscount: totalPromotionDiscount,
      promotionDiscounts,
      finalAmount,
      currency: selectedRoom.currency || 'RWF', // Add room currency to breakdown
    });

    // Always update total amount when calculation is triggered
    // This ensures amount is prefilled when room, dates, or promotion is selected
      setReservationForm(prev => ({ ...prev, totalAmount: finalAmount.toFixed(2) }));
      setOriginalTotalAmount(finalAmount.toFixed(2));
  };

  // Auto-calculate amount when room, dates, rate plan, or promotion changes
  useEffect(() => {
    if (reservationForm.roomId && reservationForm.checkInDate && reservationForm.checkOutDate) {
      // Use setTimeout to ensure state is updated
      const timer = setTimeout(() => {
        calculateReservationAmount();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setAmountBreakdown(null);
    }
  }, [reservationForm.roomId, reservationForm.checkInDate, reservationForm.checkOutDate, reservationForm.ratePlanId, reservationForm.promotionId, reservationForm.promotionIds, rooms, ratePlans, promotions]);

  // Calculate discount when promotion is selected (for manual edits)
  const applyPromotionDiscount = (promotionId: string, currentTotal: string) => {
    if (!promotionId || !currentTotal) return currentTotal;
    
    const promotion = promotions.find(p => p.id === promotionId && p.isActive);
    if (!promotion) return currentTotal;

    const total = parseFloat(currentTotal);
    if (isNaN(total) || total <= 0) return currentTotal;

    let discountedTotal = total;

    if (promotion.discountType === 'PERCENTAGE') {
      const discount = total * (parseFloat(promotion.discountValue.toString()) / 100);
      discountedTotal = total - discount;
    } else if (promotion.discountType === 'FIXED_AMOUNT') {
      discountedTotal = Math.max(0, total - parseFloat(promotion.discountValue.toString()));
    } else if (promotion.discountType === 'FREE_NIGHT') {
      // For free night, we'd need to calculate based on nightly rate
      // For now, just apply a percentage equivalent
      const freeNights = parseInt(promotion.discountValue.toString());
      // This is a simplified calculation - you might want to enhance this
      discountedTotal = total; // Placeholder - would need nightly rate calculation
    }

    return discountedTotal.toFixed(2);
  };

  const resetGuestForm = () => {
    setGuestForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
      idType: '',
      passportNumber: '',
      idNumber: '',
    });
    setSelectedRoom(null);
  };

  // Enhanced status badge with better colors
  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      PENDING: { label: 'Pending', className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' },
      CONFIRMED: { label: 'Confirmed', className: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' },
      CHECKED_IN: { label: 'Checked In', className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
      CHECKED_OUT: { label: 'Checked Out', className: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' },
      CANCELLED: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
      NO_SHOW: { label: 'No Show', className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
    };
    
    const config = statusConfig[status] || { label: status, className: 'bg-muted text-muted-foreground' };
    
    return (
      <Badge variant="outline" className={`${config.className} font-medium`}>
        {config.label}
      </Badge>
    );
  };

  // Enhanced payment status badge
  const getPaymentStatusBadge = (paymentStatus: string) => {
    const paymentConfig: Record<string, { label: string; className: string }> = {
      UNPAID: { label: 'Unpaid', className: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' },
      PARTIAL: { label: 'Partial', className: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100' },
      PAID: { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100' },
      LOAN: { label: 'Loan', className: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100' },
      REFUNDED: { label: 'Refunded', className: 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100' },
    };
    
    const config = paymentConfig[paymentStatus] || { label: paymentStatus, className: 'bg-muted text-muted-foreground' };
    
    return (
      <Badge variant="outline" className={`${config.className} font-medium`}>
        {config.label}
      </Badge>
    );
  };

  const getStatusBadgeOld = (status: string) => {
    const statusColors: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      CONFIRMED: 'bg-blue-100 text-blue-800',
      CHECKED_IN: 'bg-green-100 text-green-800',
      CHECKED_OUT: 'bg-gray-100 text-gray-800',
      CANCELLED: 'bg-red-100 text-red-800',
      NO_SHOW: 'bg-orange-100 text-orange-800',
    };
    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  // Calculate statistics
  const calculateStatistics = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayEnd = new Date(today);
    todayEnd.setHours(23, 59, 59, 999);

    // Room statistics
    const totalRooms = rooms.length;
    const activeRooms = rooms.filter(r => r.isActive).length;
    const occupiedRoomIds = new Set(
      reservations
        .filter(r => r.status === 'CHECKED_IN' || r.status === 'CONFIRMED')
        .map(r => r.roomId)
    );
    const occupiedRooms = occupiedRoomIds.size;
    const availableRooms = activeRooms - occupiedRooms;
    const occupancyRate = activeRooms > 0 ? (occupiedRooms / activeRooms) * 100 : 0;

    // Reservation statistics
    const totalReservations = reservations.length;
    const pendingReservations = reservations.filter(r => r.status === 'PENDING').length;
    const confirmedReservations = reservations.filter(r => r.status === 'CONFIRMED').length;
    const checkedInReservations = reservations.filter(r => r.status === 'CHECKED_IN').length;
    const checkedOutReservations = reservations.filter(r => r.status === 'CHECKED_OUT').length;
    const cancelledReservations = reservations.filter(r => r.status === 'CANCELLED').length;

    // Today's statistics
    const checkInsToday = reservations.filter(r => {
      if (!r.actualCheckIn) return false;
      const checkInDate = new Date(r.actualCheckIn);
      checkInDate.setHours(0, 0, 0, 0);
      return checkInDate.getTime() === today.getTime();
    }).length;

    const checkOutsToday = reservations.filter(r => {
      if (!r.actualCheckOut) return false;
      const checkOutDate = new Date(r.actualCheckOut);
      checkOutDate.setHours(0, 0, 0, 0);
      return checkOutDate.getTime() === today.getTime();
    }).length;

    const arrivalsToday = reservations.filter(r => {
      const checkInDate = new Date(r.checkInDate);
      checkInDate.setHours(0, 0, 0, 0);
      return checkInDate.getTime() === today.getTime() && 
             (r.status === 'CONFIRMED' || r.status === 'PENDING');
    }).length;

    const departuresToday = reservations.filter(r => {
      const checkOutDate = new Date(r.checkOutDate);
      checkOutDate.setHours(0, 0, 0, 0);
      return checkOutDate.getTime() === today.getTime() && 
             (r.status === 'CHECKED_IN' || r.status === 'CONFIRMED');
    }).length;

    // Revenue statistics - Total revenue should only count paid amounts (converted to FRW)
    const totalRevenue = payments
      .filter(p => p.status === 'COMPLETED')
      .reduce((sum, p) => {
        const amount = Number(p.amount || 0);
        const currency = p.currency || p.reservation?.room?.currency || 'FRW';
        const convertedAmount = convertCurrency(amount, currency, 'FRW');
        return sum + convertedAmount;
      }, 0);
    
    const todayRevenue = payments
      .filter(p => {
        if (p.status !== 'COMPLETED') return false;
        const paymentDate = new Date(p.paymentDate || p.createdAt);
        paymentDate.setHours(0, 0, 0, 0);
        return paymentDate.getTime() === today.getTime();
      })
      .reduce((sum, p) => {
        const amount = Number(p.amount || 0);
        const currency = p.currency || p.reservation?.room?.currency || 'FRW';
        const convertedAmount = convertCurrency(amount, currency, 'FRW');
        return sum + convertedAmount;
      }, 0);
    
    const paidAmount = payments
      .filter(p => p.status === 'COMPLETED')
      .reduce((sum, p) => {
        const amount = Number(p.amount || 0);
        const currency = p.currency || p.reservation?.room?.currency || 'FRW';
        const convertedAmount = convertCurrency(amount, currency, 'FRW');
        return sum + convertedAmount;
      }, 0);
    
    const loanPayments = payments.filter(p => p.status === 'loan');
    const loanAmount = loanPayments.reduce((sum, p) => {
      const amount = Number(p.amount || 0);
      const currency = p.currency || p.reservation?.room?.currency || 'FRW';
      const convertedAmount = convertCurrency(amount, currency, 'FRW');
      return sum + convertedAmount;
    }, 0);
    
    // Pending payments from payment records (converted to FRW)
    const pendingPaymentRecords = payments
      .filter(p => p.status !== 'COMPLETED' && p.status !== 'loan')
      .reduce((sum, p) => {
        const amount = Number(p.amount || 0);
        const currency = p.currency || p.reservation?.room?.currency || 'FRW';
        const convertedAmount = convertCurrency(amount, currency, 'FRW');
        return sum + convertedAmount;
      }, 0);
    
    // Calculate unpaid balances from reservations (UNPAID and PARTIAL status, excluding CANCELLED)
    const unpaidReservationBalances = reservations
      .filter(r => 
        (r.paymentStatus === 'UNPAID' || r.paymentStatus === 'PARTIAL') && 
        r.status !== 'CANCELLED'
      )
      .reduce((sum, r) => {
        const roomCurrency = r.room?.currency || 'FRW';
        const totalAmount = Number(r.totalAmount || 0);
        const convertedTotal = convertCurrency(totalAmount, roomCurrency, 'FRW');
        
        // Calculate how much has been paid for this reservation (converted to FRW)
        const paidForReservation = payments
          .filter(p => p.reservationId === r.id && (p.status === 'COMPLETED' || p.status === 'loan'))
          .reduce((paidSum, p) => {
            const amount = Number(p.amount || 0);
            const currency = p.currency || roomCurrency;
            const convertedAmount = convertCurrency(amount, currency, 'FRW');
            return paidSum + convertedAmount;
          }, 0);
        
        // Add the remaining balance
        return sum + (convertedTotal - paidForReservation);
      }, 0);
    
    // Total pending = pending payment records + unpaid reservation balances
    const pendingPayments = pendingPaymentRecords + unpaidReservationBalances;

    // Guest statistics
    const totalGuests = guests.length;
    const activeGuests = reservations
      .filter(r => r.status === 'CHECKED_IN')
      .map(r => r.guestId)
      .filter((id, index, self) => self.indexOf(id) === index).length;

    // Payment statistics
    const totalPayments = payments.length;
    const completedPayments = payments.filter(p => p.status === 'COMPLETED').length;
    const loanPaymentsCount = payments.filter(p => p.status === 'loan').length;
    const pendingPaymentCount = payments.filter(p => p.status !== 'COMPLETED' && p.status !== 'loan').length;

    // Housekeeping statistics
    const totalHousekeepingTasks = housekeepingTasks.length;
    const completedHousekeepingTasks = housekeepingTasks.filter(t => t.status === 'COMPLETED').length;
    const pendingHousekeepingTasks = housekeepingTasks.filter(t => t.status === 'PENDING').length;

    // Maintenance statistics
    const totalMaintenanceRequests = maintenanceRequests.length;
    const openMaintenanceRequests = maintenanceRequests.filter(r => r.status === 'OPEN').length;
    const completedMaintenanceRequests = maintenanceRequests.filter(r => r.status === 'COMPLETED').length;

    // Average stay duration
    const completedStays = reservations.filter(r => r.actualCheckIn && r.actualCheckOut);
    const avgStayDuration = completedStays.length > 0
      ? completedStays.reduce((sum, r) => {
          const checkIn = new Date(r.actualCheckIn!);
          const checkOut = new Date(r.actualCheckOut!);
          const diffTime = Math.abs(checkOut.getTime() - checkIn.getTime());
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          return sum + diffDays;
        }, 0) / completedStays.length
      : 0;

    // Average room rate
    const avgRoomRate = rooms.length > 0
      ? rooms.reduce((sum, r) => sum + Number(r.basePricePerNight || 0), 0) / rooms.length
      : 0;

    return {
      rooms: {
        total: totalRooms,
        active: activeRooms,
        occupied: occupiedRooms,
        available: availableRooms,
        occupancyRate: Math.round(occupancyRate * 10) / 10,
      },
      reservations: {
        total: totalReservations,
        pending: pendingReservations,
        confirmed: confirmedReservations,
        checkedIn: checkedInReservations,
        checkedOut: checkedOutReservations,
        cancelled: cancelledReservations,
      },
      today: {
        checkIns: checkInsToday,
        checkOuts: checkOutsToday,
        arrivals: arrivalsToday,
        departures: departuresToday,
      },
      revenue: {
        total: totalRevenue,
        today: todayRevenue,
        paid: paidAmount,
        loan: loanAmount,
        pending: pendingPayments,
      },
      guests: {
        total: totalGuests,
        active: activeGuests,
      },
      payments: {
        total: totalPayments,
        completed: completedPayments,
        pending: pendingPaymentCount,
      },
      housekeeping: {
        total: totalHousekeepingTasks,
        completed: completedHousekeepingTasks,
        pending: pendingHousekeepingTasks,
      },
      maintenance: {
        total: totalMaintenanceRequests,
        open: openMaintenanceRequests,
        completed: completedMaintenanceRequests,
      },
      averages: {
        stayDuration: Math.round(avgStayDuration * 10) / 10,
        roomRate: avgRoomRate,
      },
    };
  };

  // Memoize statistics calculation for performance
  const stats = useMemo(() => calculateStatistics(), [rooms, reservations, payments, housekeepingTasks, maintenanceRequests]);

  // Calculate reservation data for the last 4 months (memoized)
  const getReservationDataByMonth = useCallback(() => {
    const today = new Date();
    const months = [];
    
    // Get last 4 months
    for (let i = 3; i >= 0; i--) {
      const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      // Count reservations by status for this month
      const monthReservations = reservations.filter(r => {
        const reservationDate = new Date(r.createdAt);
        const reservationMonth = `${reservationDate.getFullYear()}-${String(reservationDate.getMonth() + 1).padStart(2, '0')}`;
        return reservationMonth === monthKey;
      });

      const confirmed = monthReservations.filter(r => r.status === 'CONFIRMED').length;
      const checkedIn = monthReservations.filter(r => r.status === 'CHECKED_IN').length;
      const checkedOut = monthReservations.filter(r => r.status === 'CHECKED_OUT').length;
      const cancelled = monthReservations.filter(r => r.status === 'CANCELLED').length;
      const pending = monthReservations.filter(r => r.status === 'PENDING').length;
      const total = monthReservations.length;

      months.push({
        month: monthName,
        total,
        confirmed,
        checkedIn,
        checkedOut,
        cancelled,
        pending,
      });
    }

    return months;
  }, [reservations]);

  const monthlyReservationData = useMemo(() => getReservationDataByMonth(), [getReservationDataByMonth]);

  // Comprehensive Analytics Calculations for BI Dashboard
  const getRevenueAnalytics = useMemo(() => {
    const today = new Date();
    const last7Days = [];
    const last30Days = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayRevenue = payments
        .filter(p => {
          const paymentDate = new Date(p.createdAt).toISOString().split('T')[0];
          return paymentDate === dateStr && p.status === 'COMPLETED';
        })
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);
      last7Days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: dayRevenue,
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
      });
    }

    for (let i = 29; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayRevenue = payments
        .filter(p => {
          const paymentDate = new Date(p.createdAt).toISOString().split('T')[0];
          return paymentDate === dateStr && p.status === 'COMPLETED';
        })
        .reduce((sum, p) => sum + Number(p.amount || 0), 0);
      last30Days.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: dayRevenue,
      });
    }

    // Revenue by room type
    const revenueByRoomType = roomTypes.map(type => {
      const typeReservations = reservations.filter(r => r.room?.roomTypeId === type.id);
      const typeRevenue = typeReservations.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
      return {
        name: type.name,
        value: typeRevenue,
        count: typeReservations.length,
      };
    }).sort((a, b) => b.value - a.value);

    // Revenue by payment method
    const revenueByPaymentMethod = payments
      .filter(p => p.status === 'COMPLETED')
      .reduce((acc, p) => {
        const method = p.paymentMethod || 'UNKNOWN';
        acc[method] = (acc[method] || 0) + Number(p.amount || 0);
        return acc;
      }, {} as Record<string, number>);

    const paymentMethodData = Object.entries(revenueByPaymentMethod).map(([method, value]) => ({
      name: method,
      value,
    }));

    return {
      last7Days,
      last30Days,
      revenueByRoomType,
      paymentMethodData,
    };
  }, [payments, reservations, roomTypes]);

  // Room Performance Analytics
  const getRoomPerformanceAnalytics = useMemo(() => {
    const roomPerformance = rooms.map(room => {
      const roomReservations = reservations.filter(r => r.roomId === room.id);
      const totalRevenue = roomReservations.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
      const occupancyDays = roomReservations.filter(r => 
        r.status === 'CHECKED_IN' || r.status === 'CHECKED_OUT'
      ).length;
      const isReserved = isRoomReserved(room.id);
      
      return {
        roomNumber: room.roomNumber,
        roomType: room.roomTypeRef?.name || room.roomType || 'Standard',
        totalRevenue,
        reservationCount: roomReservations.length,
        occupancyDays,
        status: isReserved ? 'OCCUPIED' : room.roomStatus,
        basePrice: Number(room.basePricePerNight || 0),
      };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue);

    // Room type occupancy
    const roomTypeOccupancy = roomTypes.map(type => {
      const typeRooms = rooms.filter(r => r.roomTypeId === type.id);
      const occupied = typeRooms.filter(r => isRoomReserved(r.id)).length;
      return {
        name: type.name,
        total: typeRooms.length,
        occupied,
        available: typeRooms.length - occupied,
        occupancyRate: typeRooms.length > 0 ? Math.round((occupied / typeRooms.length) * 100) : 0,
      };
    });

    return {
      roomPerformance,
      roomTypeOccupancy,
    };
  }, [rooms, reservations, roomTypes, isRoomReserved]);

  // Guest Analytics
  const getGuestAnalytics = useMemo(() => {
    // Guest demographics by country
    const guestsByCountry = guests.reduce((acc, guest) => {
      const country = guest.country || 'Unknown';
      acc[country] = (acc[country] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const countryData = Object.entries(guestsByCountry)
      .map(([country, count]) => ({ name: country, value: Number(count) || 0 }))
      .sort((a, b) => (b.value as number) - (a.value as number))
      .slice(0, 10);

    // Repeat guests
    const repeatGuests = guests.filter(guest => {
      const guestReservations = reservations.filter(r => r.guestId === guest.id);
      return guestReservations.length > 1;
    });

    // Guest lifetime value
    const guestLTV = guests.map(guest => {
      const guestReservations = reservations.filter(r => r.guestId === guest.id);
      const totalSpent = guestReservations.reduce((sum, r) => sum + Number(r.totalAmount || 0), 0);
      return {
        guestId: guest.id,
        name: `${guest.firstName} ${guest.lastName}`,
        email: guest.email,
        reservationCount: guestReservations.length,
        totalSpent,
        avgReservationValue: guestReservations.length > 0 
          ? totalSpent / guestReservations.length 
          : 0,
      };
    }).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 10);

    return {
      countryData,
      repeatGuests: repeatGuests.length,
      repeatGuestRate: guests.length > 0 
        ? Math.round((repeatGuests.length / guests.length) * 100) 
        : 0,
      guestLTV,
    };
  }, [guests, reservations]);

  // Payment Status Distribution
  const getPaymentStatusData = useMemo(() => {
    const statusCounts = reservations.reduce((acc, r) => {
      const status = r.paymentStatus || 'UNPAID';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(statusCounts).map(([status, count]) => ({
      name: status,
      value: count,
    }));
  }, [reservations]);

  return (
    <PageLayout
      title={tSync("Hotel Management", "Hotel Management")}
      description={tSync("Manage rooms, reservations, guests, and hotel operations", "Manage rooms, reservations, guests, and hotel operations")}
    >
      <Tabs value={activeTab} onValueChange={(tab) => {
        setActiveTab(tab);
        // Close calendar when switching tabs
        setShowAvailabilityCalendar(false);
        setSelectedRoomForCalendar(null);
      }} className="space-y-4 pr-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pb-2 border-b">
          {/* Primary Tabs - Most Used */}
        <div 
            className="flex-1 overflow-x-auto -mx-4 px-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        >
            <TabsList className="inline-flex w-max gap-1 justify-start flex-nowrap bg-muted p-1 rounded-lg">
              <TabsTrigger 
                value="overview" 
                className="!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Overview dashboard"
              >
                <Home className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline"><TranslatedText text="Overview" /></span>
                <span className="sm:hidden"><TranslatedText text="Home" /></span>
          </TabsTrigger>
              <TabsTrigger 
                value="rooms" 
                className="!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Rooms management"
              >
                <Bed className="w-4 h-4 shrink-0" />
            <TranslatedText text="Rooms" />
          </TabsTrigger>
              <TabsTrigger 
                value="reservations" 
                className="!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Reservations management"
              >
                <Calendar className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline"><TranslatedText text="Reservations" /></span>
                <span className="sm:hidden"><TranslatedText text="Bookings" /></span>
          </TabsTrigger>
              <TabsTrigger 
                value="guests" 
                className="!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Guests management"
              >
                <Users className="w-4 h-4 shrink-0" />
            <TranslatedText text="Guests" />
          </TabsTrigger>
              <TabsTrigger 
                value="availability" 
                className="!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Check room availability"
              >
                <Search className="w-4 h-4 shrink-0" />
                <span className="hidden sm:inline"><TranslatedText text="Availability" /></span>
                <span className="sm:hidden"><TranslatedText text="Available" /></span>
          </TabsTrigger>
              <TabsTrigger 
                value="folio" 
                className="hidden sm:!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Guest bills"
              >
                <Receipt className="w-4 h-4 shrink-0" />
            <TranslatedText text="Bills" />
          </TabsTrigger>
              <TabsTrigger 
                value="payments" 
                className="hidden sm:!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Payments management"
              >
                <CreditCard className="w-4 h-4 shrink-0" />
            <TranslatedText text="Payments" />
          </TabsTrigger>
              <TabsTrigger 
                value="services" 
                className="hidden sm:!inline-flex !items-center !gap-1.5 !whitespace-nowrap text-xs sm:text-sm data-[state=active]:bg-background data-[state=active]:shadow-sm transition-all duration-200"
                aria-label="Hotel services"
              >
                <Sparkles className="w-4 h-4 shrink-0" />
                <TranslatedText text="Services" />
          </TabsTrigger>
            </TabsList>
          </div>
          
          {/* More Options Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant={['housekeeping', 'maintenance', 'reviews', 'keycards', 'group-bookings', 'waitlist', 'promotions', 'reports', 'statistics'].includes(activeTab) ? 'default' : 'outline'}
                className={`gap-2 shrink-0 ${
                  ['housekeeping', 'maintenance', 'reviews', 'keycards', 'group-bookings', 'waitlist', 'promotions', 'reports', 'statistics'].includes(activeTab) 
                    ? '!text-white [&_svg]:!text-white hover:!text-white hover:[&_svg]:!text-white' 
                    : '[&_svg]:text-current hover:[&_svg]:text-current'
                }`}
              >
                <MoreVertical className={`w-4 h-4 ${['housekeeping', 'maintenance', 'reviews', 'keycards', 'group-bookings', 'waitlist', 'promotions', 'reports', 'statistics'].includes(activeTab) ? '!text-white' : ''}`} />
                <span className="hidden sm:inline"><TranslatedText text="More" /></span>
                <ChevronDown className={`w-4 h-4 ${['housekeeping', 'maintenance', 'reviews', 'keycards', 'group-bookings', 'waitlist', 'promotions', 'reports', 'statistics'].includes(activeTab) ? '!text-white' : ''}`} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 [&_svg]:text-current">
              <DropdownMenuItem 
                onClick={() => setActiveTab('housekeeping')} 
                className={`gap-2 ${activeTab === 'housekeeping' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Sparkles className={`w-4 h-4 ${activeTab === 'housekeeping' ? '!text-white' : ''}`} />
                <TranslatedText text="Housekeeping" />
                {activeTab === 'housekeeping' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('maintenance')} 
                className={`gap-2 ${activeTab === 'maintenance' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Wrench className={`w-4 h-4 ${activeTab === 'maintenance' ? '!text-white' : ''}`} />
            <TranslatedText text="Maintenance" />
                {activeTab === 'maintenance' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('reviews')} 
                className={`gap-2 ${activeTab === 'reviews' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Star className={`w-4 h-4 ${activeTab === 'reviews' ? '!text-white' : ''}`} />
            <TranslatedText text="Reviews" />
                {activeTab === 'reviews' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('keycards')} 
                className={`gap-2 ${activeTab === 'keycards' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Key className={`w-4 h-4 ${activeTab === 'keycards' ? '!text-white' : ''}`} />
            <TranslatedText text="Key Cards" />
                {activeTab === 'keycards' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('group-bookings')} 
                className={`gap-2 ${activeTab === 'group-bookings' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Users2 className={`w-4 h-4 ${activeTab === 'group-bookings' ? '!text-white' : ''}`} />
            <TranslatedText text="Group Bookings" />
                {activeTab === 'group-bookings' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('waitlist')} 
                className={`gap-2 ${activeTab === 'waitlist' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Clock className={`w-4 h-4 ${activeTab === 'waitlist' ? '!text-white' : ''}`} />
            <TranslatedText text="Waitlist" />
                {activeTab === 'waitlist' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('promotions')} 
                className={`gap-2 ${activeTab === 'promotions' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <Tag className={`w-4 h-4 ${activeTab === 'promotions' ? '!text-white' : ''}`} />
            <TranslatedText text="Promotions" />
                {activeTab === 'promotions' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('reports')} 
                className={`gap-2 ${activeTab === 'reports' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <FileBarChart className={`w-4 h-4 ${activeTab === 'reports' ? '!text-white' : ''}`} />
            <TranslatedText text="Reports" />
                {activeTab === 'reports' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setActiveTab('statistics')} 
                className={`gap-2 ${activeTab === 'statistics' ? 'bg-accent font-medium !text-white [&_svg]:!text-white' : 'focus:!bg-muted focus:!text-black hover:!bg-muted hover:!text-black'} [&_svg]:text-current`}
              >
                <BarChart3 className={`w-4 h-4 ${activeTab === 'statistics' ? '!text-white' : ''}`} />
            <TranslatedText text="Statistics" />
                {activeTab === 'statistics' && <Check className="w-4 h-4 ml-auto !text-white" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {loadingOverview && (loadingRooms || loadingReservations || loadingGuests) ? (
            <div className="space-y-6">
              {/* Loading State for Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <StatCardSkeleton key={i} />
                ))}
              </div>
              {/* Loading State for Quick Actions */}
              <Card className="border-2">
                <CardHeader>
                  <div className="h-6 bg-muted rounded w-32 animate-pulse" />
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="h-12 bg-muted rounded animate-pulse" />
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
          <div className="space-y-6">
            {/* Currency Switcher */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDisplayCurrency(displayCurrency === 'FRW' ? 'USD' : 'FRW')}
                className="gap-2 font-medium hover:bg-accent hover:text-accent-foreground transition-all"
              >
                <RefreshCw className="h-4 w-4" />
                {displayCurrency === 'FRW' ? (
                  <>
                    <span>FRW</span>
                    <span className="text-muted-foreground">→ USD</span>
                  </>
                ) : (
                  <>
                    <span>USD</span>
                    <span className="text-muted-foreground">→ FRW</span>
                  </>
                )}
              </Button>
            </div>
            
            {/* Main Statistics Grid - 8 Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Rooms */}
              <Card className="group relative overflow-hidden border-2 border-blue-200/50 hover:border-blue-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-50 to-blue-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-blue-700 uppercase tracking-wide">
                    <TranslatedText text="Total Rooms" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                    <Home className="h-5 w-5 text-blue-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                      {stats.rooms.total}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-muted-foreground"><TranslatedText text="Active" />: <span className="font-semibold text-blue-900">{stats.rooms.active}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="text-muted-foreground"><TranslatedText text="Occupied" />: <span className="font-semibold text-blue-900">{stats.rooms.occupied}</span></span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Occupancy Rate */}
              <Card className="group relative overflow-hidden border-2 border-green-200/50 hover:border-green-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-50 to-green-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-emerald-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-green-700 uppercase tracking-wide">
                    <TranslatedText text="Occupancy Rate" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-green-500/10 group-hover:bg-green-500/20 transition-colors">
                    <BarChart3 className="h-5 w-5 text-green-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                      {stats.rooms.occupancyRate}%
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-2 mb-3">
                    {stats.rooms.available} <TranslatedText text="rooms available" />
                  </p>
                  {/* Progress bar */}
                  <div className="h-2 bg-green-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(stats.rooms.occupancyRate, 100)}%` }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Total Revenue */}
              <Card className="group relative overflow-hidden border-2 border-purple-200/50 hover:border-purple-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-purple-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-purple-700 uppercase tracking-wide">
                    <TranslatedText text="Total Revenue" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                      {formatCurrencyDisplay(stats.revenue.total, 'FRW', displayCurrency)}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-xs">
                    <TrendingUp className="h-3 w-3 text-green-600" />
                    <span className="text-green-600 font-medium"><TranslatedText text="Today" />: {formatCurrencyDisplay(stats.revenue.today, 'FRW', displayCurrency)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Total Reservations */}
              <Card className="group relative overflow-hidden border-2 border-orange-200/50 hover:border-orange-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-orange-50 to-orange-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-orange-700 uppercase tracking-wide">
                    <TranslatedText text="Total Reservations" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                    <Calendar className="h-5 w-5 text-orange-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                      {stats.reservations.total}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-muted-foreground">{stats.reservations.checkedIn} <TranslatedText text="checked in" /></span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Today's Check-Ins */}
              <Card className="group relative overflow-hidden border-2 border-emerald-200/50 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-emerald-50 to-emerald-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">
                    <TranslatedText text="Today's Check-Ins" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                    <UserCheck className="h-5 w-5 text-emerald-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                      {stats.today.checkIns}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-xs text-muted-foreground">{stats.today.arrivals} <TranslatedText text="arrivals scheduled" /></span>
                  </div>
                </CardContent>
              </Card>

              {/* Today's Check-Outs */}
              <Card className="group relative overflow-hidden border-2 border-rose-200/50 hover:border-rose-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-rose-50 to-rose-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-rose-500/5 via-transparent to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-rose-700 uppercase tracking-wide">
                    <TranslatedText text="Today's Check-Outs" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-rose-500/10 group-hover:bg-rose-500/20 transition-colors">
                    <UserX className="h-5 w-5 text-rose-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                      {stats.today.checkOuts}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-orange-500" />
                    <span className="text-xs text-muted-foreground">{stats.today.departures} <TranslatedText text="departures scheduled" /></span>
                  </div>
                </CardContent>
              </Card>

              {/* Total Guests */}
              <Card className="group relative overflow-hidden border-2 border-indigo-200/50 hover:border-indigo-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-indigo-50 to-indigo-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-indigo-700 uppercase tracking-wide">
                    <TranslatedText text="Total Guests" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-indigo-500/10 group-hover:bg-indigo-500/20 transition-colors">
                    <Users className="h-5 w-5 text-indigo-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      {stats.guests.total}
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-xs text-muted-foreground">{stats.guests.active} <TranslatedText text="currently active" /></span>
                  </div>
                </CardContent>
              </Card>

              {/* Payments */}
              <Card className="group relative overflow-hidden border-2 border-teal-200/50 hover:border-teal-300 hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-teal-50 to-teal-100/50">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 via-transparent to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <CardHeader className="relative flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-semibold text-teal-700 uppercase tracking-wide">
                    <TranslatedText text="Payments" />
                  </CardTitle>
                  <div className="p-2 rounded-lg bg-teal-500/10 group-hover:bg-teal-500/20 transition-colors">
                    <CreditCard className="h-5 w-5 text-teal-600" />
                  </div>
                </CardHeader>
                <CardContent className="relative">
                  <div className="flex items-baseline gap-2">
                    <div className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                      {stats.payments.completed}
                    </div>
                  </div>
                  <div className="mt-3 space-y-1.5 text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span className="text-muted-foreground"><TranslatedText text="Paid" />: <span className="font-semibold text-teal-900">{formatCurrencyDisplay(stats.revenue.paid, 'FRW', displayCurrency)}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-orange-500" />
                      <span className="text-muted-foreground"><TranslatedText text="Loan" />: <span className="font-semibold text-teal-900">{formatCurrencyDisplay(stats.revenue.loan, 'FRW', displayCurrency)}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-gray-500" />
                      <span className="text-muted-foreground"><TranslatedText text="Pending" />: <span className="font-semibold text-teal-900">{formatCurrencyDisplay(stats.revenue.pending, 'FRW', displayCurrency)}</span></span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions Section */}
            <Card className="border-2">
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  <TranslatedText text="Quick Actions" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Common tasks and shortcuts" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  <Button
                    onClick={() => {
                      setShowReservationDialog(true);
                      loadPromotions();
                    }}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="Create new reservation"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    <TranslatedText text="New Reservation" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('reservations')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="View all reservations"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    <TranslatedText text="View Reservations" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('guests')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="Manage guests"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    <TranslatedText text="Manage Guests" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('rooms')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="Manage rooms"
                  >
                    <Bed className="h-4 w-4 mr-2" />
                    <TranslatedText text="Manage Rooms" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('payments')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="View payments"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    <TranslatedText text="View Payments" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('availability')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="Check room availability"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    <TranslatedText text="Check Availability" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('folio')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="View guest bills"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    <TranslatedText text="View Bills" />
                  </Button>
                  
                  <Button
                    onClick={() => setActiveTab('statistics')}
                    className="w-full justify-start transition-all duration-200 hover:scale-105 hover:shadow-md"
                    variant="outline"
                    aria-label="View statistics and analytics"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    <TranslatedText text="View Statistics" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
          )}
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="statistics" className="space-y-4">
          <div className="space-y-6">
            {/* Main Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Rooms */}
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 hover:shadow-lg transition-all duration-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-blue-700">
                    <TranslatedText text="Total Rooms" />
                  </CardTitle>
                  <Home className="h-5 w-5 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-blue-900">{stats.rooms.total}</div>
                  <p className="text-xs text-blue-600 mt-1">
                    <TranslatedText text="Active" />: {stats.rooms.active} | <TranslatedText text="Occupied" />: {stats.rooms.occupied}
                  </p>
                </CardContent>
              </Card>

              {/* Occupancy Rate */}
              <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:shadow-lg transition-all duration-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-green-700">
                    <TranslatedText text="Occupancy Rate" />
                  </CardTitle>
                  <BarChart3 className="h-5 w-5 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-green-900">{stats.rooms.occupancyRate}%</div>
                  <p className="text-xs text-green-600 mt-1">
                    {stats.rooms.available} <TranslatedText text="rooms available" />
                  </p>
                </CardContent>
              </Card>

              {/* Total Revenue */}
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-lg transition-all duration-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-purple-700">
                    <TranslatedText text="Total Revenue" />
                  </CardTitle>
                  <DollarSign className="h-5 w-5 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-purple-900">{formatCurrency(stats.revenue.total)}</div>
                  <p className="text-xs text-purple-600 mt-1">
                    <TranslatedText text="Today" />: {formatCurrency(stats.revenue.today)}
                  </p>
                </CardContent>
              </Card>

              {/* Total Reservations */}
              <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:shadow-lg transition-all duration-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-orange-700">
                    <TranslatedText text="Total Reservations" />
                  </CardTitle>
                  <Calendar className="h-5 w-5 text-orange-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-orange-900">{stats.reservations.total}</div>
                  <p className="text-xs text-orange-600 mt-1">
                    {stats.reservations.checkedIn} <TranslatedText text="checked in" />
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Secondary Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Today's Check-Ins */}
              <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-emerald-700">
                    <TranslatedText text="Today's Check-Ins" />
                  </CardTitle>
                  <UserCheck className="h-4 w-4 text-emerald-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-emerald-900">{stats.today.checkIns}</div>
                  <p className="text-xs text-emerald-600 mt-1">
                    {stats.today.arrivals} <TranslatedText text="arrivals scheduled" />
                  </p>
                </CardContent>
              </Card>

              {/* Today's Check-Outs */}
              <Card className="bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-rose-700">
                    <TranslatedText text="Today's Check-Outs" />
                  </CardTitle>
                  <UserX className="h-4 w-4 text-rose-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-rose-900">{stats.today.checkOuts}</div>
                  <p className="text-xs text-rose-600 mt-1">
                    {stats.today.departures} <TranslatedText text="departures scheduled" />
                  </p>
                </CardContent>
              </Card>

              {/* Total Guests */}
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-indigo-700">
                    <TranslatedText text="Total Guests" />
                  </CardTitle>
                  <Users className="h-4 w-4 text-indigo-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-indigo-900">{stats.guests.total}</div>
                  <p className="text-xs text-indigo-600 mt-1">
                    {stats.guests.active} <TranslatedText text="currently active" />
                  </p>
                </CardContent>
              </Card>

              {/* Payments */}
              <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-teal-700">
                    <TranslatedText text="Payments" />
                  </CardTitle>
                  <CreditCard className="h-4 w-4 text-teal-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-teal-900">{stats.payments.completed}</div>
                  <div className="text-xs text-teal-600 mt-1 space-y-0.5">
                    <div><span className="text-green-700 font-semibold">{formatCurrency(stats.revenue.paid)}</span> <TranslatedText text="paid" /></div>
                    <div><span className="text-orange-700 font-semibold">{formatCurrency(stats.revenue.loan || 0)}</span> <TranslatedText text="loan" /></div>
                    <div><span className="text-gray-600 font-semibold">{formatCurrency(stats.revenue.pending)}</span> <TranslatedText text="pending" /></div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Reservation Status Breakdown */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">
                    <TranslatedText text="Reservation Status" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Pending" /></span>
                    <span className="text-sm font-semibold">{stats.reservations.pending}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Confirmed" /></span>
                    <span className="text-sm font-semibold">{stats.reservations.confirmed}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Checked In" /></span>
                    <span className="text-sm font-semibold text-green-600">{stats.reservations.checkedIn}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Checked Out" /></span>
                    <span className="text-sm font-semibold">{stats.reservations.checkedOut}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Cancelled" /></span>
                    <span className="text-sm font-semibold text-red-600">{stats.reservations.cancelled}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Housekeeping Tasks */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4" />
                    <TranslatedText text="Housekeeping" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Total Tasks" /></span>
                    <span className="text-sm font-semibold">{stats.housekeeping.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Completed" /></span>
                    <span className="text-sm font-semibold text-green-600">{stats.housekeeping.completed}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Pending" /></span>
                    <span className="text-sm font-semibold text-yellow-600">{stats.housekeeping.pending}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground"><TranslatedText text="Completion Rate" /></span>
                      <span className="text-sm font-semibold">
                        {stats.housekeeping.total > 0 
                          ? Math.round((stats.housekeeping.completed / stats.housekeeping.total) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Maintenance Requests */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    <TranslatedText text="Maintenance" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Total Requests" /></span>
                    <span className="text-sm font-semibold">{stats.maintenance.total}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Open" /></span>
                    <span className="text-sm font-semibold text-orange-600">{stats.maintenance.open}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Completed" /></span>
                    <span className="text-sm font-semibold text-green-600">{stats.maintenance.completed}</span>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground"><TranslatedText text="Completion Rate" /></span>
                      <span className="text-sm font-semibold">
                        {stats.maintenance.total > 0 
                          ? Math.round((stats.maintenance.completed / stats.maintenance.total) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance Metrics */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    <TranslatedText text="Performance" />
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Avg Stay Duration" /></span>
                    <span className="text-sm font-semibold">{stats.averages.stayDuration} <TranslatedText text="days" /></span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Avg Room Rate" /></span>
                    <span className="text-sm font-semibold">{formatCurrency(stats.averages.roomRate)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground"><TranslatedText text="Revenue per Room" /></span>
                    <span className="text-sm font-semibold">
                      {stats.rooms.active > 0 
                        ? formatCurrency(stats.revenue.total / stats.rooms.active)
                        : formatCurrency(0)}
                    </span>
                  </div>
                  <div className="mt-2 pt-2 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-muted-foreground"><TranslatedText text="Payment Completion" /></span>
                      <span className="text-sm font-semibold">
                        {stats.payments.total > 0 
                          ? Math.round((stats.payments.completed / stats.payments.total) * 100)
                          : 0}%
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Reservation Trends Graph */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <TranslatedText text="Reservation Trends - Last 4 Months" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Comparison of reservations by status over the last 4 months" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyReservationData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.2}/>
                        </linearGradient>
                        <linearGradient id="colorConfirmed" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.2}/>
                        </linearGradient>
                        <linearGradient id="colorCheckedIn" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#06b6d4" stopOpacity={0.2}/>
                        </linearGradient>
                        <linearGradient id="colorCheckedOut" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.2}/>
                        </linearGradient>
                        <linearGradient id="colorCancelled" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.2}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        cursor={{ fill: 'hsl(var(--muted))', opacity: 0.1 }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="circle"
                      />
                      <Bar 
                        dataKey="total" 
                        fill="url(#colorTotal)" 
                        name={tSync("Total", "Total")}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="confirmed" 
                        fill="url(#colorConfirmed)" 
                        name={tSync("Confirmed", "Confirmed")}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="checkedIn" 
                        fill="url(#colorCheckedIn)" 
                        name={tSync("Checked In", "Checked In")}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="checkedOut" 
                        fill="url(#colorCheckedOut)" 
                        name={tSync("Checked Out", "Checked Out")}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="cancelled" 
                        fill="url(#colorCancelled)" 
                        name={tSync("Cancelled", "Cancelled")}
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        dataKey="pending" 
                        fill="#f59e0b" 
                        name={tSync("Pending", "Pending")}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Line Chart for Total Reservations Trend */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <LineChart className="h-5 w-5 text-primary" />
                  <TranslatedText text="Reservation Trends - Line Chart" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Monthly trend of total reservations over the last 4 months" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={monthlyReservationData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <YAxis 
                        tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        cursor={{ stroke: 'hsl(var(--primary))', strokeWidth: 2, strokeDasharray: '5 5' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="total" 
                        stroke="#3b82f6" 
                        strokeWidth={3}
                        name={tSync("Total Reservations", "Total Reservations")}
                        dot={{ r: 5, fill: '#3b82f6' }}
                        activeDot={{ r: 8, fill: '#3b82f6' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="confirmed" 
                        stroke="#10b981" 
                        strokeWidth={2}
                        name={tSync("Confirmed", "Confirmed")}
                        dot={{ r: 4, fill: '#10b981' }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="checkedIn" 
                        stroke="#06b6d4" 
                        strokeWidth={2}
                        name={tSync("Checked In", "Checked In")}
                        dot={{ r: 4, fill: '#06b6d4' }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Revenue Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Trend - Last 7 Days */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    <TranslatedText text="Revenue Trend - Last 7 Days" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Daily revenue performance" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart
                        data={getRevenueAnalytics.last7Days}
                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                      >
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis 
                          dataKey="day" 
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                          tickFormatter={(value) => formatCurrency(value).replace(' FRW', 'K')}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          formatter={(value: any) => formatCurrency(value)}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          fill="url(#colorRevenue)" 
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue by Room Type - Pie Chart */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <PieChartIcon className="h-5 w-5 text-purple-600" />
                    <TranslatedText text="Revenue by Room Type" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Revenue distribution across room types" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getRevenueAnalytics.revenueByRoomType}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getRevenueAnalytics.revenueByRoomType.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={CHART_COLORS.primary[index % CHART_COLORS.primary.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          formatter={(value: any) => formatCurrency(value)}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Status Distribution */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                    <TranslatedText text="Payment Status Distribution" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Breakdown of payment statuses" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getPaymentStatusData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getPaymentStatusData.map((entry, index) => {
                            const colorMap: Record<string, string> = {
                              'PAID': CHART_COLORS.status.paid,
                              'PARTIAL': CHART_COLORS.status.partial,
                              'UNPAID': CHART_COLORS.status.unpaid,
                            };
                            return (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={colorMap[entry.name] || CHART_COLORS.primary[index % CHART_COLORS.primary.length]} 
                              />
                            );
                          })}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={36}
                          iconType="circle"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue by Payment Method */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-green-600" />
                    <TranslatedText text="Revenue by Payment Method" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Payment method preferences" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={getRevenueAnalytics.paymentMethodData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                          tickFormatter={(value) => formatCurrency(value).replace(' FRW', 'K')}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                          formatter={(value: any) => formatCurrency(value)}
                        />
                        <Bar 
                          dataKey="value" 
                          fill="#10b981" 
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Room Performance Analytics */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Bed className="h-5 w-5 text-primary" />
                  <TranslatedText text="Room Performance Analytics" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Top performing rooms by revenue and occupancy" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={getRoomPerformanceAnalytics.roomPerformance.slice(0, 10)}
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis 
                        type="number"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                        tickFormatter={(value) => formatCurrency(value).replace(' FRW', 'K')}
                      />
                      <YAxis 
                        type="category"
                        dataKey="roomNumber"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                        width={80}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                        formatter={(value: any, name: string) => {
                          if (name === 'totalRevenue') return formatCurrency(value);
                          return value;
                        }}
                      />
                      <Legend />
                      <Bar 
                        dataKey="totalRevenue"
                        name="Total Revenue" 
                        fill="#3b82f6" 
                        radius={[0, 8, 8, 0]}
                      />
                      <Bar 
                        dataKey="reservationCount" 
                        fill="#10b981" 
                        radius={[0, 8, 8, 0]}
                        name="Reservation Count"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Guest Analytics Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Guests by Lifetime Value */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <Users className="h-5 w-5 text-indigo-600" />
                    <TranslatedText text="Top Guests by Lifetime Value" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Most valuable guests" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {getGuestAnalytics.guestLTV.map((guest, index) => (
                      <div key={guest.guestId} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div>
                            <div className="font-semibold text-sm">{guest.name}</div>
                            <div className="text-xs text-muted-foreground">{guest.email}</div>
                            <div className="text-xs text-muted-foreground mt-1">
                              {guest.reservationCount} <TranslatedText text="reservations" />
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-primary">{formatCurrency(guest.totalSpent)}</div>
                          <div className="text-xs text-muted-foreground">
                            <TranslatedText text="Avg" />: {formatCurrency(guest.avgReservationValue)}
                          </div>
                        </div>
                      </div>
                    ))}
                    {getGuestAnalytics.guestLTV.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">
                        <TranslatedText text="No guest data available" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Guest Demographics by Country */}
              <Card className="border-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-teal-600" />
                    <TranslatedText text="Guest Demographics - Top Countries" />
                  </CardTitle>
                  <CardDescription>
                    <TranslatedText text="Guest distribution by country" />
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={getGuestAnalytics.countryData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                          stroke="hsl(var(--border))"
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--background))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                            padding: '12px',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                          }}
                        />
                        <Bar 
                          dataKey="value" 
                          fill="#14b8a6" 
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Room Type Occupancy Analytics */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <Activity className="h-5 w-5 text-orange-600" />
                  <TranslatedText text="Room Type Occupancy Analysis" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Occupancy rates by room type" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart
                      data={getRoomPerformanceAnalytics.roomTypeOccupancy}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                      <XAxis 
                        dataKey="name" 
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                        stroke="hsl(var(--border))"
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--background))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          padding: '12px',
                          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                      />
                      <Legend />
                      <Bar 
                        yAxisId="left"
                        dataKey="occupied" 
                        fill="#f59e0b" 
                        name="Occupied"
                        radius={[8, 8, 0, 0]}
                      />
                      <Bar 
                        yAxisId="left"
                        dataKey="available" 
                        fill="#10b981" 
                        name="Available"
                        radius={[8, 8, 0, 0]}
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="occupancyRate" 
                        stroke="#ef4444" 
                        strokeWidth={3}
                        name="Occupancy Rate %"
                        dot={{ r: 5 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Key Performance Metrics Summary Table */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <FileBarChart className="h-5 w-5 text-primary" />
                  <TranslatedText text="Key Performance Metrics Summary" />
                </CardTitle>
                <CardDescription>
                  <TranslatedText text="Comprehensive performance overview" />
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 font-semibold text-sm"><TranslatedText text="Metric" /></th>
                        <th className="text-right p-3 font-semibold text-sm"><TranslatedText text="Value" /></th>
                        <th className="text-right p-3 font-semibold text-sm"><TranslatedText text="Target" /></th>
                        <th className="text-right p-3 font-semibold text-sm"><TranslatedText text="Status" /></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Occupancy Rate" /></td>
                        <td className="p-3 text-sm text-right font-semibold">{stats.rooms.occupancyRate}%</td>
                        <td className="p-3 text-sm text-right text-muted-foreground">75%</td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant={stats.rooms.occupancyRate >= 75 ? "default" : "secondary"}>
                            {stats.rooms.occupancyRate >= 75 ? <TranslatedText text="On Target" /> : <TranslatedText text="Below Target" />}
                          </Badge>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Average Stay Duration" /></td>
                        <td className="p-3 text-sm text-right font-semibold">{stats.averages.stayDuration} <TranslatedText text="days" /></td>
                        <td className="p-3 text-sm text-right text-muted-foreground">3 <TranslatedText text="days" /></td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant={stats.averages.stayDuration >= 3 ? "default" : "secondary"}>
                            {stats.averages.stayDuration >= 3 ? <TranslatedText text="Good" /> : <TranslatedText text="Low" />}
                          </Badge>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Revenue per Available Room" /></td>
                        <td className="p-3 text-sm text-right font-semibold">
                          {stats.rooms.active > 0 
                            ? formatCurrency(stats.revenue.total / stats.rooms.active)
                            : formatCurrency(0)}
                        </td>
                        <td className="p-3 text-sm text-right text-muted-foreground">-</td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant="outline"><TranslatedText text="Tracking" /></Badge>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Payment Completion Rate" /></td>
                        <td className="p-3 text-sm text-right font-semibold">
                          {stats.payments.total > 0 
                            ? Math.round((stats.payments.completed / stats.payments.total) * 100)
                            : 0}%
                        </td>
                        <td className="p-3 text-sm text-right text-muted-foreground">95%</td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant={stats.payments.total > 0 && (stats.payments.completed / stats.payments.total) >= 0.95 ? "default" : "secondary"}>
                            {stats.payments.total > 0 && (stats.payments.completed / stats.payments.total) >= 0.95 ? <TranslatedText text="Excellent" /> : <TranslatedText text="Needs Improvement" />}
                          </Badge>
                        </td>
                      </tr>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Repeat Guest Rate" /></td>
                        <td className="p-3 text-sm text-right font-semibold">{getGuestAnalytics.repeatGuestRate}%</td>
                        <td className="p-3 text-sm text-right text-muted-foreground">30%</td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant={getGuestAnalytics.repeatGuestRate >= 30 ? "default" : "secondary"}>
                            {getGuestAnalytics.repeatGuestRate >= 30 ? <TranslatedText text="Good" /> : <TranslatedText text="Low" />}
                          </Badge>
                        </td>
                      </tr>
                      <tr className="hover:bg-muted/50">
                        <td className="p-3 text-sm"><TranslatedText text="Housekeeping Completion Rate" /></td>
                        <td className="p-3 text-sm text-right font-semibold">
                          {stats.housekeeping.total > 0 
                            ? Math.round((stats.housekeeping.completed / stats.housekeeping.total) * 100)
                            : 0}%
                        </td>
                        <td className="p-3 text-sm text-right text-muted-foreground">100%</td>
                        <td className="p-3 text-sm text-right">
                          <Badge variant={stats.housekeeping.total > 0 && stats.housekeeping.completed === stats.housekeeping.total ? "default" : "secondary"}>
                            {stats.housekeeping.total > 0 && stats.housekeeping.completed === stats.housekeeping.total ? <TranslatedText text="Perfect" /> : <TranslatedText text="In Progress" />}
                          </Badge>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Rooms Tab */}
        <TabsContent value="rooms" className="space-y-4">
          {/* Quick Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  <TranslatedText text="Total Rooms" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold">{rooms.length}</div>
                  <Bed className="h-8 w-8 text-blue-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {rooms.filter(r => r.isActive).length} <TranslatedText text="active" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  <TranslatedText text="Room Types" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold">{roomTypes.length}</div>
                  <Home className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  <TranslatedText text="Categories available" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  <TranslatedText text="Conference Rooms" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold">{conferenceRooms.length}</div>
                  <Building2 className="h-8 w-8 text-teal-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {conferenceRooms.filter(cr => cr.isActive).length} <TranslatedText text="available" />
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  <TranslatedText text="Equipment" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-3xl font-bold">{equipment.length}</div>
                  <Package className="h-8 w-8 text-orange-500 opacity-50" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {equipment.filter(eq => eq.isActive).length} <TranslatedText text="available" />
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions Bar */}
          <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm font-medium text-teal-900">
                  <Sparkles className="h-4 w-4" />
                  <TranslatedText text="Quick Actions" />
                </div>
                <div className="flex-1" />
                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRoomTypeForm({ name: '', description: '', maxCapacity: '2', basePrice: '' });
                        setShowRoomTypeDialog(true);
                      }}
                      className="bg-white hover:bg-teal-50 border-teal-300 text-gray-900 hover:text-gray-900"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <TranslatedText text="Room Type" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowRoomDialog(true)}
                      className="bg-white hover:bg-teal-50 border-teal-300 text-gray-900 hover:text-gray-900"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <TranslatedText text="Hotel Room" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setConferenceRoomForm({
                          name: '',
                          roomNumber: '',
                          capacity: '10',
                          floor: '',
                          size: '',
                          hourlyRate: '',
                          dailyRate: '',
                          amenities: '',
                          description: '',
                          isActive: true,
                          status: 'AVAILABLE',
                        });
                        setSelectedConferenceRoom(null);
                        setShowConferenceRoomDialog(true);
                      }}
                      className="bg-white hover:bg-teal-50 border-teal-300 text-gray-900 hover:text-gray-900"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <TranslatedText text="Conference Room" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEquipmentForm({
                          name: '',
                          equipmentNumber: '',
                          category: '',
                          brand: '',
                          model: '',
                          hourlyRate: '',
                          dailyRate: '',
                          description: '',
                          isActive: true,
                          status: 'AVAILABLE',
                          quantity: '1',
                        });
                        setSelectedEquipment(null);
                        setShowEquipmentDialog(true);
                      }}
                      className="bg-white hover:bg-teal-50 border-teal-300 text-gray-900 hover:text-gray-900"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <TranslatedText text="Equipment" />
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sub-navigation Tabs for Rooms */}
          <Tabs defaultValue="hotel-rooms" className="w-full">
            <TabsList className="grid w-full grid-cols-4 bg-muted/50">
              <TabsTrigger value="hotel-rooms" className="flex items-center gap-2">
                <Bed className="h-4 w-4" />
                <TranslatedText text="Hotel Rooms" />
                <Badge variant="secondary" className="ml-1">{rooms.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="room-types" className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                <TranslatedText text="Room Types" />
                <Badge variant="secondary" className="ml-1">{roomTypes.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="conference-rooms" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <TranslatedText text="Conference" />
                <Badge variant="secondary" className="ml-1">{conferenceRooms.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="equipment" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                <TranslatedText text="Equipment" />
                <Badge variant="secondary" className="ml-1">{equipment.length}</Badge>
              </TabsTrigger>
            </TabsList>

            {/* Hotel Rooms Sub-tab */}
            <TabsContent value="hotel-rooms" className="space-y-4 mt-4">
              {/* Rooms Management Section */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <div className="min-w-0">
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    <TranslatedText text="Hotel Rooms" />
                    {companyName && (
                      <span className="text-xs font-medium text-muted-foreground truncate max-w-[200px]">
                        • {companyName}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    <TranslatedText text="Manage your hotel rooms" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({rooms.length} <TranslatedText text="total" />)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadRooms();
                      setRoomsPage(1);
                    }}
                    title={tSync("Refresh rooms", "Refresh rooms")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                <Button 
                  onClick={() => setShowRoomDialog(true)}
                  aria-label="Add new room"
                  className="transition-all duration-200 hover:scale-105"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add Room" />
                </Button>
                )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Enhanced Filters Section */}
              <div className="mb-6 rounded-xl border bg-card shadow-sm">
                <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-primary" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                    {(() => {
                      const activeCount = [
                        roomsFilters.isActive !== 'all',
                        roomsFilters.reservationStatus !== 'all',
                        roomsFilters.roomTypeId !== 'all',
                        roomsFilters.floor !== '',
                        roomsFilters.search !== '',
                      ].filter(Boolean).length;
                      return activeCount > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {activeCount} <TranslatedText text="active" />
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const hasActiveFilters = roomsFilters.isActive !== 'all' || 
                        roomsFilters.reservationStatus !== 'all' || 
                        roomsFilters.roomTypeId !== 'all' || 
                        roomsFilters.floor || 
                        roomsFilters.search;
                      return hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setRoomsFilters({
                              isActive: 'all',
                              reservationStatus: 'all',
                              roomTypeId: 'all',
                              floor: '',
                              search: '',
                            });
                          }}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4 mr-1" />
                          <TranslatedText text="Clear All" />
                        </Button>
                      );
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowRoomsFilters(!showRoomsFilters)}
                    >
                      {showRoomsFilters ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          <TranslatedText text="Hide Filters" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          <TranslatedText text="Show Filters" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Filter Chips (Always Visible) */}
                {(() => {
                  const activeFilters: Array<{ key: string; label: string; value: string }> = [];
                  if (roomsFilters.isActive !== 'all') {
                    activeFilters.push({ key: 'isActive', label: 'Status', value: roomsFilters.isActive });
                  }
                  if (roomsFilters.reservationStatus !== 'all') {
                    activeFilters.push({ key: 'reservationStatus', label: 'Reservation', value: roomsFilters.reservationStatus });
                  }
                  if (roomsFilters.roomTypeId !== 'all') {
                    const roomType = roomTypes.find(t => t.id === roomsFilters.roomTypeId);
                    activeFilters.push({ key: 'roomTypeId', label: 'Room Type', value: roomType?.name || roomsFilters.roomTypeId });
                  }
                  if (roomsFilters.floor) {
                    activeFilters.push({ key: 'floor', label: 'Floor', value: roomsFilters.floor });
                  }
                  if (roomsFilters.search) {
                    activeFilters.push({ key: 'search', label: 'Search', value: roomsFilters.search });
                  }
                  
                  return activeFilters.length > 0 && (
                    <div className="p-4 border-b bg-muted/20">
                      <div className="flex flex-wrap gap-2">
                        {activeFilters.map((filter) => (
                          <Badge
                            key={filter.key}
                            variant="secondary"
                            className="gap-1 pr-1"
                          >
                            <span className="capitalize">{filter.label}:</span>
                            <span>{filter.value}</span>
                            <button
                              onClick={() => {
                                if (filter.key === 'isActive') {
                                  setRoomsFilters({ ...roomsFilters, isActive: 'all' });
                                } else if (filter.key === 'reservationStatus') {
                                  setRoomsFilters({ ...roomsFilters, reservationStatus: 'all' });
                                } else if (filter.key === 'roomTypeId') {
                                  setRoomsFilters({ ...roomsFilters, roomTypeId: 'all' });
                                } else if (filter.key === 'floor') {
                                  setRoomsFilters({ ...roomsFilters, floor: '' });
                                } else if (filter.key === 'search') {
                                  setRoomsFilters({ ...roomsFilters, search: '' });
                                }
                              }}
                              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Expandable Filter Form */}
                {showRoomsFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Search Room" /></Label>
                    <Input
                      placeholder={tSync("Room number...", "Room number...")}
                      value={roomsFilters.search}
                      onChange={(e) => setRoomsFilters({ ...roomsFilters, search: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Status" /></Label>
                    <Select
                      value={roomsFilters.isActive}
                      onValueChange={(value) => setRoomsFilters({ ...roomsFilters, isActive: value })}
                    >
                      <SelectTrigger className="h-9 border border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all"><TranslatedText text="All" /></SelectItem>
                        <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                        <SelectItem value="inactive"><TranslatedText text="Inactive" /></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Reservation" /></Label>
                    <Select
                      value={roomsFilters.reservationStatus}
                      onValueChange={(value) => setRoomsFilters({ ...roomsFilters, reservationStatus: value })}
                    >
                      <SelectTrigger className="h-9 border border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all"><TranslatedText text="All" /></SelectItem>
                        <SelectItem value="reserved"><TranslatedText text="Reserved" /></SelectItem>
                        <SelectItem value="notReserved"><TranslatedText text="Not Reserved" /></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Room Type" /></Label>
                    <Select
                      value={roomsFilters.roomTypeId}
                      onValueChange={(value) => setRoomsFilters({ ...roomsFilters, roomTypeId: value })}
                    >
                      <SelectTrigger className="h-9 border border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                        {roomTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Floor" /></Label>
                    <Input
                      placeholder={tSync("Floor number...", "Floor number...")}
                      value={roomsFilters.floor}
                      onChange={(e) => setRoomsFilters({ ...roomsFilters, floor: e.target.value })}
                      className="h-9 border border-gray-300"
                      type="number"
                    />
                  </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={roomsPerPage.toString()}
                    onValueChange={(value) => {
                      setRoomsPerPage(Number(value));
                      setRoomsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="9">9</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="18">18</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((roomsPage - 1) * roomsPerPage) + 1} - {Math.min(roomsPage * roomsPerPage, filteredRooms.length)} <TranslatedText text="of" /> {filteredRooms.length} ({rooms.length} <TranslatedText text="total" />)
                </div>
              </div>
              {loadingRooms ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <RoomCardSkeleton key={i} />
                  ))}
                </div>
              ) : getPaginatedRooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed rounded-lg">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse" />
                    <div className="relative p-6 bg-primary/10 rounded-full">
                      <Bed className="h-16 w-16 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    <TranslatedText text="No rooms found" />
                  </h3>
                  <p className="text-muted-foreground text-center max-w-md mb-6">
                    <TranslatedText text="Get started by creating your first room. Add room details, set pricing, upload images, and manage availability." />
                  </p>
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                  <Button onClick={() => setShowRoomDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Add Your First Room" />
                  </Button>
                  )}
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {getPaginatedRooms.map((room) => (
                  <Card key={room.id} className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                    {/* Room Image Preview */}
                    <div className="relative h-48 bg-gradient-to-br from-blue-100 to-purple-100 overflow-hidden">
                      {room.images && room.images.length > 0 ? (
                        <>
                          <img 
                            src={room.images[0].imageUrl?.startsWith('http') 
                              ? room.images[0].imageUrl 
                              : room.images[0].imageUrl?.startsWith('/')
                              ? `${apiService['baseUrl']}${room.images[0].imageUrl}`
                              : `${apiService['baseUrl']}/${room.images[0].imageUrl}`} 
                            alt={room.roomNumber}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              console.error('🖼️  [ROOM CARD] ❌ Failed to load room image:', {
                                roomId: room.id,
                                roomNumber: room.roomNumber,
                                imageUrl: room.images[0].imageUrl,
                                fullUrl: room.images[0].imageUrl?.startsWith('http') 
                                  ? room.images[0].imageUrl 
                                  : room.images[0].imageUrl?.startsWith('/')
                                  ? `${apiService['baseUrl']}${room.images[0].imageUrl}`
                                  : `${apiService['baseUrl']}/${room.images[0].imageUrl}`,
                                totalImages: room.images.length
                              });
                              // Remove the failed image from room state to prevent showing broken images
                              setRooms(prevRooms => 
                                prevRooms.map(r => 
                                  r.id === room.id 
                                    ? { ...r, images: r.images.filter((img: any) => img.id !== room.images[0].id) }
                                    : r
                                )
                              );
                            }}
                          />
                          {room.images.length > 1 && (
                            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                              <ImageIcon className="w-3 h-3 inline mr-1" />
                              {room.images.length}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Bed className="h-16 w-16 text-muted-foreground/30" />
                        </div>
                      )}
                      {/* Status Badge Overlay */}
                      <div className="absolute top-3 left-3">
                        <Badge 
                          variant={isRoomReserved(room.id) ? 'destructive' : 'default'}
                          className={`shadow-lg backdrop-blur-sm ${isRoomReserved(room.id) ? 'bg-red-500/90' : 'bg-green-500/90'}`}
                        >
                          {isRoomReserved(room.id) ? (
                            <TranslatedText text="Reserved" />
                          ) : (
                            <TranslatedText text="Available" />
                          )}
                        </Badge>
                      </div>
                      {/* Quick Actions Overlay on Hover */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => handleViewRoom(room)}
                          className="backdrop-blur-sm bg-white/90 text-gray-900 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105"
                          aria-label={`View room ${room.roomNumber} details`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button 
                          size="sm" 
                          variant="secondary"
                          onClick={() => handleEditRoom(room)}
                          className="backdrop-blur-sm bg-white/90 text-gray-900 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105"
                          aria-label={`Edit room ${room.roomNumber}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                    
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-xl font-bold mb-1">
                            <TranslatedText text="Room" /> {room.roomNumber}
                          </CardTitle>
                          {room.title && (
                            <CardDescription className="text-sm font-medium mb-2">
                              {room.title}
                            </CardDescription>
                          )}
                          <CardDescription className="text-xs">
                            {room.roomTypeRef?.name || room.roomType || tSync('Standard Room', 'Standard Room')}
                          </CardDescription>
                        </div>
                        <TooltipProvider>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleViewRoom(room)}>
                                <Eye className="mr-2 h-4 w-4" />
                                <TranslatedText text="View Details" />
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditRoom(room)}>
                                <Edit className="mr-2 h-4 w-4" />
                                <TranslatedText text="Edit" />
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedRoomForCalendar(room);
                                setShowAvailabilityCalendar(true);
                              }}>
                                <Calendar className="mr-2 h-4 w-4" />
                                <TranslatedText text="Block Dates" />
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TooltipProvider>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-3">
                        {/* Key Info Grid */}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              <span className="font-semibold">{room.capacity}</span> <TranslatedText text="guests" />
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-semibold text-primary">
                              {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                            </span>
                          </div>
                          {room.floor && (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm"><TranslatedText text="Floor" /> <span className="font-semibold">{room.floor}</span></span>
                            </div>
                          )}
                          {room.size && (
                            <div className="flex items-center gap-2">
                              <Home className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm font-medium">{room.size}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Amenities Preview */}
                        {room.amenities && room.amenities.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                            {room.amenities.slice(0, 3).map((amenity: any) => (
                              <Badge key={amenity.id} variant="outline" className="text-xs">
                                {amenity.amenity?.name || amenity.name}
                              </Badge>
                            ))}
                            {room.amenities.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{room.amenities.length - 3} <TranslatedText text="more" />
                              </Badge>
                            )}
                          </div>
                        )}

                        {/* View Calendar & Block Dates Button */}
                        <div className="pt-3 mt-3 border-t">
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              setSelectedRoomForCalendar(room);
                              setShowAvailabilityCalendar(true);
                            }}
                          >
                            <Calendar className="h-4 w-4 mr-2" />
                            <TranslatedText text="View Calendar & Block Dates" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              )}
              {totalRoomsPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setRoomsPage(prev => Math.max(1, prev - 1));
                          }}
                          className={roomsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalRoomsPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalRoomsPages ||
                          (page >= roomsPage - 1 && page <= roomsPage + 1)
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setRoomsPage(page);
                                }}
                                isActive={roomsPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (page === roomsPage - 2 || page === roomsPage + 2) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}
                      <PaginationItem>
                        <PaginationNext 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setRoomsPage(prev => Math.min(totalRoomsPages, prev + 1));
                          }}
                          className={roomsPage === totalRoomsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            {/* Room Types Sub-tab */}
            <TabsContent value="room-types" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle><TranslatedText text="Room Types" /></CardTitle>
                      <CardDescription>
                        <TranslatedText text="Manage room types (Single, Double, Suite, Deluxe, etc.)" />
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({roomTypes.length} <TranslatedText text="types" />)
                        </span>
                      </CardDescription>
                    </div>
                    {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                    <Button 
                      onClick={() => {
                        setRoomTypeForm({ name: '', description: '', maxCapacity: '2', basePrice: '' });
                        setShowRoomTypeDialog(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Add Room Type" />
                    </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {roomTypes.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 px-4">
                      <div className="relative mb-6">
                        <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse" />
                        <div className="relative p-6 bg-primary/10 rounded-full">
                          <Bed className="h-16 w-16 text-primary" />
                        </div>
                      </div>
                      <h3 className="text-xl font-semibold mb-2">
                        <TranslatedText text="No room types found" />
                      </h3>
                      <p className="text-muted-foreground text-center max-w-md mb-6">
                        <TranslatedText text="Get started by creating your first room type. Define room categories, set capacity limits, and base pricing." />
                      </p>
                      {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                        <Button 
                          onClick={() => {
                            setRoomTypeForm({ name: '', description: '', maxCapacity: '2', basePrice: '' });
                            setShowRoomTypeDialog(true);
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          <TranslatedText text="Create Room Type" />
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {roomTypes.map((type) => (
                        <Card key={type.id} className="relative hover:shadow-md transition-shadow">
                          <CardHeader>
                            <CardTitle className="text-lg">{type.name}</CardTitle>
                            {type.description && (
                              <CardDescription className="mt-1">{type.description}</CardDescription>
                            )}
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground"><TranslatedText text="Max Capacity" />:</span>
                                <span className="text-sm font-medium">{type.maxCapacity || 'N/A'}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground"><TranslatedText text="Rooms" />:</span>
                                <span className="text-sm font-medium">
                                  {rooms.filter((r: any) => r.roomTypeId === type.id).length}
                                </span>
                              </div>
                              {type.basePrice && (
                                <div className="flex justify-between items-center pt-2 border-t">
                                  <span className="text-sm text-muted-foreground"><TranslatedText text="Base Price" />:</span>
                                  <span className="text-sm font-semibold text-primary">{formatCurrency(type.basePrice)}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Conference Rooms Sub-tab */}
            <TabsContent value="conference-rooms" className="space-y-4 mt-4">
              <Card>
            <CardHeader>
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <div className="min-w-0">
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    <TranslatedText text="Conference Rooms" />
                    {companyName && (
                      <span className="text-xs font-medium text-muted-foreground truncate max-w-[200px]">
                        • {companyName}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    <TranslatedText text="Manage conference rooms and meeting spaces" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({conferenceRooms.length} <TranslatedText text="total" />)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadConferenceRooms();
                    }}
                    title={tSync("Refresh conference rooms", "Refresh conference rooms")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                    <Button 
                      onClick={() => {
                        setConferenceRoomForm({
                          name: '',
                          roomNumber: '',
                          capacity: '10',
                          floor: '',
                          size: '',
                          hourlyRate: '',
                          dailyRate: '',
                          amenities: '',
                          description: '',
                          isActive: true,
                          status: 'AVAILABLE',
                        });
                        setSelectedConferenceRoom(null);
                        setShowConferenceRoomDialog(true);
                      }}
                      className="transition-all duration-200 hover:scale-105"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Add Conference Room" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {conferenceRooms.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  <TranslatedText text="No conference rooms found. Create one to get started." />
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {conferenceRooms.map((cr) => (
                    <Card key={cr.id} className="group relative overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
                      {/* Conference Room Image Preview */}
                      <div className="relative h-48 bg-gradient-to-br from-teal-100 to-cyan-100 overflow-hidden">
                        {cr.images && cr.images.length > 0 ? (
                          <>
                            <img 
                              src={cr.images[0].imageUrl?.startsWith('http') 
                                ? cr.images[0].imageUrl 
                                : cr.images[0].imageUrl?.startsWith('/')
                                ? `${apiService['baseUrl']}${cr.images[0].imageUrl}`
                                : `${apiService['baseUrl']}/${cr.images[0].imageUrl}`} 
                              alt={cr.name}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/images/webflow/outside.jpg';
                              }}
                            />
                            {cr.images.length > 1 && (
                              <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                                <ImageIcon className="w-3 h-3 inline mr-1" />
                                {cr.images.length}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Building2 className="h-16 w-16 text-muted-foreground/30" />
                          </div>
                        )}
                        {/* Status Badge Overlay */}
                        <div className="absolute top-3 left-3">
                          <Badge 
                            variant={cr.isActive && cr.status === 'AVAILABLE' ? 'default' : 'secondary'}
                            className={`shadow-lg backdrop-blur-sm ${cr.isActive && cr.status === 'AVAILABLE' ? 'bg-green-500/90' : 'bg-gray-500/90'}`}
                          >
                            {cr.status}
                          </Badge>
                        </div>
                        {/* Quick Actions Overlay on Hover */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
                          <Button 
                            size="sm" 
                            variant="secondary"
                            onClick={async () => {
                              setSelectedConferenceRoom(cr);
                              setConferenceRoomForm({
                                name: cr.name || '',
                                roomNumber: cr.roomNumber || '',
                                capacity: cr.capacity?.toString() || '10',
                                floor: cr.floor?.toString() || '',
                                size: cr.size || '',
                                hourlyRate: cr.hourlyRate?.toString() || '',
                                dailyRate: cr.dailyRate?.toString() || '',
                                amenities: cr.amenities || '',
                                description: cr.description || '',
                                isActive: cr.isActive ?? true,
                                status: cr.status || 'AVAILABLE',
                              });
                              setConferenceRoomImages([]);
                              setConferenceRoomImagePreviews([]);
                              await loadConferenceRoomImages(cr.id);
                              setShowConferenceRoomDialog(true);
                            }}
                            className="backdrop-blur-sm bg-white/90 text-gray-900 hover:bg-white hover:text-gray-900 transition-all duration-200 hover:scale-105"
                            aria-label={`Edit conference room ${cr.name}`}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            <TranslatedText text="Edit" />
                          </Button>
                          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={async () => {
                                if (confirm(tSync('Are you sure you want to delete this conference room?', 'Are you sure you want to delete this conference room?'))) {
                                  try {
                                    await apiService.deleteConferenceRoom(cr.id);
                                    toast({
                                      title: tSync('Success'),
                                      description: tSync('Conference room deleted successfully'),
                                    });
                                    loadConferenceRooms();
                                  } catch (error: any) {
                                    toast({
                                      title: tSync('Error'),
                                      description: error.message || tSync('Failed to delete conference room'),
                                      variant: 'destructive',
                                    });
                                  }
                                }
                              }}
                              className="backdrop-blur-sm bg-red-600/90 text-white hover:bg-red-600 hover:text-white transition-all duration-200 hover:scale-105"
                              aria-label={`Delete conference room ${cr.name}`}
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              <TranslatedText text="Delete" />
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-xl font-bold mb-1">
                              {cr.name}
                            </CardTitle>
                            {cr.roomNumber && (
                              <CardDescription className="text-sm font-medium mb-2">
                                <TranslatedText text="Room" /> {cr.roomNumber}
                              </CardDescription>
                            )}
                            <CardDescription className="text-xs">
                              <TranslatedText text="Conference Room" />
                            </CardDescription>
                          </div>
                          <TooltipProvider>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={async () => {
                                  setSelectedConferenceRoom(cr);
                                  setConferenceRoomForm({
                                    name: cr.name || '',
                                    roomNumber: cr.roomNumber || '',
                                    capacity: cr.capacity?.toString() || '10',
                                    floor: cr.floor?.toString() || '',
                                    size: cr.size || '',
                                    hourlyRate: cr.hourlyRate?.toString() || '',
                                    dailyRate: cr.dailyRate?.toString() || '',
                                    amenities: cr.amenities || '',
                                    description: cr.description || '',
                                    isActive: cr.isActive ?? true,
                                    status: cr.status || 'AVAILABLE',
                                  });
                                  setConferenceRoomImages([]);
                                  setConferenceRoomImagePreviews([]);
                                  await loadConferenceRoomImages(cr.id);
                                  setShowConferenceRoomDialog(true);
                                }}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  <TranslatedText text="Edit" />
                                </DropdownMenuItem>
                                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                                  <DropdownMenuItem 
                                    onClick={async () => {
                                      if (confirm(tSync('Are you sure you want to delete this conference room?', 'Are you sure you want to delete this conference room?'))) {
                                        try {
                                          await apiService.deleteConferenceRoom(cr.id);
                                          toast({
                                            title: tSync('Success'),
                                            description: tSync('Conference room deleted successfully'),
                                          });
                                          loadConferenceRooms();
                                        } catch (error: any) {
                                          toast({
                                            title: tSync('Error'),
                                            description: error.message || tSync('Failed to delete conference room'),
                                            variant: 'destructive',
                                          });
                                        }
                                      }
                                    }}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    <TranslatedText text="Delete" />
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TooltipProvider>
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <div className="space-y-3">
                          {/* Key Info Grid */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                <span className="font-semibold">{cr.capacity}</span> <TranslatedText text="people" />
                              </span>
                            </div>
                            {cr.hourlyRate && (
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-semibold text-primary">
                                  {formatCurrency(cr.hourlyRate)}/<TranslatedText text="hour" />
                                </span>
                              </div>
                            )}
                            {cr.dailyRate && !cr.hourlyRate && (
                              <div className="flex items-center gap-2">
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-semibold text-primary">
                                  {formatCurrency(cr.dailyRate)}/<TranslatedText text="day" />
                                </span>
                              </div>
                            )}
                            {cr.floor && (
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm"><TranslatedText text="Floor" /> <span className="font-semibold">{cr.floor}</span></span>
                              </div>
                            )}
                            {cr.size && (
                              <div className="flex items-center gap-2">
                                <Home className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">{cr.size}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Amenities Preview */}
                          {cr.amenities && (
                            <div className="flex flex-wrap gap-1.5 pt-2 border-t">
                              {cr.amenities.split(',').slice(0, 3).map((amenity: string, index: number) => (
                                <Badge key={index} variant="outline" className="text-xs">
                                  {amenity.trim()}
                                </Badge>
                              ))}
                              {cr.amenities.split(',').length > 3 && (
                                <Badge variant="outline" className="text-xs">
                                  +{cr.amenities.split(',').length - 3} <TranslatedText text="more" />
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>

            {/* Equipment Sub-tab */}
            <TabsContent value="equipment" className="space-y-4 mt-4">
              <Card>
            <CardHeader>
              <div className="flex justify-between items-center gap-3 flex-wrap">
                <div className="min-w-0">
                  <CardTitle className="flex flex-wrap items-center gap-2">
                    <TranslatedText text="Equipment" />
                    {companyName && (
                      <span className="text-xs font-medium text-muted-foreground truncate max-w-[200px]">
                        • {companyName}
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription className="mt-1">
                    <TranslatedText text="Manage equipment and rental items" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({equipment.length} <TranslatedText text="total" />)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadEquipment();
                    }}
                    title={tSync("Refresh equipment", "Refresh equipment")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                    <Button 
                      onClick={() => {
                        setEquipmentForm({
                          name: '',
                          equipmentNumber: '',
                          category: '',
                          brand: '',
                          model: '',
                          hourlyRate: '',
                          dailyRate: '',
                          description: '',
                          isActive: true,
                          status: 'AVAILABLE',
                          quantity: '1',
                        });
                        setSelectedEquipment(null);
                        setShowEquipmentDialog(true);
                      }}
                      className="transition-all duration-200 hover:scale-105"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Add Equipment" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {equipment.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  <TranslatedText text="No equipment found. Create one to get started." />
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {equipment.map((eq) => (
                    <Card key={eq.id} className="group relative">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="text-lg">{eq.name}</CardTitle>
                            {eq.equipmentNumber && (
                              <CardDescription>#{eq.equipmentNumber}</CardDescription>
                            )}
                            {eq.category && (
                              <CardDescription className="text-xs">{eq.category}</CardDescription>
                            )}
                          </div>
                          <Badge variant={eq.isActive ? 'default' : 'secondary'}>
                            {eq.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {eq.quantity > 1 && (
                            <div className="flex items-center gap-2">
                              <Package className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">Quantity: {eq.quantity}</span>
                            </div>
                          )}
                          {eq.hourlyRate && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{formatCurrency(eq.hourlyRate)}/hour</span>
                            </div>
                          )}
                          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                            <div className="flex gap-2 mt-4">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => {
                                  setSelectedEquipment(eq);
                                  setEquipmentForm({
                                    name: eq.name || '',
                                    equipmentNumber: eq.equipmentNumber || '',
                                    category: eq.category || '',
                                    brand: eq.brand || '',
                                    model: eq.model || '',
                                    hourlyRate: eq.hourlyRate?.toString() || '',
                                    dailyRate: eq.dailyRate?.toString() || '',
                                    description: eq.description || '',
                                    isActive: eq.isActive ?? true,
                                    status: eq.status || 'AVAILABLE',
                                    quantity: eq.quantity?.toString() || '1',
                                  });
                                  setEquipmentImages([]);
                                  setEquipmentImagePreviews([]);
                                  await loadEquipmentImages(eq.id);
                                  setShowEquipmentDialog(true);
                                }}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                <TranslatedText text="Edit" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={async () => {
                                  if (confirm(tSync('Are you sure you want to delete this equipment?', 'Are you sure you want to delete this equipment?'))) {
                                    try {
                                      await apiService.deleteEquipment(eq.id);
                                      toast({
                                        title: tSync('Success'),
                                        description: tSync('Equipment deleted successfully'),
                                      });
                                      loadEquipment();
                                    } catch (error: any) {
                                      toast({
                                        title: tSync('Error'),
                                        description: error.message || tSync('Failed to delete equipment'),
                                        variant: 'destructive',
                                      });
                                    }
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                <TranslatedText text="Delete" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* Reservations Tab */}
        <TabsContent value="reservations" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Reservations" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage hotel reservations and bookings" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({reservations.length} <TranslatedText text="total" />)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-2 items-center">
                  {/* Quick Status Filter */}
                  <Select
                    value={reservationsFilters.status}
                    onValueChange={(value) => {
                      setReservationsFilters({ ...reservationsFilters, status: value });
                      setReservationsPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-[180px] border border-gray-300">
                      <SelectValue placeholder={tSync("All Statuses", "All Statuses")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                      <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                      <SelectItem value="PENDING"><TranslatedText text="Pending" /></SelectItem>
                      <SelectItem value="CONFIRMED"><TranslatedText text="Confirmed" /></SelectItem>
                      <SelectItem value="CHECKED_IN"><TranslatedText text="Checked In" /></SelectItem>
                      <SelectItem value="CHECKED_OUT"><TranslatedText text="Checked Out" /></SelectItem>
                      <SelectItem value="CANCELLED"><TranslatedText text="Cancelled" /></SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadReservations();
                      setReservationsPage(1);
                    }}
                    title={tSync("Refresh reservations", "Refresh reservations")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                <Button 
                  onClick={() => {
                    setShowReservationDialog(true);
                    // Load promotions when opening reservation dialog
                    if (companyId) {
                      loadPromotions();
                    }
                  }}
                  aria-label="Create new reservation"
                  className="transition-all duration-200 hover:scale-105"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="New Reservation" />
                </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Enhanced Filters Section */}
              <div className="mb-6 rounded-xl border bg-card shadow-sm">
                <div className="flex items-center justify-between p-4 border-b bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-primary" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                    {(() => {
                      const activeCount = [
                        reservationsFilters.status !== 'all' && reservationsFilters.status !== 'active',
                        reservationsFilters.guestSearch !== '',
                        reservationsFilters.roomNumber !== '',
                        reservationsFilters.reservationNumber !== '',
                        reservationsFilters.paymentStatus !== 'all',
                      ].filter(Boolean).length;
                      return activeCount > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {activeCount} <TranslatedText text="active" />
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const hasActiveFilters = (reservationsFilters.status !== 'all' && reservationsFilters.status !== 'active') || 
                        reservationsFilters.guestSearch || 
                        reservationsFilters.roomNumber || 
                        reservationsFilters.reservationNumber ||
                        reservationsFilters.paymentStatus !== 'all';
                      return hasActiveFilters && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReservationsFilters({
                              status: 'active',
                              guestSearch: '',
                              roomNumber: '',
                              reservationNumber: '',
                              paymentStatus: 'all',
                            });
                          }}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4 mr-1" />
                          <TranslatedText text="Clear All" />
                        </Button>
                      );
                    })()}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowReservationsFilters(!showReservationsFilters)}
                    >
                      {showReservationsFilters ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          <TranslatedText text="Hide Filters" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          <TranslatedText text="Show Filters" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                
                {/* Filter Chips (Always Visible) */}
                {(() => {
                  const activeFilters: Array<{ key: string; label: string; value: string }> = [];
                  if (reservationsFilters.status !== 'all' && reservationsFilters.status !== 'active') {
                    activeFilters.push({ key: 'status', label: 'Status', value: reservationsFilters.status });
                  }
                  if (reservationsFilters.paymentStatus !== 'all') {
                    activeFilters.push({ key: 'paymentStatus', label: 'Payment', value: reservationsFilters.paymentStatus });
                  }
                  if (reservationsFilters.guestSearch) {
                    activeFilters.push({ key: 'guestSearch', label: 'Guest', value: reservationsFilters.guestSearch });
                  }
                  if (reservationsFilters.roomNumber) {
                    activeFilters.push({ key: 'roomNumber', label: 'Room', value: reservationsFilters.roomNumber });
                  }
                  if (reservationsFilters.reservationNumber) {
                    activeFilters.push({ key: 'reservationNumber', label: 'Reservation', value: reservationsFilters.reservationNumber });
                  }
                  
                  return activeFilters.length > 0 && (
                    <div className="p-4 border-b bg-muted/20">
                      <div className="flex flex-wrap gap-2">
                        {activeFilters.map((filter) => (
                          <Badge
                            key={filter.key}
                            variant="secondary"
                            className="gap-1 pr-1"
                          >
                            <span className="capitalize">{filter.label}:</span>
                            <span>{filter.value}</span>
                            <button
                              onClick={() => {
                                if (filter.key === 'status') {
                                  setReservationsFilters({ ...reservationsFilters, status: 'active' });
                                } else if (filter.key === 'paymentStatus') {
                                  setReservationsFilters({ ...reservationsFilters, paymentStatus: 'all' });
                                } else if (filter.key === 'guestSearch') {
                                  setReservationsFilters({ ...reservationsFilters, guestSearch: '' });
                                } else if (filter.key === 'roomNumber') {
                                  setReservationsFilters({ ...reservationsFilters, roomNumber: '' });
                                } else if (filter.key === 'reservationNumber') {
                                  setReservationsFilters({ ...reservationsFilters, reservationNumber: '' });
                                }
                              }}
                              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Expandable Filter Form */}
                {showReservationsFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Reservation Number" /></Label>
                    <Input
                      placeholder={tSync("Reservation number...", "Reservation number...")}
                      value={reservationsFilters.reservationNumber}
                      onChange={(e) => setReservationsFilters({ ...reservationsFilters, reservationNumber: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Search Guest" /></Label>
                    <Input
                      placeholder={tSync("Guest name...", "Guest name...")}
                      value={reservationsFilters.guestSearch}
                      onChange={(e) => setReservationsFilters({ ...reservationsFilters, guestSearch: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Room Number" /></Label>
                    <Input
                      placeholder={tSync("Room number...", "Room number...")}
                      value={reservationsFilters.roomNumber}
                      onChange={(e) => setReservationsFilters({ ...reservationsFilters, roomNumber: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Status" /></Label>
                    <Select
                      value={reservationsFilters.status}
                      onValueChange={(value) => setReservationsFilters({ ...reservationsFilters, status: value })}
                    >
                      <SelectTrigger className="h-9 border border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                        <SelectItem value="active"><TranslatedText text="Active" /></SelectItem>
                        <SelectItem value="PENDING"><TranslatedText text="Pending" /></SelectItem>
                        <SelectItem value="CONFIRMED"><TranslatedText text="Confirmed" /></SelectItem>
                        <SelectItem value="CHECKED_IN"><TranslatedText text="Checked In" /></SelectItem>
                        <SelectItem value="CHECKED_OUT"><TranslatedText text="Checked Out" /></SelectItem>
                        <SelectItem value="CANCELLED"><TranslatedText text="Cancelled" /></SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Payment Status" /></Label>
                    <Select
                      value={reservationsFilters.paymentStatus}
                      onValueChange={(value) => setReservationsFilters({ ...reservationsFilters, paymentStatus: value })}
                    >
                      <SelectTrigger className="h-9 border border-gray-300">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all"><TranslatedText text="All" /></SelectItem>
                        <SelectItem value="PAID"><TranslatedText text="Paid" /></SelectItem>
                        <SelectItem value="PARTIAL"><TranslatedText text="Partial" /></SelectItem>
                        <SelectItem value="UNPAID"><TranslatedText text="Unpaid" /></SelectItem>
                        <SelectItem value="LOAN"><TranslatedText text="Loan" /></SelectItem>
                    </SelectContent>
                  </Select>
                  </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={reservationsPerPage.toString()}
                    onValueChange={(value) => {
                      setReservationsPerPage(Number(value));
                      setReservationsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((reservationsPage - 1) * reservationsPerPage) + 1} - {Math.min(reservationsPage * reservationsPerPage, filteredReservations.length)} <TranslatedText text="of" /> {filteredReservations.length} ({reservations.length} <TranslatedText text="total" />)
                </div>
              </div>
              {loadingReservations ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <ReservationCardSkeleton key={i} />
                  ))}
                </div>
              ) : getPaginatedReservations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 sm:py-16 px-4 border-2 border-dashed rounded-lg bg-muted/10">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse" />
                    <div className="relative p-6 bg-primary/10 rounded-full">
                      <Calendar className="h-16 w-16 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-center">
                    <TranslatedText text="No reservations found" />
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground text-center max-w-md mb-6">
                    <TranslatedText text="Start managing your hotel by creating your first reservation. Book rooms, check in guests, and track payments." />
                  </p>
                  <div className="w-full max-w-xs sm:max-w-sm">
                    <Button
                      className="w-full h-11 text-sm sm:text-base"
                      onClick={() => {
                        setShowReservationDialog(true);
                        if (companyId) {
                          loadPromotions();
                        }
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      <TranslatedText text="Create Reservation" />
                    </Button>
                  </div>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {getPaginatedReservations.map((reservation) => (
                  <Card key={reservation.id} className="group relative overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <CardTitle className="text-lg font-bold">
                              #{reservation.reservationNumber}
                            </CardTitle>
                            
                            {/* Quick Status Changer - Only if not CHECKED_OUT */}
                            {reservation.status !== 'CHECKED_OUT' ? (
                              <Select
                                value={reservation.status}
                                disabled={updatingStatusId === reservation.id}
                                onValueChange={async (newStatus) => {
                                  // Validate: Cannot set to CHECKED_IN if not paid or loan
                                  if (newStatus === 'CHECKED_IN' && reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') {
                                    toast({
                                      title: 'Payment Required',
                                      description: 'Guest must complete payment before check-in',
                                      variant: 'destructive',
                                    });
                                    return;
                                  }
                                  // Validate: Cannot set to CHECKED_OUT if not paid or loan
                                  if (newStatus === 'CHECKED_OUT' && reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') {
                                    toast({
                                      title: 'Payment Required',
                                      description: 'Guest must complete payment before check-out',
                                      variant: 'destructive',
                                    });
                                    return;
                                  }
                                  
                                  setUpdatingStatusId(reservation.id);
                                  try {
                                    await apiService.updateHotelReservation(reservation.id, {
                                      status: newStatus as any
                                    });
                                    await loadReservations();
                                  } finally {
                                    setUpdatingStatusId(null);
                                  }
                                }}
                              >
                                <SelectTrigger className="h-6 w-auto px-2 text-xs border-0 bg-transparent hover:bg-gray-100 focus:ring-0">
                                  <SelectValue>
                                    {updatingStatusId === reservation.id ? (
                                      <div className="flex items-center gap-1">
                                        <MoonLoader size="sm" color="teal" />
                                        <span className="text-xs">Updating...</span>
                                      </div>
                                    ) : (
                                      getStatusBadge(reservation.status)
                                    )}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="PENDING">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                      <TranslatedText text="Pending" />
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="CONFIRMED">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                      <TranslatedText text="Confirmed" />
                                    </div>
                                  </SelectItem>
                                  <SelectItem 
                                    value="CHECKED_IN"
                                    disabled={reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN'}
                                    className={(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                      <TranslatedText text="Checked In" />
                                      {(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') && (
                                        <span className="text-xs text-red-600">
                                          (<TranslatedText text="Payment Required" />)
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                  <SelectItem 
                                    value="CHECKED_OUT" 
                                    disabled={reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN'}
                                    className={(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                                      <TranslatedText text="Checked Out" />
                                      {(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') && (
                                        <span className="text-xs text-red-600">
                                          (<TranslatedText text="Payment Required" />)
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                  <SelectItem 
                                    value="CANCELLED"
                                    disabled={reservation.paymentStatus === 'PAID' || reservation.paymentStatus === 'LOAN'}
                                    className={(reservation.paymentStatus === 'PAID' || reservation.paymentStatus === 'LOAN') ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}
                                  >
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                      <TranslatedText text="Cancelled" />
                                      {(reservation.paymentStatus === 'PAID' || reservation.paymentStatus === 'LOAN') && (
                                        <span className="text-xs text-gray-600">
                                          (<TranslatedText text="Cannot cancel paid reservation" />)
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="NO_SHOW">
                                    <div className="flex items-center gap-2">
                                      <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                      <TranslatedText text="No Show" />
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              getStatusBadge(reservation.status)
                            )}
                            {reservation.bookingType && reservation.bookingType !== 'ROOM' && (
                              <Badge variant="outline" className="text-xs">
                                {reservation.bookingType.replace('_', ' ')}
                              </Badge>
                            )}
                            {isReservationOverdue(reservation) && (
                              <Badge className="bg-red-600 text-white hover:bg-red-700 text-xs animate-pulse">
                                <TranslatedText text="OVERDUE" />
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4" />
                              <span>{reservation.guest?.firstName} {reservation.guest?.lastName}</span>
                            </div>
                            {reservation.createdByUser && (
                              <p className="text-xs text-muted-foreground mt-1">
                                <TranslatedText text="Created by" />{' '}
                                {reservation.createdByUser.name || reservation.createdByUser.email}
                                {reservation.createdByUser.role && (
                                  <span className="text-muted-foreground/70">
                                    {' '}({formatRole(reservation.createdByUser.role)})
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent className="flex-1 flex flex-col pt-0 pb-3">
                      {isReservationOverdue(reservation) && (
                        <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-red-600" />
                            <span className="text-xs font-semibold text-red-600">
                              {reservation.status === 'CHECKED_IN' ? (
                                <TranslatedText text="Check-out date has passed" />
                              ) : (
                                <TranslatedText text="Check-in date has passed" />
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Date Range */}
                      <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-lg mb-3">
                        <div className="flex items-center gap-2 flex-1">
                          <Calendar className="h-4 w-4 text-primary" />
                          <div>
                            <div className="text-xs text-muted-foreground"><TranslatedText text="Check-in" /></div>
                            <div className="text-sm font-semibold">
                              {new Date(reservation.checkInDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                        <div className="h-8 w-px bg-border" />
                        <div className="flex items-center gap-2 flex-1">
                          <Calendar className="h-4 w-4 text-primary" />
                          <div>
                            <div className="text-xs text-muted-foreground"><TranslatedText text="Check-out" /></div>
                            <div className="text-sm font-semibold">
                              {new Date(reservation.checkOutDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Financial Info */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <div className="text-xs text-muted-foreground mb-1">
                            {reservation.bookingType === 'ROOM' ? <TranslatedText text="Room" /> : <TranslatedText text="Item" />}
                          </div>
                          <div className="flex items-center gap-1.5">
                            {reservation.bookingType === 'ROOM' ? (
                              <>
                                <Bed className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-sm font-semibold">{reservation.room?.roomNumber || 'N/A'}</span>
                              </>
                            ) : (
                              <>
                                <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                                <span className="text-sm font-semibold">
                                  {reservation.bookableItemId || reservation.room?.roomNumber || 'N/A'}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-muted-foreground mb-1"><TranslatedText text="Total Amount" /></div>
                          <div className="text-lg font-bold text-primary">
                            {(() => {
                              const roomCurrency = reservation.room?.currency || 'RWF';
                              
                              // If folio exists with items, calculate from converted items (excluding payments)
                              if (reservation.folio?.items && reservation.folio.items.length > 0) {
                                const calculatedTotal = reservation.folio.items.reduce((sum: number, item: any) => {
                                  // Skip payment items
                                  if (item.name?.toLowerCase().includes('payment')) {
                                    return sum;
                                  }
                                  
                                  let itemCurrency = roomCurrency;
                                  
                                  // Detect currency from POS items
                                  if (item.name?.includes('POS Invoice') && item.description) {
                                    const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                    if (currencyMatch) {
                                      itemCurrency = currencyMatch[1];
                                    }
                                  }
                                  
                                  const itemTotal = Number(item.total);
                                  const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                                  return sum + convertedTotal;
                                }, 0);
                                
                                return formatCurrencyWithApprox(calculatedTotal, roomCurrency);
                              }
                              
                              // Otherwise use reservation totalAmount
                              return formatCurrencyWithApprox(reservation.totalAmount, roomCurrency);
                            })()}
                          </div>
                        </div>
                      </div>
                      
                      {/* Payment Status */}
                      <div className="pt-2 border-t">
                        <div className="text-xs text-muted-foreground mb-1.5"><TranslatedText text="Payment Status" /></div>
                        <div className="flex items-center gap-2">
                          {getPaymentStatusBadge(reservation.paymentStatus || 'UNPAID')}
                          {reservation.paymentStatus === 'PARTIAL' && reservation.folio && (
                            <span className="text-xs font-medium text-orange-600">
                              {(() => {
                                const roomCurrency = reservation.room?.currency || 'RWF';
                                
                                // Calculate total from converted items (excluding payments)
                                if (reservation.folio?.items && reservation.folio.items.length > 0) {
                                  const calculatedTotal = reservation.folio.items.reduce((sum: number, item: any) => {
                                    // Skip payment items
                                    if (item.name?.toLowerCase().includes('payment')) {
                                      return sum;
                                    }
                                    
                                    let itemCurrency = roomCurrency;
                                    
                                    // Detect currency from POS items
                                    if (item.name?.includes('POS Invoice') && item.description) {
                                      const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                      if (currencyMatch) {
                                        itemCurrency = currencyMatch[1];
                                      }
                                    }
                                    
                                    const itemTotal = Number(item.total);
                                    const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                                    return sum + convertedTotal;
                                  }, 0);
                                  
                                  // Calculate payments
                                  const totalPaid = reservation.payments?.reduce((sum: number, payment: any) => {
                                    if (payment.status === 'COMPLETED') {
                                      return sum + Number(payment.amount || 0);
                                    }
                                    return sum;
                                  }, 0) || 0;
                                  
                                  const balance = calculatedTotal - totalPaid;
                                  return formatCurrencyWithApprox(balance, roomCurrency);
                                }
                                
                                // Fallback to folio balance
                                return formatCurrency(reservation.folio.balance, roomCurrency);
                              })()} <TranslatedText text="remaining" />
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t">
                        {/* Check In Button - Only for CONFIRMED and PAID/LOAN */}
                        {reservation.status === 'CONFIRMED' && (
                          <Button 
                            size="sm" 
                            variant="default" 
                            className={`flex-1 min-w-[100px] ${
                              (reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN')
                                ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed opacity-60'
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                            onClick={() => handleCheckIn(reservation.id)}
                            disabled={isCheckingIn || (reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN')}
                            title={(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') ? 'Payment required before check-in' : ''}
                          >
                            {isCheckingIn ? (
                              <>
                                <MoonLoader size="sm" className="mr-1" color="teal" />
                                <TranslatedText text="Checking In..." />
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                <TranslatedText text="Check In" />
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Check Out Button - Only for CHECKED_IN and PAID/LOAN */}
                        {reservation.status === 'CHECKED_IN' && (
                          <Button 
                            size="sm" 
                            variant="default" 
                            className={`flex-1 min-w-[100px] ${
                              (reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN')
                                ? 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed opacity-60'
                                : 'bg-orange-600 hover:bg-orange-700'
                            }`}
                            onClick={() => handleCheckOut(reservation.id)}
                            disabled={isCheckingOut || (reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN')}
                            title={(reservation.paymentStatus !== 'PAID' && reservation.paymentStatus !== 'LOAN') ? 'Payment required before check-out' : ''}
                          >
                            {isCheckingOut ? (
                              <>
                                <MoonLoader size="sm" className="mr-1" color="teal" />
                                <TranslatedText text="Checking Out..." />
                              </>
                            ) : (
                              <>
                                <XCircle className="h-4 w-4 mr-1" />
                                <TranslatedText text="Check Out" />
                              </>
                            )}
                          </Button>
                        )}
                        
                        {/* Mark as Loan Button - For UNPAID or PARTIAL payments */}
                        {(reservation.paymentStatus === 'UNPAID' || reservation.paymentStatus === 'PARTIAL') && (
                          <Button 
                            size="sm" 
                            variant="default" 
                            className="flex-1 min-w-[100px] bg-orange-500 hover:bg-orange-600"
                            onClick={async () => {
                              try {
                                // Calculate balance
                                const roomCurrency = reservation.room?.currency || 'RWF';
                                let totalAmount = reservation.totalAmount;
                                
                                // Calculate from folio if available
                                if (reservation.folio?.items && reservation.folio.items.length > 0) {
                                  totalAmount = reservation.folio.items.reduce((sum: number, item: any) => {
                                    if (item.name?.toLowerCase().includes('payment')) {
                                      return sum;
                                    }
                                    let itemCurrency = roomCurrency;
                                    if (item.name?.includes('POS Invoice') && item.description) {
                                      const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                      if (currencyMatch) {
                                        itemCurrency = currencyMatch[1];
                                      }
                                    }
                                    const itemTotal = Number(item.total);
                                    const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                                    return sum + convertedTotal;
                                  }, 0);
                                }
                                
                                // Calculate remaining balance
                                const totalPaid = reservation.payments?.reduce((sum: number, payment: any) => {
                                  if (payment.status === 'COMPLETED') {
                                    return sum + Number(payment.amount || 0);
                                  }
                                  return sum;
                                }, 0) || 0;
                                
                                const balance = totalAmount - totalPaid;
                                
                                // Create loan payment directly
                                const result = await apiService.createHotelPayment({
                                  companyId,
                                  reservationId: reservation.id,
                                  guestId: reservation.guestId,
                                  paymentType: 'full_payment',
                                  amount: balance,
                                  paymentMethod: 'loan',
                                  transactionId: `LOAN-${Date.now()}`,
                                  status: 'loan',
                                  notes: `Loan payment for reservation ${reservation.reservationNumber} - Guest: ${reservation.guest?.firstName} ${reservation.guest?.lastName}`,
                                });
                                
                                toast({
                                  title: 'Loan Payment Recorded',
                                  description: `Loan payment of ${formatCurrency(balance, roomCurrency)} recorded for ${reservation.guest?.firstName} ${reservation.guest?.lastName}`,
                                });
                                
                                // Refresh data
                                await Promise.all([
                                  loadReservations(),
                                  loadFolios(),
                                  loadPayments(),
                                ]);
                              } catch (error: any) {
                                toast({
                                  title: 'Error',
                                  description: error.message || 'Failed to record loan payment',
                                  variant: 'destructive',
                                });
                              }
                            }}
                          >
                            <CreditCard className="h-4 w-4 mr-1" />
                            <TranslatedText text="Loan" />
                          </Button>
                        )}
                        
                        {/* Mark Loan as Paid Button - For LOAN payments */}
                        {reservation.paymentStatus === 'LOAN' && (
                          <Button 
                            size="sm" 
                            variant="default" 
                            className="flex-1 min-w-[100px] bg-green-600 hover:bg-green-700"
                            onClick={async () => {
                              try {
                                // Find the loan payment for this reservation
                                const loanPayment = reservation.payments?.find((p: any) => p.status === 'loan');
                                
                                if (!loanPayment) {
                                  toast({
                                    title: 'Error',
                                    description: 'No loan payment found for this reservation',
                                    variant: 'destructive',
                                  });
                                  return;
                                }
                                
                                // Mark the loan payment as paid
                                await apiService.markHotelPaymentAsPaid(loanPayment.id);
                                
                                toast({
                                  title: 'Loan Marked as Paid',
                                  description: `Loan payment of ${formatCurrency(loanPayment.amount, reservation.room?.currency || 'RWF')} has been marked as paid`,
                                });
                                
                                // Refresh data
                                await Promise.all([
                                  loadReservations(),
                                  loadFolios(),
                                  loadPayments(),
                                ]);
                              } catch (error: any) {
                                toast({
                                  title: 'Error',
                                  description: error.message || 'Failed to mark loan as paid',
                                  variant: 'destructive',
                                });
                              }
                            }}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            <TranslatedText text="Pay Loan" />
                          </Button>
                        )}
                        
                        {/* Edit Button - Not for CHECKED_OUT */}
                        {reservation.status !== 'CHECKED_OUT' && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 min-w-[100px]"
                            onClick={() => handleEditReservation(reservation)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            <TranslatedText text="Edit" />
                          </Button>
                        )}
                        
                        {/* View Details Button - Always available */}
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="flex-1 min-w-[100px]"
                          onClick={() => handleViewReservation(reservation)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          <TranslatedText text="Details" />
                        </Button>
                        
                        {/* Cancel/Delete Button removed - use status dropdown instead */}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              )}
              {totalReservationsPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setReservationsPage(prev => Math.max(1, prev - 1));
                          }}
                          className={reservationsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalReservationsPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalReservationsPages ||
                          (page >= reservationsPage - 1 && page <= reservationsPage + 1)
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setReservationsPage(page);
                                }}
                                isActive={reservationsPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (page === reservationsPage - 2 || page === reservationsPage + 2) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}
                      <PaginationItem>
                        <PaginationNext 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setReservationsPage(prev => Math.min(totalReservationsPages, prev + 1));
                          }}
                          className={reservationsPage === totalReservationsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Guests Tab */}
        <TabsContent value="guests" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Guests" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage guest information and profiles" />
                    <span className="ml-2 text-xs text-muted-foreground">
                      ({guests.length} <TranslatedText text="total" />)
                    </span>
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={async () => {
                      console.log('Manual refresh triggered for companyId:', companyId);
                      await loadGuests();
                      setGuestsPage(1);
                    }}
                    title={tSync("Refresh guests", "Refresh guests")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                <Button 
                  onClick={() => setShowGuestDialog(true)}
                  aria-label="Add new guest"
                  className="transition-all duration-200 hover:scale-105"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add Guest" />
                </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="mb-4 border rounded-lg bg-muted/50">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(guestsFilters.search || guestsFilters.city || guestsFilters.state) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setGuestsFilters({
                            search: '',
                            city: '',
                            state: '',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowGuestsFilters(!showGuestsFilters)}
                    >
                      {showGuestsFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showGuestsFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="Search" /></Label>
                    <Input
                      placeholder={tSync("Name, email, or phone...", "Name, email, or phone...")}
                      value={guestsFilters.search}
                      onChange={(e) => setGuestsFilters({ ...guestsFilters, search: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="City" /></Label>
                    <Input
                      placeholder={tSync("City...", "City...")}
                      value={guestsFilters.city}
                      onChange={(e) => setGuestsFilters({ ...guestsFilters, city: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs"><TranslatedText text="State" /></Label>
                    <Input
                      placeholder={tSync("State...", "State...")}
                      value={guestsFilters.state}
                      onChange={(e) => setGuestsFilters({ ...guestsFilters, state: e.target.value })}
                      className="h-9 border border-gray-300"
                    />
                  </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={guestsPerPage.toString()}
                    onValueChange={(value) => {
                      setGuestsPerPage(Number(value));
                      setGuestsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="9">9</SelectItem>
                      <SelectItem value="12">12</SelectItem>
                      <SelectItem value="18">18</SelectItem>
                      <SelectItem value="24">24</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((guestsPage - 1) * guestsPerPage) + 1} - {Math.min(guestsPage * guestsPerPage, filteredGuests.length)} <TranslatedText text="of" /> {filteredGuests.length} ({guests.length} <TranslatedText text="total" />)
                </div>
              </div>
              {loadingGuests ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <GuestCardSkeleton key={i} />
                  ))}
                </div>
              ) : getPaginatedGuests.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed rounded-lg">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-primary/5 rounded-full animate-pulse" />
                    <div className="relative p-6 bg-primary/10 rounded-full">
                      <Users className="h-16 w-16 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">
                    <TranslatedText text="No guests found" />
                  </h3>
                  <p className="text-muted-foreground text-center max-w-md mb-6">
                    <TranslatedText text="Start building your guest database by adding your first guest. Track guest information, preferences, and booking history." />
                  </p>
                  <Button onClick={() => setShowGuestDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Add Your First Guest" />
                  </Button>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {getPaginatedGuests.map((guest) => (
                  <Card key={guest.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle className="text-lg">
                            {guest.firstName} {guest.lastName}
                          </CardTitle>
                          <CardDescription>{guest.email || guest.phone}</CardDescription>
                        </div>
                        <TooltipProvider>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleEditGuest(guest)}>
                                <Edit className="mr-2 h-4 w-4" />
                                <TranslatedText text="Edit" />
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteGuest(guest.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                <TranslatedText text="Delete" />
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TooltipProvider>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {guest.phone && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground"><TranslatedText text="Phone" />:</span>
                            <span className="text-sm font-medium">{guest.phone}</span>
                          </div>
                        )}
                        {guest.country && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground"><TranslatedText text="Country" />:</span>
                            <span className="text-sm font-medium">{guest.country}</span>
                          </div>
                        )}
                        {guest.city && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground"><TranslatedText text="Location" />:</span>
                            <span className="text-sm font-medium">
                              {guest.city}, {guest.state}
                            </span>
                          </div>
                        )}
                        {guest.passportNumber && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground"><TranslatedText text="Passport" />:</span>
                            <span className="text-sm font-medium">{guest.passportNumber}</span>
                          </div>
                        )}
                        {guest.idNumber && (
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground"><TranslatedText text="National ID" />:</span>
                            <span className="text-sm font-medium">{guest.idNumber}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              )}
              {totalGuestsPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setGuestsPage(prev => Math.max(1, prev - 1));
                          }}
                          className={guestsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                      {Array.from({ length: totalGuestsPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalGuestsPages ||
                          (page >= guestsPage - 1 && page <= guestsPage + 1)
                        ) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationLink
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setGuestsPage(page);
                                }}
                                isActive={guestsPage === page}
                                className="cursor-pointer"
                              >
                                {page}
                              </PaginationLink>
                            </PaginationItem>
                          );
                        } else if (page === guestsPage - 2 || page === guestsPage + 2) {
                          return (
                            <PaginationItem key={page}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          );
                        }
                        return null;
                      })}
                      <PaginationItem>
                        <PaginationNext 
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setGuestsPage(prev => Math.min(totalGuestsPages, prev + 1));
                          }}
                          className={guestsPage === totalGuestsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Availability Tab */}
        <TabsContent value="availability" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle><TranslatedText text="Check Room Availability" /></CardTitle>
              <CardDescription><TranslatedText text="Search for available rooms by date range" /></CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <Label><TranslatedText text="Check-In Date" /></Label>
                  <Input
                    type="date"
                    value={availabilityCheck.checkInDate}
                    onChange={(e) =>
                      setAvailabilityCheck({ ...availabilityCheck, checkInDate: e.target.value })
                    }
                    className="border border-gray-300"
                  />
                </div>
                <div>
                  <Label><TranslatedText text="Check-Out Date" /></Label>
                  <Input
                    type="date"
                    value={availabilityCheck.checkOutDate}
                    onChange={(e) =>
                      setAvailabilityCheck({ ...availabilityCheck, checkOutDate: e.target.value })
                    }
                    className="border border-gray-300"
                  />
                </div>
                <div>
                  <Label><TranslatedText text="Room Type" /></Label>
                  <Select
                    value={availabilityCheck.roomTypeId || 'all'}
                    onValueChange={(value) =>
                      setAvailabilityCheck({ ...availabilityCheck, roomTypeId: value === 'all' ? '' : value })
                    }
                  >
                <SelectTrigger className="border border-gray-300 w-full">
                  <SelectValue placeholder={tSync("All Types", "All Types")} />
                </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                      {roomTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={handleCheckAvailability} className="w-full" disabled={isCheckingAvailability}>
                    {isCheckingAvailability ? (
                      <>
                        <MoonLoader size="sm" className="mr-2" color="teal" />
                        <TranslatedText text="Checking..." />
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        <TranslatedText text="Check Availability" />
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {availableRooms.length > 0 && (
                <div className="space-y-4 mt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">
                      <TranslatedText text="Available Rooms" /> ({availableRooms.length} <TranslatedText text="total" />)
                    </h3>
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={() => {
                        handleCheckAvailability();
                        setAvailableRoomsPage(1);
                      }}
                      title={tSync("Refresh availability", "Refresh availability")}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Filters Section */}
                  <div className="border rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between p-4 border-b">
                      <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                      </div>
                      <div className="flex items-center gap-2">
                        {(availableRoomsFilters.search || 
                          availableRoomsFilters.roomTypeId !== 'all' || 
                          availableRoomsFilters.floor || 
                          availableRoomsFilters.minPrice || 
                          availableRoomsFilters.maxPrice) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAvailableRoomsFilters({
                                search: '',
                                roomTypeId: 'all',
                                floor: '',
                                minPrice: '',
                                maxPrice: '',
                              });
                            }}
                          >
                            <X className="w-4 h-4 mr-1" />
                            <TranslatedText text="Clear All" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAvailableRoomsFilters(!showAvailableRoomsFilters)}
                        >
                          {showAvailableRoomsFilters ? (
                            <>
                              <ChevronUp className="w-4 h-4 mr-1" />
                              <TranslatedText text="Hide" />
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4 mr-1" />
                              <TranslatedText text="Show" />
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {showAvailableRoomsFilters && (
                      <div className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs"><TranslatedText text="Search Room" /></Label>
                            <Input
                              placeholder={tSync("Room number...", "Room number...")}
                              value={availableRoomsFilters.search}
                              onChange={(e) => setAvailableRoomsFilters({ ...availableRoomsFilters, search: e.target.value })}
                              className="h-9 border border-gray-300"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs"><TranslatedText text="Room Type" /></Label>
                            <Select
                              value={availableRoomsFilters.roomTypeId}
                              onValueChange={(value) => setAvailableRoomsFilters({ ...availableRoomsFilters, roomTypeId: value })}
                            >
                              <SelectTrigger className="h-9 border border-gray-300">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                                {roomTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs"><TranslatedText text="Floor" /></Label>
                            <Input
                              placeholder={tSync("Floor number...", "Floor number...")}
                              value={availableRoomsFilters.floor}
                              onChange={(e) => setAvailableRoomsFilters({ ...availableRoomsFilters, floor: e.target.value })}
                              className="h-9 border border-gray-300"
                              type="number"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs"><TranslatedText text="Min Price" /></Label>
                            <Input
                              placeholder={tSync("Min price...", "Min price...")}
                              value={availableRoomsFilters.minPrice}
                              onChange={(e) => setAvailableRoomsFilters({ ...availableRoomsFilters, minPrice: e.target.value })}
                              className="h-9 border border-gray-300"
                              type="number"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs"><TranslatedText text="Max Price" /></Label>
                            <Input
                              placeholder={tSync("Max price...", "Max price...")}
                              value={availableRoomsFilters.maxPrice}
                              onChange={(e) => setAvailableRoomsFilters({ ...availableRoomsFilters, maxPrice: e.target.value })}
                              className="h-9 border border-gray-300"
                              type="number"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mb-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                      <Select
                        value={availableRoomsPerPage.toString()}
                        onValueChange={(value) => {
                          setAvailableRoomsPerPage(Number(value));
                          setAvailableRoomsPage(1);
                        }}
                      >
                        <SelectTrigger className="w-20 border border-gray-300">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6">6</SelectItem>
                          <SelectItem value="9">9</SelectItem>
                          <SelectItem value="12">12</SelectItem>
                          <SelectItem value="18">18</SelectItem>
                          <SelectItem value="24">24</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <TranslatedText text="Showing" /> {((availableRoomsPage - 1) * availableRoomsPerPage) + 1} - {Math.min(availableRoomsPage * availableRoomsPerPage, filteredAvailableRooms.length)} <TranslatedText text="of" /> {filteredAvailableRooms.length} ({availableRooms.length} <TranslatedText text="total" />)
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {getPaginatedAvailableRooms().map((room) => (
                      <Card key={room.id} className="relative">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="text-lg"><TranslatedText text="Room" /> {room.roomNumber}</CardTitle>
                              <CardDescription>
                                {room.roomTypeRef?.name || room.roomType || tSync('Standard Room', 'Standard Room')}
                              </CardDescription>
                              {companyName && (
                                <div className="mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    <Hotel className="w-3 h-3 mr-1" />
                                    {companyName}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={room.isActive ? 'default' : 'secondary'}>
                                {room.isActive ? <TranslatedText text="Active" /> : <TranslatedText text="Inactive" />}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Status" />:</span>
                              <Badge 
                                variant="default"
                                className="bg-green-200 text-green-800"
                              >
                                <TranslatedText text="Available" />
                              </Badge>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Capacity" />:</span>
                              <span className="text-sm font-medium">{room.capacity} <TranslatedText text="guests" /></span>
                            </div>
                            {room.size && (
                              <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground"><TranslatedText text="Size" />:</span>
                                <span className="text-sm font-medium">{room.size}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Price/Night" />:</span>
                              <span className="text-sm font-medium">
                                {formatCurrency(room.basePricePerNight, room.currency || 'RWF')}
                              </span>
                            </div>
                            {room.floor && (
                              <div className="flex justify-between">
                                <span className="text-sm text-muted-foreground"><TranslatedText text="Floor" />:</span>
                                <span className="text-sm font-medium">{room.floor}</span>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {totalAvailableRoomsPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setAvailableRoomsPage(prev => Math.max(1, prev - 1));
                              }}
                              className={availableRoomsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          {Array.from({ length: totalAvailableRoomsPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 ||
                              page === totalAvailableRoomsPages ||
                              (page >= availableRoomsPage - 1 && page <= availableRoomsPage + 1)
                            ) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationLink
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setAvailableRoomsPage(page);
                                    }}
                                    isActive={availableRoomsPage === page}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            } else if (page === availableRoomsPage - 2 || page === availableRoomsPage + 2) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              );
                            }
                            return null;
                          })}
                          <PaginationItem>
                            <PaginationNext 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setAvailableRoomsPage(prev => Math.min(totalAvailableRoomsPages, prev + 1));
                              }}
                              className={availableRoomsPage === totalAvailableRoomsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Bills Tab */}
        <TabsContent value="folio" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Bills" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage billing and charges for reservations" /> ({folios.length} <TranslatedText text="total" />)
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => {
                    loadFolios();
                    setFoliosPage(1);
                  }}
                  title={tSync("Refresh bills", "Refresh bills")}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="border rounded-lg bg-muted/50 mb-4">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(foliosFilters.reservationNumber || 
                      foliosFilters.guestSearch || 
                      foliosFilters.status !== 'all' || 
                      foliosFilters.minTotalAmount || 
                      foliosFilters.maxTotalAmount ||
                      foliosFilters.minBalance ||
                      foliosFilters.maxBalance) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFoliosFilters({
                            reservationNumber: '',
                            guestSearch: '',
                            status: 'all',
                            minTotalAmount: '',
                            maxTotalAmount: '',
                            minBalance: '',
                            maxBalance: '',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowFoliosFilters(!showFoliosFilters)}
                    >
                      {showFoliosFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showFoliosFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Reservation Number" /></Label>
                        <Input
                          placeholder={tSync("Reservation number...", "Reservation number...")}
                          value={foliosFilters.reservationNumber}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, reservationNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Search Guest" /></Label>
                        <Input
                          placeholder={tSync("Guest name...", "Guest name...")}
                          value={foliosFilters.guestSearch}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, guestSearch: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Status" /></Label>
                        <Select
                          value={foliosFilters.status}
                          onValueChange={(value) => setFoliosFilters({ ...foliosFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                            <SelectItem value="SETTLED"><TranslatedText text="Settled" /></SelectItem>
                            <SelectItem value="PENDING"><TranslatedText text="Pending" /></SelectItem>
                            <SelectItem value="OPEN"><TranslatedText text="Open" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Min Total Amount" /></Label>
                        <Input
                          placeholder={tSync("Min amount...", "Min amount...")}
                          value={foliosFilters.minTotalAmount}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, minTotalAmount: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Max Total Amount" /></Label>
                        <Input
                          placeholder={tSync("Max amount...", "Max amount...")}
                          value={foliosFilters.maxTotalAmount}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, maxTotalAmount: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Min Balance" /></Label>
                        <Input
                          placeholder={tSync("Min balance...", "Min balance...")}
                          value={foliosFilters.minBalance}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, minBalance: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Max Balance" /></Label>
                        <Input
                          placeholder={tSync("Max balance...", "Max balance...")}
                          value={foliosFilters.maxBalance}
                          onChange={(e) => setFoliosFilters({ ...foliosFilters, maxBalance: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={foliosPerPage.toString()}
                    onValueChange={(value) => {
                      setFoliosPerPage(Number(value));
                      setFoliosPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((foliosPage - 1) * foliosPerPage) + 1} - {Math.min(foliosPage * foliosPerPage, filteredFolios.length)} <TranslatedText text="of" /> {filteredFolios.length} ({folios.length} <TranslatedText text="total" />)
                </div>
              </div>

              <div className="space-y-4">
                {filteredFolios.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {folios.length === 0 ? (
                      <TranslatedText text="No folios found. Folios are created automatically when reservations are made." />
                    ) : (
                      <TranslatedText text="No folios match the current filters." />
                    )}
                  </p>
                ) : (
                  <>
                    {getPaginatedFolios().map((folio) => (
                      <Card key={folio.id}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg"><TranslatedText text="Bill" /> #{folio.id.slice(-8)}</CardTitle>
                              <CardDescription>
                                <TranslatedText text="Reservation" />: {folio.reservation?.reservationNumber}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                            <Badge variant={folio.status === 'SETTLED' ? 'default' : 'secondary'}>
                              {folio.status}
                            </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadFolioPDF(folio)}
                                className="h-8"
                                title={tSync("Download folio PDF", "Download folio PDF")}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Total Amount" />:</span>
                              <span className="text-sm font-medium">
                                {formatCurrency(folio.totalAmount, folio.reservation?.room?.currency || 'FRW')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-muted-foreground"><TranslatedText text="Balance" />:</span>
                              <span className="text-sm font-medium">
                                {formatCurrency(folio.balance, folio.reservation?.room?.currency || 'FRW')}
                              </span>
                            </div>
                            {folio.items && folio.items.length > 0 && (
                              <div className="mt-4 pt-4 border-t">
                                <Label className="text-xs text-muted-foreground mb-2 block"><TranslatedText text="Items" />:</Label>
                                <div className="space-y-1">
                                  {folio.items.map((item: any) => {
                                    let itemCurrency = folio.reservation?.room?.currency || 'FRW';
                                    
                                    // Detect currency from POS items
                                    if (item.name?.includes('POS Invoice') && item.description) {
                                      const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                      if (currencyMatch) {
                                        itemCurrency = currencyMatch[1];
                                      }
                                    }
                                    
                                    return (
                                      <div key={item.id} className="flex justify-between text-sm">
                                        <span>{item.name} x{item.quantity}</span>
                                        <span>{formatCurrency(item.total, itemCurrency)}</span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {totalFoliosPages > 1 && (
                      <div className="mt-4 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFoliosPage(prev => Math.max(1, prev - 1));
                                }}
                                className={foliosPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalFoliosPages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalFoliosPages ||
                                (page >= foliosPage - 1 && page <= foliosPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setFoliosPage(page);
                                      }}
                                      isActive={foliosPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (page === foliosPage - 2 || page === foliosPage + 2) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setFoliosPage(prev => Math.min(totalFoliosPages, prev + 1));
                                }}
                                className={foliosPage === totalFoliosPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Services" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage hotel services and charges" /> ({services.length} <TranslatedText text="total" />)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadServices();
                      setServicesPage(1);
                    }}
                    title={tSync("Refresh services", "Refresh services")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setShowServiceDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Add Service" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="border rounded-lg bg-muted/50 mb-4">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(servicesFilters.reservationNumber || 
                      servicesFilters.serviceName || 
                      servicesFilters.serviceType !== 'all' || 
                      servicesFilters.status !== 'all' ||
                      servicesFilters.minPrice ||
                      servicesFilters.maxPrice) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setServicesFilters({
                            reservationNumber: '',
                            serviceName: '',
                            serviceType: 'all',
                            status: 'all',
                            minPrice: '',
                            maxPrice: '',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowServicesFilters(!showServicesFilters)}
                    >
                      {showServicesFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showServicesFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Reservation Number" /></Label>
                        <Input
                          placeholder={tSync("Reservation number...", "Reservation number...")}
                          value={servicesFilters.reservationNumber}
                          onChange={(e) => setServicesFilters({ ...servicesFilters, reservationNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Service Name" /></Label>
                        <Input
                          placeholder={tSync("Service name...", "Service name...")}
                          value={servicesFilters.serviceName}
                          onChange={(e) => setServicesFilters({ ...servicesFilters, serviceName: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Service Type" /></Label>
                        <Select
                          value={servicesFilters.serviceType}
                          onValueChange={(value) => setServicesFilters({ ...servicesFilters, serviceType: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Types" /></SelectItem>
                            {Array.from(new Set(services.map((s: any) => s.serviceType).filter(Boolean))).map((type) => (
                              <SelectItem key={type as string} value={type as string}>
                                {type as string}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Status" /></Label>
                        <Select
                          value={servicesFilters.status}
                          onValueChange={(value) => setServicesFilters({ ...servicesFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                            {Array.from(new Set(services.map((s: any) => s.status).filter(Boolean))).map((status) => (
                              <SelectItem key={status as string} value={status as string}>
                                {status as string}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Min Price" /></Label>
                        <Input
                          placeholder={tSync("Min price...", "Min price...")}
                          value={servicesFilters.minPrice}
                          onChange={(e) => setServicesFilters({ ...servicesFilters, minPrice: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Max Price" /></Label>
                        <Input
                          placeholder={tSync("Max price...", "Max price...")}
                          value={servicesFilters.maxPrice}
                          onChange={(e) => setServicesFilters({ ...servicesFilters, maxPrice: e.target.value })}
                          className="h-9 border border-gray-300"
                          type="number"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={servicesPerPage.toString()}
                    onValueChange={(value) => {
                      setServicesPerPage(Number(value));
                      setServicesPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((servicesPage - 1) * servicesPerPage) + 1} - {Math.min(servicesPage * servicesPerPage, filteredServices.length)} <TranslatedText text="of" /> {filteredServices.length} ({services.length} <TranslatedText text="total" />)
                </div>
              </div>

              <div className="space-y-4">
                {filteredServices.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {services.length === 0 ? (
                      <TranslatedText text="No services found." />
                    ) : (
                      <TranslatedText text="No services match the current filters." />
                    )}
                  </p>
                ) : (
                  <>
                    {getPaginatedServices().map((service) => {
                      const reservation = reservations.find(r => r.id === service.reservationId);
                      return (
                        <Card key={service.id}>
                          <CardHeader>
                            <div className="flex justify-between items-start">
                              <div>
                                <CardTitle className="text-lg">{service.serviceName}</CardTitle>
                                <CardDescription>{service.serviceType}</CardDescription>
                              </div>
                              <Badge>{service.status}</Badge>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                              <div>
                                <Label className="text-xs text-muted-foreground"><TranslatedText text="Reservation Number" /></Label>
                                <p className="text-sm font-medium">{reservation?.reservationNumber || service.reservationId?.slice(-8)}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground"><TranslatedText text="Quantity" /></Label>
                                <p className="text-sm font-medium">{service.quantity}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground"><TranslatedText text="Unit Price" /></Label>
                                <p className="text-sm font-medium">{formatCurrency(service.unitPrice)}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground"><TranslatedText text="Total" /></Label>
                                <p className="text-sm font-medium">{formatCurrency(service.totalPrice)}</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}

                    {totalServicesPages > 1 && (
                      <div className="mt-4 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setServicesPage(prev => Math.max(1, prev - 1));
                                }}
                                className={servicesPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalServicesPages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalServicesPages ||
                                (page >= servicesPage - 1 && page <= servicesPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setServicesPage(page);
                                      }}
                                      isActive={servicesPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (page === servicesPage - 2 || page === servicesPage + 2) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setServicesPage(prev => Math.min(totalServicesPages, prev + 1));
                                }}
                                className={servicesPage === totalServicesPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Payments" /></CardTitle>
                  <CardDescription><TranslatedText text="Track hotel payments and transactions" /></CardDescription>
                </div>
                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner' || (user?.role as any) === 'receptionist' || (user?.role as any) === 'waiter') && (
                  <Button onClick={() => setShowPaymentDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Record Payment" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {payments.map((payment) => {
                  const reservation = payment.reservationId ? reservations.find(r => r.id === payment.reservationId) : null;
                  const guest = payment.guestId ? guests.find(g => g.id === payment.guestId) : null;
                  const paymentCurrency = reservation?.room?.currency || 'FRW';
                  
                  return (
                    <Card key={payment.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">
                              {payment.paymentType.replace('_', ' ').toUpperCase()}
                            </CardTitle>
                            <CardDescription>
                              {reservation ? (
                                <><TranslatedText text="Reservation" />: {reservation.reservationNumber}</>
                              ) : guest ? (
                                <><TranslatedText text="Guest" />: {guest.firstName} {guest.lastName}</>
                              ) : (
                                <TranslatedText text="Standalone Payment" />
                              )}
                            </CardDescription>
                          </div>
                          <Badge variant={
                            payment.status === 'COMPLETED' ? 'default' : 
                            payment.status === 'DRAFT' ? 'secondary' : 
                            'outline'
                          }>
                            {payment.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Amount" /></Label>
                            <p className="text-sm font-medium">{formatCurrency(payment.amount, paymentCurrency)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Method" /></Label>
                            <p className="text-sm font-medium">{payment.paymentMethod}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Date" /></Label>
                            <p className="text-sm font-medium">
                              {payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : <TranslatedText text="Pending" />}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Transaction ID" /></Label>
                            <p className="text-sm font-medium">{payment.transactionId || <TranslatedText text="N/A" />}</p>
                          </div>
                        </div>
                        {payment.status === 'DRAFT' && (
                          <div className="flex gap-2 pt-4 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedPaymentForEdit(payment);
                                setPaymentForm({
                                  reservationId: payment.reservationId || '',
                                  groupBookingId: '',
                                  guestId: payment.guestId || '',
                                  paymentType: payment.paymentType,
                                  amount: payment.amount.toString(),
                                  paymentMethod: payment.paymentMethod,
                                  transactionId: payment.transactionId || '',
                                  notes: payment.notes || '',
                                });
                                setPaymentMode(payment.reservationId ? 'reservation' : 'group');
                                setShowEditPaymentDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              <TranslatedText text="Edit" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setConfirmDialogConfig({
                                  title: 'Delete Payment',
                                  description: 'Are you sure you want to delete this payment? This action cannot be undone.',
                                  onConfirm: async () => {
                                    setShowConfirmDialog(false);
                                setIsDeletingPayment(true);
                                try {
                                  await apiService.deleteHotelPayment(payment.id);
                                  toast({ title: 'Success', description: 'Payment deleted successfully' });
                                  loadPayments();
                                } catch (error: any) {
                                  toast({ title: 'Error', description: error.message || 'Failed to delete payment', variant: 'destructive' });
                                } finally {
                                  setIsDeletingPayment(false);
                                }
                                  },
                                  confirmText: 'Delete',
                                  cancelText: 'Cancel',
                                  variant: 'destructive',
                                });
                                setShowConfirmDialog(true);
                              }}
                              disabled={isDeletingPayment}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              <TranslatedText text="Delete" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  // Get company data with proper colors
                                  const companyData = {
                                    name: companyName || localStorage.getItem('company_name') || 'Hotel',
                                    logoUrl: localStorage.getItem('company_logoUrl') || undefined,
                                    primaryColor: localStorage.getItem('company_primaryColor') || '#009688', // Teal default
                                    secondaryColor: localStorage.getItem('company_secondaryColor') || '#1565c0', // Blue default
                                    address: localStorage.getItem('company_address') || '',
                                    city: localStorage.getItem('company_city') || '',
                                    state: localStorage.getItem('company_state') || '',
                                    postalCode: localStorage.getItem('company_postalCode') || '',
                                    email: localStorage.getItem('company_email') || '',
                                    phone: localStorage.getItem('company_phone') || '',
                                    website: localStorage.getItem('company_website') || '',
                                    invoiceTerms: 'Payment is due upon receipt.',
                                    invoiceFooter: 'Thank you for your business!'
                                  };

                                  // Get folio data if reservation exists
                                  let folioData = undefined;
                                  if (reservation?.folio) {
                                    // Fetch folio with items
                                    try {
                                      const reservationWithFolio = await apiService.getHotelReservation(reservation.id);
                                      if (reservationWithFolio?.folio) {
                                        folioData = {
                                          totalAmount: Number(reservationWithFolio.folio.totalAmount),
                                          balance: Number(reservationWithFolio.folio.balance),
                                          items: reservationWithFolio.folio.items?.map((item: any) => ({
                                            name: item.name,
                                            description: item.description,
                                            price: Number(item.price),
                                            quantity: item.quantity,
                                            total: Number(item.total),
                                            category: item.category
                                          })) || []
                                        };
                                      }
                                    } catch (error) {
                                      console.error('Error fetching folio:', error);
                                      // Use folio from reservation if available
                                      if (reservation.folio) {
                                        folioData = {
                                          totalAmount: Number(reservation.folio.totalAmount),
                                          balance: Number(reservation.folio.balance),
                                          items: []
                                        };
                                      }
                                    }
                                  }

                                  // Generate PDF using frontend generator
                                  const generator = new PaymentInvoicePDFGenerator({
                                    payment: {
                                      id: payment.id,
                                      transactionId: payment.transactionId,
                                      amount: Number(payment.amount),
                                      paymentMethod: payment.paymentMethod,
                                      paymentType: payment.paymentType,
                                      status: payment.status,
                                      paidAt: payment.paidAt,
                                      createdAt: payment.createdAt || new Date().toISOString(),
                                      notes: payment.notes,
                                      reservation: reservation ? {
                                        reservationNumber: reservation.reservationNumber,
                                        checkInDate: reservation.checkInDate,
                                        checkOutDate: reservation.checkOutDate,
                                        room: reservation.room ? {
                                          roomNumber: reservation.room.roomNumber
                                        } : undefined
                                      } : undefined,
                                      guest: guest ? {
                                        firstName: guest.firstName,
                                        lastName: guest.lastName,
                                        email: guest.email,
                                        phone: guest.phone,
                                        address: guest.address
                                      } : undefined
                                    },
                                    folio: folioData,
                                    company: companyData
                                  });

                                  await generator.download();
                                  toast({ title: 'Success', description: 'Invoice downloaded successfully' });
                                } catch (error: any) {
                                  console.error('Error generating PDF:', error);
                                  // Fallback to backend API
                                  try {
                                    const blob = await apiService.downloadHotelPaymentInvoice(payment.id);
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `payment-${payment.transactionId || payment.id}.pdf`;
                                    document.body.appendChild(a);
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                    document.body.removeChild(a);
                                    toast({ title: 'Success', description: 'Invoice downloaded' });
                                  } catch (backendError: any) {
                                    toast({ title: 'Error', description: backendError.message || 'Failed to download invoice', variant: 'destructive' });
                                  }
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              <TranslatedText text="Download Invoice" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedPaymentForEmail(payment);
                                setPaymentEmailTo(guest?.email || reservation?.guest?.email || '');
                                setPaymentEmailSubject(`Payment Invoice - ${payment.transactionId || payment.id}`);
                                setPaymentEmailMessage(`Please find your payment invoice attached. Transaction ID: ${payment.transactionId || payment.id}`);
                                setShowPaymentEmailDialog(true);
                              }}
                            >
                              <Send className="w-4 h-4 mr-2" />
                              <TranslatedText text="Send Invoice" />
                            </Button>
                            <Button
                              size="sm"
                              onClick={async () => {
                                try {
                                  await apiService.markHotelPaymentAsPaid(payment.id);
                                  toast({ title: 'Success', description: 'Payment marked as paid' });
                                  loadPayments();
                                } catch (error: any) {
                                  toast({ title: 'Error', description: error.message || 'Failed to mark payment as paid', variant: 'destructive' });
                                }
                              }}
                            >
                              <Check className="w-4 h-4 mr-2" />
                              <TranslatedText text="Mark as Paid" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Housekeeping Tab */}
        <TabsContent value="housekeeping" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Housekeeping Tasks" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage housekeeping assignments and tasks" /> ({housekeepingTasks.length} <TranslatedText text="total" />)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadHousekeepingTasks();
                      setHousekeepingPage(1);
                    }}
                    title={tSync("Refresh housekeeping tasks", "Refresh housekeeping tasks")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setShowHousekeepingDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="New Task" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="border rounded-lg bg-muted/50 mb-4">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(housekeepingFilters.roomNumber || 
                      housekeepingFilters.status !== 'all' || 
                      housekeepingFilters.priority !== 'all' ||
                      housekeepingFilters.assignedTo) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setHousekeepingFilters({
                            roomNumber: '',
                            status: 'all',
                            priority: 'all',
                            assignedTo: '',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowHousekeepingFilters(!showHousekeepingFilters)}
                    >
                      {showHousekeepingFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showHousekeepingFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Room Number" /></Label>
                        <Input
                          placeholder={tSync("Room number...", "Room number...")}
                          value={housekeepingFilters.roomNumber}
                          onChange={(e) => setHousekeepingFilters({ ...housekeepingFilters, roomNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Status" /></Label>
                        <Select
                          value={housekeepingFilters.status}
                          onValueChange={(value) => setHousekeepingFilters({ ...housekeepingFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                            <SelectItem value="PENDING"><TranslatedText text="Pending" /></SelectItem>
                            <SelectItem value="IN_PROGRESS"><TranslatedText text="In Progress" /></SelectItem>
                            <SelectItem value="COMPLETED"><TranslatedText text="Completed" /></SelectItem>
                            <SelectItem value="CANCELLED"><TranslatedText text="Cancelled" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Priority" /></Label>
                        <Select
                          value={housekeepingFilters.priority}
                          onValueChange={(value) => setHousekeepingFilters({ ...housekeepingFilters, priority: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Priorities" /></SelectItem>
                            <SelectItem value="LOW"><TranslatedText text="Low" /></SelectItem>
                            <SelectItem value="NORMAL"><TranslatedText text="Normal" /></SelectItem>
                            <SelectItem value="HIGH"><TranslatedText text="High" /></SelectItem>
                            <SelectItem value="URGENT"><TranslatedText text="Urgent" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Assigned To" /></Label>
                        <Input
                          placeholder={tSync("Staff name...", "Staff name...")}
                          value={housekeepingFilters.assignedTo}
                          onChange={(e) => setHousekeepingFilters({ ...housekeepingFilters, assignedTo: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={housekeepingPerPage.toString()}
                    onValueChange={(value) => {
                      setHousekeepingPerPage(Number(value));
                      setHousekeepingPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((housekeepingPage - 1) * housekeepingPerPage) + 1} - {Math.min(housekeepingPage * housekeepingPerPage, filteredHousekeepingTasks.length)} <TranslatedText text="of" /> {filteredHousekeepingTasks.length} ({housekeepingTasks.length} <TranslatedText text="total" />)
                </div>
              </div>

              <div className="space-y-4">
                {filteredHousekeepingTasks.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {housekeepingTasks.length === 0 ? (
                      <TranslatedText text="No housekeeping tasks found." />
                    ) : (
                      <TranslatedText text="No housekeeping tasks match the current filters." />
                    )}
                  </p>
                ) : (
                  <>
                    {getPaginatedHousekeepingTasks().map((task) => (
                      <Card key={task.id}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg"><TranslatedText text="Room" /> {task.room?.roomNumber}</CardTitle>
                              <CardDescription>{task.description || tSync('Housekeeping task', 'Housekeeping task')}</CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await apiService.updateHousekeepingTask(task.id, { status: 'PENDING' });
                                        toast({ title: 'Success', description: 'Task status updated to Pending' });
                                        loadHousekeepingTasks();
                                      } catch (error: any) {
                                        toast({ title: 'Error', description: error.message || 'Failed to update task status', variant: 'destructive' });
                                      }
                                    }}
                                    disabled={task.status === 'PENDING'}
                                  >
                                    <TranslatedText text="Set to Pending" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await apiService.updateHousekeepingTask(task.id, { status: 'IN_PROGRESS' });
                                        toast({ title: 'Success', description: 'Task status updated to In Progress' });
                                        loadHousekeepingTasks();
                                      } catch (error: any) {
                                        toast({ title: 'Error', description: error.message || 'Failed to update task status', variant: 'destructive' });
                                      }
                                    }}
                                    disabled={task.status === 'IN_PROGRESS'}
                                  >
                                    <TranslatedText text="Set to In Progress" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await apiService.updateHousekeepingTask(task.id, { status: 'COMPLETED' });
                                        toast({ title: 'Success', description: 'Task status updated to Completed' });
                                        loadHousekeepingTasks();
                                      } catch (error: any) {
                                        toast({ title: 'Error', description: error.message || 'Failed to update task status', variant: 'destructive' });
                                      }
                                    }}
                                    disabled={task.status === 'COMPLETED'}
                                  >
                                    <TranslatedText text="Set to Completed" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await apiService.updateHousekeepingTask(task.id, { status: 'CANCELLED' });
                                        toast({ title: 'Success', description: 'Task status updated to Cancelled' });
                                        loadHousekeepingTasks();
                                      } catch (error: any) {
                                        toast({ title: 'Error', description: error.message || 'Failed to update task status', variant: 'destructive' });
                                      }
                                    }}
                                    disabled={task.status === 'CANCELLED'}
                                  >
                                    <TranslatedText text="Set to Cancelled" />
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Badge variant={task.status === 'COMPLETED' ? 'default' : 'secondary'}>
                                {task.status}
                              </Badge>
                              <Badge variant="outline">{task.priority}</Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Scheduled" /></Label>
                              <p className="text-sm font-medium">
                                {new Date(task.scheduledAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Assigned To" /></Label>
                              <p className="text-sm font-medium">{task.assignedTo || <TranslatedText text="Unassigned" />}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Completed" /></Label>
                              <p className="text-sm font-medium">
                                {task.completedAt ? new Date(task.completedAt).toLocaleDateString() : <TranslatedText text="Pending" />}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Priority" /></Label>
                              <p className="text-sm font-medium">{task.priority}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {totalHousekeepingPages > 1 && (
                      <div className="mt-4 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setHousekeepingPage(prev => Math.max(1, prev - 1));
                                }}
                                className={housekeepingPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalHousekeepingPages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalHousekeepingPages ||
                                (page >= housekeepingPage - 1 && page <= housekeepingPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setHousekeepingPage(page);
                                      }}
                                      isActive={housekeepingPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (page === housekeepingPage - 2 || page === housekeepingPage + 2) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setHousekeepingPage(prev => Math.min(totalHousekeepingPages, prev + 1));
                                }}
                                className={housekeepingPage === totalHousekeepingPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Maintenance Tab */}
        <TabsContent value="maintenance" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Maintenance Requests" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage maintenance and repair requests" /> ({maintenanceRequests.length} <TranslatedText text="total" />)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadMaintenanceRequests();
                      setMaintenancePage(1);
                    }}
                    title={tSync("Refresh maintenance requests", "Refresh maintenance requests")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setShowMaintenanceDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="New Request" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="border rounded-lg bg-muted/50 mb-4">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(maintenanceFilters.roomNumber || 
                      maintenanceFilters.status !== 'all' || 
                      maintenanceFilters.priority !== 'all' ||
                      maintenanceFilters.assignedTo ||
                      maintenanceFilters.requestedBy) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setMaintenanceFilters({
                            roomNumber: '',
                            status: 'all',
                            priority: 'all',
                            assignedTo: '',
                            requestedBy: '',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMaintenanceFilters(!showMaintenanceFilters)}
                    >
                      {showMaintenanceFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showMaintenanceFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Room Number" /></Label>
                        <Input
                          placeholder={tSync("Room number...", "Room number...")}
                          value={maintenanceFilters.roomNumber}
                          onChange={(e) => setMaintenanceFilters({ ...maintenanceFilters, roomNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Status" /></Label>
                        <Select
                          value={maintenanceFilters.status}
                          onValueChange={(value) => setMaintenanceFilters({ ...maintenanceFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                            <SelectItem value="OPEN"><TranslatedText text="Open" /></SelectItem>
                            <SelectItem value="IN_PROGRESS"><TranslatedText text="In Progress" /></SelectItem>
                            <SelectItem value="COMPLETED"><TranslatedText text="Completed" /></SelectItem>
                            <SelectItem value="CANCELLED"><TranslatedText text="Cancelled" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Priority" /></Label>
                        <Select
                          value={maintenanceFilters.priority}
                          onValueChange={(value) => setMaintenanceFilters({ ...maintenanceFilters, priority: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Priorities" /></SelectItem>
                            <SelectItem value="LOW"><TranslatedText text="Low" /></SelectItem>
                            <SelectItem value="NORMAL"><TranslatedText text="Normal" /></SelectItem>
                            <SelectItem value="HIGH"><TranslatedText text="High" /></SelectItem>
                            <SelectItem value="URGENT"><TranslatedText text="Urgent" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Assigned To" /></Label>
                        <Input
                          placeholder={tSync("Staff name...", "Staff name...")}
                          value={maintenanceFilters.assignedTo}
                          onChange={(e) => setMaintenanceFilters({ ...maintenanceFilters, assignedTo: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Requested By" /></Label>
                        <Input
                          placeholder={tSync("Requester name...", "Requester name...")}
                          value={maintenanceFilters.requestedBy}
                          onChange={(e) => setMaintenanceFilters({ ...maintenanceFilters, requestedBy: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={maintenancePerPage.toString()}
                    onValueChange={(value) => {
                      setMaintenancePerPage(Number(value));
                      setMaintenancePage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((maintenancePage - 1) * maintenancePerPage) + 1} - {Math.min(maintenancePage * maintenancePerPage, filteredMaintenanceRequests.length)} <TranslatedText text="of" /> {filteredMaintenanceRequests.length} ({maintenanceRequests.length} <TranslatedText text="total" />)
                </div>
              </div>

              <div className="space-y-4">
                {filteredMaintenanceRequests.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {maintenanceRequests.length === 0 ? (
                      <TranslatedText text="No maintenance requests found." />
                    ) : (
                      <TranslatedText text="No maintenance requests match the current filters." />
                    )}
                  </p>
                ) : (
                  <>
                    {getPaginatedMaintenanceRequests().map((request) => (
                  <Card key={request.id}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg"><TranslatedText text="Room" /> {request.room?.roomNumber}</CardTitle>
                          <CardDescription>{request.description}</CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await apiService.updateMaintenanceRequest(request.id, { status: 'OPEN' });
                                    toast({ title: 'Success', description: 'Request status updated to Open' });
                                    loadMaintenanceRequests();
                                  } catch (error: any) {
                                    toast({ title: 'Error', description: error.message || 'Failed to update request status', variant: 'destructive' });
                                  }
                                }}
                                disabled={request.status === 'OPEN'}
                              >
                                <TranslatedText text="Set to Open" />
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await apiService.updateMaintenanceRequest(request.id, { status: 'IN_PROGRESS' });
                                    toast({ title: 'Success', description: 'Request status updated to In Progress' });
                                    loadMaintenanceRequests();
                                  } catch (error: any) {
                                    toast({ title: 'Error', description: error.message || 'Failed to update request status', variant: 'destructive' });
                                  }
                                }}
                                disabled={request.status === 'IN_PROGRESS'}
                              >
                                <TranslatedText text="Set to In Progress" />
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await apiService.updateMaintenanceRequest(request.id, { status: 'COMPLETED' });
                                    toast({ title: 'Success', description: 'Request status updated to Completed' });
                                    loadMaintenanceRequests();
                                  } catch (error: any) {
                                    toast({ title: 'Error', description: error.message || 'Failed to update request status', variant: 'destructive' });
                                  }
                                }}
                                disabled={request.status === 'COMPLETED'}
                              >
                                <TranslatedText text="Set to Completed" />
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={async () => {
                                  try {
                                    await apiService.updateMaintenanceRequest(request.id, { status: 'CANCELLED' });
                                    toast({ title: 'Success', description: 'Request status updated to Cancelled' });
                                    loadMaintenanceRequests();
                                  } catch (error: any) {
                                    toast({ title: 'Error', description: error.message || 'Failed to update request status', variant: 'destructive' });
                                  }
                                }}
                                disabled={request.status === 'CANCELLED'}
                              >
                                <TranslatedText text="Set to Cancelled" />
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Badge variant={request.status === 'COMPLETED' ? 'default' : 'secondary'}>
                            {request.status}
                          </Badge>
                          <Badge variant="outline">{request.priority}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground"><TranslatedText text="Requested By" /></Label>
                          <p className="text-sm font-medium">{request.requestedBy || <TranslatedText text="N/A" />}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground"><TranslatedText text="Assigned To" /></Label>
                          <p className="text-sm font-medium">{request.assignedTo || <TranslatedText text="Unassigned" />}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground"><TranslatedText text="Completed" /></Label>
                          <p className="text-sm font-medium">
                            {request.completedAt ? new Date(request.completedAt).toLocaleDateString() : <TranslatedText text="Open" />}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground"><TranslatedText text="Priority" /></Label>
                          <p className="text-sm font-medium">{request.priority}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                    ))}

                    {totalMaintenancePages > 1 && (
                      <div className="mt-4 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setMaintenancePage(prev => Math.max(1, prev - 1));
                                }}
                                className={maintenancePage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalMaintenancePages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalMaintenancePages ||
                                (page >= maintenancePage - 1 && page <= maintenancePage + 1)
                              ) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setMaintenancePage(page);
                                      }}
                                      isActive={maintenancePage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (page === maintenancePage - 2 || page === maintenancePage + 2) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setMaintenancePage(prev => Math.min(totalMaintenancePages, prev + 1));
                                }}
                                className={maintenancePage === totalMaintenancePages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
              <div>
                <CardTitle><TranslatedText text="Guest Reviews" /></CardTitle>
                <CardDescription><TranslatedText text="View and manage guest reviews and ratings" /></CardDescription>
                </div>
                <Button onClick={() => handleOpenReviewDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add Review" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {reviews.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    <TranslatedText text="No reviews yet. Reviews will appear here when guests submit them." />
                  </p>
                ) : (
                  reviews.map((review) => (
                    <Card key={review.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">
                              <TranslatedText text="Room" /> {review.room?.roomNumber}
                            </CardTitle>
                            <CardDescription>
                              {review.guest?.firstName} {review.guest?.lastName}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {review.comment && (
                          <p className="text-sm text-muted-foreground">{review.comment}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </p>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Key Cards Tab */}
        <TabsContent value="keycards" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Key Cards" /></CardTitle>
                  <CardDescription>
                    <TranslatedText text="Manage key card assignments and tracking" /> ({keyCards.length} <TranslatedText text="total" />)
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      loadKeyCards();
                      setKeyCardsPage(1);
                    }}
                    title={tSync("Refresh key cards", "Refresh key cards")}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                  <Button onClick={() => setShowKeyCardDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Issue Key Card" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters Section */}
              <div className="border rounded-lg bg-muted/50 mb-4">
                <div className="flex items-center justify-between p-4 border-b">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    <Label className="text-sm font-semibold"><TranslatedText text="Filters" /></Label>
                  </div>
                  <div className="flex items-center gap-2">
                    {(keyCardsFilters.cardNumber || 
                      keyCardsFilters.roomNumber || 
                      keyCardsFilters.reservationNumber ||
                      keyCardsFilters.status !== 'all') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setKeyCardsFilters({
                            cardNumber: '',
                            roomNumber: '',
                            reservationNumber: '',
                            status: 'all',
                          });
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        <TranslatedText text="Clear All" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowKeyCardsFilters(!showKeyCardsFilters)}
                    >
                      {showKeyCardsFilters ? (
                        <>
                          <ChevronUp className="w-4 h-4 mr-1" />
                          <TranslatedText text="Hide" />
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4 mr-1" />
                          <TranslatedText text="Show" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                {showKeyCardsFilters && (
                  <div className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Card Number" /></Label>
                        <Input
                          placeholder={tSync("Card number...", "Card number...")}
                          value={keyCardsFilters.cardNumber}
                          onChange={(e) => setKeyCardsFilters({ ...keyCardsFilters, cardNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Room Number" /></Label>
                        <Input
                          placeholder={tSync("Room number...", "Room number...")}
                          value={keyCardsFilters.roomNumber}
                          onChange={(e) => setKeyCardsFilters({ ...keyCardsFilters, roomNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Reservation Number" /></Label>
                        <Input
                          placeholder={tSync("Reservation number...", "Reservation number...")}
                          value={keyCardsFilters.reservationNumber}
                          onChange={(e) => setKeyCardsFilters({ ...keyCardsFilters, reservationNumber: e.target.value })}
                          className="h-9 border border-gray-300"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs"><TranslatedText text="Status" /></Label>
                        <Select
                          value={keyCardsFilters.status}
                          onValueChange={(value) => setKeyCardsFilters({ ...keyCardsFilters, status: value })}
                        >
                          <SelectTrigger className="h-9 border border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all"><TranslatedText text="All Statuses" /></SelectItem>
                            <SelectItem value="ACTIVE"><TranslatedText text="Active" /></SelectItem>
                            <SelectItem value="DEACTIVATED"><TranslatedText text="Deactivated" /></SelectItem>
                            <SelectItem value="LOST"><TranslatedText text="Lost" /></SelectItem>
                            <SelectItem value="RETURNED"><TranslatedText text="Returned" /></SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={keyCardsPerPage.toString()}
                    onValueChange={(value) => {
                      setKeyCardsPerPage(Number(value));
                      setKeyCardsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="30">30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((keyCardsPage - 1) * keyCardsPerPage) + 1} - {Math.min(keyCardsPage * keyCardsPerPage, filteredKeyCards.length)} <TranslatedText text="of" /> {filteredKeyCards.length} ({keyCards.length} <TranslatedText text="total" />)
                </div>
              </div>

              <div className="space-y-4">
                {filteredKeyCards.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    {keyCards.length === 0 ? (
                      <TranslatedText text="No key cards issued yet. Issue key cards for reservations." />
                    ) : (
                      <TranslatedText text="No key cards match the current filters." />
                    )}
                  </p>
                ) : (
                  <>
                    {getPaginatedKeyCards().map((card) => (
                      <Card key={card.id}>
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle className="text-lg"><TranslatedText text="Card" /> #{card.cardNumber}</CardTitle>
                              <CardDescription>
                                <TranslatedText text="Room" /> {card.room?.roomNumber}
                                {card.reservation && <> - <TranslatedText text="Reservation" /> {card.reservation.reservationNumber}</>}
                              </CardDescription>
                            </div>
                            <div className="flex items-center gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setSelectedKeyCardForEdit(card);
                                      setEditKeyCardForm({
                                        status: card.status,
                                        returnedAt: card.returnedAt ? new Date(card.returnedAt).toISOString().split('T')[0] : '',
                                        expiresAt: card.expiresAt ? new Date(card.expiresAt).toISOString().split('T')[0] : '',
                                        notes: card.notes || '',
                                      });
                                      setShowEditKeyCardDialog(true);
                                    }}
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    <TranslatedText text="Edit" />
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <Badge variant={card.status === 'ACTIVE' ? 'default' : 'secondary'}>
                                {card.status}
                              </Badge>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Issued" /></Label>
                              <p className="text-sm font-medium">
                                {new Date(card.issuedAt).toLocaleDateString()}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Returned" /></Label>
                              <p className="text-sm font-medium">
                                {card.returnedAt ? new Date(card.returnedAt).toLocaleDateString() : <TranslatedText text="Not returned" />}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Expires" /></Label>
                              <p className="text-sm font-medium">
                                {card.expiresAt ? new Date(card.expiresAt).toLocaleDateString() : <TranslatedText text="No expiry" />}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Status" /></Label>
                              <p className="text-sm font-medium">{card.status}</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}

                    {totalKeyCardsPages > 1 && (
                      <div className="mt-4 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setKeyCardsPage(prev => Math.max(1, prev - 1));
                                }}
                                className={keyCardsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalKeyCardsPages }, (_, i) => i + 1).map((page) => {
                              if (
                                page === 1 ||
                                page === totalKeyCardsPages ||
                                (page >= keyCardsPage - 1 && page <= keyCardsPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setKeyCardsPage(page);
                                      }}
                                      isActive={keyCardsPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (page === keyCardsPage - 2 || page === keyCardsPage + 2) {
                                return (
                                  <PaginationItem key={page}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setKeyCardsPage(prev => Math.min(totalKeyCardsPages, prev + 1));
                                }}
                                className={keyCardsPage === totalKeyCardsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Group Bookings Tab */}
        <TabsContent value="group-bookings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Group Bookings" /></CardTitle>
                  <CardDescription><TranslatedText text="Manage corporate, tour, and event bookings" /></CardDescription>
                </div>
                <Button onClick={() => setShowGroupBookingDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="New Group Booking" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={groupBookingsPerPage.toString()}
                    onValueChange={(value) => {
                      setGroupBookingsPerPage(Number(value));
                      setGroupBookingsPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((groupBookingsPage - 1) * groupBookingsPerPage) + 1} - {Math.min(groupBookingsPage * groupBookingsPerPage, groupBookings.length)} <TranslatedText text="of" /> {groupBookings.length}
                </p>
              </div>

                {groupBookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    <TranslatedText text="No group bookings found" />
                  </p>
                ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {getPaginatedGroupBookings().map((booking) => (
                      <Card key={booking.id} className="flex flex-col">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{booking.groupName}</CardTitle>
                            <CardDescription>
                              {booking.groupType} - {booking.contactPerson?.firstName} {booking.contactPerson?.lastName}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        const details = await apiService.getGroupBooking(booking.id);
                                        setSelectedGroupBookingForDetails(details);
                                        setShowGroupBookingDetailsDialog(true);
                                      } catch (error: any) {
                                        toast({
                                          title: 'Error',
                                          description: error.message || 'Failed to load group booking details',
                                          variant: 'destructive',
                                        });
                                      }
                                    }}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    <TranslatedText text="View Details" />
                                  </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedGroupBookingForEdit(booking);
                                    setGroupBookingForm({
                                      groupName: booking.groupName,
                                      groupType: booking.groupType,
                                      contactPersonId: booking.contactPersonId,
                                      contactEmail: booking.contactEmail || '',
                                      contactPhone: booking.contactPhone || '',
                                      checkInDate: booking.checkInDate ? new Date(booking.checkInDate).toISOString().split('T')[0] : '',
                                      checkOutDate: booking.checkOutDate ? new Date(booking.checkOutDate).toISOString().split('T')[0] : '',
                                        numberOfRooms: booking.numberOfRooms ? booking.numberOfRooms.toString() : '',
                                        numberOfGuests: booking.numberOfGuests ? booking.numberOfGuests.toString() : '',
                                        totalAmount: booking.totalAmount ? booking.totalAmount.toString() : '',
                                      depositAmount: booking.depositAmount.toString(),
                                        status: booking.status || 'PENDING',
                                      specialRequests: booking.specialRequests || '',
                                      notes: booking.notes || '',
                                    });
                                    setGroupBookingErrors({});
                                    setShowGroupBookingDialog(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4 mr-2" />
                                  <TranslatedText text="Edit" />
                                </DropdownMenuItem>
                                {(booking.status === 'PENDING' || booking.status === 'CANCELLED') && (
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteGroupBooking(booking.id)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    <TranslatedText text={booking.status === 'CANCELLED' ? 'Delete Group Booking' : 'Cancel Group Booking'} />
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <Badge variant={booking.status === 'CONFIRMED' ? 'default' : 'secondary'}>
                              {booking.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                        <CardContent className="flex-1 flex flex-col">
                          <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-In" /></Label>
                            <p className="text-sm font-medium">{new Date(booking.checkInDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-Out" /></Label>
                            <p className="text-sm font-medium">{new Date(booking.checkOutDate).toLocaleDateString()}</p>
                          </div>
                          <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Rooms Reserved" /></Label>
                            <p className="text-sm font-medium">
                                {booking.linkedReservationsCount || 0} {booking.numberOfRooms ? ` / ${booking.numberOfRooms}` : ''} <TranslatedText text="rooms" />
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Amount" /></Label>
                              <p className="text-sm font-medium">
                                {booking.totalAmount ? formatCurrency(booking.totalAmount) : <span className="text-muted-foreground text-xs"><TranslatedText text="Calculated from reservations" /></span>}
                              </p>
                            </div>
                          </div>
                          <div className="mt-auto pt-4 border-t">
                          <Button
                              variant="default"
                            size="sm"
                            className="w-full"
                            onClick={() => {
                              setSelectedGroupForReservations(booking);
                              setShowAddReservationsToGroupDialog(true);
                            }}
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            <TranslatedText text="Add Reservations" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                    ))}
              </div>
                  {totalGroupBookingsPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setGroupBookingsPage(prev => Math.max(1, prev - 1));
                              }}
                              className={groupBookingsPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          {Array.from({ length: totalGroupBookingsPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 ||
                              page === totalGroupBookingsPages ||
                              (page >= groupBookingsPage - 1 && page <= groupBookingsPage + 1)
                            ) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationLink
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setGroupBookingsPage(page);
                                    }}
                                    isActive={groupBookingsPage === page}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            } else if (page === groupBookingsPage - 2 || page === groupBookingsPage + 2) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              );
                            }
                            return null;
                          })}
                          <PaginationItem>
                            <PaginationNext 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setGroupBookingsPage(prev => Math.min(totalGroupBookingsPages, prev + 1));
                              }}
                              className={groupBookingsPage === totalGroupBookingsPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Waitlist Tab */}
        <TabsContent value="waitlist" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Waitlist" /></CardTitle>
                  <CardDescription><TranslatedText text="Manage guests waiting for room availability" /></CardDescription>
                </div>
                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                <Button onClick={() => setShowWaitlistDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="Add to Waitlist" />
                </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label className="text-sm"><TranslatedText text="Items per page" />:</Label>
                  <Select
                    value={waitlistPerPage.toString()}
                    onValueChange={(value) => {
                      setWaitlistPerPage(Number(value));
                      setWaitlistPage(1);
                    }}
                  >
                    <SelectTrigger className="w-20 border border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5</SelectItem>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="15">15</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-sm text-muted-foreground">
                  <TranslatedText text="Showing" /> {((waitlistPage - 1) * waitlistPerPage) + 1} - {Math.min(waitlistPage * waitlistPerPage, activeWaitlist.length)} <TranslatedText text="of" /> {activeWaitlist.length}
                </p>
              </div>

              {activeWaitlist.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    <TranslatedText text="No active waitlist entries found" />
                  </p>
                ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {getPaginatedWaitlist().map((entry) => (
                      <Card key={entry.id} className="flex flex-col">
                        <CardHeader className="pb-3">
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base leading-tight">
                                {entry.guest?.firstName} {entry.guest?.lastName}
                              </CardTitle>
                              <CardDescription className="text-xs mt-1">
                                {entry.roomType?.name || tSync('Any Room Type', 'Any Room Type')}
                              </CardDescription>
                            </div>
                            <Badge variant={entry.status === 'ACTIVE' ? 'default' : 'secondary'} className="flex-shrink-0">
                              {entry.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col pt-0 pb-3">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-In" /></Label>
                              <p className="text-sm font-medium">{new Date(entry.checkInDate).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-Out" /></Label>
                              <p className="text-sm font-medium">{new Date(entry.checkOutDate).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Priority" /></Label>
                              <p className="text-sm font-medium">{entry.priority || 0}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Guests" /></Label>
                              <p className="text-sm font-medium">{entry.numberOfGuests}</p>
                            </div>
                          </div>
                          {entry.notes && (
                            <div className="mt-2 pt-2 border-t">
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Notes" /></Label>
                              <p className="text-xs text-muted-foreground mt-0.5">{entry.notes}</p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
              </div>
                  {totalWaitlistPages > 1 && (
                    <div className="mt-4 flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setWaitlistPage(prev => Math.max(1, prev - 1));
                              }}
                              className={waitlistPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                          {Array.from({ length: totalWaitlistPages }, (_, i) => i + 1).map((page) => {
                            if (
                              page === 1 ||
                              page === totalWaitlistPages ||
                              (page >= waitlistPage - 1 && page <= waitlistPage + 1)
                            ) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationLink
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setWaitlistPage(page);
                                    }}
                                    isActive={waitlistPage === page}
                                    className="cursor-pointer"
                                  >
                                    {page}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            } else if (page === waitlistPage - 2 || page === waitlistPage + 2) {
                              return (
                                <PaginationItem key={page}>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              );
                            }
                            return null;
                          })}
                          <PaginationItem>
                            <PaginationNext 
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setWaitlistPage(prev => Math.min(totalWaitlistPages, prev + 1));
                              }}
                              className={waitlistPage === totalWaitlistPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promotions Tab */}
        <TabsContent value="promotions" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Promotions" /></CardTitle>
                  <CardDescription><TranslatedText text="Manage discount codes and special offers" /></CardDescription>
                </div>
                <Button onClick={() => setShowPromotionDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="New Promotion" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {promotions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    <TranslatedText text="No promotions found" />
                  </p>
                ) : (
                  promotions.map((promo) => (
                    <Card key={promo.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{promo.name}</CardTitle>
                            <CardDescription>
                              {promo.code && <><TranslatedText text="Code" />: {promo.code}</>}
                            </CardDescription>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={promo.isActive ? 'default' : 'secondary'}>
                              {promo.isActive ? <TranslatedText text="Active" /> : <TranslatedText text="Inactive" />}
                            </Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEditPromotion(promo)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  <TranslatedText text="Edit" />
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleTogglePromotion(promo.id, !promo.isActive)}>
                              {promo.isActive ? (
                                <>
                                      <XCircle className="w-4 h-4 mr-2" />
                                  <TranslatedText text="Deactivate" />
                                </>
                              ) : (
                                <>
                                      <CheckCircle className="w-4 h-4 mr-2" />
                                  <TranslatedText text="Activate" />
                                </>
                              )}
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeletePromotion(promo.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  <TranslatedText text="Delete" />
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Discount Type" /></Label>
                            <p className="text-sm font-medium">{promo.discountType}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Discount Value" /></Label>
                            <p className="text-sm font-medium">
                              {promo.discountType === 'PERCENTAGE' ? `${promo.discountValue}%` : formatCurrency(promo.discountValue)}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Valid From" /></Label>
                            <p className="text-sm font-medium">{new Date(promo.validFrom).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Valid To" /></Label>
                            <p className="text-sm font-medium">{new Date(promo.validTo).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Packages Tab - Hidden for now */}
        {/* <TabsContent value="packages" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Packages" /></CardTitle>
                  <CardDescription><TranslatedText text="Manage bundled deals and package offers" /></CardDescription>
                </div>
                <Button onClick={() => setShowPackageDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  <TranslatedText text="New Package" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                {packages.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    <TranslatedText text="No packages found" />
                  </p>
                ) : (
                  packages.map((pkg) => (
                    <Card key={pkg.id}>
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-lg">{pkg.name}</CardTitle>
                            <CardDescription>
                              {pkg.roomType?.name || tSync('All Room Types', 'All Room Types')}
                            </CardDescription>
                          </div>
                          <Badge variant={pkg.isActive ? 'default' : 'secondary'}>
                            {pkg.isActive ? <TranslatedText text="Active" /> : <TranslatedText text="Inactive" />}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Base Price" /></Label>
                            <p className="text-sm font-medium">{formatCurrency(pkg.basePrice)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Min Stay" /></Label>
                            <p className="text-sm font-medium">{pkg.minStay} <TranslatedText text="nights" /></p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Valid From" /></Label>
                            <p className="text-sm font-medium">{new Date(pkg.validFrom).toLocaleDateString()}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Valid To" /></Label>
                            <p className="text-sm font-medium">{new Date(pkg.validTo).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {pkg.description && (
                          <p className="text-sm text-muted-foreground mt-4">{pkg.description}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent> */}

        {/* Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle><TranslatedText text="Guest Messages" /></CardTitle>
              <CardDescription><TranslatedText text="Chat with guests about their reservations and inquiries" /></CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]">
                {/* Guests List */}
                <div className="border rounded-lg overflow-hidden flex flex-col">
                  <div className="p-4 border-b bg-muted/50">
                    <h3 className="font-semibold"><TranslatedText text="Guests" /></h3>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {loadingGuestsWithMessages ? (
                      <div className="flex items-center justify-center py-8">
                        <MoonLoader />
                      </div>
                    ) : guestsWithMessages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                        <MessageSquare className="h-12 w-12 mb-2 opacity-50" />
                        <p className="text-sm"><TranslatedText text="No messages yet" /></p>
                      </div>
                    ) : (
                      <div className="divide-y">
                        {guestsWithMessages.map((guest) => (
                          <button
                            key={guest.id}
                            onClick={() => {
                              setSelectedGuestForChat(guest.id);
                              loadChatMessages(guest.id);
                            }}
                            className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                              selectedGuestForChat === guest.id ? 'bg-muted' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">
                                  {guest.firstName} {guest.lastName}
                                </p>
                                {guest.lastMessage && (
                                  <p className="text-sm text-muted-foreground truncate mt-1">
                                    {guest.lastMessage.text}
                                  </p>
                                )}
                                {guest.lastMessage && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {new Date(guest.lastMessage.createdAt).toLocaleString()}
                                  </p>
                                )}
                              </div>
                              {guest.unreadCount > 0 && (
                                <Badge className="bg-red-500 text-white">
                                  {guest.unreadCount > 99 ? '99+' : guest.unreadCount}
                                </Badge>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Chat Area */}
                <div className="lg:col-span-2 border rounded-lg flex flex-col">
                  {selectedGuestForChat ? (
                    <>
                      {/* Chat Header */}
                      <div className="p-4 border-b bg-muted/50">
                        {(() => {
                          const guest = guestsWithMessages.find(g => g.id === selectedGuestForChat);
                          return guest ? (
                            <div>
                              <h3 className="font-semibold">
                                {guest.firstName} {guest.lastName}
                              </h3>
                              {guest.email && (
                                <p className="text-sm text-muted-foreground">{guest.email}</p>
                              )}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      {/* Messages */}
                      <div className="flex-1 overflow-y-auto p-4 bg-gray-50 space-y-4">
                        {loadingChatMessages ? (
                          <div className="flex items-center justify-center py-8">
                            <MoonLoader />
                          </div>
                        ) : chatMessages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
                            <MessageSquare className="h-12 w-12 mb-2 opacity-50" />
                            <p className="text-sm"><TranslatedText text="No messages yet" /></p>
                          </div>
                        ) : (
                          chatMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${message.isFromGuest ? 'justify-start' : 'justify-end'}`}
                            >
                              <div
                                className={`max-w-[70%] rounded-lg p-3 ${
                                  message.isFromGuest
                                    ? 'bg-white border border-gray-200 text-gray-900'
                                    : 'bg-primary text-white'
                                }`}
                              >
                                <p className="text-sm font-medium mb-1">
                                  {message.isFromGuest
                                    ? `${message.guest?.firstName || ''} ${message.guest?.lastName || ''}`
                                    : message.user?.name || 'You'}
                                </p>
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {message.messageText}
                                </p>
                                <p className={`text-xs mt-2 ${message.isFromGuest ? 'text-muted-foreground' : 'opacity-70'}`}>
                                  {new Date(message.createdAt).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Message Input */}
                      <div className="p-4 border-t bg-white">
                        <div className="flex gap-2">
                          <Textarea
                            value={newChatMessage}
                            onChange={(e) => setNewChatMessage(e.target.value)}
                            placeholder={tSync("Type your message...", "Type your message...")}
                            className="flex-1 min-h-[80px] resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendChatMessage();
                              }
                            }}
                            disabled={sendingChatMessage}
                          />
                          <Button
                            onClick={handleSendChatMessage}
                            disabled={!newChatMessage.trim() || sendingChatMessage}
                            className="px-6"
                          >
                            {sendingChatMessage ? (
                              <MoonLoader />
                            ) : (
                              <Send className="h-5 w-5" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-center text-muted-foreground">
                      <div>
                        <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p className="text-sm"><TranslatedText text="Select a guest to start chatting" /></p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle><TranslatedText text="Reports" /></CardTitle>
                  <CardDescription><TranslatedText text="View revenue and occupancy reports" /></CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        // Load reports data if not already loaded
                        if (!revenueReport || !occupancyReport) {
                          await loadReports();
                        }
                        
                        // Dynamically import jsPDF and autoTable
                        const { jsPDF } = await import('jspdf');
                        const autoTable = (await import('jspdf-autotable')).default;
                        
                        const doc = new jsPDF();
                        
                        // Fetch company data from API
                        let companyData: any = null;
                        let companyLogoUrl = '';
                        let companyNameForPDF = companyName || localStorage.getItem('company_name') || 'Hotel';
                        
                        try {
                          const companyId = localStorage.getItem('company_id');
                          if (companyId) {
                            const companies = await apiService.getCompanies({ page: 1, pageSize: 100 }) as any;
                            const companiesArray = Array.isArray(companies) ? companies : 
                                                  companies?.data ? companies.data : 
                                                  companies?.items ? companies.items : [];
                            companyData = companiesArray.find((c: any) => c.id === companyId);
                            
                            if (companyData) {
                              companyNameForPDF = companyData.name || companyNameForPDF;
                              // Convert relative logo URL to absolute URL
                              const logoUrl = companyData.logoUrl || '';
                              if (logoUrl) {
                                if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
                                  companyLogoUrl = logoUrl;
                                } else {
                                  // Use getApiUrl to get the correct backend URL (works in both dev and production)
                                  companyLogoUrl = getApiUrl(logoUrl.startsWith('/') ? logoUrl.substring(1) : logoUrl);
                                }
                              }
                            }
                          }
                        } catch (err) {
                          console.error('Error fetching company data:', err);
                        }
                        
                        // Page setup
                        const pageWidth = doc.internal.pageSize.getWidth();
                        const pageHeight = doc.internal.pageSize.getHeight();
                        
                        // Header section - Logo and Company Name on left, Report Title on right
                        let yPosition = 20;
                        let logoLoaded = false;
                        
                        // Add logo on the left
                        if (companyLogoUrl) {
                          try {
                            const img = new Image();
                            img.crossOrigin = 'anonymous';
                            await new Promise((resolve, reject) => {
                              img.onload = () => resolve(true);
                              img.onerror = (e) => reject(e);
                              img.src = companyLogoUrl;
                            });
                            doc.addImage(img, 'PNG', 14, yPosition, 25, 25);
                            logoLoaded = true;
                          } catch (err) {
                            // Logo failed to load, continue without it
                          }
                        }
                        
                        // Company name next to logo on the same row
                        doc.setFontSize(18);
                        doc.setTextColor(0, 0, 0);
                        doc.text(companyNameForPDF, logoLoaded ? 43 : 14, yPosition + 13);
                        
                        // Report title on the right side
                        doc.setFontSize(24);
                        doc.setTextColor(0, 0, 0);
                        const reportTitle = 'IKORO REPORT';
                        const titleWidth = doc.getTextWidth(reportTitle);
                        doc.text(reportTitle, pageWidth - titleWidth - 14, yPosition + 13);
                        
                        yPosition += 28;
                        
                        // Period and Generated date on same line
                        doc.setFontSize(10);
                        doc.setTextColor(100, 100, 100);
                        doc.text(`Period: ${reportDateRange.startDate} - ${reportDateRange.endDate}`, 14, yPosition);
                        
                        const currentDate = new Date().toLocaleDateString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        });
                        const generatedText = `Generated: ${currentDate}`;
                        const generatedWidth = doc.getTextWidth(generatedText);
                        doc.text(generatedText, pageWidth - generatedWidth - 14, yPosition);
                        
                        yPosition += 10;
                        
                        // Summary section title
                        doc.setFontSize(14);
                        doc.setTextColor(0, 0, 0);
                        doc.text('Summary', 14, yPosition);
                        yPosition += 8;
                        
                        // Summary boxes - First row: Total Revenue, Total Paid, Avg Rate/Night, Occupancy Rate
                        if (revenueReport && occupancyReport && selectedRoomForReport === 'all') {
                          const boxWidth = 45;
                          const boxHeight = 20;
                          const boxSpacing = 2;
                          const startX = 14;
                          
                          // First row of boxes
                          const boxes1 = [
                            { label: 'Total Revenue', value: formatCurrencyDisplay(revenueReport.totalRevenue || 0, 'FRW', displayCurrency) },
                            { label: 'Total Paid', value: formatCurrencyDisplay(revenueReport.totalPaid || 0, 'FRW', displayCurrency) },
                            { label: 'Avg Rate / Night', value: formatCurrencyDisplay(revenueReport.averageRate || 0, 'FRW', displayCurrency) },
                            { label: 'Occupancy Rate', value: `${occupancyReport.occupancyRate || 0}%` }
                          ];
                          
                          boxes1.forEach((box, index) => {
                            const x = startX + (index * (boxWidth + boxSpacing));
                            
                            // Black header
                            doc.setFillColor(0, 0, 0);
                            doc.rect(x, yPosition, boxWidth, 8, 'F');
                            doc.setTextColor(255, 255, 255);
                            doc.setFontSize(9);
                            doc.text(box.label, x + 2, yPosition + 5.5);
                            
                            // Light gray value box
                            doc.setFillColor(240, 240, 240);
                            doc.rect(x, yPosition + 8, boxWidth, 12, 'F');
                            doc.setTextColor(0, 0, 0);
                            doc.setFontSize(11);
                            doc.text(box.value, x + 2, yPosition + 16);
                          });
                          
                          yPosition += 25;
                          
                          // Second row of boxes
                          const boxes2 = [
                            { label: 'Total Rooms', value: String(occupancyReport.totalRooms || 0) },
                            { label: 'Occupied Rooms', value: String(occupancyReport.occupiedRooms || 0) },
                            { label: 'Available Rooms', value: String(occupancyReport.availableRooms || 0) },
                            { label: 'Total Reservations', value: String(revenueReport.totalRooms || 0) }
                          ];
                          
                          boxes2.forEach((box, index) => {
                            const x = startX + (index * (boxWidth + boxSpacing));
                            
                            // Black header
                            doc.setFillColor(0, 0, 0);
                            doc.rect(x, yPosition, boxWidth, 8, 'F');
                            doc.setTextColor(255, 255, 255);
                            doc.setFontSize(9);
                            doc.text(box.label, x + 2, yPosition + 5.5);
                            
                            // Light gray value box
                            doc.setFillColor(240, 240, 240);
                            doc.rect(x, yPosition + 8, boxWidth, 12, 'F');
                            doc.setTextColor(0, 0, 0);
                            doc.setFontSize(11);
                            doc.text(box.value, x + 2, yPosition + 16);
                          });
                          
                          yPosition += 25;
                        }

                        // Individual Room Reports
                        const roomsToInclude = selectedRoomForReport === 'all' 
                          ? Object.values(roomReports) 
                          : [roomReports[selectedRoomForReport]].filter(Boolean);

                        if (roomsToInclude.length > 0) {
                          if (selectedRoomForReport === 'all') {
                            if (yPosition > 250) { doc.addPage(); yPosition = 20; }
                            doc.setFontSize(14);
                            doc.setTextColor(0, 0, 0); // Black color
                            doc.text('Individual Room Performance', 14, yPosition);
                            yPosition += 8;

                            const tableBody = roomsToInclude.map((room: any) => [
                              room.roomNumber,
                              room.roomTitle || '-',
                              formatCurrency(room.revenue?.totalRevenue || 0, room.currency || 'FRW'),
                              formatCurrency(room.revenue?.totalPaid || 0, room.currency || 'FRW'),
                              room.revenue?.totalRooms || 0,
                              formatCurrency(room.revenue?.averageRate || 0, room.currency || 'FRW'),
                              room.occupancy ? `${room.occupancy.occupancyRate || 0}%` : '-'
                            ]);

                            autoTable(doc, {
                              startY: yPosition,
                              head: [['Room', 'Title', 'Revenue', 'Paid', 'Res', 'Avg Rate', 'Occ %']],
                              body: tableBody,
                              theme: 'grid',
                              headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] }, // Black header
                              styles: { fontSize: 8, textColor: [0, 0, 0] }, // Black text
                              margin: { left: 14, right: 16 }, // Match the summary boxes margin (14 left, 16 right to reach 194)
                              tableWidth: 'auto'
                            });
                          } else {
                            const room = roomsToInclude[0];
                            if (yPosition > 250) { doc.addPage(); yPosition = 20; }
                            doc.setFontSize(14);
                            doc.setTextColor(0, 0, 0); // Black color
                            doc.text(`Room ${room.roomNumber} ${room.roomTitle ? `- ${room.roomTitle}` : ''} Report`, 14, yPosition);
                            yPosition += 8;

                            autoTable(doc, {
                              startY: yPosition,
                              head: [['Revenue', 'Paid', 'Reservations', 'Avg Rate', 'Occupancy', 'Occupied', 'Available']],
                              body: [[
                                formatCurrency(room.revenue?.totalRevenue || 0, room.currency || 'FRW'),
                                formatCurrency(room.revenue?.totalPaid || 0, room.currency || 'FRW'),
                                room.revenue?.totalRooms || 0,
                                formatCurrency(room.revenue?.averageRate || 0, room.currency || 'FRW'),
                                `${room.occupancy?.occupancyRate || 0}%`,
                                room.occupancy?.occupiedRooms || 0,
                                room.occupancy?.availableRooms || 0
                              ]],
                              theme: 'grid',
                              headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] }, // Black header
                              styles: { fontSize: 8, textColor: [0, 0, 0] } // Black text
                            });
                          }
                        }
                        
                        // Add centered footer without "Period"
                        const pageCount = doc.getNumberOfPages();
                        for (let i = 1; i <= pageCount; i++) {
                          doc.setPage(i);
                          doc.setFontSize(10);
                          doc.setTextColor(0, 0, 0); // Black color
                          const footerText = `${reportDateRange.startDate} to ${reportDateRange.endDate}`;
                          const textWidth = doc.getTextWidth(footerText);
                          doc.text(footerText, (pageWidth - textWidth) / 2, doc.internal.pageSize.getHeight() - 10);
                        }
                        
                        // Save PDF
                        const fileName = selectedRoomForReport === 'all' ? 'hotel-full-report' : `room-${roomReports[selectedRoomForReport]?.roomNumber}-report`;
                        doc.save(`${fileName}-${reportDateRange.startDate}-to-${reportDateRange.endDate}.pdf`);
                        toast({ title: 'Success', description: 'PDF report downloaded successfully' });
                      } catch (error: any) {
                        console.error('Error generating PDF:', error);
                        toast({ title: 'Error', description: 'Failed to download PDF report', variant: 'destructive' });
                      }
                    }}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    <TranslatedText text="Download PDF" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        // Dynamically import xlsx
                        const XLSX = await import('xlsx');
                        
                        // Prepare data for Excel
                        const workbook = XLSX.utils.book_new();
                        const sheetData: any[][] = [];
                        
                        // Title and Metadata
                        sheetData.push([`${companyName || 'Hotel'} - Comprehensive Report`]);
                        sheetData.push(['Date Range', `${reportDateRange.startDate} to ${reportDateRange.endDate}`]);
                        sheetData.push([]); // Spacer

                        // 1. Global Revenue Summary
                        sheetData.push(['GLOBAL REVENUE SUMMARY']);
                        sheetData.push(['Total Revenue', 'Total Paid', 'Total Reservations', 'Average Rate']);
                        sheetData.push([
                          revenueReport?.totalRevenue || 0,
                          revenueReport?.totalPaid || 0,
                          revenueReport?.totalRooms || 0,
                          revenueReport?.averageRate || 0
                        ]);
                        sheetData.push([]); // Spacer

                        // 2. Global Occupancy Summary
                        sheetData.push(['GLOBAL OCCUPANCY SUMMARY']);
                        sheetData.push(['Total Rooms', 'Occupied Rooms', 'Available Rooms', 'Occupancy Rate']);
                        sheetData.push([
                          occupancyReport?.totalRooms || 0,
                          occupancyReport?.occupiedRooms || 0,
                          occupancyReport?.availableRooms || 0,
                          `${occupancyReport?.occupancyRate || 0}%`
                        ]);
                        sheetData.push([]); // Spacer

                        // 3. Room-Specific Performance
                        const roomsToInclude = selectedRoomForReport === 'all' 
                          ? Object.values(roomReports) 
                          : [roomReports[selectedRoomForReport]].filter(Boolean);

                        if (roomsToInclude.length > 0) {
                          sheetData.push(['INDIVIDUAL ROOM PERFORMANCE']);
                          sheetData.push(['Room', 'Title', 'Revenue', 'Paid', 'Res', 'Avg Rate', 'Occ %', 'Occupied Days', 'Available Days']);
                          
                          roomsToInclude.forEach((room: any) => {
                            sheetData.push([
                              room.roomNumber,
                              room.roomTitle || '-',
                              room.revenue?.totalRevenue || 0,
                              room.revenue?.totalPaid || 0,
                              room.revenue?.totalRooms || 0,
                              room.revenue?.averageRate || 0,
                              room.occupancy ? `${room.occupancy.occupancyRate || 0}%` : '-',
                              room.occupancy?.occupiedRooms || 0,
                              room.occupancy?.availableRooms || 0
                            ]);
                          });
                        }

                        const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

                        // Set Column Widths for better design
                        const wscols = [
                          { wch: 20 }, // A: Room / Date Range
                          { wch: 25 }, // B: Title / Total Paid
                          { wch: 20 }, // C: Revenue / Total Res
                          { wch: 15 }, // D: Paid / Avg Rate
                          { wch: 10 }, // E: Res
                          { wch: 15 }, // F: Avg Rate
                          { wch: 15 }, // G: Occ %
                          { wch: 15 }, // H: Occupied Days
                          { wch: 15 }, // I: Available Days
                        ];
                        worksheet['!cols'] = wscols;

                        XLSX.utils.book_append_sheet(workbook, worksheet, 'Hotel Report');
                        
                        // Save Excel file
                        const fileName = selectedRoomForReport === 'all' ? 'hotel-full-report' : `room-${roomReports[selectedRoomForReport]?.roomNumber}-report`;
                        XLSX.writeFile(workbook, `${fileName}-${reportDateRange.startDate}-to-${reportDateRange.endDate}.xlsx`);
                        toast({ title: 'Success', description: 'Professional Excel report downloaded successfully' });
                      } catch (error: any) {
                        console.error('Error generating Excel:', error);
                        toast({ title: 'Error', description: 'Failed to download Excel report', variant: 'destructive' });
                      }
                    }}
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    <TranslatedText text="Download Excel" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <Label><TranslatedText text="Start Date" /></Label>
                  <Input
                    type="date"
                    value={reportDateRange.startDate}
                    onChange={(e) => setReportDateRange({ ...reportDateRange, startDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label><TranslatedText text="End Date" /></Label>
                  <Input
                    type="date"
                    value={reportDateRange.endDate}
                    onChange={(e) => setReportDateRange({ ...reportDateRange, endDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label><TranslatedText text="Room" /></Label>
                  <Select
                    value={selectedRoomForReport}
                    onValueChange={setSelectedRoomForReport}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={<TranslatedText text="All Rooms" />} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all"><TranslatedText text="All Rooms" /></SelectItem>
                      {rooms.map((room) => (
                        <SelectItem key={room.id} value={room.id}>
                          Room {room.roomNumber} {room.title ? `- ${room.title}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={loadReports} disabled={loadingReports} className="mb-6">
                {loadingReports ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <TranslatedText text="Generating..." />
                  </>
                ) : (
                  <>
                    <FileBarChart className="w-4 h-4 mr-2" />
                    <TranslatedText text="Generate Reports" />
                  </>
                )}
              </Button>

              {revenueReport && (
                <Card className="mb-4">
                  <CardHeader>
                    <CardTitle><TranslatedText text="Revenue Report" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Revenue" /></Label>
                        <p className="text-2xl font-bold">{formatCurrencyDisplay(revenueReport.totalRevenue, 'FRW', displayCurrency)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Paid" /></Label>
                        <p className="text-2xl font-bold">{formatCurrencyDisplay(revenueReport.totalPaid, 'FRW', displayCurrency)}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Rooms" /></Label>
                        <p className="text-2xl font-bold">{revenueReport.totalRooms}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Average Rate" /></Label>
                        <p className="text-2xl font-bold">{formatCurrencyDisplay(revenueReport.averageRate, 'FRW', displayCurrency)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {occupancyReport && (
                <Card className="mb-4">
                  <CardHeader>
                    <CardTitle><TranslatedText text="Occupancy Report" /></CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Date Range (Days)" /></Label>
                        <p className="text-2xl font-bold">
                          {Math.ceil((new Date(reportDateRange.endDate).getTime() - new Date(reportDateRange.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Rooms" /></Label>
                        <p className="text-2xl font-bold">{occupancyReport.totalRooms}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Occupied Rooms" /></Label>
                        <p className="text-2xl font-bold">{occupancyReport.occupiedRooms}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Available Rooms" /></Label>
                        <p className="text-2xl font-bold">{occupancyReport.availableRooms}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Occupancy Rate" /></Label>
                        <p className="text-2xl font-bold">{occupancyReport.occupancyRate}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Single Room Report */}
              {selectedRoomForReport !== 'all' && roomReports[selectedRoomForReport] && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      Room {roomReports[selectedRoomForReport].roomNumber} {roomReports[selectedRoomForReport].roomTitle ? `- ${roomReports[selectedRoomForReport].roomTitle}` : ''} Report
                    </CardTitle>
                    <CardDescription>
                      <TranslatedText text="Detailed report for selected room" />
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-semibold mb-3"><TranslatedText text="Revenue Summary" /></h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Revenue" /></Label>
                            <p className="text-2xl font-bold">{formatCurrency(roomReports[selectedRoomForReport].revenue?.totalRevenue || 0, roomReports[selectedRoomForReport].currency || 'FRW')}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Paid" /></Label>
                            <p className="text-2xl font-bold">{formatCurrency(roomReports[selectedRoomForReport].revenue?.totalPaid || 0, roomReports[selectedRoomForReport].currency || 'FRW')}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Reservations" /></Label>
                            <p className="text-2xl font-bold">{roomReports[selectedRoomForReport].revenue?.totalRooms || 0}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground"><TranslatedText text="Average Rate" /></Label>
                            <p className="text-2xl font-bold">{formatCurrency(roomReports[selectedRoomForReport].revenue?.averageRate || 0, roomReports[selectedRoomForReport].currency || 'FRW')}</p>
                          </div>
                        </div>
                      </div>
                      {roomReports[selectedRoomForReport].occupancy && (
                        <div>
                          <h4 className="text-sm font-semibold mb-3"><TranslatedText text="Occupancy Summary" /></h4>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Date Range (Days)" /></Label>
                              <p className="text-2xl font-bold">
                                {Math.ceil((new Date(reportDateRange.endDate).getTime() - new Date(reportDateRange.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1}
                              </p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Days" /></Label>
                              <p className="text-2xl font-bold">{roomReports[selectedRoomForReport].occupancy.totalRooms || 0}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Occupied Days" /></Label>
                              <p className="text-2xl font-bold">{roomReports[selectedRoomForReport].occupancy.occupiedRooms || 0}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Available Days" /></Label>
                              <p className="text-2xl font-bold">{roomReports[selectedRoomForReport].occupancy.availableRooms || 0}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Occupancy Rate" /></Label>
                              <p className="text-2xl font-bold">{roomReports[selectedRoomForReport].occupancy.occupancyRate || 0}%</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* All Rooms Reports - Compact Mode */}
              {selectedRoomForReport === 'all' && Object.keys(roomReports).length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle><TranslatedText text="Individual Room Reports" /></CardTitle>
                    <CardDescription>
                      <TranslatedText text="Detailed report for each room in compact view" />
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(roomReports).map(([roomId, report]: [string, any]) => (
                        <div key={roomId} className="border-2 rounded-lg p-3 bg-slate-50 hover:bg-slate-100 transition-colors shadow-sm">
                          <div className="flex items-center justify-between mb-2">
                            <h5 className="font-semibold text-sm text-slate-700">
                              Room {report.roomNumber} {report.roomTitle ? `- ${report.roomTitle}` : ''}
                            </h5>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
                            <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                              <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Revenue" /></Label>
                              <p className="font-bold text-sm text-slate-900">{formatCurrency(report.revenue?.totalRevenue || 0, report.currency || 'FRW')}</p>
                            </div>
                            <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                              <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Paid" /></Label>
                              <p className="font-bold text-sm text-slate-900">{formatCurrency(report.revenue?.totalPaid || 0, report.currency || 'FRW')}</p>
                            </div>
                            <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                              <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Reservations" /></Label>
                              <p className="font-bold text-sm text-slate-900">{report.revenue?.totalRooms || 0}</p>
                            </div>
                            <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                              <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Avg Rate" /></Label>
                              <p className="font-bold text-sm text-slate-900">{formatCurrency(report.revenue?.averageRate || 0, report.currency || 'FRW')}</p>
                            </div>
                            <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                              <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Days" /></Label>
                              <p className="font-bold text-sm text-slate-900">
                                {Math.ceil((new Date(reportDateRange.endDate).getTime() - new Date(reportDateRange.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1}
                              </p>
                            </div>
                            {report.occupancy && (
                              <>
                                <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                                  <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Occupied" /></Label>
                                  <p className="font-bold text-sm text-slate-900">{report.occupancy.occupiedRooms || 0}</p>
                                </div>
                                <div className="bg-white border border-slate-200 rounded p-2 shadow-sm">
                                  <Label className="text-[10px] text-muted-foreground"><TranslatedText text="Rate" /></Label>
                                  <p className="font-bold text-sm text-slate-900">{report.occupancy.occupancyRate || 0}%</p>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Room Dialog */}
      <Dialog 
        open={showRoomDialog} 
        onOpenChange={async (open) => {
        setShowRoomDialog(open);
        if (!open) {
          resetRoomForm();
          // Auto-refresh rooms when dialog closes (in case user cancelled after making changes)
          if (companyId && activeTab === 'rooms') {
            await loadRooms();
          }
        }
      }}
        aria-labelledby="room-dialog-title"
        aria-describedby="room-dialog-description"
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle id="room-dialog-title">{selectedRoom ? 'Edit Room' : 'Add New Room'}</DialogTitle>
            <DialogDescription id="room-dialog-description">{selectedRoom ? 'Update room information' : 'Create a new hotel room'}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 pr-2">
            <div>
              <Label>Room Title</Label>
              <Input
                value={roomForm.title}
                onChange={(e) => setRoomForm({ ...roomForm, title: e.target.value })}
                placeholder="e.g., Deluxe Ocean View Suite"
                className="border border-gray-300 w-full"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room Number *</Label>
                <Input
                  value={roomForm.roomNumber}
                  onChange={(e) => {
                    setRoomForm({ ...roomForm, roomNumber: e.target.value });
                    // Clear error when user starts typing
                    if (roomErrors.roomNumber) {
                      setRoomErrors({ ...roomErrors, roomNumber: '' });
                    }
                  }}
                  placeholder="101"
                  className={`border w-full ${roomErrors.roomNumber ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                />
                {roomErrors.roomNumber && (
                  <p className="text-sm text-red-600 mt-1">{roomErrors.roomNumber}</p>
                )}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                <Label>Room Type *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRoomTypeForm({ name: '', description: '', maxCapacity: '2', basePrice: '' });
                      setShowRoomTypeDialog(true);
                    }}
                    className="h-6 text-xs px-2"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Create New
                  </Button>
                </div>
                {roomTypes.length > 0 ? (
                  <div className={`flex flex-wrap gap-2 border rounded-md p-2 min-h-[42px] ${roomErrors.roomTypeId ? 'border-red-500' : 'border-gray-300'}`}>
                    {roomTypes.map((type) => (
                      <Badge
                        key={type.id}
                        variant={roomForm.roomTypeId === type.id ? "default" : "outline"}
                        className={`cursor-pointer hover:bg-gray-100 ${roomForm.roomTypeId === type.id ? '!text-white' : ''}`}
                        onClick={() => {
                          setRoomForm({ ...roomForm, roomTypeId: roomForm.roomTypeId === type.id ? '' : type.id });
                          // Clear error when user selects a room type
                          if (roomErrors.roomTypeId) {
                            setRoomErrors({ ...roomErrors, roomTypeId: '' });
                          }
                        }}
                      >
                        {type.name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className={`border rounded-md p-3 text-sm text-center ${roomErrors.roomTypeId ? 'border-red-500 text-red-600' : 'border-gray-300 text-gray-500'}`}>
                    No room types available. Click "Create New" to add one.
                  </div>
                )}
                {roomForm.roomTypeId && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {roomTypes.find(t => t.id === roomForm.roomTypeId)?.name}
                  </p>
                )}
                {roomErrors.roomTypeId && (
                  <p className="text-sm text-red-600 mt-1">{roomErrors.roomTypeId}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Floor</Label>
                <Input
                  type="number"
                  value={roomForm.floor}
                  onChange={(e) => setRoomForm({ ...roomForm, floor: e.target.value })}
                  placeholder="1"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Capacity *</Label>
                <Input
                  type="number"
                  value={roomForm.capacity}
                  onChange={(e) => {
                    setRoomForm({ ...roomForm, capacity: e.target.value });
                    // Clear error when user starts typing
                    if (roomErrors.capacity) {
                      setRoomErrors({ ...roomErrors, capacity: '' });
                    }
                  }}
                  placeholder="2"
                  className={`border ${roomErrors.capacity ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                />
                {roomErrors.capacity && (
                  <p className="text-sm text-red-600 mt-1">{roomErrors.capacity}</p>
                )}
              </div>
              <div>
                <Label>Size</Label>
                <Input
                  value={roomForm.size}
                  onChange={(e) => setRoomForm({ ...roomForm, size: e.target.value })}
                  placeholder="e.g., 25 m² or 250 sq ft"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price/Night *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={roomForm.basePricePerNight}
                  onChange={(e) => {
                    setRoomForm({ ...roomForm, basePricePerNight: e.target.value });
                    // Clear error when user starts typing
                    if (roomErrors.basePricePerNight) {
                      setRoomErrors({ ...roomErrors, basePricePerNight: '' });
                    }
                  }}
                  placeholder="100.00"
                  className={`border ${roomErrors.basePricePerNight ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-gray-300'}`}
                />
                {roomErrors.basePricePerNight && (
                  <p className="text-sm text-red-600 mt-1">{roomErrors.basePricePerNight}</p>
                )}
              </div>
              <div>
                <Label>Currency *</Label>
                <Select
                  value={roomForm.currency}
                  onValueChange={(value) => setRoomForm({ ...roomForm, currency: value })}
                >
                  <SelectTrigger className="border border-gray-300">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RWF">RWF - Rwandan Franc</SelectItem>
                    <SelectItem value="USD">USD - US Dollar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={roomForm.description}
                onChange={(e) => setRoomForm({ ...roomForm, description: e.target.value })}
                placeholder="Room description... (Use semicolons or commas to separate points: Point 1; Point 2; Point 3)"
                className="border border-gray-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: Separate description points with semicolons (;) or commas (,) to display them as a list
              </p>
            </div>

            {/* Amenities Section */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Amenities</Label>
                {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager' || (user?.role as any) === 'owner') && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAmenityForm({ name: '', description: '', icon: '', category: '' });
                    setShowAmenityDialog(true);
                  }}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Create New
                </Button>
                )}
              </div>
              {amenities.length > 0 ? (
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                  <div className="flex flex-wrap gap-2">
                    {amenities.map((amenity) => (
                      <Badge
                        key={amenity.id}
                        variant={roomForm.amenityIds.includes(amenity.id) ? "default" : "outline"}
                        className={`cursor-pointer hover:bg-gray-100 flex items-center gap-1 ${roomForm.amenityIds.includes(amenity.id) ? '!text-white' : ''}`}
                        onClick={() => {
                          if (roomForm.amenityIds.includes(amenity.id)) {
                            setRoomForm({
                              ...roomForm,
                              amenityIds: roomForm.amenityIds.filter((id) => id !== amenity.id),
                            });
                          } else {
                            setRoomForm({
                              ...roomForm,
                              amenityIds: [...roomForm.amenityIds, amenity.id],
                            });
                          }
                        }}
                      >
                        {amenity.icon && <span>{amenity.icon}</span>}
                        <span>{amenity.name}</span>
                        {roomForm.amenityIds.includes(amenity.id) && (
                          <Check className="h-3 w-3" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-md p-3 text-sm text-gray-500 text-center">
                  No amenities available. Click "Create New" to add one.
                </div>
              )}
              <p className="text-xs text-gray-500">
                Click on amenities to select/deselect. {roomForm.amenityIds.length} selected.
              </p>
            </div>

            {/* Room Status */}
            <div className="flex items-center space-x-2 border-t pt-4">
              <input
                type="checkbox"
                id="roomActive"
                checked={roomForm.isActive}
                onChange={(e) => setRoomForm({ ...roomForm, isActive: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="roomActive" className="cursor-pointer">
                <TranslatedText text="Active (Room is available for booking)" />
              </Label>
            </div>

            {/* Room Images Section */}
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label htmlFor="roomImages" className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  <TranslatedText text="Room Images" />
                </Label>
                <Input
                  id="roomImages"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="cursor-pointer mt-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <TranslatedText text="Upload up to 10 images. First image will be the primary image." />
                </p>
              </div>

              {/* New Image Previews */}
              {roomImagePreviews.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TranslatedText text={`Selected Images (${roomImagePreviews.length})`} />
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {roomImagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        {index === 0 && existingImages.length === 0 && (
                          <Badge className="absolute top-1 left-1 text-xs bg-blue-600">
                            <TranslatedText text="Primary" />
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveImage(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Images (for edit mode) */}
              {selectedRoom && existingImages.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <TranslatedText text={`Current Images (${existingImages.length})`} />
                  </Label>
                  <div className="grid grid-cols-4 gap-2">
                    {existingImages.map((image) => {
                      // Construct full URL if it's a relative path (same as view details)
                      const imageUrl = image.imageUrl?.startsWith('http') 
                        ? image.imageUrl 
                        : `${apiService['baseUrl']}${image.imageUrl}`;
                      
                      return (
                      <div key={image.id} className="relative group">
                        <img
                            src={imageUrl}
                          alt={image.altText || 'Room image'}
                          className="w-full h-24 object-cover rounded-lg border cursor-pointer"
                          onClick={() => handleSetPrimaryImage(selectedRoom.id, image.id)}
                          title="Click to set as primary"
                            onError={(e) => {
                              console.error('🖼️  [EDIT] ❌ Failed to load image:', imageUrl);
                              // Remove the failed image from the existingImages state
                              setExistingImages((prev) => prev.filter(img => img.id !== image.id));
                            }}
                        />
                        {image.isPrimary && (
                          <Badge className="absolute top-1 left-1 text-xs bg-blue-600">
                            <Star className="h-3 w-3 mr-1" />
                            <TranslatedText text="Primary" />
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeleteExistingImage(selectedRoom.id, image.id)}
                          disabled={deletingImageId === image.id}
                        >
                          {deletingImageId === image.id ? (
                            <MoonLoader size="sm" color="gray" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-gray-500">
                    <TranslatedText text="Click on an image to set it as primary. Hover to delete." />
                  </p>
                </div>
              )}

              {/* Upload Button for Existing Room */}
              {selectedRoom && roomImages.length > 0 && (
                <Button
                  type="button"
                  onClick={() => uploadRoomImages(selectedRoom.id)}
                  disabled={uploadingImages}
                  className="w-full"
                  variant="outline"
                >
                  {uploadingImages ? (
                    <>
                      <MoonLoader size="sm" className="mr-2" color="teal" />
                      <TranslatedText text="Uploading..." />
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      <TranslatedText text={`Upload ${roomImages.length} Image(s)`} />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              setShowRoomDialog(false);
              resetRoomForm();
              // Auto-refresh rooms to ensure UI is up to date
              if (companyId && activeTab === 'rooms') {
                await loadRooms();
              }
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateRoom} disabled={isCreatingRoom}>
              {isCreatingRoom ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  {selectedRoom ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                selectedRoom ? 'Update Room' : 'Create Room'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Details Dialog */}
      <Dialog open={showRoomDetailsDialog} onOpenChange={async (open) => {
        setShowRoomDetailsDialog(open);
        if (!open) {
          // Reset state when dialog closes
          setSelectedRoomDetails(null);
          setLoadingRoomDetails(false);
          // Auto-refresh rooms data to show any updates
          if (companyId && activeTab === 'rooms') {
            await loadRooms();
          }
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Hotel className="h-5 w-5" />
              <TranslatedText text="Room Details" />
            </DialogTitle>
          </DialogHeader>
          
          {loadingRoomDetails ? (
            <div className="flex justify-center py-12">
              <MoonLoader size="lg" color="teal" />
            </div>
          ) : selectedRoomDetails ? (
            <div className="space-y-6">
              {/* Room Images Gallery */}
              {selectedRoomDetails.images && selectedRoomDetails.images.length > 0 && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {selectedRoomDetails.images.map((image: any, index: number) => {
                      // Construct full URL if it's a relative path
                      const imageUrl = image.imageUrl?.startsWith('http') 
                        ? image.imageUrl 
                        : `${apiService['baseUrl']}${image.imageUrl}`;
                      
                      return (
                        <div 
                          key={image.id} 
                          className="relative group cursor-pointer"
                          onClick={() => {
                            setViewRoomCurrentImageIndex(index);
                            setShowViewRoomImageGallery(true);
                          }}
                        >
                          <img
                            src={imageUrl}
                            alt={image.altText || `Room ${selectedRoomDetails.roomNumber} - Image ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-colors"
                            onError={(e) => {
                              console.error('🖼️  [UI] ❌ Failed to load image:', imageUrl);
                              // Remove the failed image from the selectedRoomDetails state
                              setSelectedRoomDetails((prev: any) => {
                                if (!prev || !prev.images) return prev;
                                const updatedImages = prev.images.filter((img: any) => img.id !== image.id);
                                return { ...prev, images: updatedImages };
                              });
                            }}
                          />
                        {image.isPrimary && (
                          <Badge className="absolute top-2 left-2 bg-blue-600">
                            <Star className="h-3 w-3 mr-1" />
                            <TranslatedText text="Primary" />
                          </Badge>
                        )}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center">
                          <Eye className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    );
                    })}
                  </div>
                </div>
              )}

              {/* No Images Message */}
              {(!selectedRoomDetails.images || selectedRoomDetails.images.length === 0) && (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <ImageIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">
                    <TranslatedText text="No images available for this room" />
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => {
                      setShowRoomDetailsDialog(false);
                      handleEditRoom(selectedRoomDetails);
                    }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    <TranslatedText text="Add Images" />
                  </Button>
                </div>
              )}

              {/* Room Information */}
              <div className="space-y-3">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Bed className="h-5 w-5" />
                  <TranslatedText text="Room Information" />
                </h3>
                {selectedRoomDetails.title && (
                  <div className="space-y-1 mb-4">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Room Title" />
                    </p>
                    <p className="font-semibold text-xl">{selectedRoomDetails.title}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Room Number" />
                    </p>
                    <p className="font-semibold text-lg">{selectedRoomDetails.roomNumber}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Room Type" />
                    </p>
                    <p className="font-medium">
                      {selectedRoomDetails.roomTypeRef?.name || selectedRoomDetails.roomType || 'N/A'}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Floor" />
                    </p>
                    <p className="font-medium">{selectedRoomDetails.floor || 'N/A'}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Capacity" />
                    </p>
                    <p className="font-medium flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {selectedRoomDetails.capacity} {selectedRoomDetails.capacity === 1 ? 'Guest' : 'Guests'}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Size" />
                    </p>
                    <p className="font-medium">{selectedRoomDetails.size || 'N/A'}</p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Price per Night" />
                    </p>
                    <p className="font-semibold text-lg text-green-600">
                      {formatCurrency(selectedRoomDetails.basePricePerNight, selectedRoomDetails.currency || 'RWF')}
                    </p>
                  </div>
                  
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">
                      <TranslatedText text="Status" />
                    </p>
                    <Badge variant={selectedRoomDetails.isActive ? 'default' : 'secondary'}>
                      {selectedRoomDetails.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedRoomDetails.description && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    <TranslatedText text="Description" />
                  </h3>
                  {(() => {
                    const description = selectedRoomDetails.description || '';
                    // Split by semicolon OR comma and filter out empty strings
                    const descriptionItems = description.split(/[;,]/).map(item => item.trim()).filter(item => item.length > 0);
                    
                    if (descriptionItems.length === 1) {
                      return (
                        <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">
                          {descriptionItems[0]}
                        </p>
                      );
                    }
                    
                    return (
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-gray-700 bg-gray-50 p-4 rounded-lg list-none">
                        {descriptionItems.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <span className="mr-2 text-[#c39d76] font-bold text-lg flex-shrink-0">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  })()}
                </div>
              )}

              {/* Amenities */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-lg flex items-center gap-2">
                    <Sparkles className="h-5 w-5" />
                    <TranslatedText text="Amenities" />
                  </h3>
                {(() => {
                  const amenities = selectedRoomDetails.amenities || [];
                  
                  if (Array.isArray(amenities) && amenities.length > 0) {
                    return (
                  <div className="flex flex-wrap gap-2">
                        {amenities.map((amenity: any, index: number) => {
                          // Handle both structures: { amenity: { name, icon } } or { name, icon }
                          const amenityData = amenity?.amenity || amenity;
                          const amenityId = amenity?.id || amenity?.amenityId || amenityData?.id || `amenity-${index}`;
                          const amenityName = amenityData?.name || amenity?.name;
                          const amenityIcon = amenityData?.icon || amenity?.icon;
                          
                          if (!amenityName) return null;
                          
                          return (
                            <Badge key={amenityId} variant="default" className="flex items-center gap-1 px-3 py-1">
                              {amenityIcon && <span>{amenityIcon}</span>}
                              <span>{amenityName}</span>
                      </Badge>
                          );
                        })}
                  </div>
                    );
                  } else {
                    return <p className="text-gray-500 text-sm">No amenities assigned to this room.</p>;
                  }
                })()}
                </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={() => {
                    const roomToEdit = selectedRoomDetails;
                    setShowRoomDetailsDialog(false);
                    setSelectedRoomDetails(null);
                    setLoadingRoomDetails(false);
                    // Small delay to ensure dialog closes before opening edit dialog
                    setTimeout(() => {
                      handleEditRoom(roomToEdit);
                    }, 100);
                  }}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  <TranslatedText text="Edit Room" />
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    setShowRoomDetailsDialog(false);
                    setSelectedRoomDetails(null);
                    setLoadingRoomDetails(false);
                    // Auto-refresh rooms data to show any updates
                    if (companyId && activeTab === 'rooms') {
                      await loadRooms();
                    }
                  }}
                  className="flex-1"
                >
                  <TranslatedText text="Close" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">
                <TranslatedText text="No room details available" />
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-screen Image Gallery Modal for View Room */}
      {showViewRoomImageGallery && selectedRoomDetails && selectedRoomDetails.images && selectedRoomDetails.images.length > 0 && (
        <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
          <button
            onClick={() => setShowViewRoomImageGallery(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
          >
            <X size={32} />
          </button>

          <button
            onClick={prevViewRoomImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronLeft size={32} />
          </button>

          <button
            onClick={nextViewRoomImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-all z-10"
          >
            <ChevronRight size={32} />
          </button>

          <img
            src={getViewRoomImageUrl(selectedRoomDetails.images[viewRoomCurrentImageIndex])}
            alt={`${selectedRoomDetails.roomNumber} - ${viewRoomCurrentImageIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
            {viewRoomCurrentImageIndex + 1} / {selectedRoomDetails.images.length}
          </div>
        </div>
      )}

      {/* Create Reservation Dialog */}
      <Dialog 
        open={showReservationDialog} 
        onOpenChange={(open) => {
        setShowReservationDialog(open);
        // Load promotions, conference rooms, and equipment when dialog opens
        if (open && companyId) {
          loadPromotions();
          loadConferenceRooms();
          loadEquipment();
          // Auto-calculate amount if room and dates are already selected
          setTimeout(() => {
            if (reservationForm.roomId && reservationForm.checkInDate && reservationForm.checkOutDate) {
              calculateReservationAmount();
            }
          }, 200);
        }
      }}
        aria-labelledby="reservation-dialog-title"
        aria-describedby="reservation-dialog-description"
      >
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle id="reservation-dialog-title">New Reservation</DialogTitle>
            <DialogDescription id="reservation-dialog-description">Create a new reservation or booking</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 pr-2">
            {/* Booking Type Selector */}
            <div>
              <Label>Booking Type *</Label>
              <Select
                value={reservationForm.bookingType}
                onValueChange={(value) => {
                  const newBookingType = value as typeof reservationForm.bookingType;
                  setReservationForm({
                    ...reservationForm,
                    bookingType: newBookingType,
                    roomId: newBookingType !== 'ROOM' ? '' : reservationForm.roomId, // Clear roomId for non-room bookings
                    bookableItemId: newBookingType === 'ROOM' ? '' : reservationForm.bookableItemId, // Clear bookableItemId for room bookings
                    ratePlanId: newBookingType !== 'ROOM' ? '' : reservationForm.ratePlanId, // Clear rate plan for non-room bookings
                    promotionId: newBookingType !== 'ROOM' ? '' : reservationForm.promotionId, // Clear promotions for non-room bookings
                    promotionIds: newBookingType !== 'ROOM' ? [] : reservationForm.promotionIds,
                  });
                  // Recalculate amount if switching to/from room booking
                  if (reservationForm.checkInDate && reservationForm.checkOutDate) {
                    setTimeout(() => {
                      if (newBookingType === 'ROOM' && reservationForm.roomId) {
                        calculateReservationAmount();
                      } else {
                        setAmountBreakdown(null);
                      }
                    }, 100);
                  }
                }}
              >
                <SelectTrigger className="border border-gray-300 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ROOM">Hotel Room</SelectItem>
                  <SelectItem value="CONFERENCE_ROOM">Conference Room</SelectItem>
                  <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                  <SelectItem value="SERVICE">Service</SelectItem>
                  <SelectItem value="TABLE">Table</SelectItem>
                  <SelectItem value="EVENT_SPACE">Event Space</SelectItem>
                  <SelectItem value="TRANSPORT">Transportation</SelectItem>
                  <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Room Selection - Only for ROOM bookings */}
              {reservationForm.bookingType === 'ROOM' && (
                <div>
                  <Label>Room *</Label>
                <SearchableSelect
                  options={rooms.filter(r => r.isActive).map((room) => {
                    const bookedDates = getRoomBookedDates(room.id);
                    const isReserved = isRoomReserved(room.id);
                    let label = `Room ${room.roomNumber}`;
                    if (isReserved && bookedDates) {
                      label += ` (Reserved: ${bookedDates})`;
                    } else if (isReserved) {
                      label += ` (Reserved)`;
                    }
                    return {
                      value: room.id,
                      label,
                    };
                  })}
                  value={reservationForm.roomId}
                  onValueChange={(value) => {
                    setReservationForm({ ...reservationForm, roomId: value });
                    if (reservationErrors.roomId) setReservationErrors({ ...reservationErrors, roomId: '' });
                    // Auto-calculate amount if dates are already set
                    if (reservationForm.checkInDate && reservationForm.checkOutDate) {
                      setTimeout(() => {
                        calculateReservationAmount();
                      }, 100);
                    }
                  }}
                  placeholder="Select room"
                  searchPlaceholder="Search rooms..."
                  emptyText="No available rooms found."
                />
                {reservationErrors.roomId && <p className="text-sm text-red-500 mt-1">{reservationErrors.roomId}</p>}
                </div>
              )}
              
              {/* Bookable Item Selection - For non-room bookings */}
              {reservationForm.bookingType !== 'ROOM' && (
                <div>
                  <Label>Item *</Label>
                  {reservationForm.bookingType === 'CONFERENCE_ROOM' ? (
                    <SearchableSelect
                      options={conferenceRooms
                        .filter(cr => cr.isActive)
                        .map((cr) => ({
                          value: cr.id,
                          label: `${cr.name}${cr.roomNumber ? ` (${cr.roomNumber})` : ''}${cr.capacity ? ` - Capacity: ${cr.capacity}` : ''}`,
                        }))}
                      value={reservationForm.bookableItemId}
                      onValueChange={(value) => {
                        setReservationForm({
                          ...reservationForm,
                          bookableItemId: value,
                          bookableItemType: 'CONFERENCE_ROOM',
                        });
                        if (reservationErrors.bookableItemId) setReservationErrors({ ...reservationErrors, bookableItemId: '' });
                      }}
                      placeholder="Select conference room"
                      searchPlaceholder="Search conference rooms..."
                      emptyText="No conference rooms found. Create one first."
                    />
                  ) : reservationForm.bookingType === 'EQUIPMENT' ? (
                    <SearchableSelect
                      options={equipment
                        .filter(eq => eq.isActive)
                        .map((eq) => ({
                          value: eq.id,
                          label: `${eq.name}${eq.equipmentNumber ? ` (${eq.equipmentNumber})` : ''}${eq.category ? ` - ${eq.category}` : ''}${eq.quantity > 1 ? ` (Qty: ${eq.quantity})` : ''}`,
                        }))}
                      value={reservationForm.bookableItemId}
                      onValueChange={(value) => {
                        setReservationForm({
                          ...reservationForm,
                          bookableItemId: value,
                          bookableItemType: 'EQUIPMENT',
                        });
                        if (reservationErrors.bookableItemId) setReservationErrors({ ...reservationErrors, bookableItemId: '' });
                      }}
                      placeholder="Select equipment"
                      searchPlaceholder="Search equipment..."
                      emptyText="No equipment found. Create one first."
                    />
                  ) : (
                    <Input
                      value={reservationForm.bookableItemId}
                      onChange={(e) => {
                        setReservationForm({
                          ...reservationForm,
                          bookableItemId: e.target.value,
                          bookableItemType: reservationForm.bookingType,
                        });
                        if (reservationErrors.bookableItemId) setReservationErrors({ ...reservationErrors, bookableItemId: '' });
                      }}
                      placeholder={`Enter ${reservationForm.bookingType.toLowerCase().replace('_', ' ')} ID or name`}
                      className={`border ${reservationErrors.bookableItemId ? 'border-red-500' : 'border-gray-300'}`}
                    />
                  )}
                  {reservationErrors.bookableItemId && <p className="text-sm text-red-500 mt-1">{reservationErrors.bookableItemId}</p>}
                  {reservationForm.bookingType !== 'CONFERENCE_ROOM' && reservationForm.bookingType !== 'EQUIPMENT' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter the ID or name of the {reservationForm.bookingType.toLowerCase().replace('_', ' ')} to book
                    </p>
                  )}
                </div>
              )}
              
              <div className={reservationForm.bookingType === 'ROOM' ? '' : 'col-span-2'}>
                <div className="flex items-center justify-between mb-2">
                  <Label>Guest *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setGuestForm({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', state: '', postalCode: '', country: '', idType: '', passportNumber: '', idNumber: '' });
                      setShowGuestDialog(true);
                    }}
                    className="h-7 text-xs"
                  >
                    <UserPlus className="h-3 w-3 mr-1" />
                    Add New Guest
                  </Button>
                </div>
                <SearchableSelect
                  options={guests.map((guest) => ({
                    value: guest.id,
                    label: `${guest.firstName} ${guest.lastName}`,
                  }))}
                  value={reservationForm.guestId}
                  onValueChange={(value) => {
                    setReservationForm({ ...reservationForm, guestId: value });
                    if (reservationErrors.guestId) setReservationErrors({ ...reservationErrors, guestId: '' });
                  }}
                  placeholder="Select guest"
                  searchPlaceholder="Search guests..."
                  emptyText="No guests found."
                />
                {reservationErrors.guestId && <p className="text-sm text-red-500 mt-1">{reservationErrors.guestId}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Check-In Date *</Label>
                <Input
                  type="date"
                  value={reservationForm.checkInDate}
                  onChange={(e) => {
                    setReservationForm({ ...reservationForm, checkInDate: e.target.value });
                    if (reservationErrors.checkInDate) setReservationErrors({ ...reservationErrors, checkInDate: '' });
                    if (reservationErrors.checkOutDate) setReservationErrors({ ...reservationErrors, checkOutDate: '' });
                    // Recalculate amount when dates change
                    if (reservationForm.roomId && e.target.value && reservationForm.checkOutDate) {
                      setTimeout(() => calculateReservationAmount(), 100);
                    }
                  }}
                  className={`border ${reservationErrors.checkInDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {reservationErrors.checkInDate && <p className="text-sm text-red-500 mt-1">{reservationErrors.checkInDate}</p>}
              </div>
              <div>
                <Label>Check-Out Date *</Label>
                <Input
                  type="date"
                  value={reservationForm.checkOutDate}
                  onChange={(e) => {
                    setReservationForm({ ...reservationForm, checkOutDate: e.target.value });
                    if (reservationErrors.checkOutDate) setReservationErrors({ ...reservationErrors, checkOutDate: '' });
                    // Recalculate amount when dates change
                    if (reservationForm.roomId && reservationForm.checkInDate && e.target.value) {
                      setTimeout(() => calculateReservationAmount(), 100);
                    }
                  }}
                  className={`border ${reservationErrors.checkOutDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {reservationErrors.checkOutDate && <p className="text-sm text-red-500 mt-1">{reservationErrors.checkOutDate}</p>}
              </div>
            </div>
            
            {/* Rate Plan - Only for ROOM bookings - Hidden */}
            {reservationForm.bookingType === 'ROOM' && (
              <div className="hidden">
                <Label><TranslatedText text="Rate Plan" /> <span className="text-xs text-muted-foreground">(Optional)</span></Label>
                <Select
                  value={reservationForm.ratePlanId || 'none'}
                  onValueChange={(value) => {
                    const newRatePlanId = value === 'none' ? '' : value;
                    setReservationForm({ ...reservationForm, ratePlanId: newRatePlanId });
                    // Recalculate amount when rate plan changes
                    if (reservationForm.roomId && reservationForm.checkInDate && reservationForm.checkOutDate) {
                      setTimeout(() => calculateReservationAmount(), 100);
                    }
                  }}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue placeholder="Select rate plan (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none"><TranslatedText text="No Rate Plan" /></SelectItem>
                    {ratePlans.filter(rp => rp.isActive).map((ratePlan) => (
                      <SelectItem key={ratePlan.id} value={ratePlan.id}>
                        {ratePlan.name} - {formatCurrency(ratePlan.price)}/night
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Promotions - Only for ROOM bookings - Hide if no active promotions */}
            {reservationForm.bookingType === 'ROOM' && promotions.filter(p => p.isActive && new Date(p.validTo) >= new Date() && new Date(p.validFrom) <= new Date()).length > 0 && (
              <div>
                <Label><TranslatedText text="Promotions" /> <span className="text-xs text-muted-foreground">(Optional - Select multiple)</span></Label>
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  <div className="space-y-2">
                    {promotions
                      .filter(p => p.isActive && new Date(p.validTo) >= new Date() && new Date(p.validFrom) <= new Date())
                      .map((promo) => (
                        <label key={promo.id} className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={reservationForm.promotionIds.includes(promo.id)}
                            onChange={(e) => {
                              const newPromotionIds = e.target.checked
                                ? [...reservationForm.promotionIds, promo.id]
                                : reservationForm.promotionIds.filter(id => id !== promo.id);
                              setReservationForm({ 
                                ...reservationForm, 
                                promotionIds: newPromotionIds,
                                promotionId: newPromotionIds.length > 0 ? newPromotionIds[0] : '' // Keep first for backward compatibility
                              });
                              // Recalculate amount when promotion changes
                              if (reservationForm.roomId && reservationForm.checkInDate && reservationForm.checkOutDate) {
                                setTimeout(() => calculateReservationAmount(), 100);
                              }
                            }}
                            className="rounded border-gray-300"
                          />
                          <span className="text-sm flex-1">
                            <span className="font-medium">{promo.name}</span>
                            {promo.code && <span className="text-muted-foreground"> ({promo.code})</span>}
                            <span className="text-muted-foreground ml-2">
                              - {promo.discountType === 'PERCENTAGE' ? `${promo.discountValue}%` : promo.discountType === 'FIXED_AMOUNT' ? formatCurrency(promo.discountValue) : `${promo.discountValue} free night(s)`}
                            </span>
                          </span>
                        </label>
                      ))}
                  </div>
              </div>
              {reservationForm.promotionIds.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {reservationForm.promotionIds.length} <TranslatedText text="promotion(s) selected" />
                </p>
              )}
              </div>
            )}
            
            {/* Amount Breakdown - Only for ROOM bookings */}
            {reservationForm.bookingType === 'ROOM' && amountBreakdown && (
              <div className="border rounded-lg p-4 bg-muted/50">
                <Label className="text-sm font-semibold mb-3"><TranslatedText text="Amount Breakdown" /></Label>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground"><TranslatedText text="Room Price" /> ({amountBreakdown.numberOfNights} <TranslatedText text="nights" /> @ {formatCurrency(amountBreakdown.roomPricePerNight, amountBreakdown.currency)}/night):</span>
                    <span className="font-medium">{formatCurrency(amountBreakdown.roomPricePerNight * amountBreakdown.numberOfNights, amountBreakdown.currency)}</span>
                  </div>
                  {amountBreakdown.ratePlanAdjustment !== 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground"><TranslatedText text="Rate Plan Adjustment" />:</span>
                      <span className={`font-medium ${amountBreakdown.ratePlanAdjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {amountBreakdown.ratePlanAdjustment > 0 ? '+' : ''}{formatCurrency(amountBreakdown.ratePlanAdjustment, amountBreakdown.currency)}
                      </span>
                    </div>
                  )}
                  {amountBreakdown.promotionDiscounts.length > 0 && (
                    <>
                      {amountBreakdown.promotionDiscounts.map((promoDiscount, index) => (
                        <div key={promoDiscount.id} className="flex justify-between">
                          <span className="text-muted-foreground">
                            {index === 0 ? <TranslatedText text="Promotion Discounts" /> : ''} {promoDiscount.name}:
                          </span>
                          <span className="font-medium text-green-600">-{formatCurrency(promoDiscount.discount, amountBreakdown.currency)}</span>
                        </div>
                      ))}
                      {amountBreakdown.promotionDiscounts.length > 1 && (
                        <div className="flex justify-between border-t pt-1 mt-1">
                          <span className="text-muted-foreground font-medium"><TranslatedText text="Total Promotion Discount" />:</span>
                          <span className="font-medium text-green-600">-{formatCurrency(amountBreakdown.promotionDiscount, amountBreakdown.currency)}</span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="border-t pt-2 mt-2 flex justify-between">
                    <span className="font-semibold"><TranslatedText text="Total Amount" />:</span>
                    <span className="font-semibold text-lg">{formatCurrency(amountBreakdown.finalAmount, amountBreakdown.currency)}</span>
                  </div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Number of Guests" /> *</Label>
                <Input
                  type="number"
                  min="1"
                  max={reservationForm.roomId ? rooms.find(r => r.id === reservationForm.roomId)?.capacity : undefined}
                  value={reservationForm.numberOfGuests}
                  onChange={(e) => {
                    const selectedRoom = rooms.find(r => r.id === reservationForm.roomId);
                    const maxGuests = selectedRoom?.capacity;
                    const value = e.target.value;
                    
                    // Prevent entering more than room capacity
                    if (maxGuests && parseInt(value) > maxGuests) {
                      setReservationForm({ ...reservationForm, numberOfGuests: maxGuests.toString() });
                    } else {
                      setReservationForm({ ...reservationForm, numberOfGuests: value });
                    }
                    if (reservationErrors.numberOfGuests) setReservationErrors({ ...reservationErrors, numberOfGuests: '' });
                  }}
                  className={`border ${reservationErrors.numberOfGuests ? 'border-red-500' : 'border-gray-300'}`}
                />
                {reservationErrors.numberOfGuests && <p className="text-sm text-red-500 mt-1">{reservationErrors.numberOfGuests}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Total Amount" /> *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={reservationForm.totalAmount}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setReservationForm({ ...reservationForm, totalAmount: newValue });
                    if (reservationErrors.totalAmount) setReservationErrors({ ...reservationErrors, totalAmount: '' });
                    // Update original amount if manually edited
                    setOriginalTotalAmount(newValue);
                    // Clear breakdown when manually edited
                    setAmountBreakdown(null);
                  }}
                  className={`border ${reservationErrors.totalAmount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {reservationErrors.totalAmount && <p className="text-sm text-red-500 mt-1">{reservationErrors.totalAmount}</p>}
                {amountBreakdown && !reservationErrors.totalAmount && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <TranslatedText text="Auto-calculated" /> - <TranslatedText text="You can edit this amount" />
                  </p>
                )}
              </div>
              {/* Deposit Amount - Hidden */}
              <div className="hidden">
                <Label><TranslatedText text="Deposit Amount" /></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={reservationForm.depositAmount}
                  onChange={(e) => setReservationForm({ ...reservationForm, depositAmount: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label>Special Requests</Label>
              <Textarea
                value={reservationForm.specialRequests}
                onChange={(e) => setReservationForm({ ...reservationForm, specialRequests: e.target.value })}
                placeholder="Any special requests..."
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter className="!flex-row !justify-between">
            <Button variant="outline" onClick={() => setShowReservationDialog(false)} className="border-2 border-gray-300 hover:border-gray-400">
              Cancel
            </Button>
            <Button onClick={handleCreateReservation} disabled={isCreatingReservation}>
              {isCreatingReservation ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Creating...
                </>
              ) : (
                'Create Reservation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Guest Dialog */}
      <Dialog 
        open={showGuestDialog} 
        onOpenChange={(open) => {
          setShowGuestDialog(open);
          if (!open) resetGuestForm();
        }}
        aria-labelledby="guest-dialog-title"
        aria-describedby="guest-dialog-description"
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle id="guest-dialog-title">{selectedRoom ? 'Edit Guest' : 'Add New Guest'}</DialogTitle>
            <DialogDescription id="guest-dialog-description">{selectedRoom ? 'Update guest information' : 'Create a new guest profile'}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>First Name *</Label>
                <Input
                  value={guestForm.firstName}
                  onChange={(e) => setGuestForm({ ...guestForm, firstName: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input
                  value={guestForm.lastName}
                  onChange={(e) => setGuestForm({ ...guestForm, lastName: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={guestForm.email}
                  onChange={(e) => {
                    setGuestForm({ ...guestForm, email: e.target.value });
                    // Clear email error when user types a valid email
                    if (e.target.value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)) {
                      setGuestErrors({ ...guestErrors, email: '' });
                    }
                  }}
                  className={`border ${guestErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                />
                {guestErrors.email && (
                  <p className="text-sm text-red-500 mt-1">{guestErrors.email}</p>
                )}
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={guestForm.phone}
                  onChange={(e) => setGuestForm({ ...guestForm, phone: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input
                value={guestForm.address}
                onChange={(e) => setGuestForm({ ...guestForm, address: e.target.value })}
                className="border border-gray-300"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={guestForm.city}
                  onChange={(e) => setGuestForm({ ...guestForm, city: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={guestForm.state}
                  onChange={(e) => setGuestForm({ ...guestForm, state: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Postal Code</Label>
                <Input
                  value={guestForm.postalCode}
                  onChange={(e) => setGuestForm({ ...guestForm, postalCode: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label>Country *</Label>
              <SearchableSelect
                options={COUNTRIES}
                value={guestForm.country}
                onValueChange={(value) => {
                  setGuestForm({ ...guestForm, country: value });
                  if (guestErrors.country) {
                    const { country, ...rest } = guestErrors;
                    setGuestErrors(rest);
                  }
                }}
                placeholder="Select country..."
                searchPlaceholder="Search countries..."
                emptyText="No countries found."
                className={`w-full ${guestErrors.country ? 'border-red-500' : ''}`}
              />
              {guestErrors.country && <p className="text-sm text-red-500 mt-1">{guestErrors.country}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>ID Type *</Label>
                <Select
                  value={guestForm.idType}
                  onValueChange={(value) => {
                    // Clear the other field when switching types
                    if (value === 'passport') {
                      setGuestForm({ ...guestForm, idType: value, idNumber: '' });
                    } else if (value === 'national_id') {
                      setGuestForm({ ...guestForm, idType: value, passportNumber: '' });
                    } else {
                      setGuestForm({ ...guestForm, idType: value, passportNumber: '', idNumber: '' });
                    }
                    // Clear error when value changes
                    if (guestErrors.idType) {
                      const { idType, ...rest } = guestErrors;
                      setGuestErrors(rest);
                    }
                  }}
                >
                  <SelectTrigger className={`border ${guestErrors.idType ? 'border-red-500' : 'border-gray-300'}`}>
                    <SelectValue placeholder="Select ID type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="passport">Passport</SelectItem>
                    <SelectItem value="national_id">National ID</SelectItem>
                  </SelectContent>
                </Select>
                {guestErrors.idType && <p className="text-sm text-red-500 mt-1">{guestErrors.idType}</p>}
              </div>
              <div>
                <Label>{guestForm.idType === 'passport' ? 'Passport Number *' : guestForm.idType === 'national_id' ? 'National ID Number *' : 'ID Number *'}</Label>
                <Input
                  value={guestForm.idType === 'passport' ? guestForm.passportNumber : guestForm.idNumber}
                  onChange={(e) => {
                    if (guestForm.idType === 'passport') {
                      setGuestForm({ ...guestForm, passportNumber: e.target.value });
                      if (guestErrors.passportNumber) {
                        const { passportNumber, ...rest } = guestErrors;
                        setGuestErrors(rest);
                      }
                    } else if (guestForm.idType === 'national_id') {
                      setGuestForm({ ...guestForm, idNumber: e.target.value });
                      if (guestErrors.idNumber) {
                        const { idNumber, ...rest } = guestErrors;
                        setGuestErrors(rest);
                      }
                    }
                  }}
                  placeholder={guestForm.idType === 'passport' ? 'Enter passport number' : guestForm.idType === 'national_id' ? 'Enter national ID number' : 'Select ID type first'}
                  className={`border ${(guestErrors.passportNumber || guestErrors.idNumber) ? 'border-red-500' : 'border-gray-300'}`}
                  disabled={!guestForm.idType}
                  required={!!guestForm.idType}
                />
                {guestErrors.passportNumber && <p className="text-sm text-red-500 mt-1">{guestErrors.passportNumber}</p>}
                {guestErrors.idNumber && <p className="text-sm text-red-500 mt-1">{guestErrors.idNumber}</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGuestDialog(false);
              resetGuestForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateGuest} disabled={isCreatingGuest}>
              {isCreatingGuest ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  {selectedRoom ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                selectedRoom ? 'Update Guest' : 'Create Guest'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      <Dialog open={showServiceDialog} onOpenChange={setShowServiceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Service</DialogTitle>
            <DialogDescription>Add a service charge to a reservation</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Reservation *</Label>
              <SearchableSelect
                options={reservations.map((res) => ({
                  value: res.id,
                  label: `${res.reservationNumber} - ${res.guest?.firstName || ''} ${res.guest?.lastName || ''}`.trim(),
                }))}
                value={serviceForm.reservationId}
                onValueChange={(value) => setServiceForm({ ...serviceForm, reservationId: value })}
                placeholder="Select reservation"
                searchPlaceholder="Search reservations..."
                emptyText="No reservations found."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Type *</Label>
                <SearchableSelect
                  options={SERVICE_TYPES}
                  value={serviceForm.serviceType}
                  onValueChange={(value) => setServiceForm({ ...serviceForm, serviceType: value })}
                  placeholder="Select service type..."
                  searchPlaceholder="Search service types..."
                  emptyText="No service types found."
                  className="w-full"
                />
              </div>
              <div>
                <Label>Service Name *</Label>
                <Input
                  value={serviceForm.serviceName}
                  onChange={(e) => setServiceForm({ ...serviceForm, serviceName: e.target.value })}
                  placeholder="Service name"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={serviceForm.quantity}
                  onChange={(e) => setServiceForm({ ...serviceForm, quantity: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Unit Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={serviceForm.unitPrice}
                  onChange={(e) => setServiceForm({ ...serviceForm, unitPrice: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={serviceForm.description}
                onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              setIsCreatingService(true);
              try {
                await apiService.createHotelService({
                  companyId,
                  ...serviceForm,
                  quantity: parseInt(serviceForm.quantity),
                  unitPrice: parseFloat(serviceForm.unitPrice),
                });
                toast({ title: 'Success', description: 'Service added successfully' });
                setShowServiceDialog(false);
                setServiceForm({
                  reservationId: '',
                  serviceType: '',
                  serviceName: '',
                  description: '',
                  quantity: '1',
                  unitPrice: '',
                  notes: '',
                });
                loadServices();
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || 'Failed to add service', variant: 'destructive' });
              } finally {
                setIsCreatingService(false);
              }
            }} disabled={isCreatingService}>
              {isCreatingService ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Adding...
                </>
              ) : (
                'Add Service'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        setShowPaymentDialog(open);
        if (open) {
          // Auto-generate transaction ID when dialog opens
          const autoGeneratedId = `TXN-${Date.now()}-${Math.floor(Math.random() * 100)}`;
          setPaymentForm(prev => ({
            ...prev,
            transactionId: prev.transactionId || autoGeneratedId
          }));
        } else {
          // Reset form when closing
          setPaymentMode('reservation');
          setPaymentForm({
            reservationId: '',
            groupBookingId: '',
            guestId: '',
            paymentType: 'full_payment',
            amount: '',
            paymentMethod: 'cash',
            transactionId: '',
            notes: '',
          });
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>Record a payment for a reservation or group booking</DialogDescription>
          </DialogHeader>
          
          {/* Payment Mode Tabs */}
          <Tabs value={paymentMode} onValueChange={(value) => {
            setPaymentMode(value as 'reservation' | 'group');
            setPaymentForm({ ...paymentForm, reservationId: '', groupBookingId: '', guestId: '', amount: '' });
          }} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="reservation">
                <TranslatedText text="Single Reservation" />
              </TabsTrigger>
              <TabsTrigger value="group">
                <TranslatedText text="Group Booking" />
              </TabsTrigger>
            </TabsList>

            {/* Single Reservation Payment */}
            <TabsContent value="reservation" className="space-y-4 mt-4">
            <div>
                <Label>Unpaid Reservation *</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  <TranslatedText text="Select from unpaid reservations only" />
                </p>
              <SearchableSelect
                  options={reservations
                    .filter((res) => 
                      res.paymentStatus !== 'PAID' && // Only show unpaid reservations
                      res.status !== 'CANCELLED' // Exclude cancelled reservations
                    )
                    .map((res) => {
                  const guestName = res.guest 
                    ? `${res.guest.firstName || ''} ${res.guest.lastName || ''}`.trim()
                    : 'No Guest';
                      
                      // Calculate balance with currency conversion
                      const roomCurrency = res.room?.currency || 'RWF';
                      let balance = 0;
                      
                      if (res.folio?.items && res.folio.items.length > 0) {
                        const calculatedTotal = res.folio.items.reduce((sum: number, item: any) => {
                          // Skip payment items
                          if (item.name?.toLowerCase().includes('payment')) {
                            return sum;
                          }
                          
                          let itemCurrency = roomCurrency;
                          
                          // Detect currency from POS items
                          if (item.name?.includes('POS Invoice') && item.description) {
                            const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                            if (currencyMatch) {
                              itemCurrency = currencyMatch[1];
                            }
                          }
                          
                          const itemTotal = Number(item.total);
                          const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                          return sum + convertedTotal;
                        }, 0);
                        
                        // Calculate payments
                        const totalPaid = res.payments?.reduce((sum: number, payment: any) => {
                          if (payment.status === 'COMPLETED') {
                            return sum + Number(payment.amount || 0);
                          }
                          return sum;
                        }, 0) || 0;
                        
                        balance = calculatedTotal - totalPaid;
                      } else {
                        balance = res.folio?.balance || res.totalAmount || 0;
                      }
                      
                      const statusBadge = res.paymentStatus === 'PARTIAL' ? ' (Partial)' : ' (Unpaid)';
                  return {
                    value: res.id,
                        label: `${res.reservationNumber} - ${guestName}${statusBadge} - ${formatCurrency(balance, roomCurrency)}`,
                  };
                })}
                value={paymentForm.reservationId || undefined}
                onValueChange={(value) => {
                  const selectedReservation = reservations.find(r => r.id === value);
                  if (selectedReservation) {
                    // Auto-fill guest and amount
                    const guestName = selectedReservation.guest 
                      ? `${selectedReservation.guest.firstName || ''} ${selectedReservation.guest.lastName || ''}`.trim()
                      : '';
                    
                    // Calculate amount based on reservation balance or total
                    const amount = selectedReservation.folio?.balance 
                      ? selectedReservation.folio.balance.toString()
                      : selectedReservation.totalAmount 
                        ? selectedReservation.totalAmount.toString()
                        : '';
                    
                    // Auto-generate transaction ID if not already set
                    const transactionId = paymentForm.transactionId || `TXN-${Date.now()}-${Math.floor(Math.random() * 100)}`;
                    
                    setPaymentForm({ 
                      ...paymentForm, 
                      reservationId: value,
                        groupBookingId: '', // Clear group booking when selecting reservation
                      guestId: selectedReservation.guestId || '',
                      amount: amount,
                      transactionId: transactionId
                    });
                  } else {
                      setPaymentForm({ ...paymentForm, reservationId: value || '', groupBookingId: '' });
                    }
                  }}
                  placeholder="Select unpaid reservation..."
                  searchPlaceholder="Search unpaid reservations..."
                  emptyText="No unpaid reservations found."
                />
                {reservations.filter((res) => 
                  res.paymentStatus !== 'PAID' && res.status !== 'CANCELLED'
                ).length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <TranslatedText text="All reservations are paid or cancelled" />
                  </p>
                )}
            </div>
            </TabsContent>

            {/* Group Booking Payment */}
            <TabsContent value="group" className="space-y-4 mt-4">
              <div>
                <Label>Group Booking *</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  <TranslatedText text="Select a group booking to view and pay for its unpaid reservations" />
                </p>
                <SearchableSelect
                  options={groupBookings.map((group) => {
                    const unpaidCount = group.linkedReservations?.filter((r: any) => r.paymentStatus !== 'PAID').length || 0;
                    const totalUnpaid = group.linkedReservations?.reduce((sum: number, r: any) => {
                      if (r.paymentStatus !== 'PAID') {
                        return sum + parseFloat(r.folio?.balance || r.totalAmount || 0);
                      }
                      return sum;
                    }, 0) || 0;
                    return {
                      value: group.id,
                      label: `${group.groupName} (${unpaidCount} unpaid reservations - ${formatCurrency(totalUnpaid)})`,
                    };
                  })}
                  value={paymentForm.groupBookingId || undefined}
                  onValueChange={(value) => {
                    const selectedGroup = groupBookings.find(g => g.id === value);
                    if (selectedGroup) {
                      const unpaidReservations = selectedGroup.linkedReservations?.filter((r: any) => r.paymentStatus !== 'PAID') || [];
                      const totalUnpaid = unpaidReservations.reduce((sum: number, r: any) => {
                        return sum + parseFloat(r.folio?.balance || r.totalAmount || 0);
                      }, 0);
                      
                      setPaymentForm({ 
                        ...paymentForm, 
                        groupBookingId: value,
                        reservationId: '', // Clear single reservation when selecting group
                        guestId: selectedGroup.contactPersonId || '',
                        amount: totalUnpaid > 0 ? totalUnpaid.toString() : '',
                        transactionId: paymentForm.transactionId || `TXN-${Date.now()}-${Math.floor(Math.random() * 100)}`
                      });
                    } else {
                      setPaymentForm({ ...paymentForm, groupBookingId: value || '', reservationId: '' });
                    }
                  }}
                  placeholder="Select group booking..."
                  searchPlaceholder="Search group bookings..."
                  emptyText="No group bookings found."
                />
              </div>
              
              {/* Show unpaid reservations for selected group */}
              {paymentForm.groupBookingId && (() => {
                const selectedGroup = groupBookings.find(g => g.id === paymentForm.groupBookingId);
                const unpaidReservations = selectedGroup?.linkedReservations?.filter((r: any) => r.paymentStatus !== 'PAID') || [];
                
                if (unpaidReservations.length > 0) {
                  return (
                    <div className="border rounded-lg p-4 bg-muted/50">
                      <Label className="text-sm font-semibold mb-3 block">
                        <TranslatedText text="Unpaid Reservations in Group" /> ({unpaidReservations.length})
                      </Label>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {unpaidReservations.map((reservation: any) => {
                          const roomCurrency = reservation.room?.currency || 'RWF';
                          
                          // Calculate balance from converted folio items
                          let balance = 0;
                          if (reservation.folio?.items && reservation.folio.items.length > 0) {
                            const calculatedTotal = reservation.folio.items.reduce((sum: number, item: any) => {
                              // Skip payment items
                              if (item.name?.toLowerCase().includes('payment')) {
                                return sum;
                              }
                              
                              let itemCurrency = roomCurrency;
                              
                              // Detect currency from POS items
                              if (item.name?.includes('POS Invoice') && item.description) {
                                const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                if (currencyMatch) {
                                  itemCurrency = currencyMatch[1];
                                }
                              }
                              
                              const itemTotal = Number(item.total);
                              const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                              return sum + convertedTotal;
                            }, 0);
                            
                            // Calculate payments
                            const totalPaid = reservation.payments?.reduce((sum: number, payment: any) => {
                              if (payment.status === 'COMPLETED') {
                                return sum + Number(payment.amount || 0);
                              }
                              return sum;
                            }, 0) || 0;
                            
                            balance = calculatedTotal - totalPaid;
                          } else {
                            balance = reservation.folio?.balance || reservation.totalAmount || 0;
                          }
                          
                          return (
                            <div key={reservation.id} className="flex items-center justify-between text-sm p-2 bg-background rounded border">
                              <div>
                                <span className="font-medium">{reservation.reservationNumber}</span>
                                <span className="text-muted-foreground ml-2">
                                  - {reservation.room?.roomNumber} - {reservation.guest?.firstName} {reservation.guest?.lastName}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{formatCurrencyWithApprox(balance, roomCurrency)}</span>
                                <Badge variant={reservation.paymentStatus === 'PARTIAL' ? 'secondary' : 'outline'}>
                                  {reservation.paymentStatus}
                                </Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold"><TranslatedText text="Total Unpaid:" /></span>
                          <span className="text-lg font-bold">
                            {formatCurrency(unpaidReservations.reduce((sum: number, r: any) => 
                              sum + parseFloat(r.folio?.balance || r.totalAmount || 0), 0
                            ))}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="border rounded-lg p-4 bg-muted/50">
                      <p className="text-sm text-muted-foreground text-center">
                        <TranslatedText text="All reservations in this group are paid" />
                      </p>
                    </div>
                  );
                }
              })()}
            </TabsContent>
          </Tabs>

          <div className="grid gap-4 py-4">
            {paymentForm.guestId && (
              <div>
                <Label>Guest</Label>
                <Input
                  value={(() => {
                    const guest = guests.find(g => g.id === paymentForm.guestId);
                    return guest ? `${guest.firstName} ${guest.lastName}` : '';
                  })()}
                  disabled
                  className="border border-gray-300 bg-muted"
                />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Type *</Label>
                <Select
                  value={paymentForm.paymentType}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, paymentType: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="full_payment">Full Payment</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                    <SelectItem value="additional_charge">Additional Charge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Method *</Label>
                <Select
                  value={paymentForm.paymentMethod}
                  onValueChange={(value) => setPaymentForm({ ...paymentForm, paymentMethod: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="loan">Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Transaction ID</Label>
                <Input
                  value={paymentForm.transactionId}
                  onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                  className="border border-gray-300"
                  placeholder="Auto-generated (can be overridden for external payments)"
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPaymentDialog(false);
              setPaymentMode('reservation');
              setPaymentForm({
                reservationId: '',
                groupBookingId: '',
                guestId: '',
                paymentType: 'full_payment',
                amount: '',
                paymentMethod: 'cash',
                transactionId: '',
                notes: '',
              });
            }}>Cancel</Button>
            <Button onClick={async () => {
              if (!validatePayment()) {
                toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
                return;
              }
              
              setIsCreatingPayment(true);
              try {
                if (paymentMode === 'group' && paymentForm.groupBookingId) {
                  // Handle group booking payment - create a single payment for the group
                  const selectedGroup = groupBookings.find(g => g.id === paymentForm.groupBookingId);
                  
                  if (!selectedGroup) {
                    toast({ title: 'Error', description: 'Group booking not found', variant: 'destructive' });
                    setIsCreatingPayment(false);
                    return;
                  }
                  
                  // Get fresh group booking data with all linked reservations
                  const fullGroupDetails = await apiService.getGroupBooking(selectedGroup.id);
                  const unpaidReservations = fullGroupDetails?.linkedReservations?.filter((r: any) => r.paymentStatus !== 'PAID') || [];
                  const totalUnpaid = unpaidReservations.reduce((sum: number, r: any) => {
                    return sum + parseFloat(r.folio?.balance || r.totalAmount || 0);
                  }, 0);
                  
                  // Create a single payment for the group booking
                  // Link it to the contact person (group's guest)
                  await apiService.createHotelPayment({
                    companyId,
                    reservationId: undefined, // No specific reservation - this is a group payment
                    guestId: selectedGroup.contactPersonId || paymentForm.guestId || undefined,
                    paymentType: paymentForm.paymentType,
                    amount: parseFloat(paymentForm.amount),
                    paymentMethod: paymentForm.paymentMethod,
                    transactionId: paymentForm.transactionId,
                    status: 'COMPLETED', // Mark payment as completed immediately
                    notes: paymentForm.notes 
                      ? `${paymentForm.notes}\nGroup Booking: ${selectedGroup.groupName}\nUnpaid Reservations: ${unpaidReservations.length}\nTotal Unpaid: ${formatCurrency(totalUnpaid)}`
                      : `Group Booking Payment: ${selectedGroup.groupName}\nUnpaid Reservations: ${unpaidReservations.length}\nTotal Unpaid: ${formatCurrency(totalUnpaid)}`,
                  });
                  
                  // If payment amount covers all unpaid reservations, automatically mark all linked reservations as paid
                  if (parseFloat(paymentForm.amount) >= totalUnpaid && totalUnpaid > 0) {
                    // Mark all linked reservations as paid
                    const updatePromises = unpaidReservations.map(async (reservation: any) => {
                      try {
                        await apiService.updateHotelReservation(reservation.id, {
                          paymentStatus: 'PAID',
                        });
                      } catch (error: any) {
                        console.error(`Failed to update reservation ${reservation.id}:`, error);
                        // Continue with other reservations even if one fails
                      }
                    });
                    
                    await Promise.all(updatePromises);
                    
                    // Update group booking payment status to PAID
                    try {
                      await apiService.updateGroupBooking(selectedGroup.id, {
                        paymentStatus: 'PAID',
                      });
                    } catch (error: any) {
                      console.error('Failed to update group booking payment status:', error);
                      // Continue even if group booking update fails
                    }
                    
                    toast({ 
                      title: 'Success', 
                      description: `Payment recorded and all ${unpaidReservations.length} reservations marked as paid for group: ${selectedGroup.groupName}` 
                    });
                  } else {
                  toast({ 
                    title: 'Success', 
                    description: `Payment recorded for group booking: ${selectedGroup.groupName}` 
                  });
                  }
                  
                  // Refresh reservations and payments after group payment
                  await loadPayments();
                  await loadReservations();
                  await loadGroupBookings();
                } else if (paymentForm.reservationId) {
                  // Handle single reservation payment
                await apiService.createHotelPayment({
                  companyId,
                  ...paymentForm,
                  amount: parseFloat(paymentForm.amount),
                  reservationId: paymentForm.reservationId,
                  guestId: paymentForm.guestId || undefined,
                  transactionId: paymentForm.transactionId,
                  status: paymentForm.paymentMethod === 'loan' ? 'loan' : 'COMPLETED', // Use loan status for loan payments
                });
                
                // Refresh reservations and payments after single reservation payment
                // The backend will automatically update reservation payment status based on payment totals
                await loadPayments();
                await loadReservations();
                
                toast({ title: 'Success', description: 'Payment recorded successfully' });
                } else {
                  toast({ title: 'Error', description: 'Please select a reservation or group booking', variant: 'destructive' });
                  setIsCreatingPayment(false);
                  return;
                }
                
                setShowPaymentDialog(false);
                setPaymentMode('reservation');
                setPaymentForm(prev => ({
                  ...prev,
                  reservationId: '',
                  groupBookingId: '',
                  guestId: '',
                  paymentType: 'full_payment',
                  amount: '',
                  paymentMethod: 'cash',
                  transactionId: '',
                  notes: '',
                }));
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || 'Failed to record payment', variant: 'destructive' });
              } finally {
                setIsCreatingPayment(false);
              }
            }} disabled={isCreatingPayment}>
              {isCreatingPayment ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Recording...
                </>
              ) : (
                paymentMode === 'group' ? 'Record Group Payment' : 'Record Payment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Dialog */}
      <Dialog open={showEditPaymentDialog} onOpenChange={(open) => {
        setShowEditPaymentDialog(open);
        if (!open) {
          setSelectedPaymentForEdit(null);
          setPaymentForm({
            reservationId: '',
            groupBookingId: '',
            guestId: '',
            paymentType: 'full_payment',
            amount: '',
            paymentMethod: 'cash',
            transactionId: '',
            notes: '',
          });
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
            <DialogDescription>Update payment details (only DRAFT payments can be edited)</DialogDescription>
          </DialogHeader>
          
          {selectedPaymentForEdit && selectedPaymentForEdit.status === 'DRAFT' && (
            <>
              {/* Same form structure as create payment */}
              <Tabs value={paymentMode} onValueChange={(value) => setPaymentMode(value as 'reservation' | 'group')} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="reservation">
                    <TranslatedText text="Single Reservation" />
                  </TabsTrigger>
                  <TabsTrigger value="group">
                    <TranslatedText text="Group Booking" />
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="reservation" className="space-y-4 mt-4">
                  <div>
                    <Label>Reservation</Label>
                    <SearchableSelect
                      options={reservations
                        .filter((res) => 
                          res.paymentStatus !== 'PAID' && // Only show unpaid reservations
                          res.status !== 'CANCELLED' // Exclude cancelled reservations
                        )
                        .map((res) => {
                        const guestName = res.guest 
                          ? `${res.guest.firstName || ''} ${res.guest.lastName || ''}`.trim()
                          : 'No Guest';
                        const balance = res.folio?.balance || res.totalAmount || 0;
                        const statusBadge = res.paymentStatus === 'PARTIAL' ? ' (Partial)' : ' (Unpaid)';
                        return {
                          value: res.id,
                          label: `${res.reservationNumber} - ${guestName}${statusBadge} - ${formatCurrency(balance)}`,
                        };
                      })}
                      value={paymentForm.reservationId || undefined}
                      onValueChange={(value) => {
                        const selectedReservation = reservations.find(r => r.id === value);
                        setPaymentForm({ 
                          ...paymentForm, 
                          reservationId: value,
                          groupBookingId: '',
                          guestId: selectedReservation?.guestId || paymentForm.guestId || '',
                        });
                      }}
                      placeholder="Select reservation..."
                      searchPlaceholder="Search reservations..."
                      emptyText="No reservations found."
                    />
                  </div>
                </TabsContent>

                <TabsContent value="group" className="space-y-4 mt-4">
                  <div>
                    <Label>Group Booking</Label>
                    <SearchableSelect
                      options={groupBookings.map((group) => ({
                        value: group.id,
                        label: `${group.groupName}`,
                      }))}
                      value={paymentForm.groupBookingId || undefined}
                      onValueChange={(value) => {
                        const selectedGroup = groupBookings.find(g => g.id === value);
                        setPaymentForm({ 
                          ...paymentForm, 
                          groupBookingId: value,
                          reservationId: '',
                          guestId: selectedGroup?.contactPersonId || paymentForm.guestId || '',
                        });
                      }}
                      placeholder="Select group booking..."
                      searchPlaceholder="Search group bookings..."
                      emptyText="No group bookings found."
                    />
                  </div>
                </TabsContent>
              </Tabs>

              <div className="grid gap-4 py-4">
                {paymentForm.guestId && (
                  <div>
                    <Label>Guest</Label>
                    <Input
                      value={(() => {
                        const guest = guests.find(g => g.id === paymentForm.guestId);
                        return guest ? `${guest.firstName} ${guest.lastName}` : '';
                      })()}
                      disabled
                      className="border border-gray-300 bg-muted"
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Payment Type *</Label>
                    <Select
                      value={paymentForm.paymentType}
                      onValueChange={(value) => setPaymentForm({ ...paymentForm, paymentType: value })}
                    >
                      <SelectTrigger className="border border-gray-300 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="deposit">Deposit</SelectItem>
                        <SelectItem value="full_payment">Full Payment</SelectItem>
                        <SelectItem value="refund">Refund</SelectItem>
                        <SelectItem value="additional_charge">Additional Charge</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Payment Method *</Label>
                    <Select
                      value={paymentForm.paymentMethod}
                      onValueChange={(value) => setPaymentForm({ ...paymentForm, paymentMethod: value })}
                    >
                      <SelectTrigger className="border border-gray-300 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="debit_card">Debit Card</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="loan">Loan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      className="border border-gray-300"
                    />
                  </div>
                  <div>
                    <Label>Transaction ID</Label>
                    <Input
                      value={paymentForm.transactionId}
                      onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                      className="border border-gray-300"
                      placeholder="Transaction ID"
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    className="border border-gray-300"
                  />
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPaymentDialog(false)}>
              Cancel
            </Button>
            {selectedPaymentForEdit && selectedPaymentForEdit.status === 'DRAFT' && (
              <Button onClick={async () => {
                if (!validatePayment()) {
                  toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
                  return;
                }
                
                setIsUpdatingPayment(true);
                try {
                  await apiService.updateHotelPayment(selectedPaymentForEdit.id, {
                    reservationId: paymentForm.reservationId || null,
                    guestId: paymentForm.guestId || null,
                    paymentType: paymentForm.paymentType,
                    amount: parseFloat(paymentForm.amount),
                    paymentMethod: paymentForm.paymentMethod,
                    transactionId: paymentForm.transactionId || null,
                    notes: paymentForm.notes || null,
                  });
                  toast({ title: 'Success', description: 'Payment updated successfully' });
                  setShowEditPaymentDialog(false);
                  setSelectedPaymentForEdit(null);
                  // Refresh reservations and payments after payment update
                  // The backend may automatically update reservation payment status based on payment totals
                  await loadPayments();
                  await loadReservations();
                  await loadGroupBookings();
                } catch (error: any) {
                  toast({ title: 'Error', description: error.message || 'Failed to update payment', variant: 'destructive' });
                } finally {
                  setIsUpdatingPayment(false);
                }
              }} disabled={isUpdatingPayment}>
                {isUpdatingPayment ? (
                  <>
                    <MoonLoader size="sm" className="mr-2" color="teal" />
                    Updating...
                  </>
                ) : (
                  'Update Payment'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Payment Invoice Email Dialog */}
      <Dialog open={showPaymentEmailDialog} onOpenChange={setShowPaymentEmailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle><TranslatedText text="Send Payment Invoice" /></DialogTitle>
            <DialogDescription><TranslatedText text="Send payment invoice to guest email" /></DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label><TranslatedText text="To" /> *</Label>
              <Input
                type="email"
                value={paymentEmailTo}
                onChange={(e) => setPaymentEmailTo(e.target.value)}
                placeholder="guest@example.com"
                className="border border-gray-300"
              />
            </div>
            <div>
              <Label><TranslatedText text="Subject" /></Label>
              <Input
                value={paymentEmailSubject}
                onChange={(e) => setPaymentEmailSubject(e.target.value)}
                className="border border-gray-300"
              />
            </div>
            <div>
              <Label><TranslatedText text="Message" /></Label>
              <Textarea
                value={paymentEmailMessage}
                onChange={(e) => setPaymentEmailMessage(e.target.value)}
                rows={4}
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentEmailDialog(false)}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button
              disabled={sendingEmail || !paymentEmailTo}
              onClick={async () => {
                if (!selectedPaymentForEmail || !paymentEmailTo) return;
                setSendingEmail(true);
                try {
                  // Fetch full payment data with folio
                  const fullPayment = await apiService.getHotelPayment(selectedPaymentForEmail.id);
                  
                  // Get company data
                  const company: any = await apiService.getCompany(companyId);
                  const companyData = {
                    name: company.name || '',
                    logoUrl: company.logoUrl || '',
                    address: company.address || '',
                    city: company.city || '',
                    state: company.state || '',
                    postalCode: company.postalCode || '',
                    email: company.email || '',
                    phone: company.phone || '',
                    website: company.website || '',
                    primaryColor: company.primaryColor || '#14b8a6',
                    invoiceTerms: company.invoiceTerms || '',
                    invoiceFooter: company.invoiceFooter || '',
                  };

                  // Generate PDF
                  const generator = new PaymentInvoicePDFGenerator({
                    payment: fullPayment,
                    company: companyData,
                    folio: fullPayment.reservation?.folio
                  });
                  const pdfBlob = await generator.generate();

                  // Send email with PDF
                  await apiService.sendHotelPaymentInvoice(selectedPaymentForEmail.id, {
                    to: paymentEmailTo,
                    subject: paymentEmailSubject || `Payment Invoice ${fullPayment.transactionId || fullPayment.id}`,
                    message: paymentEmailMessage || `Please find your payment invoice ${fullPayment.transactionId || fullPayment.id} attached. Thank you for your business!`,
                    attachPdf: true,
                    pdfBlob: pdfBlob
                  });
                  
                  toast({ title: 'Success', description: 'Invoice sent successfully' });
                  setShowPaymentEmailDialog(false);
                  setPaymentEmailTo('');
                  setPaymentEmailSubject('');
                  setPaymentEmailMessage('');
                  setSelectedPaymentForEmail(null);
                } catch (error: any) {
                  console.error('Error sending payment invoice email:', error);
                  
                  // Fallback to backend API if frontend generation fails
                  try {
                    const result = await apiService.sendHotelPaymentInvoice(selectedPaymentForEmail.id, { 
                      to: paymentEmailTo, 
                      subject: paymentEmailSubject,
                      message: paymentEmailMessage,
                      attachPdf: true 
                    });
                    
                    // Check if there's a warning about SMTP not being configured
                    if (result?.warning) {
                      toast({
                        title: "Email Queued",
                        description: result.warning,
                        variant: "default"
                      });
                    } else {
                      toast({
                        title: "Email Sent",
                        description: `Payment invoice sent to ${paymentEmailTo}`,
                      });
                    }
                    
                    setShowPaymentEmailDialog(false);
                    setPaymentEmailTo('');
                    setPaymentEmailSubject('');
                    setPaymentEmailMessage('');
                    setSelectedPaymentForEmail(null);
                  } catch (backendError: any) {
                    console.error('Backend email sending failed:', backendError);
                    
                    toast({
                      title: "Email Service Unavailable",
                      description: "Email service is not configured. Please contact your administrator to set up SMTP settings.",
                      variant: "destructive"
                    });
                  }
                } finally {
                  setSendingEmail(false);
                }
              }}
            >
              {sendingEmail ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  <TranslatedText text="Sending..." />
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  <TranslatedText text="Send" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Key Card Dialog */}
      <Dialog open={showEditKeyCardDialog} onOpenChange={async (open) => {
        setShowEditKeyCardDialog(open);
        if (!open) {
          // Reset state when dialog closes to prevent UI blocking
          setSelectedKeyCardForEdit(null);
          setEditKeyCardForm({
            status: 'ACTIVE',
            returnedAt: '',
            expiresAt: '',
            notes: '',
          });
          // Auto-refresh key cards data to show any updates
          if (companyId && activeTab === 'keycards') {
            await loadKeyCards();
          }
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Key Card</DialogTitle>
            <DialogDescription>Update key card status, return date, and expiration</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status *</Label>
                <Select
                  value={editKeyCardForm.status}
                  onValueChange={(value) => {
                    setEditKeyCardForm({ ...editKeyCardForm, status: value });
                    // If status is RETURNED and returnedAt is not set, set it to today
                    if (value === 'RETURNED' && !editKeyCardForm.returnedAt) {
                      setEditKeyCardForm({ 
                        ...editKeyCardForm, 
                        status: value,
                        returnedAt: new Date().toISOString().split('T')[0],
                      });
                    }
                  }}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                    <SelectItem value="LOST">Lost</SelectItem>
                    <SelectItem value="RETURNED">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Returned Date</Label>
                <Input
                  type="date"
                  value={editKeyCardForm.returnedAt}
                  onChange={(e) => setEditKeyCardForm({ ...editKeyCardForm, returnedAt: e.target.value })}
                  className="border border-gray-300 w-full"
                />
              </div>
            </div>
            <div>
              <Label>Expiration Date</Label>
              <Input
                type="date"
                value={editKeyCardForm.expiresAt}
                onChange={(e) => setEditKeyCardForm({ ...editKeyCardForm, expiresAt: e.target.value })}
                className="border border-gray-300 w-full"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={editKeyCardForm.notes}
                onChange={(e) => setEditKeyCardForm({ ...editKeyCardForm, notes: e.target.value })}
                placeholder="Additional notes..."
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              setShowEditKeyCardDialog(false);
              setSelectedKeyCardForEdit(null);
              setEditKeyCardForm({
                status: 'ACTIVE',
                returnedAt: '',
                expiresAt: '',
                notes: '',
              });
              // Auto-refresh key cards data to show any updates
              if (companyId && activeTab === 'keycards') {
                await loadKeyCards();
              }
            }}>Cancel</Button>
            <Button onClick={async () => {
              try {
                if (!selectedKeyCardForEdit) return;
                setIsUpdatingKeyCard(true);
                await apiService.updateKeyCard(selectedKeyCardForEdit.id, {
                  status: editKeyCardForm.status,
                  returnedAt: editKeyCardForm.returnedAt || undefined,
                  expiresAt: editKeyCardForm.expiresAt || undefined,
                  notes: editKeyCardForm.notes || undefined,
                });
                toast({ title: 'Success', description: 'Key card updated successfully' });
                
                // Reset state before closing to prevent UI blocking
                setSelectedKeyCardForEdit(null);
                setEditKeyCardForm({
                  status: 'ACTIVE',
                  returnedAt: '',
                  expiresAt: '',
                  notes: '',
                });
                setShowEditKeyCardDialog(false);
                
                // Reload key cards to show updated data
                await loadKeyCards();
              } catch (error: any) {
                toast({
                  title: 'Error',
                  description: error.message || 'Failed to update key card',
                  variant: 'destructive',
                });
              } finally {
                setIsUpdatingKeyCard(false);
              }
            }} disabled={isUpdatingKeyCard}>
              {isUpdatingKeyCard ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Updating...
                </>
              ) : (
                'Update Key Card'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Housekeeping Task Dialog */}
      <Dialog open={showHousekeepingDialog} onOpenChange={setShowHousekeepingDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Housekeeping Task</DialogTitle>
            <DialogDescription>Create a housekeeping task for a room</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room *</Label>
                <SearchableSelect
                  options={rooms.filter(r => r.isActive).map((room) => ({
                    value: room.id,
                    label: `Room ${room.roomNumber}`,
                  }))}
                  value={housekeepingForm.roomId}
                  onValueChange={(value) => setHousekeepingForm({ ...housekeepingForm, roomId: value })}
                  placeholder="Select room"
                  searchPlaceholder="Search rooms..."
                  emptyText="No rooms found."
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={housekeepingForm.priority}
                  onValueChange={(value) => setHousekeepingForm({ ...housekeepingForm, priority: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Assigned To</Label>
                <Input
                  value={housekeepingForm.assignedTo}
                  onChange={(e) => setHousekeepingForm({ ...housekeepingForm, assignedTo: e.target.value })}
                  placeholder="Staff member name"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Scheduled At *</Label>
                <Input
                  type="datetime-local"
                  value={housekeepingForm.scheduledAt}
                  className="border border-gray-300"
                  onChange={(e) => setHousekeepingForm({ ...housekeepingForm, scheduledAt: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={housekeepingForm.description}
                onChange={(e) => setHousekeepingForm({ ...housekeepingForm, description: e.target.value })}
                placeholder="Task description..."
                className="border border-gray-300"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHousekeepingDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                await apiService.createHousekeepingTask({
                  companyId,
                  ...housekeepingForm,
                  scheduledAt: new Date(housekeepingForm.scheduledAt).toISOString(),
                });
                toast({ title: 'Success', description: 'Housekeeping task created successfully' });
                setShowHousekeepingDialog(false);
                setHousekeepingForm({
                  roomId: '',
                  assignedTo: '',
                  status: 'PENDING',
                  priority: 'NORMAL',
                  description: '',
                  scheduledAt: '',
                });
                loadHousekeepingTasks();
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || 'Failed to create task', variant: 'destructive' });
              }
            }}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Maintenance Request Dialog */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Maintenance Request</DialogTitle>
            <DialogDescription>Create a maintenance request for a room</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room *</Label>
                <SearchableSelect
                  options={rooms.filter(r => r.isActive).map((room) => ({
                    value: room.id,
                    label: `Room ${room.roomNumber}`,
                  }))}
                  value={maintenanceForm.roomId}
                  onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, roomId: value })}
                  placeholder="Select room"
                  searchPlaceholder="Search rooms..."
                  emptyText="No rooms found."
                />
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={maintenanceForm.priority}
                  onValueChange={(value) => setMaintenanceForm({ ...maintenanceForm, priority: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Low</SelectItem>
                    <SelectItem value="NORMAL">Normal</SelectItem>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={maintenanceForm.description}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                placeholder="Describe the maintenance issue..."
                className="border border-gray-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Requested By</Label>
                <Input
                  value={maintenanceForm.requestedBy}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, requestedBy: e.target.value })}
                  placeholder="Staff member or guest name"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Assigned To</Label>
                <Input
                  value={maintenanceForm.assignedTo}
                  onChange={(e) => setMaintenanceForm({ ...maintenanceForm, assignedTo: e.target.value })}
                  placeholder="Staff member name"
                  className="border border-gray-300"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceDialog(false)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                await apiService.createMaintenanceRequest({
                  companyId,
                  ...maintenanceForm,
                });
                toast({ title: 'Success', description: 'Maintenance request created successfully' });
                setShowMaintenanceDialog(false);
                setMaintenanceForm({
                  roomId: '',
                  description: '',
                  status: 'OPEN',
                  priority: 'NORMAL',
                  requestedBy: '',
                  assignedTo: '',
                });
                loadMaintenanceRequests();
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || 'Failed to create request', variant: 'destructive' });
              }
            }}>Create Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Key Card Dialog */}
      <Dialog open={showKeyCardDialog} onOpenChange={setShowKeyCardDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Issue Key Card</DialogTitle>
            <DialogDescription>Issue a key card for a room or reservation</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room *</Label>
                <SearchableSelect
                  options={rooms
                    .filter(r => {
                      // Only show rooms that are active and have active reservations
                      if (!r.isActive) return false;
                      return reservations.some(
                        res => res.roomId === r.id && 
                        (res.status === 'CHECKED_IN' || res.status === 'CONFIRMED') &&
                        res.status !== 'CANCELLED' &&
                        res.status !== 'CHECKED_OUT'
                      );
                    })
                    .map((room) => ({
                      value: room.id,
                      label: `Room ${room.roomNumber}`,
                    }))}
                  value={keyCardForm.roomId}
                  onValueChange={(value) => {
                    const selectedRoom = rooms.find(r => r.id === value);
                    // Auto-fill reservation if there's a matching reservation for this room
                    const matchingReservation = reservations.find(
                      r => r.roomId === value && (r.status === 'CHECKED_IN' || r.status === 'CONFIRMED')
                    );
                    setKeyCardForm({ 
                      ...keyCardForm, 
                      roomId: value,
                      reservationId: matchingReservation?.id || '',
                    });
                  }}
                  placeholder="Select room"
                  searchPlaceholder="Search reserved rooms..."
                  emptyText="No reserved rooms found."
                />
              </div>
              <div>
                <Label>Reservation</Label>
                <SearchableSelect
                  options={reservations
                    .filter(r => r.status === 'CHECKED_IN' || r.status === 'CONFIRMED')
                    .map((res) => {
                      const guestName = res.guest 
                        ? `${res.guest.firstName || ''} ${res.guest.lastName || ''}`.trim()
                        : 'No Guest';
                      return {
                        value: res.id,
                        label: `${res.reservationNumber} - ${guestName}`,
                      };
                    })}
                  value={keyCardForm.reservationId || undefined}
                  onValueChange={(value) => {
                    const selectedReservation = reservations.find(r => r.id === value);
                    // Auto-fill room if reservation is selected
                    if (selectedReservation) {
                      setKeyCardForm({ 
                        ...keyCardForm, 
                        reservationId: value || '',
                        roomId: selectedReservation.roomId || '',
                      });
                    } else {
                      setKeyCardForm({ ...keyCardForm, reservationId: value || '' });
                    }
                  }}
                  placeholder="Select reservation (optional)"
                  searchPlaceholder="Search reservations..."
                  emptyText="No reservations found."
                />
              </div>
            </div>
            {keyCardForm.reservationId && keyCardForm.roomId && (() => {
              const selectedReservation = reservations.find(r => r.id === keyCardForm.reservationId);
              const isMismatch = selectedReservation && selectedReservation.roomId !== keyCardForm.roomId;
              return isMismatch ? (
                <div className="col-span-2">
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                    <strong>Warning:</strong> The selected reservation does not match the selected room. Please select a matching reservation or remove the reservation.
                  </div>
                </div>
              ) : null;
            })()}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Card Number</Label>
                <Input
                  value={keyCardForm.cardNumber}
                  onChange={(e) => setKeyCardForm({ ...keyCardForm, cardNumber: e.target.value })}
                  placeholder="Auto-generated if empty"
                  className="border border-gray-300 w-full"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={keyCardForm.status}
                  onValueChange={(value) => setKeyCardForm({ ...keyCardForm, status: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                    <SelectItem value="LOST">Lost</SelectItem>
                    <SelectItem value="RETURNED">Returned</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Expiration Date</Label>
              <Input
                type="date"
                value={keyCardForm.expiresAt}
                onChange={(e) => setKeyCardForm({ ...keyCardForm, expiresAt: e.target.value })}
                className="border border-gray-300 w-full"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKeyCardDialog(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!keyCardForm.roomId) {
                  toast({ title: 'Error', description: 'Please select a room', variant: 'destructive' });
                  return;
                }
                try {
                await apiService.createKeyCard({
                  roomId: keyCardForm.roomId,
                  reservationId: keyCardForm.reservationId || undefined,
                  cardNumber: keyCardForm.cardNumber || undefined,
                  status: keyCardForm.status,
                  expiresAt: keyCardForm.expiresAt || undefined,
                });
                toast({ title: 'Success', description: 'Key card issued successfully' });
                setShowKeyCardDialog(false);
                setKeyCardForm({
                  roomId: '',
                  reservationId: '',
                  cardNumber: '',
                  status: 'ACTIVE',
                  expiresAt: '',
                });
                  loadKeyCards();
                } catch (error: any) {
                  toast({ title: 'Error', description: error.message || 'Failed to issue key card', variant: 'destructive' });
                }
              }}
              disabled={(() => {
                // Disable if no room selected
                if (!keyCardForm.roomId) return true;
                
                // If reservation is selected, check if it matches the room
                if (keyCardForm.reservationId) {
                  const selectedReservation = reservations.find(r => r.id === keyCardForm.reservationId);
                  if (selectedReservation && selectedReservation.roomId !== keyCardForm.roomId) {
                    return true; // Disable if reservation doesn't match room
                  }
                }
                
                return false;
              })()}
            >
              Issue Key Card
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reservation Details Dialog */}
      <Dialog open={showReservationDetailsDialog} onOpenChange={async (open) => {
        setShowReservationDetailsDialog(open);
        if (!open) {
          // Reset state when dialog closes
          setSelectedReservationDetails(null);
          // Auto-refresh reservations data to show any updates
          if (companyId && (activeTab === 'reservations' || activeTab === 'folio')) {
            await loadReservations();
            if (activeTab === 'folio') {
              await loadFolios();
            }
          }
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Reservation Details</DialogTitle>
            <DialogDescription>
              Complete information for reservation #{selectedReservationDetails?.reservationNumber}
            </DialogDescription>
          </DialogHeader>
          {selectedReservationDetails && (
            <div className="space-y-6 py-4">
              {/* Status and Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Reservation Number</Label>
                  <p className="text-sm font-medium">{selectedReservationDetails.reservationNumber}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    {getStatusBadge(selectedReservationDetails.status)}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Payment Status</Label>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge className="bg-teal-500 text-white hover:bg-teal-600">
                      {selectedReservationDetails.paymentStatus || 'UNPAID'}
                    </Badge>
                    {selectedReservationDetails.paymentStatus === 'PARTIAL' && selectedReservationDetails.folio && (
                      <span className="text-sm font-medium text-orange-600">
                        {(() => {
                          const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                          
                          // Calculate balance from converted folio items
                          if (selectedReservationDetails.folio?.items && selectedReservationDetails.folio.items.length > 0) {
                            const calculatedTotal = selectedReservationDetails.folio.items.reduce((sum: number, item: any) => {
                              // Skip payment items
                              if (item.name?.toLowerCase().includes('payment')) {
                                return sum;
                              }
                              
                              let itemCurrency = roomCurrency;
                              
                              // Detect currency from POS items
                              if (item.name?.includes('POS Invoice') && item.description) {
                                const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                if (currencyMatch) {
                                  itemCurrency = currencyMatch[1];
                                }
                              }
                              
                              const itemTotal = Number(item.total);
                              const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                              return sum + convertedTotal;
                            }, 0);
                            
                            // Calculate payments
                            const totalPaid = selectedReservationDetails.payments?.reduce((sum: number, payment: any) => {
                              if (payment.status === 'COMPLETED') {
                                return sum + Number(payment.amount || 0);
                              }
                              return sum;
                            }, 0) || 0;
                            
                            const balance = calculatedTotal - totalPaid;
                            return formatCurrencyWithApprox(balance, roomCurrency);
                          }
                          
                          // Fallback to folio balance
                          return formatCurrencyWithApprox(selectedReservationDetails.folio.balance, roomCurrency);
                        })()} <TranslatedText text="remaining" />
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Number of Guests</Label>
                  <p className="text-sm font-medium">{selectedReservationDetails.numberOfGuests || 1}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Room Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Room Number</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.room?.roomNumber || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Room Type</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.room?.roomTypeRef?.name || 
                       selectedReservationDetails.room?.roomType || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Room Capacity</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.room?.capacity || 'N/A'} guests
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Price Per Night</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.room?.basePricePerNight 
                        ? formatCurrency(selectedReservationDetails.room.basePricePerNight, selectedReservationDetails.room.currency || 'RWF')
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Guest Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.guest?.firstName} {selectedReservationDetails.guest?.lastName}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Email</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.guest?.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.guest?.phone || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Address</Label>
                    <p className="text-sm font-medium">
                      {selectedReservationDetails.guest?.address || 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Dates</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Check-In Date</Label>
                    <p className="text-sm font-medium">
                      {new Date(selectedReservationDetails.checkInDate).toLocaleDateString()}
                    </p>
                    {selectedReservationDetails.actualCheckIn && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Actual: {new Date(selectedReservationDetails.actualCheckIn).toLocaleString()}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Check-Out Date</Label>
                    <p className="text-sm font-medium">
                      {new Date(selectedReservationDetails.checkOutDate).toLocaleDateString()}
                    </p>
                    {selectedReservationDetails.actualCheckOut && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Actual: {new Date(selectedReservationDetails.actualCheckOut).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Financial Information</h3>
                <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Amount</Label>
                    <p className="text-lg font-semibold">
                      {(() => {
                        const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                        
                        // If folio exists with items, calculate from converted items (excluding payments)
                        if (selectedReservationDetails.folio?.items && selectedReservationDetails.folio.items.length > 0) {
                          const calculatedTotal = selectedReservationDetails.folio.items.reduce((sum: number, item: any) => {
                            // Skip payment items (they reduce balance, not add to total)
                            if (item.name?.toLowerCase().includes('payment')) {
                              return sum;
                            }
                            
                            let itemCurrency = roomCurrency;
                            
                            // Detect currency from POS items
                            if (item.name?.includes('POS Invoice') && item.description) {
                              const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                              if (currencyMatch) {
                                itemCurrency = currencyMatch[1];
                              }
                            }
                            
                            const itemTotal = Number(item.total);
                            const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                            return sum + convertedTotal;
                          }, 0);
                          
                          return formatCurrencyWithApprox(calculatedTotal, roomCurrency);
                        }
                        
                        // Otherwise use reservation totalAmount
                        return formatCurrency(selectedReservationDetails.totalAmount, roomCurrency);
                      })()}
                    </p>
                  </div>
                  {/* Deposit Amount - Hidden */}
                  <div className="hidden">
                    <Label className="text-xs text-muted-foreground">Deposit Amount</Label>
                    <p className="text-sm font-medium">
                      {formatCurrency(selectedReservationDetails.depositAmount || 0, selectedReservationDetails.room?.currency || 'RWF')}
                    </p>
                  </div>
                  </div>
                  
                  {(selectedReservationDetails.ratePlan || selectedReservationDetails.promotion) && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-3"><TranslatedText text="Pricing Details" /></h4>
                      <div className="space-y-3">
                  {/* Rate Plan - Hidden */}
                  {false && selectedReservationDetails.ratePlan && (
                          <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
                    <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Rate Plan" /></Label>
                      <p className="text-sm font-medium">
                        {selectedReservationDetails.ratePlan.name}
                              </p>
                            </div>
                            <p className="text-sm font-medium">
                              {formatCurrency(selectedReservationDetails.ratePlan.price, selectedReservationDetails.room?.currency || 'RWF')}/night
                      </p>
                    </div>
                  )}
                        {selectedReservationDetails.promotion && (
                          <div className="flex items-start justify-between p-2 bg-muted/50 rounded">
                            <div className="flex-1">
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Applied Promotion" /></Label>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-sm font-medium">
                                  {selectedReservationDetails.promotion.name}
                                  {selectedReservationDetails.promotion.code && (
                                    <span className="text-muted-foreground ml-1">({selectedReservationDetails.promotion.code})</span>
                                  )}
                                </p>
                                <Badge variant="outline" className="text-xs">
                                  {selectedReservationDetails.promotion.discountType === 'PERCENTAGE' 
                                    ? `${selectedReservationDetails.promotion.discountValue}%`
                                    : selectedReservationDetails.promotion.discountType === 'FIXED_AMOUNT'
                                    ? formatCurrency(selectedReservationDetails.promotion.discountValue, selectedReservationDetails.room?.currency || 'RWF')
                                    : `${selectedReservationDetails.promotion.discountValue} free night(s)`}
                                </Badge>
                              </div>
                              {selectedReservationDetails.promotion.description && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {selectedReservationDetails.promotion.description}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {selectedReservationDetails.folio && (
                <div className="border-t pt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold">Folio Information</h3>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadFolioPDF({
                          ...selectedReservationDetails.folio,
                          reservation: selectedReservationDetails,
                        })}
                        className="h-8"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        <TranslatedText text="Download PDF" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadUnpaidItemsPDF(selectedReservationDetails)}
                        className="h-8"
                        disabled={Number(selectedReservationDetails.folio.balance) <= 0}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        <TranslatedText text="Unpaid Items" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Folio Total</Label>
                      <p className="text-sm font-medium">
                        {(() => {
                          const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                          // Calculate total by converting each item to room currency (excluding payments)
                          const calculatedTotal = selectedReservationDetails.folio.items?.reduce((sum: number, item: any) => {
                            // Skip payment items
                            if (item.name?.toLowerCase().includes('payment')) {
                              return sum;
                            }
                            
                            let itemCurrency = roomCurrency;
                            
                            // Detect currency from POS items
                            if (item.name?.includes('POS Invoice') && item.description) {
                              const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                              if (currencyMatch) {
                                itemCurrency = currencyMatch[1];
                              }
                            }
                            
                            const itemTotal = Number(item.total);
                            const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                            return sum + convertedTotal;
                          }, 0) || Number(selectedReservationDetails.folio.totalAmount);
                          
                          return formatCurrencyWithApprox(calculatedTotal, roomCurrency);
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Balance</Label>
                      <p className={`text-sm font-medium ${
                        Number(selectedReservationDetails.folio.balance) > 0 
                          ? 'text-red-600' 
                          : 'text-green-600'
                      }`}>
                        {(() => {
                          const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                          // Calculate total by converting each item to room currency (excluding payments)
                          const calculatedTotal = selectedReservationDetails.folio.items?.reduce((sum: number, item: any) => {
                            // Skip payment items
                            if (item.name?.toLowerCase().includes('payment')) {
                              return sum;
                            }
                            
                            let itemCurrency = roomCurrency;
                            
                            // Detect currency from POS items
                            if (item.name?.includes('POS Invoice') && item.description) {
                              const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                              if (currencyMatch) {
                                itemCurrency = currencyMatch[1];
                              }
                            }
                            
                            const itemTotal = Number(item.total);
                            const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                            return sum + convertedTotal;
                          }, 0) || Number(selectedReservationDetails.folio.totalAmount);
                          
                          // Calculate payments in room currency
                          const totalPaid = selectedReservationDetails.payments?.reduce((sum: number, payment: any) => {
                            if (payment.status === 'COMPLETED') {
                              return sum + Number(payment.amount || 0);
                            }
                            return sum;
                          }, 0) || 0;
                          
                          const balance = calculatedTotal - totalPaid;
                          
                          return formatCurrencyWithApprox(balance, roomCurrency);
                        })()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Status</Label>
                      <p className="text-sm font-medium">
                        {selectedReservationDetails.folio.status || 'OPEN'}
                      </p>
                    </div>
                  </div>
                  {selectedReservationDetails.folio.items && selectedReservationDetails.folio.items.length > 0 && (
                    <div className="mt-4">
                      <Label className="text-xs text-muted-foreground mb-2 block">Folio Items:</Label>
                      <div className="border rounded-lg">
                        <div className="grid grid-cols-5 gap-4 p-3 bg-muted font-semibold text-sm border-b">
                          <div>Item</div>
                          <div>Description</div>
                          <div className="text-right">Price</div>
                          <div className="text-right">Quantity</div>
                          <div className="text-right">Total</div>
                        </div>
                        {selectedReservationDetails.folio.items.map((item: any) => {
                          // Detect currency from item - check if it's a POS item with different currency
                          const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                          let itemCurrency = roomCurrency;
                          
                          // If item is from POS and description mentions currency, extract it
                          if (item.name?.includes('POS Invoice') && item.description) {
                            const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                            if (currencyMatch) {
                              itemCurrency = currencyMatch[1];
                            }
                          }
                          
                          const itemPrice = Number(item.price);
                          const itemTotal = Number(item.total);
                          const needsConversion = itemCurrency !== roomCurrency;
                          
                          return (
                            <div key={item.id} className="grid grid-cols-5 gap-4 p-3 border-b last:border-b-0 text-sm">
                              <div className="font-medium">{item.name}</div>
                              <div className="text-muted-foreground text-xs">{item.description || '-'}</div>
                              <div className="text-right">
                                {needsConversion ? (
                                  <div className="flex flex-col items-end">
                                    <span>{formatCurrency(convertCurrency(itemPrice, itemCurrency, roomCurrency), roomCurrency)}</span>
                                    <span className="text-xs text-muted-foreground">({formatCurrency(itemPrice, itemCurrency)})</span>
                                  </div>
                                ) : (
                                  formatCurrency(itemPrice, itemCurrency)
                                )}
                              </div>
                              <div className="text-right">{item.quantity}</div>
                              <div className="text-right font-medium">
                                {needsConversion ? (
                                  <div className="flex flex-col items-end">
                                    <span>{formatCurrency(convertCurrency(itemTotal, itemCurrency, roomCurrency), roomCurrency)}</span>
                                    <span className="text-xs text-muted-foreground">({formatCurrency(itemTotal, itemCurrency)})</span>
                                  </div>
                                ) : (
                                  formatCurrency(itemTotal, itemCurrency)
                                )}
                              </div>
                            </div>
                          );
                        })}
                        <div className="grid grid-cols-5 gap-4 p-3 bg-muted font-semibold border-t">
                          <div className="col-span-4 text-right">Total (in {selectedReservationDetails.room?.currency || 'RWF'}):</div>
                          <div className="text-right">
                            {(() => {
                              const roomCurrency = selectedReservationDetails.room?.currency || 'RWF';
                              // Calculate total by converting each item to room currency (excluding payments)
                              const calculatedTotal = selectedReservationDetails.folio.items.reduce((sum: number, item: any) => {
                                // Skip payment items
                                if (item.name?.toLowerCase().includes('payment')) {
                                  return sum;
                                }
                                
                                let itemCurrency = roomCurrency;
                                
                                // Detect currency from POS items
                                if (item.name?.includes('POS Invoice') && item.description) {
                                  const currencyMatch = item.description.match(/\((FRW|RWF|USD|EUR|GBP)\)/);
                                  if (currencyMatch) {
                                    itemCurrency = currencyMatch[1];
                                  }
                                }
                                
                                const itemTotal = Number(item.total);
                                const convertedTotal = convertCurrency(itemTotal, itemCurrency, roomCurrency);
                                return sum + convertedTotal;
                              }, 0);
                              
                              return formatCurrencyWithApprox(calculatedTotal, roomCurrency);
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedReservationDetails.specialRequests && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Special Requests</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedReservationDetails.specialRequests}
                  </p>
                </div>
              )}

              {selectedReservationDetails.notes && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Notes</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedReservationDetails.notes}
                  </p>
                </div>
              )}

              {selectedReservationDetails.cancellationDate && (
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3 text-destructive">Cancellation Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Cancellation Date</Label>
                      <p className="text-sm font-medium">
                        {new Date(selectedReservationDetails.cancellationDate).toLocaleString()}
                      </p>
                    </div>
                    {selectedReservationDetails.cancellationReason && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Reason</Label>
                        <p className="text-sm font-medium">
                          {selectedReservationDetails.cancellationReason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Timestamps</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Created At</Label>
                    <p className="text-sm font-medium">
                      {new Date(selectedReservationDetails.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Last Updated</Label>
                    <p className="text-sm font-medium">
                      {new Date(selectedReservationDetails.updatedAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              setShowReservationDetailsDialog(false);
              setSelectedReservationDetails(null);
              // Auto-refresh reservations data to show any updates
              if (companyId && (activeTab === 'reservations' || activeTab === 'folio')) {
                await loadReservations();
                if (activeTab === 'folio') {
                  await loadFolios();
                }
              }
            }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Reservation Status Dialog */}
      <Dialog open={showEditReservationDialog} onOpenChange={async (open) => {
        setShowEditReservationDialog(open);
        if (!open) {
          setSelectedReservationForEdit(null);
          // Auto-refresh reservations data to show any updates
          if (companyId && (activeTab === 'reservations' || activeTab === 'folio')) {
            await loadReservations();
            if (activeTab === 'folio') {
              await loadFolios();
            }
          }
        }
      }}>
        <DialogContent className={selectedReservationForEdit?.status === 'PENDING' ? "max-w-2xl max-h-[90vh] overflow-y-auto" : "max-w-md"}>
          <DialogHeader>
            <DialogTitle>
              {selectedReservationForEdit?.status === 'PENDING' ? 'Edit Reservation' : 'Edit Reservation Status'}
            </DialogTitle>
            <DialogDescription>
              {selectedReservationForEdit?.status === 'PENDING' 
                ? `Update reservation details for reservation #${selectedReservationForEdit?.reservationNumber}`
                : `Update status and payment status for reservation #${selectedReservationForEdit?.reservationNumber}`
              }
            </DialogDescription>
          </DialogHeader>
          {selectedReservationForEdit && (
            <div className="grid gap-4 py-4">
              {selectedReservationForEdit.status === 'CHECKED_OUT' && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive font-medium flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    This reservation is checked out and cannot be edited. The reservation is complete.
                  </p>
                </div>
              )}
              
              {/* For PENDING reservations, show editable fields */}
              {selectedReservationForEdit.status === 'PENDING' && (
                <>
                  <div className="border-b pb-4 space-y-4">
                    <h3 className="font-semibold text-sm">Reservation Details</h3>
                    
                    {/* Check-in Date */}
                    <div>
                      <Label>Check-in Date *</Label>
                      <Input
                        type="date"
                        value={selectedReservationForEdit.editCheckInDate || new Date(selectedReservationForEdit.checkInDate).toISOString().split('T')[0]}
                        onChange={(e) => {
                          setSelectedReservationForEdit({ 
                            ...selectedReservationForEdit, 
                            editCheckInDate: e.target.value 
                          });
                        }}
                        min={new Date().toISOString().split('T')[0]}
                        className="border border-gray-300 w-full mt-1"
                        required
                      />
                    </div>
                    
                    {/* Check-out Date */}
                    <div>
                      <Label>Check-out Date *</Label>
                      <Input
                        type="date"
                        value={selectedReservationForEdit.editCheckOutDate || new Date(selectedReservationForEdit.checkOutDate).toISOString().split('T')[0]}
                        onChange={(e) => {
                          setSelectedReservationForEdit({ 
                            ...selectedReservationForEdit, 
                            editCheckOutDate: e.target.value 
                          });
                        }}
                        min={selectedReservationForEdit.editCheckInDate || new Date(selectedReservationForEdit.checkInDate).toISOString().split('T')[0]}
                        className="border border-gray-300 w-full mt-1"
                        required
                      />
                    </div>
                    
                    {/* Number of Guests */}
                    <div>
                      <Label>Number of Guests *</Label>
                      <Input
                        type="number"
                        min="1"
                        max={selectedReservationForEdit.room?.capacity}
                        value={selectedReservationForEdit.editNumberOfGuests !== undefined ? selectedReservationForEdit.editNumberOfGuests : selectedReservationForEdit.numberOfGuests}
                        onChange={(e) => {
                          const maxGuests = selectedReservationForEdit.room?.capacity;
                          const value = parseInt(e.target.value) || 1;
                          
                          // Prevent entering more than room capacity
                          if (maxGuests && value > maxGuests) {
                            setSelectedReservationForEdit({ 
                              ...selectedReservationForEdit, 
                              editNumberOfGuests: maxGuests
                            });
                          } else {
                            setSelectedReservationForEdit({ 
                              ...selectedReservationForEdit, 
                              editNumberOfGuests: value
                            });
                          }
                        }}
                        className="border border-gray-300 w-full mt-1"
                        required
                      />
                    </div>
                    
                    {/* Special Requests */}
                    <div>
                      <Label>Special Requests</Label>
                      <Textarea
                        value={selectedReservationForEdit.editSpecialRequests !== undefined ? selectedReservationForEdit.editSpecialRequests : (selectedReservationForEdit.specialRequests || '')}
                        onChange={(e) => {
                          setSelectedReservationForEdit({ 
                            ...selectedReservationForEdit, 
                            editSpecialRequests: e.target.value 
                          });
                        }}
                        placeholder="Any special requests or notes..."
                        rows={3}
                        className="border border-gray-300 w-full mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="border-b pb-4">
                    <h3 className="font-semibold text-sm mb-4">Status & Payment</h3>
                  </div>
                </>
              )}
              <div>
                <Label>Reservation Status *</Label>
                {(() => {
                  const payments = selectedReservationForEdit.payments || [];
                  const completedPayments = payments.filter((p: any) => p.status === 'COMPLETED');
                  const totalPaid = completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
                  const hasFolio = selectedReservationForEdit.folio !== null && selectedReservationForEdit.folio !== undefined;
                  const totalAmount = hasFolio 
                    ? Number(selectedReservationForEdit.folio?.totalAmount || 0)
                    : Number(selectedReservationForEdit.totalAmount || 0);
                  const folioBalance = hasFolio ? Number(selectedReservationForEdit.folio?.balance || 0) : (totalAmount - totalPaid);
                  const isPaid = selectedReservationForEdit.paymentStatus === 'PAID' || 
                                 (hasFolio && folioBalance <= 0) || 
                                 (!hasFolio && totalPaid >= totalAmount && totalAmount > 0);
                  
                  const isCheckedOut = selectedReservationForEdit.status === 'CHECKED_OUT';
                  
                  // Debug logging
                  console.log('=== STATUS SELECT DEBUG ===');
                  console.log('Payment Status:', selectedReservationForEdit.paymentStatus);
                  console.log('Has Folio:', hasFolio);
                  console.log('Folio Balance:', folioBalance);
                  console.log('Total Paid:', totalPaid);
                  console.log('Total Amount:', totalAmount);
                  console.log('isPaid:', isPaid);
                  console.log('isCheckedOut:', isCheckedOut);
                  console.log('========================');
                  
                  return (
                    <Select
                      value={selectedReservationForEdit.status}
                      disabled={isCheckedOut}
                      onValueChange={(value) => {
                        // Prevent changing status if already checked out
                        if (isCheckedOut) {
                          toast({
                            title: 'Cannot Edit',
                            description: 'Checked out reservations cannot be edited. The reservation has been completed.',
                            variant: 'destructive',
                          });
                          return;
                        }
                        // Validate: Cannot set to CHECKED_IN if not paid
                        if (value === 'CHECKED_IN' && !isPaid) {
                          toast({
                            title: 'Payment Required',
                            description: `Cannot check in guest. Payment status must be PAID. Current: ${selectedReservationForEdit.paymentStatus || 'UNPAID'}. Balance: ${formatCurrency(folioBalance)}.`,
                            variant: 'destructive',
                          });
                          return;
                        }
                        // Validate: Cannot set to CHECKED_OUT if not paid
                        if (value === 'CHECKED_OUT' && !isPaid) {
                          toast({
                            title: 'Payment Required',
                            description: `Cannot set status to CHECKED_OUT. Payment status must be PAID. Current: ${selectedReservationForEdit.paymentStatus || 'UNPAID'}. Balance: ${formatCurrency(folioBalance)}.`,
                            variant: 'destructive',
                          });
                          return;
                        }
                        setSelectedReservationForEdit({ ...selectedReservationForEdit, status: value });
                      }}
                    >
                      <SelectTrigger className="border border-gray-300 w-full" disabled={isCheckedOut}>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDING">PENDING</SelectItem>
                        <SelectItem value="CONFIRMED">CONFIRMED</SelectItem>
                        <SelectItem 
                          value="CHECKED_IN" 
                          disabled={!isPaid}
                          className={!isPaid ? 'opacity-50 cursor-not-allowed text-gray-400 pointer-events-none' : ''}
                          onClick={() => console.log('CHECKED_IN clicked, isPaid:', isPaid, 'disabled:', !isPaid)}
                        >
                          CHECKED_IN {!isPaid && '(Requires payment)'} {/* DEBUG: isPaid={String(isPaid)} */}
                        </SelectItem>
                        <SelectItem 
                          value="CHECKED_OUT" 
                          disabled={!isPaid || isCheckedOut}
                          className={(!isPaid || isCheckedOut) ? 'opacity-50 cursor-not-allowed text-gray-400 pointer-events-none' : ''}
                          onClick={() => console.log('CHECKED_OUT clicked, isPaid:', isPaid, 'disabled:', !isPaid || isCheckedOut)}
                        >
                          CHECKED_OUT {!isPaid && '(Requires payment)'} {/* DEBUG: isPaid={String(isPaid)} */}
                        </SelectItem>
                        <SelectItem value="CANCELLED">CANCELLED</SelectItem>
                        <SelectItem value="NO_SHOW">NO_SHOW</SelectItem>
                      </SelectContent>
                    </Select>
                  );
                })()}
              </div>
              <div>
                <Label>Payment Status *</Label>
                {(() => {
                  const payments = selectedReservationForEdit.payments || [];
                  const completedPayments = payments.filter((p: any) => p.status === 'COMPLETED');
                  const totalPaid = completedPayments.reduce((sum: number, p: any) => sum + Number(p.amount || 0), 0);
                  const hasFolio = selectedReservationForEdit.folio !== null && selectedReservationForEdit.folio !== undefined;
                  const totalAmount = hasFolio 
                    ? Number(selectedReservationForEdit.folio?.totalAmount || 0)
                    : Number(selectedReservationForEdit.totalAmount || 0);
                  const folioBalance = hasFolio ? Number(selectedReservationForEdit.folio?.balance || 0) : (totalAmount - totalPaid);
                  const canSetPaid = totalPaid >= totalAmount && totalAmount > 0;
                  
                  return (
                    <>
                      <Select
                        value={selectedReservationForEdit.paymentStatus || 'UNPAID'}
                        onValueChange={(value) => {
                          // Warn if trying to set PAID without payment
                          if (value === 'PAID' && !canSetPaid) {
                            toast({
                              title: 'Payment Validation',
                              description: `Cannot set to PAID. Total paid: ${formatCurrency(totalPaid)}, Total amount: ${formatCurrency(totalAmount)}. Please record payment first.`,
                              variant: 'destructive',
                            });
                            return;
                          }
                          
                          // If setting payment status to PAID and reservation is CHECKED_IN, suggest CHECKED_OUT
                          if (value === 'PAID' && selectedReservationForEdit.status === 'CHECKED_IN') {
                            setSelectedReservationForEdit({ 
                              ...selectedReservationForEdit, 
                              paymentStatus: value,
                              status: 'CHECKED_OUT' // Auto-set to CHECKED_OUT when paid
                            });
                            toast({
                              title: 'Status Updated',
                              description: 'Payment status set to PAID. Reservation status automatically set to CHECKED_OUT.',
                            });
                            return;
                          }
                          
                          setSelectedReservationForEdit({ ...selectedReservationForEdit, paymentStatus: value });
                        }}
                      >
                        <SelectTrigger className="border border-gray-300 w-full">
                          <SelectValue placeholder="Select payment status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="UNPAID">UNPAID</SelectItem>
                          <SelectItem value="PARTIAL">PARTIAL</SelectItem>
                          <SelectItem value="PAID" disabled={!canSetPaid}>
                            PAID {!canSetPaid && '(Requires full payment)'}
                          </SelectItem>
                          <SelectItem value="REFUNDED">REFUNDED</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Paid: {formatCurrency(totalPaid)} / {formatCurrency(totalAmount)} 
                        {hasFolio && ` | Balance: ${formatCurrency(folioBalance)}`}
                      </p>
                    </>
                  );
                })()}
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={selectedReservationForEdit.notes || ''}
                  onChange={(e) => setSelectedReservationForEdit({ ...selectedReservationForEdit, notes: e.target.value })}
                  placeholder="Add any notes about this reservation..."
                  rows={3}
                  className="border border-gray-300"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={async () => {
              setShowEditReservationDialog(false);
              setSelectedReservationForEdit(null);
              // Auto-refresh reservations data to show any updates
              if (companyId && (activeTab === 'reservations' || activeTab === 'folio')) {
                await loadReservations();
                if (activeTab === 'folio') {
                  await loadFolios();
                }
              }
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateReservation} 
              disabled={isUpdatingReservation || selectedReservationForEdit?.status === 'CHECKED_OUT'}
            >
              {isUpdatingReservation ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Updating...
                </>
              ) : (
                'Update Reservation'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Group Booking Dialog */}
      <Dialog open={showGroupBookingDialog} onOpenChange={(open) => {
        setShowGroupBookingDialog(open);
        if (!open) {
          setGroupBookingErrors({});
          setSelectedGroupBookingForEdit(null);
          setGroupBookingForm({
            groupName: '',
            groupType: 'CORPORATE',
            contactPersonId: '',
            contactEmail: '',
            contactPhone: '',
            checkInDate: '',
            checkOutDate: '',
            numberOfRooms: '', // Optional - rooms added via reservations
            numberOfGuests: '', // Optional - guests tracked via reservations
            totalAmount: '', // Optional - calculated from reservations
            depositAmount: '0',
            status: 'PENDING',
            specialRequests: '',
            notes: '',
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedGroupBookingForEdit ? <TranslatedText text="Edit Group Booking" /> : <TranslatedText text="New Group Booking" />}
            </DialogTitle>
            <DialogDescription>
              {selectedGroupBookingForEdit ? <TranslatedText text="Update group booking details" /> : <TranslatedText text="Create a group booking for corporate, tour, or event" />}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Group Name" /> *</Label>
                <Input
                  value={groupBookingForm.groupName}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, groupName: e.target.value });
                    if (groupBookingErrors.groupName) setGroupBookingErrors({ ...groupBookingErrors, groupName: '' });
                  }}
                  placeholder="Corporate Group, Tour Group, etc."
                  className={`border ${groupBookingErrors.groupName ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.groupName && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.groupName}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Group Type" /> *</Label>
                <Select
                  value={groupBookingForm.groupType}
                  onValueChange={(value) => setGroupBookingForm({ ...groupBookingForm, groupType: value })}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CORPORATE"><TranslatedText text="Corporate" /></SelectItem>
                    <SelectItem value="TOUR"><TranslatedText text="Tour" /></SelectItem>
                    <SelectItem value="EVENT"><TranslatedText text="Event" /></SelectItem>
                    <SelectItem value="WEDDING"><TranslatedText text="Wedding" /></SelectItem>
                    <SelectItem value="CONFERENCE"><TranslatedText text="Conference" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Contact Person" /> *</Label>
              <SearchableSelect
                options={guests.map((guest) => ({
                  value: guest.id,
                  label: `${guest.firstName} ${guest.lastName}`,
                }))}
                value={groupBookingForm.contactPersonId}
                onValueChange={(value) => {
                  setGroupBookingForm({ ...groupBookingForm, contactPersonId: value });
                  if (groupBookingErrors.contactPersonId) setGroupBookingErrors({ ...groupBookingErrors, contactPersonId: '' });
                }}
                placeholder="Select contact person"
                searchPlaceholder="Search guests..."
                emptyText="No guests found."
              />
              {groupBookingErrors.contactPersonId && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.contactPersonId}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Contact Email" /></Label>
                <Input
                  type="email"
                  value={groupBookingForm.contactEmail}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, contactEmail: e.target.value });
                    if (groupBookingErrors.contactEmail) setGroupBookingErrors({ ...groupBookingErrors, contactEmail: '' });
                  }}
                  className={`border ${groupBookingErrors.contactEmail ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.contactEmail && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.contactEmail}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Contact Phone" /></Label>
                <Input
                  value={groupBookingForm.contactPhone}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, contactPhone: e.target.value });
                    if (groupBookingErrors.contactPhone) setGroupBookingErrors({ ...groupBookingErrors, contactPhone: '' });
                  }}
                  placeholder="+1234567890"
                  className={`border ${groupBookingErrors.contactPhone ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.contactPhone && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.contactPhone}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Check-In Date" /> *</Label>
                <Input
                  type="date"
                  value={groupBookingForm.checkInDate}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, checkInDate: e.target.value });
                    if (groupBookingErrors.checkInDate) setGroupBookingErrors({ ...groupBookingErrors, checkInDate: '' });
                  }}
                  className={`border ${groupBookingErrors.checkInDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.checkInDate && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.checkInDate}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Check-Out Date" /> *</Label>
                <Input
                  type="date"
                  value={groupBookingForm.checkOutDate}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, checkOutDate: e.target.value });
                    if (groupBookingErrors.checkOutDate) setGroupBookingErrors({ ...groupBookingErrors, checkOutDate: '' });
                  }}
                  className={`border ${groupBookingErrors.checkOutDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.checkOutDate && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.checkOutDate}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Total Amount" /> <span className="text-muted-foreground text-xs">(Optional)</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  value={groupBookingForm.totalAmount}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, totalAmount: e.target.value });
                    if (groupBookingErrors.totalAmount) setGroupBookingErrors({ ...groupBookingErrors, totalAmount: '' });
                  }}
                  placeholder="Can be calculated from reservations"
                  className={`border ${groupBookingErrors.totalAmount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.totalAmount && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.totalAmount}</p>}
                <p className="text-xs text-muted-foreground mt-1">Amount can be calculated from linked reservations</p>
              </div>
              {/* Deposit Amount - Hidden */}
              <div className="hidden">
                <Label><TranslatedText text="Deposit Amount" /></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={groupBookingForm.depositAmount}
                  onChange={(e) => {
                    setGroupBookingForm({ ...groupBookingForm, depositAmount: e.target.value });
                    if (groupBookingErrors.depositAmount) setGroupBookingErrors({ ...groupBookingErrors, depositAmount: '' });
                  }}
                  className={`border ${groupBookingErrors.depositAmount ? 'border-red-500' : 'border-gray-300'}`}
                />
                {groupBookingErrors.depositAmount && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.depositAmount}</p>}
              </div>
            </div>
            {selectedGroupBookingForEdit && (() => {
              const isGroupPaid = selectedGroupBookingForEdit.paymentStatus === 'PAID';
              return (
              <div>
                <Label><TranslatedText text="Status" /></Label>
                <Select
                  value={groupBookingForm.status}
                    onValueChange={(value) => {
                      // Prevent setting to CHECKED_OUT if group is not paid
                      if (value === 'CHECKED_OUT' && !isGroupPaid) {
                        toast({
                          title: 'Payment Required',
                          description: 'Cannot set status to Checked Out. Group booking payment status must be PAID.',
                          variant: 'destructive',
                        });
                        return;
                      }
                      setGroupBookingForm({ ...groupBookingForm, status: value });
                    }}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING"><TranslatedText text="Pending" /></SelectItem>
                    <SelectItem value="CONFIRMED"><TranslatedText text="Confirmed" /></SelectItem>
                    <SelectItem value="CHECKED_IN"><TranslatedText text="Checked In" /></SelectItem>
                      <SelectItem 
                        value="CHECKED_OUT" 
                        disabled={!isGroupPaid}
                        className={!isGroupPaid ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        <TranslatedText text="Checked Out" />
                        {!isGroupPaid && <span className="text-xs text-muted-foreground ml-2">(Payment Required)</span>}
                      </SelectItem>
                    <SelectItem value="CANCELLED"><TranslatedText text="Cancelled" /></SelectItem>
                  </SelectContent>
                </Select>
                  {!isGroupPaid && (
                    <p className="text-xs text-muted-foreground mt-1">
                      <TranslatedText text="Group booking must be paid before checking out" />
                    </p>
            )}
                </div>
              );
            })()}
            <div>
              <Label><TranslatedText text="Special Requests" /></Label>
              <Textarea
                value={groupBookingForm.specialRequests}
                onChange={(e) => {
                  setGroupBookingForm({ ...groupBookingForm, specialRequests: e.target.value });
                  if (groupBookingErrors.specialRequests) setGroupBookingErrors({ ...groupBookingErrors, specialRequests: '' });
                }}
                placeholder="Any special requests or requirements..."
                maxLength={1000}
                className={`border ${groupBookingErrors.specialRequests ? 'border-red-500' : 'border-gray-300'}`}
              />
              {groupBookingErrors.specialRequests && <p className="text-sm text-red-500 mt-1">{groupBookingErrors.specialRequests}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                {groupBookingForm.specialRequests.length}/1000 <TranslatedText text="characters" />
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGroupBookingDialog(false);
              setSelectedGroupBookingForEdit(null);
              setGroupBookingErrors({});
            }}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={async () => {
              if (!validateGroupBooking()) {
                toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
                return;
              }
              
              // Validate: Cannot set status to CHECKED_OUT if group booking is not paid
              if (selectedGroupBookingForEdit && groupBookingForm.status === 'CHECKED_OUT') {
                const isGroupPaid = selectedGroupBookingForEdit.paymentStatus === 'PAID';
                if (!isGroupPaid) {
                  toast({
                    title: 'Payment Required',
                    description: 'Cannot set status to Checked Out. Group booking payment status must be PAID.',
                    variant: 'destructive',
                  });
                  return;
                }
              }
              
              try {
                // Convert dates to ISO-8601 format with UTC midnight to avoid timezone issues
                // Parse date string (YYYY-MM-DD) and create UTC date at midnight
                let checkInDate = '';
                let checkOutDate = '';
                
                if (groupBookingForm.checkInDate) {
                  const [year, month, day] = groupBookingForm.checkInDate.split('-').map(Number);
                  checkInDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
                }
                
                if (groupBookingForm.checkOutDate) {
                  const [year, month, day] = groupBookingForm.checkOutDate.split('-').map(Number);
                  checkOutDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0)).toISOString();
                }
                
                if (selectedGroupBookingForEdit) {
                  // Update existing group booking
                  await apiService.updateGroupBooking(selectedGroupBookingForEdit.id, {
                    ...groupBookingForm,
                    checkInDate,
                    checkOutDate,
                    numberOfRooms: groupBookingForm.numberOfRooms ? parseInt(groupBookingForm.numberOfRooms) : undefined,
                    numberOfGuests: groupBookingForm.numberOfGuests ? parseInt(groupBookingForm.numberOfGuests) : undefined,
                    totalAmount: groupBookingForm.totalAmount ? parseFloat(groupBookingForm.totalAmount) : undefined,
                    depositAmount: parseFloat(groupBookingForm.depositAmount),
                    status: groupBookingForm.status,
                  });
                  toast({ title: 'Success', description: 'Group booking updated successfully' });
                } else {
                  // Create new group booking
                  await apiService.createGroupBooking({
                    companyId,
                    ...groupBookingForm,
                    checkInDate,
                    checkOutDate,
                  numberOfRooms: groupBookingForm.numberOfRooms ? parseInt(groupBookingForm.numberOfRooms) : undefined,
                  numberOfGuests: groupBookingForm.numberOfGuests ? parseInt(groupBookingForm.numberOfGuests) : undefined,
                  totalAmount: groupBookingForm.totalAmount ? parseFloat(groupBookingForm.totalAmount) : undefined,
                    depositAmount: parseFloat(groupBookingForm.depositAmount),
                  });
                  toast({ title: 'Success', description: 'Group booking created successfully' });
                }
                setShowGroupBookingDialog(false);
                setGroupBookingErrors({});
                setSelectedGroupBookingForEdit(null);
                setGroupBookingForm({
                  groupName: '',
                  groupType: 'CORPORATE',
                  contactPersonId: '',
                  contactEmail: '',
                  contactPhone: '',
                  checkInDate: '',
                  checkOutDate: '',
                  numberOfRooms: '', // Optional - rooms added via reservations
                  numberOfGuests: '', // Optional - guests tracked via reservations
                  totalAmount: '', // Optional - calculated from reservations
                  depositAmount: '0',
                  status: 'PENDING',
                  specialRequests: '',
                  notes: '',
                });
                loadGroupBookings();
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || `Failed to ${selectedGroupBookingForEdit ? 'update' : 'create'} group booking`, variant: 'destructive' });
              }
            }}>
              {selectedGroupBookingForEdit ? <TranslatedText text="Update Group Booking" /> : <TranslatedText text="Create Group Booking" />}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Waitlist Dialog */}
      <Dialog open={showWaitlistDialog} onOpenChange={(open) => {
        setShowWaitlistDialog(open);
        if (!open) {
          setWaitlistErrors({});
          setWaitlistForm({
            guestId: '',
            roomTypeId: '',
            checkInDate: '',
            checkOutDate: '',
            numberOfGuests: '1',
            priority: '0',
            notes: '',
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle><TranslatedText text="Add to Waitlist" /></DialogTitle>
            <DialogDescription><TranslatedText text="Add a guest to the waitlist for room availability" /></DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label><TranslatedText text="Guest" /> *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setGuestForm({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', state: '', postalCode: '', country: '', idType: '', passportNumber: '', idNumber: '' });
                    setShowGuestDialog(true);
                  }}
                  className="h-7 text-xs"
                >
                  <UserPlus className="h-3 w-3 mr-1" />
                  Add New Guest
                </Button>
              </div>
              <SearchableSelect
                options={guests.map((guest) => ({
                  value: guest.id,
                  label: `${guest.firstName} ${guest.lastName}`,
                }))}
                value={waitlistForm.guestId}
                onValueChange={(value) => {
                  setWaitlistForm({ ...waitlistForm, guestId: value });
                  if (waitlistErrors.guestId) setWaitlistErrors({ ...waitlistErrors, guestId: '' });
                }}
                placeholder="Select guest"
                searchPlaceholder="Search guests..."
                emptyText="No guests found."
              />
              {waitlistErrors.guestId && <p className="text-sm text-red-500 mt-1">{waitlistErrors.guestId}</p>}
            </div>
            <div>
              <Label><TranslatedText text="Preferred Room Type" /></Label>
              <Select
                value={waitlistForm.roomTypeId}
                onValueChange={(value) => setWaitlistForm({ ...waitlistForm, roomTypeId: value })}
              >
                <SelectTrigger className="border border-gray-300 w-full">
                  <SelectValue placeholder="Any room type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {roomTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Check-In Date" /> *</Label>
                <Input
                  type="date"
                  value={waitlistForm.checkInDate}
                  onChange={(e) => {
                    setWaitlistForm({ ...waitlistForm, checkInDate: e.target.value });
                    if (waitlistErrors.checkInDate) setWaitlistErrors({ ...waitlistErrors, checkInDate: '' });
                  }}
                  className={`border ${waitlistErrors.checkInDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {waitlistErrors.checkInDate && <p className="text-sm text-red-500 mt-1">{waitlistErrors.checkInDate}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Check-Out Date" /> *</Label>
                <Input
                  type="date"
                  value={waitlistForm.checkOutDate}
                  onChange={(e) => {
                    setWaitlistForm({ ...waitlistForm, checkOutDate: e.target.value });
                    if (waitlistErrors.checkOutDate) setWaitlistErrors({ ...waitlistErrors, checkOutDate: '' });
                  }}
                  className={`border ${waitlistErrors.checkOutDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                {waitlistErrors.checkOutDate && <p className="text-sm text-red-500 mt-1">{waitlistErrors.checkOutDate}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Number of Guests" /></Label>
                <Input
                  type="number"
                  min="1"
                  value={waitlistForm.numberOfGuests}
                  onChange={(e) => {
                    setWaitlistForm({ ...waitlistForm, numberOfGuests: e.target.value });
                    if (waitlistErrors.numberOfGuests) setWaitlistErrors({ ...waitlistErrors, numberOfGuests: '' });
                  }}
                  className={`border ${waitlistErrors.numberOfGuests ? 'border-red-500' : 'border-gray-300'}`}
                />
                {waitlistErrors.numberOfGuests && <p className="text-sm text-red-500 mt-1">{waitlistErrors.numberOfGuests}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Priority" /></Label>
                <Input
                  type="number"
                  value={waitlistForm.priority}
                  onChange={(e) => setWaitlistForm({ ...waitlistForm, priority: e.target.value })}
                  placeholder="Higher number = higher priority"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Notes" /></Label>
              <Textarea
                value={waitlistForm.notes}
                className="border border-gray-300"
                onChange={(e) => setWaitlistForm({ ...waitlistForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWaitlistDialog(false)}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={async () => {
              if (!validateWaitlist()) {
                toast({ 
                  title: 'Validation Error', 
                  description: 'Please fill in all required fields correctly', 
                  variant: 'destructive' 
                });
                return;
              }

              try {
                await apiService.createWaitlist({
                  companyId,
                  ...waitlistForm,
                  numberOfGuests: parseInt(waitlistForm.numberOfGuests),
                  priority: parseInt(waitlistForm.priority),
                  roomTypeId: waitlistForm.roomTypeId || undefined,
                });
                toast({ title: 'Success', description: 'Added to waitlist successfully' });
                setShowWaitlistDialog(false);
                setWaitlistErrors({});
                setWaitlistForm({
                  guestId: '',
                  roomTypeId: '',
                  checkInDate: '',
                  checkOutDate: '',
                  numberOfGuests: '1',
                  priority: '0',
                  notes: '',
                });
                loadWaitlist();
              } catch (error: any) {
                // Extract error message from API response
                let errorMessage = 'Failed to add to waitlist';
                if (error.response?.data?.message) {
                  errorMessage = error.response.data.message;
                } else if (error.response?.data?.error?.message) {
                  errorMessage = error.response.data.error.message;
                } else if (error.message) {
                  errorMessage = error.message;
                }

                // Check for validation errors in response
                if (error.response?.data?.errors) {
                  setWaitlistErrors(error.response.data.errors);
                }

                toast({ 
                  title: 'Error', 
                  description: errorMessage, 
                  variant: 'destructive' 
                });
              }
            }}>
              <TranslatedText text="Add to Waitlist" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Promotion Dialog */}
      <Dialog open={showPromotionDialog} onOpenChange={(open) => {
        setShowPromotionDialog(open);
        if (!open) {
          setSelectedPromotionForEdit(null);
          setPromotionErrors({});
          setPromotionForm({
            name: '',
            code: '',
            description: '',
            discountType: 'PERCENTAGE',
            discountValue: '',
            minStay: '',
            maxStay: '',
            validFrom: '',
            validTo: '',
            maxUses: '',
            isActive: true,
          });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedPromotionForEdit ? <TranslatedText text="Edit Promotion" /> : <TranslatedText text="New Promotion" />}</DialogTitle>
            <DialogDescription>{selectedPromotionForEdit ? <TranslatedText text="Update promotion details" /> : <TranslatedText text="Create a discount code or special offer" />}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Promotion Name" /> *</Label>
                <Input
                  value={promotionForm.name}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, name: e.target.value });
                    if (promotionErrors.name) setPromotionErrors({ ...promotionErrors, name: '' });
                  }}
                  className={`border ${promotionErrors.name ? 'border-red-500' : 'border-gray-300'}`}
                />
                {promotionErrors.name && <p className="text-sm text-red-500 mt-1">{promotionErrors.name}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Promotion Code" /></Label>
                <Input
                  value={promotionForm.code}
                  onChange={(e) => setPromotionForm({ ...promotionForm, code: e.target.value })}
                  placeholder="Optional code"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Description" /></Label>
              <Textarea
                value={promotionForm.description}
                onChange={(e) => setPromotionForm({ ...promotionForm, description: e.target.value })}
                className="border border-gray-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Discount Type" /> *</Label>
                <Select
                  value={promotionForm.discountType}
                  onValueChange={(value) => {
                    setPromotionForm({ ...promotionForm, discountType: value });
                    if (promotionErrors.discountValue) setPromotionErrors({ ...promotionErrors, discountValue: '' });
                  }}
                >
                  <SelectTrigger className="border border-gray-300 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PERCENTAGE"><TranslatedText text="Percentage" /></SelectItem>
                    <SelectItem value="FIXED_AMOUNT"><TranslatedText text="Fixed Amount" /></SelectItem>
                    <SelectItem value="FREE_NIGHT"><TranslatedText text="Free Night" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label><TranslatedText text="Discount Value" /> *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={promotionForm.discountType === 'PERCENTAGE' ? 100 : undefined}
                  value={promotionForm.discountValue}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, discountValue: e.target.value });
                    if (promotionErrors.discountValue) setPromotionErrors({ ...promotionErrors, discountValue: '' });
                  }}
                  className={`border ${promotionErrors.discountValue ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder={promotionForm.discountType === 'PERCENTAGE' ? '0-100' : 'Enter amount'}
                />
                {promotionErrors.discountValue && <p className="text-sm text-red-500 mt-1">{promotionErrors.discountValue}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Min Stay (nights)" /></Label>
                <Input
                  type="number"
                  min="1"
                  value={promotionForm.minStay}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, minStay: e.target.value });
                    if (promotionErrors.minStay) setPromotionErrors({ ...promotionErrors, minStay: '' });
                  }}
                  className={`border ${promotionErrors.minStay ? 'border-red-500' : 'border-gray-300'}`}
                />
                {promotionErrors.minStay && <p className="text-sm text-red-500 mt-1">{promotionErrors.minStay}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Max Stay (nights)" /></Label>
                <Input
                  type="number"
                  min="1"
                  value={promotionForm.maxStay}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, maxStay: e.target.value });
                    if (promotionErrors.maxStay) setPromotionErrors({ ...promotionErrors, maxStay: '' });
                  }}
                  className={`border ${promotionErrors.maxStay ? 'border-red-500' : 'border-gray-300'}`}
                />
                {promotionErrors.maxStay && <p className="text-sm text-red-500 mt-1">{promotionErrors.maxStay}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Valid From" /> *</Label>
                <Input
                  type="date"
                  value={promotionForm.validFrom}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, validFrom: e.target.value });
                    if (promotionErrors.validFrom) setPromotionErrors({ ...promotionErrors, validFrom: '' });
                    if (promotionErrors.validTo) setPromotionErrors({ ...promotionErrors, validTo: '' });
                  }}
                  className={`border ${promotionErrors.validFrom ? 'border-red-500' : 'border-gray-300'}`}
                />
                {promotionErrors.validFrom && <p className="text-sm text-red-500 mt-1">{promotionErrors.validFrom}</p>}
              </div>
              <div>
                <Label><TranslatedText text="Valid To" /> *</Label>
                <Input
                  type="date"
                  value={promotionForm.validTo}
                  onChange={(e) => {
                    setPromotionForm({ ...promotionForm, validTo: e.target.value });
                    if (promotionErrors.validTo) setPromotionErrors({ ...promotionErrors, validTo: '' });
                  }}
                  className={`border ${promotionErrors.validTo ? 'border-red-500' : 'border-gray-300'}`}
                />
                {promotionErrors.validTo && <p className="text-sm text-red-500 mt-1">{promotionErrors.validTo}</p>}
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Max Uses" /></Label>
              <Input
                type="number"
                min="1"
                value={promotionForm.maxUses}
                onChange={(e) => {
                  setPromotionForm({ ...promotionForm, maxUses: e.target.value });
                  if (promotionErrors.maxUses) setPromotionErrors({ ...promotionErrors, maxUses: '' });
                }}
                placeholder="Unlimited if empty"
                className={`border ${promotionErrors.maxUses ? 'border-red-500' : 'border-gray-300'}`}
              />
              {promotionErrors.maxUses && <p className="text-sm text-red-500 mt-1">{promotionErrors.maxUses}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowPromotionDialog(false)}
              disabled={isCreatingPromotion}
            >
              <TranslatedText text="Cancel" />
            </Button>
            <Button 
              onClick={async () => {
                if (!validatePromotion()) {
                  toast({ 
                    title: 'Validation Error', 
                    description: 'Please fill in all required fields correctly', 
                    variant: 'destructive' 
                  });
                  return;
                }

                if (selectedPromotionForEdit) {
                  setIsUpdatingPromotion(true);
                } else {
                setIsCreatingPromotion(true);
                }
                
              try {
                const promotionData = {
                  companyId,
                  ...promotionForm,
                  discountValue: parseFloat(promotionForm.discountValue),
                  minStay: promotionForm.minStay ? parseInt(promotionForm.minStay) : undefined,
                  maxStay: promotionForm.maxStay ? parseInt(promotionForm.maxStay) : undefined,
                  maxUses: promotionForm.maxUses ? parseInt(promotionForm.maxUses) : undefined,
                  code: promotionForm.code || undefined,
                };

                if (selectedPromotionForEdit) {
                  await apiService.updatePromotion(selectedPromotionForEdit.id, promotionData);
                  toast({ title: 'Success', description: 'Promotion updated successfully' });
                } else {
                  await apiService.createPromotion(promotionData);
                toast({ title: 'Success', description: 'Promotion created successfully' });
                }
                
                setShowPromotionDialog(false);
                setSelectedPromotionForEdit(null);
                  setPromotionErrors({});
                setPromotionForm({
                  name: '',
                  code: '',
                  description: '',
                  discountType: 'PERCENTAGE',
                  discountValue: '',
                  minStay: '',
                  maxStay: '',
                  validFrom: '',
                  validTo: '',
                  maxUses: '',
                  isActive: true,
                });
                loadPromotions();
              } catch (error: any) {
                  // Extract error message from API response
                  let errorMessage = selectedPromotionForEdit ? 'Failed to update promotion' : 'Failed to create promotion';
                  if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                  } else if (error.response?.data?.error?.message) {
                    errorMessage = error.response.data.error.message;
                  } else if (error.message) {
                    errorMessage = error.message;
                  }

                  // Check for validation errors in response
                  if (error.response?.data?.errors) {
                    setPromotionErrors(error.response.data.errors);
                  }

                  toast({ 
                    title: 'Error', 
                    description: errorMessage, 
                    variant: 'destructive' 
                  });
                } finally {
                  setIsCreatingPromotion(false);
                  setIsUpdatingPromotion(false);
                }
              }}
              disabled={isCreatingPromotion || isUpdatingPromotion}
            >
              {(isCreatingPromotion || isUpdatingPromotion) ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <TranslatedText text={selectedPromotionForEdit ? "Updating..." : "Creating..."} />
                </>
              ) : (
              <TranslatedText text={selectedPromotionForEdit ? "Update Promotion" : "Create Promotion"} />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Reservations to Group Booking Dialog */}
      <Dialog open={showAddReservationsToGroupDialog} onOpenChange={(open) => {
        setShowAddReservationsToGroupDialog(open);
        if (open && selectedGroupForReservations) {
          // Pre-check reservations that are already linked to this group
          // First try to get IDs from linkedReservations if available
          let alreadyLinkedReservationIds: string[] = [];
          if (selectedGroupForReservations.linkedReservations && selectedGroupForReservations.linkedReservations.length > 0) {
            alreadyLinkedReservationIds = selectedGroupForReservations.linkedReservations.map((r: any) => r.id);
          } else {
            // Fallback to checking reservations array
            alreadyLinkedReservationIds = reservations
              .filter((res: any) => res.groupBookingId === selectedGroupForReservations.id)
              .map((res: any) => res.id);
          }
          // Store initial state for comparison when updating
          setInitialLinkedReservationIds(alreadyLinkedReservationIds);
          setSelectedReservationsToLink(alreadyLinkedReservationIds);
        }
        if (!open) {
          setSelectedGroupForReservations(null);
          setAddReservationMode('link');
          setSelectedReservationsToLink([]);
          setInitialLinkedReservationIds([]);
          setReservationForm({
            bookingType: 'ROOM',
            roomId: '',
            bookableItemId: '',
            bookableItemType: '',
            guestId: '',
            ratePlanId: '',
            groupBookingId: '',
            promotionId: '',
            promotionIds: [],
            checkInDate: '',
            checkOutDate: '',
            numberOfGuests: '1',
            totalAmount: '',
            depositAmount: '0',
            specialRequests: '',
          });
          setOriginalTotalAmount('');
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <TranslatedText text="Manage Group Reservations" />
            </DialogTitle>
            <DialogDescription>
              {selectedGroupForReservations && (
                <>
                  {selectedGroupForReservations.groupName}
                  <br />
                  <span className="text-xs text-muted-foreground">
                    ({selectedGroupForReservations.linkedReservationsCount || 0} <TranslatedText text="rooms already reserved" />{selectedGroupForReservations.numberOfRooms ? ` / ${selectedGroupForReservations.numberOfRooms} total` : ''})
                    <br />
                    <TranslatedText text="Select or deselect reservations to link or unlink them from this group" />
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
            {selectedGroupForReservations && (
              <>
              <div className="p-3 bg-muted rounded-lg mb-4">
                  <p className="text-sm font-semibold mb-1"><TranslatedText text="Group Details" /></p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground"><TranslatedText text="Check-In:" /></span>{' '}
                      {new Date(selectedGroupForReservations.checkInDate).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="text-muted-foreground"><TranslatedText text="Check-Out:" /></span>{' '}
                      {new Date(selectedGroupForReservations.checkOutDate).toLocaleDateString()}
                    </div>
                  </div>
                </div>

              {/* Tabs for Link vs Create */}
              <Tabs value={addReservationMode} onValueChange={(value) => setAddReservationMode(value as 'link' | 'create')} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="link">
                    <TranslatedText text="Link Existing Reservations" />
                  </TabsTrigger>
                  <TabsTrigger value="create">
                    <TranslatedText text="Create New Reservation" />
                  </TabsTrigger>
                </TabsList>

                {/* Link Existing Reservations Tab */}
                <TabsContent value="link" className="space-y-4 mt-4">
                  <div>
                    <Label><TranslatedText text="Select Reservations to Link" /></Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      <TranslatedText text="Select reservations to link to this group. Already linked reservations are pre-checked. Unchecking a linked reservation will unlink it from the group." />
                    </p>
                    <div className="border rounded-lg max-h-64 overflow-y-auto">
                      {reservations.length === 0 ? (
                        <p className="text-sm text-muted-foreground p-4 text-center">
                          <TranslatedText text="No reservations found" />
                        </p>
                      ) : (
                        <div className="divide-y">
                          {reservations.map((reservation: any) => {
                            const isAlreadyLinked = reservation.groupBookingId === selectedGroupForReservations?.id;
                            return (
                              <label
                                key={reservation.id}
                                className="flex items-center space-x-3 p-3 hover:bg-muted cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedReservationsToLink.includes(reservation.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      // Add to selection if not already there
                                      if (!selectedReservationsToLink.includes(reservation.id)) {
                                        setSelectedReservationsToLink([...selectedReservationsToLink, reservation.id]);
                                      }
                                    } else {
                                      // If unchecking an already linked reservation, confirm first
                                      if (isAlreadyLinked) {
                                        e.preventDefault();
                                        e.target.checked = true; // Keep checked until confirmed
                                        setConfirmDialogConfig({
                                          title: 'Unlink Reservation',
                                          description: `Are you sure you want to unlink reservation ${reservation.reservationNumber} from this group?`,
                                          onConfirm: () => {
                                            setShowConfirmDialog(false);
                                            // Remove from selection (this will unlink if it was already linked)
                                            setSelectedReservationsToLink(selectedReservationsToLink.filter(id => id !== reservation.id));
                                          },
                                          confirmText: 'Unlink',
                                          cancelText: 'Cancel',
                                        });
                                        setShowConfirmDialog(true);
                                          return;
                                      }
                                      // Remove from selection (this will unlink if it was already linked)
                                      setSelectedReservationsToLink(selectedReservationsToLink.filter(id => id !== reservation.id));
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <span className="font-medium">
                                      {reservation.reservationNumber} - {reservation.room?.roomNumber || 'N/A'}
                                      {isAlreadyLinked && (
                                        <Badge variant="secondary" className="ml-2 text-xs">
                                          <TranslatedText text="Already Linked" />
                                        </Badge>
                                      )}
                                    </span>
                                    <Badge variant="outline">{reservation.status}</Badge>
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {reservation.guest?.firstName} {reservation.guest?.lastName} • {' '}
                                    {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()} • {' '}
                                    {formatCurrency(reservation.totalAmount, reservation.room?.currency || 'RWF')}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    {selectedReservationsToLink.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {selectedReservationsToLink.length} <TranslatedText text="reservation(s) selected" />
                      </p>
                    )}
                  </div>
                </TabsContent>

                {/* Create New Reservation Tab */}
                <TabsContent value="create" className="space-y-4 mt-4">
                <div>
                  <Label><TranslatedText text="Room" /> *</Label>
                  <SearchableSelect
                    options={rooms
                      .filter(room => room.isActive)
                      .map((room) => {
                        const bookedDates = getRoomBookedDates(room.id);
                        const isReserved = isRoomReserved(room.id);
                        let label = `${room.roomNumber} - ${room.roomType?.name || 'N/A'}`;
                        if (isReserved && bookedDates) {
                          label += ` (Reserved: ${bookedDates})`;
                        } else if (isReserved) {
                          label += ` (Reserved)`;
                        }
                        return {
                          value: room.id,
                          label,
                        };
                      })}
                    value={reservationForm.roomId}
                    onValueChange={(value) => {
                      setReservationForm({ ...reservationForm, roomId: value });
                      if (reservationErrors.roomId) setReservationErrors({ ...reservationErrors, roomId: '' });
                        setAmountBreakdown(null);
                        setOriginalTotalAmount('');
                        // Recalculate if dates are available from group
                        if (selectedGroupForReservations?.checkInDate && selectedGroupForReservations?.checkOutDate) {
                          setTimeout(() => {
                            // Temporarily set dates for calculation
                            const tempForm = { ...reservationForm, roomId: value };
                            if (tempForm.roomId && selectedGroupForReservations.checkInDate && selectedGroupForReservations.checkOutDate) {
                              calculateReservationAmount();
                            }
                          }, 100);
                        }
                    }}
                    placeholder="Select room..."
                    searchPlaceholder="Search rooms..."
                    emptyText="No rooms found."
                  />
                  {reservationErrors.roomId && <p className="text-sm text-red-500 mt-1">{reservationErrors.roomId}</p>}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label><TranslatedText text="Guest" /> *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setGuestForm({ firstName: '', lastName: '', email: '', phone: '', address: '', city: '', state: '', postalCode: '', country: '', idType: '', passportNumber: '', idNumber: '' });
                        setShowGuestDialog(true);
                      }}
                      className="h-7 text-xs"
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Add New Guest
                    </Button>
                  </div>
                  <SearchableSelect
                    options={guests.map((guest) => ({
                      value: guest.id,
                      label: `${guest.firstName} ${guest.lastName}${guest.email ? ` (${guest.email})` : ''}`,
                    }))}
                    value={reservationForm.guestId}
                    onValueChange={(value) => {
                      setReservationForm({ ...reservationForm, guestId: value });
                      if (reservationErrors.guestId) setReservationErrors({ ...reservationErrors, guestId: '' });
                    }}
                    placeholder="Select guest..."
                    searchPlaceholder="Search guests..."
                    emptyText="No guests found."
                  />
                  {reservationErrors.guestId && <p className="text-sm text-red-500 mt-1">{reservationErrors.guestId}</p>}
                </div>
                  {/* Rate Plan - Hidden */}
                  <div className="hidden">
                    <Label><TranslatedText text="Rate Plan" /> <span className="text-xs text-muted-foreground">(Optional)</span></Label>
                    <Select
                      value={reservationForm.ratePlanId || 'none'}
                      onValueChange={(value) => {
                        const newRatePlanId = value === 'none' ? '' : value;
                        setReservationForm({ ...reservationForm, ratePlanId: newRatePlanId });
                        if (reservationForm.roomId && selectedGroupForReservations?.checkInDate && selectedGroupForReservations?.checkOutDate) {
                          setTimeout(() => calculateReservationAmount(), 100);
                        }
                      }}
                    >
                      <SelectTrigger className="border border-gray-300 w-full">
                        <SelectValue placeholder="Select rate plan (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none"><TranslatedText text="No Rate Plan" /></SelectItem>
                        {ratePlans.filter(rp => rp.isActive).map((ratePlan) => (
                          <SelectItem key={ratePlan.id} value={ratePlan.id}>
                            {ratePlan.name} - {formatCurrency(ratePlan.price)}/night
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Promotions - Hide if no active promotions */}
                  {promotions.filter(p => p.isActive && new Date(p.validTo) >= new Date() && new Date(p.validFrom) <= new Date()).length > 0 && (
                  <div>
                    <Label><TranslatedText text="Promotions" /> <span className="text-xs text-muted-foreground">(Optional - Select multiple)</span></Label>
                    <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                        <div className="space-y-2">
                          {promotions
                            .filter(p => p.isActive && new Date(p.validTo) >= new Date() && new Date(p.validFrom) <= new Date())
                            .map((promo) => (
                              <label key={promo.id} className="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={reservationForm.promotionIds.includes(promo.id)}
                                  onChange={(e) => {
                                    const newPromotionIds = e.target.checked
                                      ? [...reservationForm.promotionIds, promo.id]
                                      : reservationForm.promotionIds.filter(id => id !== promo.id);
                                    setReservationForm({ 
                                      ...reservationForm, 
                                      promotionIds: newPromotionIds,
                                      promotionId: newPromotionIds.length > 0 ? newPromotionIds[0] : '' // Keep first for backward compatibility
                                    });
                                    // Recalculate amount when promotion changes
                                    if (reservationForm.roomId && selectedGroupForReservations?.checkInDate && selectedGroupForReservations?.checkOutDate) {
                                      setTimeout(() => calculateReservationAmount(), 100);
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm flex-1">
                                  <span className="font-medium">{promo.name}</span>
                                  {promo.code && <span className="text-muted-foreground"> ({promo.code})</span>}
                                  <span className="text-muted-foreground ml-2">
                                    - {promo.discountType === 'PERCENTAGE' ? `${promo.discountValue}%` : promo.discountType === 'FIXED_AMOUNT' ? formatCurrency(promo.discountValue) : `${promo.discountValue} free night(s)`}
                                  </span>
                                </span>
                              </label>
                            ))}
                        </div>
                    </div>
                    {reservationForm.promotionIds.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {reservationForm.promotionIds.length} <TranslatedText text="promotion(s) selected" />
                      </p>
                    )}
                  </div>
                  )}
                  {amountBreakdown && selectedGroupForReservations && (
                    <div className="border rounded-lg p-4 bg-muted/50">
                      <Label className="text-sm font-semibold mb-3"><TranslatedText text="Amount Breakdown" /></Label>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground"><TranslatedText text="Room Price" /> ({amountBreakdown.numberOfNights} <TranslatedText text="nights" /> @ {formatCurrency(amountBreakdown.roomPricePerNight, amountBreakdown.currency)}/night):</span>
                          <span className="font-medium">{formatCurrency(amountBreakdown.roomPricePerNight * amountBreakdown.numberOfNights, amountBreakdown.currency)}</span>
                        </div>
                        {amountBreakdown.ratePlanAdjustment !== 0 && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground"><TranslatedText text="Rate Plan Adjustment" />:</span>
                            <span className={`font-medium ${amountBreakdown.ratePlanAdjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {amountBreakdown.ratePlanAdjustment > 0 ? '+' : ''}{formatCurrency(amountBreakdown.ratePlanAdjustment, amountBreakdown.currency)}
                            </span>
                          </div>
                        )}
                        {amountBreakdown.promotionDiscounts.length > 0 && (
                          <>
                            {amountBreakdown.promotionDiscounts.map((promoDiscount, index) => (
                              <div key={promoDiscount.id} className="flex justify-between">
                                <span className="text-muted-foreground">
                                  {index === 0 ? <TranslatedText text="Promotion Discounts" /> : ''} {promoDiscount.name}:
                                </span>
                                <span className="font-medium text-green-600">-{formatCurrency(promoDiscount.discount, amountBreakdown.currency)}</span>
                              </div>
                            ))}
                            {amountBreakdown.promotionDiscounts.length > 1 && (
                              <div className="flex justify-between border-t pt-1 mt-1">
                                <span className="text-muted-foreground font-medium"><TranslatedText text="Total Promotion Discount" />:</span>
                                <span className="font-medium text-green-600">-{formatCurrency(amountBreakdown.promotionDiscount, amountBreakdown.currency)}</span>
                              </div>
                            )}
                          </>
                        )}
                        <div className="border-t pt-2 mt-2 flex justify-between">
                          <span className="font-semibold"><TranslatedText text="Total Amount" />:</span>
                          <span className="font-semibold text-lg">{formatCurrency(amountBreakdown.finalAmount, amountBreakdown.currency)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label><TranslatedText text="Number of Guests" /> *</Label>
                    <Input
                      type="number"
                      min="1"
                      max={reservationForm.roomId ? rooms.find(r => r.id === reservationForm.roomId)?.capacity : undefined}
                      value={reservationForm.numberOfGuests}
                      onChange={(e) => {
                        const selectedRoom = rooms.find(r => r.id === reservationForm.roomId);
                        const maxGuests = selectedRoom?.capacity;
                        const value = e.target.value;
                        
                        // Prevent entering more than room capacity
                        if (maxGuests && parseInt(value) > maxGuests) {
                          setReservationForm({ ...reservationForm, numberOfGuests: maxGuests.toString() });
                        } else {
                          setReservationForm({ ...reservationForm, numberOfGuests: value });
                        }
                        if (reservationErrors.numberOfGuests) setReservationErrors({ ...reservationErrors, numberOfGuests: '' });
                      }}
                      className={`border ${reservationErrors.numberOfGuests ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {reservationErrors.numberOfGuests && <p className="text-sm text-red-500 mt-1">{reservationErrors.numberOfGuests}</p>}
                  </div>
                  <div>
                    <Label><TranslatedText text="Total Amount" /> *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={reservationForm.totalAmount}
                      onChange={(e) => {
                          const newValue = e.target.value;
                          setReservationForm({ ...reservationForm, totalAmount: newValue });
                          setOriginalTotalAmount(newValue);
                          setAmountBreakdown(null);
                        if (reservationErrors.totalAmount) setReservationErrors({ ...reservationErrors, totalAmount: '' });
                      }}
                      className={`border ${reservationErrors.totalAmount ? 'border-red-500' : 'border-gray-300'}`}
                    />
                    {reservationErrors.totalAmount && <p className="text-sm text-red-500 mt-1">{reservationErrors.totalAmount}</p>}
                      {amountBreakdown && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <TranslatedText text="Auto-calculated" /> - <TranslatedText text="You can edit this amount" />
                        </p>
                      )}
                  </div>
                </div>
                </TabsContent>
              </Tabs>
              </>
            )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddReservationsToGroupDialog(false);
              setSelectedGroupForReservations(null);
              setAddReservationMode('link');
              setSelectedReservationsToLink([]);
              setAmountBreakdown(null);
              setOriginalTotalAmount('');
            }}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={async () => {
              if (!selectedGroupForReservations) return;
              
              if (addReservationMode === 'link') {
                // Use the stored initial state from when dialog opened
                const initialLinkedIds = initialLinkedReservationIds;
                
                // Current selection includes both newly selected and originally linked (if still checked)
                const currentSelectedIds = selectedReservationsToLink;
                
                // Find reservations to link (in current selection but not in initial)
                const toLink = currentSelectedIds.filter(id => !initialLinkedIds.includes(id));
                
                // Find reservations to unlink (in initial but not in current selection)
                const toUnlink = initialLinkedIds.filter(id => !currentSelectedIds.includes(id));
                
                if (toLink.length === 0 && toUnlink.length === 0) {
                  toast({ title: 'Info', description: 'No changes to apply', variant: 'default' });
                  return;
                }
                
                setIsCreatingReservation(true);
                try {
                  // Link new reservations
                  const linkPromises = toLink.map((reservationId: string) =>
                    apiService.updateHotelReservation(reservationId, {
                      groupBookingId: selectedGroupForReservations.id,
                    })
                  );
                  
                  // Unlink removed reservations
                  const unlinkPromises = toUnlink.map((reservationId: string) =>
                    apiService.updateHotelReservation(reservationId, {
                      groupBookingId: null,
                    })
                  );
                  
                  await Promise.all([...linkPromises, ...unlinkPromises]);
                  
                  const messages = [];
                  if (toLink.length > 0) messages.push(`${toLink.length} reservation(s) linked`);
                  if (toUnlink.length > 0) messages.push(`${toUnlink.length} reservation(s) unlinked`);
                  
                  toast({
                    title: 'Success',
                    description: messages.join(', '),
                  });
                  setShowAddReservationsToGroupDialog(false);
                  setSelectedReservationsToLink([]);
                  loadGroupBookings();
                  loadReservations();
                } catch (error: any) {
                  toast({
                    title: 'Error',
                    description: error.message || 'Failed to update reservations',
                    variant: 'destructive',
                  });
                } finally {
                  setIsCreatingReservation(false);
                }
              } else {
                // Create new reservation
              if (!validateReservation()) {
                toast({ title: 'Validation Error', description: 'Please fill in all required fields correctly', variant: 'destructive' });
                return;
              }
              
              setIsCreatingReservation(true);
              try {
                const checkInDate = selectedGroupForReservations.checkInDate ? new Date(selectedGroupForReservations.checkInDate + 'T00:00:00').toISOString() : '';
                const checkOutDate = selectedGroupForReservations.checkOutDate ? new Date(selectedGroupForReservations.checkOutDate + 'T00:00:00').toISOString() : '';
                
                await apiService.createHotelReservation({
                  companyId,
                  ...reservationForm,
                    groupBookingId: selectedGroupForReservations.id,
                    promotionId: reservationForm.promotionId || undefined,
                  checkInDate,
                  checkOutDate,
                  numberOfGuests: parseInt(reservationForm.numberOfGuests),
                  totalAmount: parseFloat(reservationForm.totalAmount),
                  depositAmount: parseFloat(reservationForm.depositAmount),
                });
                toast({
                  title: 'Success',
                  description: 'Reservation created and linked to group booking',
                });
                setShowAddReservationsToGroupDialog(false);
                setReservationErrors({});
                setReservationForm({
                  bookingType: 'ROOM',
                  roomId: '',
                  bookableItemId: '',
                  bookableItemType: '',
                  guestId: '',
                  ratePlanId: '',
                  groupBookingId: '',
                  promotionId: '',
                  promotionIds: [],
                  checkInDate: '',
                  checkOutDate: '',
                  numberOfGuests: '1',
                  totalAmount: '',
                  depositAmount: '0',
                  specialRequests: '',
                });
                  setOriginalTotalAmount('');
                  setAmountBreakdown(null);
                loadGroupBookings();
                loadReservations();
              } catch (error: any) {
                toast({
                  title: 'Error',
                  description: error.message || 'Failed to create reservation',
                  variant: 'destructive',
                });
              } finally {
                setIsCreatingReservation(false);
                }
              }
            }}>
              {isCreatingReservation && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {addReservationMode === 'link' ? (
                <TranslatedText text={selectedReservationsToLink.length > 0 ? `Update ${selectedReservationsToLink.length} Reservation(s)` : 'Update Reservations'} />
              ) : (
              <TranslatedText text="Create Reservation" />
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Booking Details Dialog */}
      <Dialog open={showGroupBookingDetailsDialog} onOpenChange={(open) => {
        setShowGroupBookingDetailsDialog(open);
        if (!open) {
          setSelectedGroupBookingForDetails(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <TranslatedText text="Group Booking Details" />
            </DialogTitle>
            <DialogDescription>
              {selectedGroupBookingForDetails?.groupName}
            </DialogDescription>
          </DialogHeader>
          
          {selectedGroupBookingForDetails && (
            <div className="space-y-6 py-4">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm"><TranslatedText text="Basic Information" /></CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Group Name" /></Label>
                      <p className="text-sm font-medium">{selectedGroupBookingForDetails.groupName}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Group Type" /></Label>
                      <Badge variant="outline">{selectedGroupBookingForDetails.groupType}</Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Status" /></Label>
                      <Badge variant={selectedGroupBookingForDetails.status === 'CONFIRMED' ? 'default' : 'secondary'}>
                        {selectedGroupBookingForDetails.status}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Payment Status" /></Label>
                      <Badge variant={selectedGroupBookingForDetails.paymentStatus === 'PAID' ? 'default' : 'secondary'}>
                        {selectedGroupBookingForDetails.paymentStatus}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm"><TranslatedText text="Dates & Duration" /></CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-In Date" /></Label>
                      <p className="text-sm font-medium">
                        {new Date(selectedGroupBookingForDetails.checkInDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Check-Out Date" /></Label>
                      <p className="text-sm font-medium">
                        {new Date(selectedGroupBookingForDetails.checkOutDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Duration" /></Label>
                      <p className="text-sm font-medium">
                        {Math.ceil((new Date(selectedGroupBookingForDetails.checkOutDate).getTime() - new Date(selectedGroupBookingForDetails.checkInDate).getTime()) / (1000 * 60 * 60 * 24))} <TranslatedText text="nights" />
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Created" /></Label>
                      <p className="text-sm font-medium">
                        {new Date(selectedGroupBookingForDetails.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm"><TranslatedText text="Contact Information" /></CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Contact Person" /></Label>
                      <p className="text-sm font-medium">
                        {selectedGroupBookingForDetails.contactPerson?.firstName} {selectedGroupBookingForDetails.contactPerson?.lastName}
                      </p>
                      {selectedGroupBookingForDetails.contactPerson?.email && (
                        <p className="text-xs text-muted-foreground">{selectedGroupBookingForDetails.contactPerson.email}</p>
                      )}
                      {selectedGroupBookingForDetails.contactPerson?.phone && (
                        <p className="text-xs text-muted-foreground">{selectedGroupBookingForDetails.contactPerson.phone}</p>
                      )}
                    </div>
                    <div>
                      {(selectedGroupBookingForDetails.contactEmail || selectedGroupBookingForDetails.contactPhone) && (
                        <>
                          {selectedGroupBookingForDetails.contactEmail && (
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Group Email" /></Label>
                              <p className="text-sm font-medium">{selectedGroupBookingForDetails.contactEmail}</p>
                            </div>
                          )}
                          {selectedGroupBookingForDetails.contactPhone && (
                            <div>
                              <Label className="text-xs text-muted-foreground"><TranslatedText text="Group Phone" /></Label>
                              <p className="text-sm font-medium">{selectedGroupBookingForDetails.contactPhone}</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Rooms & Guests Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm"><TranslatedText text="Rooms & Guests Summary" /></CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Rooms Reserved" /></Label>
                      <p className="text-2xl font-bold">
                        {selectedGroupBookingForDetails.reservations?.length || 0}
                        {selectedGroupBookingForDetails.numberOfRooms && ` / ${selectedGroupBookingForDetails.numberOfRooms}`}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Guests" /></Label>
                      <p className="text-2xl font-bold">
                        {selectedGroupBookingForDetails.reservations?.reduce((sum: number, r: any) => sum + (r.numberOfGuests || 0), 0) || selectedGroupBookingForDetails.numberOfGuests || 0}
                        {selectedGroupBookingForDetails.numberOfGuests && selectedGroupBookingForDetails.reservations && ` / ${selectedGroupBookingForDetails.numberOfGuests}`}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground"><TranslatedText text="Total Amount" /></Label>
                      <p className="text-2xl font-bold">
                        {selectedGroupBookingForDetails.reservations?.reduce((sum: number, r: any) => sum + parseFloat(r.totalAmount || 0), 0) 
                          ? formatCurrency(selectedGroupBookingForDetails.reservations.reduce((sum: number, r: any) => sum + parseFloat(r.totalAmount || 0), 0))
                          : selectedGroupBookingForDetails.totalAmount 
                            ? formatCurrency(selectedGroupBookingForDetails.totalAmount)
                            : 'Calculated from reservations'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Linked Reservations */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">
                    <TranslatedText text="Linked Reservations" /> ({selectedGroupBookingForDetails.reservations?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedGroupBookingForDetails.reservations && selectedGroupBookingForDetails.reservations.length > 0 ? (
                    <div className="space-y-3">
                      {selectedGroupBookingForDetails.reservations.map((reservation: any) => (
                        <div key={reservation.id} className="border rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold">{reservation.reservationNumber}</span>
                                <Badge variant="outline">{reservation.status}</Badge>
                                <Badge variant={reservation.paymentStatus === 'PAID' ? 'default' : 'secondary'}>
                                  {reservation.paymentStatus}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <Label className="text-xs text-muted-foreground"><TranslatedText text="Room" /></Label>
                                  <p className="font-medium">{reservation.room?.roomNumber || 'N/A'}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground"><TranslatedText text="Guest" /></Label>
                                  <p className="font-medium">
                                    {reservation.guest?.firstName} {reservation.guest?.lastName}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground"><TranslatedText text="Guests" /></Label>
                                  <p className="font-medium">{reservation.numberOfGuests}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-muted-foreground"><TranslatedText text="Amount" /></Label>
                                  <p className="font-medium">{formatCurrency(reservation.totalAmount, reservation.room?.currency || 'RWF')}</p>
                                </div>
                              </div>
                              <div className="mt-2 text-xs text-muted-foreground">
                                {new Date(reservation.checkInDate).toLocaleDateString()} - {new Date(reservation.checkOutDate).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="ml-4 flex items-center gap-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      // Prevent editing checked out reservations
                                      if (reservation.status === 'CHECKED_OUT') {
                                        toast({
                                          title: 'Cannot Edit',
                                          description: 'Checked out reservations cannot be edited. The reservation has been completed.',
                                          variant: 'destructive',
                                        });
                                        return;
                                      }
                                      try {
                                        // Load full reservation details
                                        const fullReservation = await apiService.getHotelReservation(reservation.id);
                                        setSelectedReservationForEdit(fullReservation);
                                        setShowEditReservationDialog(true);
                                        // Optionally close group booking details
                                        // setShowGroupBookingDetailsDialog(false);
                                      } catch (error: any) {
                                        // Fallback to the reservation from list
                                        setSelectedReservationForEdit(reservation);
                                        setShowEditReservationDialog(true);
                                        console.error('Failed to load full reservation details:', error);
                                      }
                                    }}
                                    disabled={reservation.status === 'CHECKED_OUT'}
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    <TranslatedText text="Edit Reservation" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        const fullReservation = await apiService.getHotelReservation(reservation.id);
                                        setSelectedReservationDetails(fullReservation);
                                        setShowReservationDetailsDialog(true);
                                      } catch (error: any) {
                                        setSelectedReservationDetails(reservation);
                                        setShowReservationDetailsDialog(true);
                                        console.error('Failed to load full reservation details:', error);
                                      }
                                    }}
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    <TranslatedText text="View Details" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      if (selectedGroupBookingForDetails) {
                                        handleUnlinkReservationFromGroup(reservation.id, selectedGroupBookingForDetails.id);
                                      }
                                    }}
                                    className="text-orange-600"
                                  >
                                    <Link2Off className="w-4 h-4 mr-2" />
                                    <TranslatedText text="Unlink from Group" />
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      <TranslatedText text="No reservations linked to this group booking" />
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Additional Information */}
              {(selectedGroupBookingForDetails.specialRequests || selectedGroupBookingForDetails.notes || selectedGroupBookingForDetails.depositAmount > 0) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm"><TranslatedText text="Additional Information" /></CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Deposit Amount - Hidden */}
                    {false && selectedGroupBookingForDetails.depositAmount > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Deposit Amount" /></Label>
                        <p className="text-sm font-medium">{formatCurrency(selectedGroupBookingForDetails.depositAmount)}</p>
                      </div>
                    )}
                    {selectedGroupBookingForDetails.specialRequests && (
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Special Requests" /></Label>
                        <p className="text-sm">{selectedGroupBookingForDetails.specialRequests}</p>
                      </div>
                    )}
                    {selectedGroupBookingForDetails.notes && (
                      <div>
                        <Label className="text-xs text-muted-foreground"><TranslatedText text="Notes" /></Label>
                        <p className="text-sm whitespace-pre-wrap">{selectedGroupBookingForDetails.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowGroupBookingDetailsDialog(false);
              setSelectedGroupBookingForDetails(null);
            }}>
              <TranslatedText text="Close" />
            </Button>
            {selectedGroupBookingForDetails && (
              <Button onClick={() => {
                setShowGroupBookingDetailsDialog(false);
                setSelectedGroupBookingForEdit(selectedGroupBookingForDetails);
                setGroupBookingForm({
                  groupName: selectedGroupBookingForDetails.groupName,
                  groupType: selectedGroupBookingForDetails.groupType,
                  contactPersonId: selectedGroupBookingForDetails.contactPersonId,
                  contactEmail: selectedGroupBookingForDetails.contactEmail || '',
                  contactPhone: selectedGroupBookingForDetails.contactPhone || '',
                  checkInDate: selectedGroupBookingForDetails.checkInDate ? new Date(selectedGroupBookingForDetails.checkInDate).toISOString().split('T')[0] : '',
                  checkOutDate: selectedGroupBookingForDetails.checkOutDate ? new Date(selectedGroupBookingForDetails.checkOutDate).toISOString().split('T')[0] : '',
                  numberOfRooms: selectedGroupBookingForDetails.numberOfRooms ? selectedGroupBookingForDetails.numberOfRooms.toString() : '',
                  numberOfGuests: selectedGroupBookingForDetails.numberOfGuests ? selectedGroupBookingForDetails.numberOfGuests.toString() : '',
                  totalAmount: selectedGroupBookingForDetails.totalAmount ? selectedGroupBookingForDetails.totalAmount.toString() : '',
                  depositAmount: selectedGroupBookingForDetails.depositAmount.toString(),
                  status: selectedGroupBookingForDetails.status || 'PENDING',
                  specialRequests: selectedGroupBookingForDetails.specialRequests || '',
                  notes: selectedGroupBookingForDetails.notes || '',
                });
                setGroupBookingErrors({});
                setShowGroupBookingDialog(true);
              }}>
                <Edit className="w-4 h-4 mr-2" />
                <TranslatedText text="Edit" />
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Package Dialog - Hidden for now */}
      {/* <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle><TranslatedText text="New Package" /></DialogTitle>
            <DialogDescription><TranslatedText text="Create a bundled package deal" /></DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label><TranslatedText text="Package Name" /> *</Label>
              <Input
                value={packageForm.name}
                onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label><TranslatedText text="Description" /></Label>
              <Textarea
                value={packageForm.description}
                onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                className="border border-gray-300"
              />
            </div>
            <div>
              <Label><TranslatedText text="Room Type" /></Label>
              <Select
                value={packageForm.roomTypeId}
                onValueChange={(value) => setPackageForm({ ...packageForm, roomTypeId: value })}
              >
                <SelectTrigger className="border border-gray-300 w-full">
                  <SelectValue placeholder="All room types" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {roomTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Base Price" /> *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={packageForm.basePrice}
                  onChange={(e) => setPackageForm({ ...packageForm, basePrice: e.target.value })}
                />
              </div>
              <div>
                <Label><TranslatedText text="Min Stay (nights)" /> *</Label>
                <Input
                  type="number"
                  value={packageForm.minStay}
                  onChange={(e) => setPackageForm({ ...packageForm, minStay: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Max Stay (nights)" /></Label>
                <Input
                  type="number"
                  value={packageForm.maxStay}
                  onChange={(e) => setPackageForm({ ...packageForm, maxStay: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Valid From" /> *</Label>
                <Input
                  type="date"
                  value={packageForm.validFrom}
                  onChange={(e) => setPackageForm({ ...packageForm, validFrom: e.target.value })}
                />
              </div>
              <div>
                <Label><TranslatedText text="Valid To" /> *</Label>
                <Input
                  type="date"
                  value={packageForm.validTo}
                  onChange={(e) => setPackageForm({ ...packageForm, validTo: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={async () => {
              try {
                await apiService.createPackage({
                  companyId,
                  ...packageForm,
                  basePrice: parseFloat(packageForm.basePrice),
                  minStay: parseInt(packageForm.minStay),
                  maxStay: packageForm.maxStay ? parseInt(packageForm.maxStay) : undefined,
                  roomTypeId: packageForm.roomTypeId || undefined,
                });
                toast({ title: 'Success', description: 'Package created successfully' });
                setShowPackageDialog(false);
                setPackageForm({
                  name: '',
                  description: '',
                  roomTypeId: '',
                  basePrice: '',
                  validFrom: '',
                  validTo: '',
                  minStay: '1',
                  maxStay: '',
                  isActive: true,
                });
                loadPackages();
              } catch (error: any) {
                toast({ title: 'Error', description: error.message || 'Failed to create package', variant: 'destructive' });
              }
            }}>
              <TranslatedText text="Create Package" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
      {/* Create Amenity Dialog */}
      <Dialog open={showAmenityDialog} onOpenChange={setShowAmenityDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Amenity</DialogTitle>
            <DialogDescription>Create a new amenity that can be assigned to rooms</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name(s) *</Label>
              <Input
                value={amenityForm.name}
                onChange={(e) => setAmenityForm({ ...amenityForm, name: e.target.value })}
                placeholder="e.g., WiFi, Air Conditioning, TV (separate multiple with commas)"
                className="border border-gray-300"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter multiple amenities separated by commas to create them all at once
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={amenityForm.description}
                onChange={(e) => setAmenityForm({ ...amenityForm, description: e.target.value })}
                placeholder="Optional description"
                className="border border-gray-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Input
                  value={amenityForm.icon}
                  onChange={(e) => setAmenityForm({ ...amenityForm, icon: e.target.value })}
                  placeholder="e.g., wifi, tv, ac"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Input
                  value={amenityForm.category}
                  onChange={(e) => setAmenityForm({ ...amenityForm, category: e.target.value })}
                  placeholder="e.g., room, hotel"
                  className="border border-gray-300"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAmenityDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!amenityForm.name.trim()) {
                  toast({
                    title: 'Error',
                    description: 'Amenity name is required',
                    variant: 'destructive',
                  });
                  return;
                }
                setIsCreatingAmenity(true);
                try {
                  // Split by comma and create multiple amenities
                  const amenityNames = amenityForm.name.split(',').map(name => name.trim()).filter(name => name.length > 0);
                  
                  if (amenityNames.length === 0) {
                    toast({
                      title: 'Error',
                      description: 'Please enter at least one amenity name',
                      variant: 'destructive',
                    });
                    setIsCreatingAmenity(false);
                    return;
                  }

                  // Validate company ID - must be set and not be the fallback 'guest-company'
                  // Also check if companyName is set as a secondary validation (indicates company was successfully loaded)
                  if (!companyId || companyId === 'guest-company' || companyId.trim() === '' || !companyName) {
                    toast({
                      title: 'Error',
                      description: 'Please select a company before creating amenities',
                      variant: 'destructive',
                    });
                    setIsCreatingAmenity(false);
                    return;
                  }

                  // Create all amenities
                  const createPromises = amenityNames.map(name => 
                    apiService.createHotelAmenity({
                      companyId,
                      name,
                      description: amenityForm.description || undefined,
                      icon: amenityForm.icon || undefined,
                      category: amenityForm.category || undefined,
                    })
                  );

                  await Promise.all(createPromises);
                  await loadAmenities();
                  setShowAmenityDialog(false);
                  setAmenityForm({ name: '', description: '', icon: '', category: '' });
                  toast({
                    title: 'Success',
                    description: `${amenityNames.length} amenit${amenityNames.length === 1 ? 'y' : 'ies'} created successfully`,
                  });
                } catch (error: any) {
                  toast({
                    title: 'Error',
                    description: error.message || 'Failed to create amenities',
                    variant: 'destructive',
                  });
                } finally {
                  setIsCreatingAmenity(false);
                }
              }}
              disabled={isCreatingAmenity || !companyId || companyId === 'guest-company' || !companyName}
            >
              {isCreatingAmenity ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Creating...
                </>
              ) : (
                'Create Amenity'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Room Type Dialog */}
      <Dialog open={showRoomTypeDialog} onOpenChange={setShowRoomTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Room Type</DialogTitle>
            <DialogDescription>
              Create different room types like Single, Double, Twin, Suite, Deluxe, Family, etc. 
              You can create as many room types as needed.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={roomTypeForm.name}
                onChange={(e) => setRoomTypeForm({ ...roomTypeForm, name: e.target.value })}
                placeholder="e.g., Single, Double, Twin, Suite, Deluxe, Family, Presidential"
                className="border border-gray-300"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Examples: Single, Double, Twin, Triple, Quad, Suite, Deluxe, Family, Presidential, etc.
              </p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={roomTypeForm.description}
                onChange={(e) => setRoomTypeForm({ ...roomTypeForm, description: e.target.value })}
                placeholder="Optional description"
                className="border border-gray-300"
              />
            </div>
              <div>
                <Label>Max Capacity *</Label>
                <Input
                  type="number"
                  value={roomTypeForm.maxCapacity}
                  onChange={(e) => setRoomTypeForm({ ...roomTypeForm, maxCapacity: e.target.value })}
                  placeholder="2"
                  className="border border-gray-300"
                />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoomTypeDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="default"
              className="!text-white"
              onClick={async () => {
                if (!roomTypeForm.name.trim()) {
                  toast({
                    title: 'Error',
                    description: 'Room type name is required',
                    variant: 'destructive',
                  });
                  return;
                }
                // Validate company ID - must be set and not be the fallback 'guest-company'
                // Also check if companyName is set as a secondary validation (indicates company was successfully loaded)
                if (!companyId || companyId === 'guest-company' || companyId.trim() === '' || !companyName) {
                  toast({
                    title: 'Error',
                    description: 'Please select a company before creating a room type',
                    variant: 'destructive',
                  });
                  return;
                }
                setIsCreatingRoomType(true);
                try {
                  await apiService.createHotelRoomType({
                    companyId,
                    name: roomTypeForm.name,
                    description: roomTypeForm.description || undefined,
                    maxCapacity: parseInt(roomTypeForm.maxCapacity) || 2,
                    basePrice: roomTypeForm.basePrice ? parseFloat(roomTypeForm.basePrice) : undefined,
                  });
                  await loadRoomTypes();
                  setShowRoomTypeDialog(false);
                  setRoomTypeForm({ name: '', description: '', maxCapacity: '2', basePrice: '' });
                  toast({
                    title: 'Success',
                    description: 'Room type created successfully',
                  });
                } catch (error: any) {
                  toast({
                    title: 'Error',
                    description: error.message || 'Failed to create room type',
                    variant: 'destructive',
                  });
                } finally {
                  setIsCreatingRoomType(false);
                }
              }}
              disabled={isCreatingRoomType || !companyId || companyId === 'guest-company' || !companyName}
            >
              {isCreatingRoomType ? (
                <>
                  <MoonLoader size="sm" className="mr-2" color="teal" />
                  Creating...
                </>
              ) : (
                'Create Room Type'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialogConfig?.title || 'Confirm Action'}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialogConfig?.description || 'Are you sure you want to proceed?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirmDialog(false)}>
              {confirmDialogConfig?.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialogConfig?.onConfirm || (() => setShowConfirmDialog(false))}
              className={confirmDialogConfig?.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''}
            >
              {confirmDialogConfig?.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Conference Room Dialog */}
      <Dialog open={showConferenceRoomDialog} onOpenChange={setShowConferenceRoomDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedConferenceRoom ? <TranslatedText text="Edit Conference Room" /> : <TranslatedText text="New Conference Room" />}
            </DialogTitle>
            <DialogDescription>
              {selectedConferenceRoom ? <TranslatedText text="Update conference room details" /> : <TranslatedText text="Create a new conference room" />}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Name" /> *</Label>
                <Input
                  value={conferenceRoomForm.name}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, name: e.target.value })}
                  placeholder="Conference Room A"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Room Number" /></Label>
                <Input
                  value={conferenceRoomForm.roomNumber}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, roomNumber: e.target.value })}
                  placeholder="CR-101"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label><TranslatedText text="Capacity" /> *</Label>
                <Input
                  type="number"
                  value={conferenceRoomForm.capacity}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, capacity: e.target.value })}
                  placeholder="10"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Floor" /></Label>
                <Input
                  type="number"
                  value={conferenceRoomForm.floor}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, floor: e.target.value })}
                  placeholder="1"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Size" /></Label>
                <Input
                  value={conferenceRoomForm.size}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, size: e.target.value })}
                  placeholder="500 sq ft"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Hourly Rate" /></Label>
                <Input
                  type="number"
                  value={conferenceRoomForm.hourlyRate}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, hourlyRate: e.target.value })}
                  placeholder="5000"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Daily Rate" /></Label>
                <Input
                  type="number"
                  value={conferenceRoomForm.dailyRate}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, dailyRate: e.target.value })}
                  placeholder="50000"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Amenities" /></Label>
              <Input
                value={conferenceRoomForm.amenities}
                onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, amenities: e.target.value })}
                placeholder="Projector, Whiteboard, WiFi"
                className="border border-gray-300"
              />
            </div>
            <div>
              <Label><TranslatedText text="Description" /></Label>
              <Textarea
                value={conferenceRoomForm.description}
                onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, description: e.target.value })}
                placeholder="Description of the conference room..."
                className="border border-gray-300"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Status" /></Label>
                <Select
                  value={conferenceRoomForm.status}
                  onValueChange={(value) => setConferenceRoomForm({ ...conferenceRoomForm, status: value })}
                >
                  <SelectTrigger className="border border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AVAILABLE"><TranslatedText text="Available" /></SelectItem>
                    <SelectItem value="OCCUPIED"><TranslatedText text="Occupied" /></SelectItem>
                    <SelectItem value="MAINTENANCE"><TranslatedText text="Maintenance" /></SelectItem>
                    <SelectItem value="OUT_OF_ORDER"><TranslatedText text="Out of Order" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-8">
                <input
                  type="checkbox"
                  id="conferenceRoomActive"
                  checked={conferenceRoomForm.isActive}
                  onChange={(e) => setConferenceRoomForm({ ...conferenceRoomForm, isActive: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="conferenceRoomActive"><TranslatedText text="Active" /></Label>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="space-y-2">
              <Label><TranslatedText text="Images" /></Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <Input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleConferenceRoomImageSelect}
                  className="cursor-pointer"
                />
                <p className="text-xs text-gray-500 mt-1">
                  <TranslatedText text="Upload up to 10 images. First image will be the primary image." />
                </p>
              </div>

              {/* New Image Previews */}
              {conferenceRoomImagePreviews.length > 0 && (
                <div className="space-y-2">
                  <Label><TranslatedText text={`Selected Images (${conferenceRoomImagePreviews.length})`} /></Label>
                  <div className="grid grid-cols-4 gap-2">
                    {conferenceRoomImagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border"
                        />
                        {index === 0 && existingConferenceRoomImages.length === 0 && (
                          <Badge className="absolute top-1 left-1 text-xs bg-blue-600">
                            <TranslatedText text="Primary" />
                          </Badge>
                        )}
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleRemoveConferenceRoomImage(index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Existing Images */}
              {selectedConferenceRoom && existingConferenceRoomImages.length > 0 && (
                <div className="space-y-2">
                  <Label><TranslatedText text="Existing Images" /></Label>
                  <div className="grid grid-cols-4 gap-2">
                    {existingConferenceRoomImages.map((image) => {
                      const imageUrl = image.imageUrl?.startsWith('http') 
                        ? image.imageUrl 
                        : `${apiService['baseUrl']}${image.imageUrl}`;
                      
                      return (
                        <div key={image.id} className="relative group">
                          <img
                            src={imageUrl}
                            alt={image.altText || 'Conference room image'}
                            className="w-full h-24 object-cover rounded-lg border cursor-pointer"
                            onClick={() => handleSetPrimaryConferenceRoomImage(selectedConferenceRoom.id, image.id)}
                            title="Click to set as primary"
                            onError={(e) => {
                              console.error('🖼️  [CONFERENCE] ❌ Failed to load image:', imageUrl);
                              // Remove the failed image from the existingConferenceRoomImages state
                              setExistingConferenceRoomImages((prev) => prev.filter(img => img.id !== image.id));
                            }}
                          />
                          {image.isPrimary && (
                            <Badge className="absolute top-1 left-1 text-xs bg-blue-600">
                              <Star className="h-3 w-3 mr-1" />
                              <TranslatedText text="Primary" />
                            </Badge>
                          )}
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleDeleteConferenceRoomImage(selectedConferenceRoom.id, image.id)}
                            disabled={deletingConferenceRoomImageId === image.id}
                          >
                            {deletingConferenceRoomImageId === image.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowConferenceRoomDialog(false);
              setSelectedConferenceRoom(null);
              setConferenceRoomImages([]);
              setConferenceRoomImagePreviews([]);
              setExistingConferenceRoomImages([]);
            }}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={handleCreateConferenceRoom} disabled={isCreatingConferenceRoom || !conferenceRoomForm.name || !conferenceRoomForm.capacity}>
              {isCreatingConferenceRoom || uploadingConferenceRoomImages ? <TranslatedText text="Saving..." /> : (selectedConferenceRoom ? <TranslatedText text="Update" /> : <TranslatedText text="Create" />)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Equipment Dialog */}
      <Dialog open={showEquipmentDialog} onOpenChange={setShowEquipmentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedEquipment ? <TranslatedText text="Edit Equipment" /> : <TranslatedText text="New Equipment" />}
            </DialogTitle>
            <DialogDescription>
              {selectedEquipment ? <TranslatedText text="Update equipment details" /> : <TranslatedText text="Create a new equipment item" />}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Name" /> *</Label>
                <Input
                  value={equipmentForm.name}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, name: e.target.value })}
                  placeholder="Projector"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Equipment Number" /></Label>
                <Input
                  value={equipmentForm.equipmentNumber}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, equipmentNumber: e.target.value })}
                  placeholder="EQ-001"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label><TranslatedText text="Category" /></Label>
                <Input
                  value={equipmentForm.category}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, category: e.target.value })}
                  placeholder="PROJECTOR"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Brand" /></Label>
                <Input
                  value={equipmentForm.brand}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, brand: e.target.value })}
                  placeholder="Sony"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Model" /></Label>
                <Input
                  value={equipmentForm.model}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, model: e.target.value })}
                  placeholder="VPL-FHZ75"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label><TranslatedText text="Hourly Rate" /></Label>
                <Input
                  type="number"
                  value={equipmentForm.hourlyRate}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, hourlyRate: e.target.value })}
                  placeholder="2000"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Daily Rate" /></Label>
                <Input
                  type="number"
                  value={equipmentForm.dailyRate}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, dailyRate: e.target.value })}
                  placeholder="15000"
                  className="border border-gray-300"
                />
              </div>
              <div>
                <Label><TranslatedText text="Quantity" /> *</Label>
                <Input
                  type="number"
                  value={equipmentForm.quantity}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, quantity: e.target.value })}
                  placeholder="1"
                  className="border border-gray-300"
                />
              </div>
            </div>
            <div>
              <Label><TranslatedText text="Description" /></Label>
              <Textarea
                value={equipmentForm.description}
                onChange={(e) => setEquipmentForm({ ...equipmentForm, description: e.target.value })}
                placeholder="Description of the equipment..."
                className="border border-gray-300"
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label><TranslatedText text="Status" /></Label>
                <Select
                  value={equipmentForm.status}
                  onValueChange={(value) => setEquipmentForm({ ...equipmentForm, status: value })}
                >
                  <SelectTrigger className="border border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AVAILABLE"><TranslatedText text="Available" /></SelectItem>
                    <SelectItem value="RENTED"><TranslatedText text="Rented" /></SelectItem>
                    <SelectItem value="MAINTENANCE"><TranslatedText text="Maintenance" /></SelectItem>
                    <SelectItem value="OUT_OF_ORDER"><TranslatedText text="Out of Order" /></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-8">
                <input
                  type="checkbox"
                  id="equipmentActive"
                  checked={equipmentForm.isActive}
                  onChange={(e) => setEquipmentForm({ ...equipmentForm, isActive: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="equipmentActive"><TranslatedText text="Active" /></Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEquipmentDialog(false);
              setSelectedEquipment(null);
            }}>
              <TranslatedText text="Cancel" />
            </Button>
            <Button onClick={handleCreateEquipment} disabled={isCreatingEquipment || !equipmentForm.name || !equipmentForm.quantity}>
              {isCreatingEquipment ? <TranslatedText text="Saving..." /> : (selectedEquipment ? <TranslatedText text="Update" /> : <TranslatedText text="Create" />)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Room Availability Calendar */}
      {showAvailabilityCalendar && selectedRoomForCalendar && (
        <RoomAvailabilityCalendar
          roomId={selectedRoomForCalendar.id}
          roomNumber={selectedRoomForCalendar.roomNumber}
          companyId={companyId!}
          onClose={() => {
            setShowAvailabilityCalendar(false);
            setSelectedRoomForCalendar(null);
            loadRooms(); // Reload rooms to reflect any changes
          }}
        />
      )}

      {/* Customer Loan Selector for Hotel Payments */}
      <CustomerLoanSelector
        open={showLoanCustomerSelector}
        onClose={() => setShowLoanCustomerSelector(false)}
        onSelectCustomer={processLoanPayment}
        companyId={companyId!}
      />
    </PageLayout>
  );
}

